import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import DailyIframe from "@daily-co/daily-js";

// ─── POLLING HOOK ─────────────────────────────────────────────────────────────
// Runs a callback at a fixed interval, pausing when the tab is hidden.
// ─── PILL SCROLL TEXT ─────────────────────────────────────────────────────────
function PillScrollText({ label }) {
  const marqueeRef = useRef(null);
  const needsScroll = label.length > 6;

  useEffect(() => {
    if (!needsScroll || !marqueeRef.current) return;
    const labelW = marqueeRef.current.scrollWidth;
    const windowW = 48;
    const dist = windowW + labelW;
    const duration = dist / 30; // 30px per second
    marqueeRef.current.style.setProperty("--pill-marquee-from", `${windowW}px`);
    marqueeRef.current.style.setProperty("--pill-marquee-to",   `-${labelW}px`);
    marqueeRef.current.style.setProperty("--pill-duration",     `${duration.toFixed(1)}s`);
  }, [label, needsScroll]);

  return (
    <div className={`tab-pill-text${needsScroll ? " scrolling" : ""}`}>
      {needsScroll
        ? <div ref={marqueeRef} className="tab-pill-marquee">{label}</div>
        : <span className="tab-pill-inner">{label}</span>
      }
    </div>
  );
}

function usePolling(callback, intervalMs, active = true) {
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);
  useEffect(() => {
    if (!active) return;
    let timerId = null;
    const tick = () => {
      if (document.visibilityState !== "hidden") cbRef.current();
      timerId = setTimeout(tick, intervalMs);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") cbRef.current();
    };
    timerId = setTimeout(tick, intervalMs);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearTimeout(timerId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs, active]);
}

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return mobile;
};

// ─── PUSH NOTIFICATIONS ───────────────────────────────────────────────────────
// Registers the service worker, fetches the VAPID key, subscribes the device,
// and posts the subscription to the server. Call once on mobile when logged in.
function usePushNotifications(token) {
  useEffect(() => {
    if (!token) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    let swReg = null;

    const setup = async () => {
      try {
        // Register (or reuse) our service worker
        swReg = await navigator.serviceWorker.register("/service-worker.js");

        // Fetch VAPID public key from server
        const keyRes = await fetch(`${API_BASE}/api/push/vapid-public-key`);
        if (!keyRes.ok) return; // push not configured on server yet
        const { key } = await keyRes.json();

        // Check existing subscription
        let sub = await swReg.pushManager.getSubscription();
        if (!sub) {
          // Request permission then subscribe
          const permission = await Notification.requestPermission();
          if (permission !== "granted") return;
          sub = await swReg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(key),
          });
        }

        // Send subscription to server
        await fetch(`${API_BASE}/api/push/subscribe`, {
          method:  "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body:    JSON.stringify(sub.toJSON()),
        });
      } catch (err) {
        console.warn("Push setup failed:", err.message);
      }
    };

    setup();
  }, [token]);
}

// Converts a base64 VAPID key string to a Uint8Array (required by subscribe())
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw     = atob(base64);
  const output  = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";


// ─── STORAGE HELPERS ──────────────────────────────────────────────────────────
const store = {
  async get(key, shared = false) {
    try {
      const r = await window.storage.get(key, shared);
      if (r) return JSON.parse(r.value);
    } catch {}
    if (!shared) {
      try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch {}
    }
    return null;
  },
  async set(key, value, shared = false) {
    const json = JSON.stringify(value);
    try { await window.storage.set(key, json, shared); } catch {}
    try { localStorage.setItem(key, json); } catch {}
  }
};

// ─── API LAYER ────────────────────────────────────────────────────────────────
// Change this to match wherever your server.js is running.
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";

const api = {
  // Read/write JWT token from localStorage so the user stays logged in across sessions
  getToken: ()         => localStorage.getItem("auth_token"),
  setToken: (t)        => localStorage.setItem("auth_token", t),
  clearToken: ()       => localStorage.removeItem("auth_token"),

  async request(method, path, body) {
    const headers = { "Content-Type": "application/json" };
    const token = api.getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body != null ? JSON.stringify(body) : undefined,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  },

  get:    (path)        => api.request("GET",    path, null),
  post:   (path, body)  => api.request("POST",   path, body),
  put:    (path, body)  => api.request("PUT",    path, body),
  delete: (path, body)  => api.request("DELETE", path, body),

  // Auth
  register: (body) => api.post("/api/auth/register", body),
  login:    (body) => api.post("/api/auth/login",    body),
  me:       ()     => api.get("/api/auth/me"),
  deleteAccount: (body) => api.delete("/api/auth/account", body),
  forgotPassword:      (body) => api.post("/api/auth/forgot-password",      body),
  resetPassword:       (body) => api.post("/api/auth/reset-password",       body),
  resendVerification:  (body) => api.post("/api/auth/resend-verification",  body),
  verifyEmail: (token) => fetch(`${API_BASE}/api/auth/verify-email?token=${token}`).then(r => r.json()),

  // Lift logs
  // Workout (new session-based)
  getTodaySession:    ()              => api.get("/api/workout/session/today"),
  addSet:             (sessionId, body) => api.post(`/api/workout/session/${sessionId}/sets`, body),
  deleteSet:          (setId)         => api.delete(`/api/workout/sets/${setId}`),
  getWorkoutHistory:  ()              => api.get("/api/workout/history"),
  getMyPrs:           ()              => api.get("/api/workout/prs"),
  getBodyWeights:     ()              => api.get("/api/bodyweight"),
  addBodyWeight:      (weight, loggedAt) => api.post("/api/bodyweight", { weight, loggedAt }),
  deleteBodyWeight:   (id)            => api.delete(`/api/bodyweight/${id}`),
  getCommunityPrs:    ()              => api.get("/api/community/prs"),
  uploadAvatar:       (file)          => {
    const fd = new FormData(); fd.append("file", file);
    return fetch(`${API_BASE}/api/profile/avatar`, { method:"POST", headers:{ Authorization:`Bearer ${api.getToken()}` }, body:fd }).then(r=>r.json());
  },
  deleteAvatar:       ()              => api.delete("/api/profile/avatar"),
  getChatAlias:       ()              => api.get("/api/profile/chat-alias"),
  updateChatAliasName: (name)         => api.put("/api/profile/chat-alias", { name }),
  uploadChatAliasAvatar: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return fetch(`${API_BASE}/api/profile/chat-alias/avatar`, { method:"POST", headers:{ Authorization:`Bearer ${api.getToken()}` }, body:fd }).then(r=>r.json());
  },
  deleteChatAliasAvatar: ()           => api.delete("/api/profile/chat-alias/avatar"),
  // Meetings
  getMeetings:        ()              => api.get("/api/meetings"),
  createMeeting:      (body)          => api.post("/api/meetings", body),
  rsvpMeeting:        (id, status)    => api.request("PATCH", `/api/meetings/${id}/rsvp`, { status }),
  cancelMeeting:      (id)            => api.delete(`/api/meetings/${id}`),
  getMeetingToken:    (id)            => api.post(`/api/meetings/${id}/token`, {}),
  leaveMeeting:       (id)            => api.post(`/api/meetings/${id}/leave`, {}),

  getMessages:   (since, before) => api.get(
    since  ? `/api/board/messages?since=${since}` :
    before ? `/api/board/messages?before=${before}&limit=${CHAT_PAGE_SIZE}` :
             `/api/board/messages?limit=${CHAT_PAGE_SIZE}`),
  postMessage:   (body) => api.post("/api/board/messages", body),
  postReaction:  (body) => api.post("/api/board/reactions", body),
  deleteMessage: (id)   => api.delete(`/api/board/messages/${id}`),

  // Admin
  getAdminUsers:  ()          => api.get("/api/admin/users"),
  adminDeleteUser:(id)         => api.delete(`/api/admin/users/${id}`),
  adminSetRole:   (id, role)   => api.post(`/api/admin/users/${id}/role`, { role }),
  adminApproveUser:(id)        => api.post(`/api/admin/users/${id}/approve`, {}),

  // Chapters
  getChapters:          ()              => api.get("/api/chapters"),
  createChapter:        (body)          => api.post("/api/chapters", body),
  editChapter:          (id, body)      => api.request("PATCH", `/api/chapters/${id}`, body),
  deactivateChapter:    (id)            => api.delete(`/api/chapters/${id}`),
  assignChapterAdmin:   (id, userId)    => api.post(`/api/chapters/${id}/admin`, { userId }),
  getMyMembership:      ()              => api.get("/api/chapters/my"),
  joinChapter:          (id)            => api.post(`/api/chapters/${id}/join`, {}),
  getChapterRequests:   (id)            => api.get(`/api/chapters/${id}/requests`),
  resolveChapterRequest:(id, userId, action) => api.request("PATCH", `/api/chapters/${id}/requests/${userId}`, { action }),
  removeMember:         (id, userId)    => api.delete(`/api/chapters/${id}/members/${userId}`),
  leaveChapter:         (id)            => api.delete(`/api/chapters/${id}/leave`),
  getChapterStats:      (id)            => api.get(`/api/chapters/${id}/stats`),
  getChapterCommunityUsers: (id)        => api.get(`/api/chapters/${id}/community-users`),
  getChapterMessages:   (id, since, before) => api.get(
    since  ? `/api/chapters/${id}/messages?since=${since}` :
    before ? `/api/chapters/${id}/messages?before=${before}&limit=${CHAT_PAGE_SIZE}` :
             `/api/chapters/${id}/messages?limit=${CHAT_PAGE_SIZE}`),
  postChapterMessage:   (id, body)      => api.post(`/api/chapters/${id}/messages`, body),
};

// ─── SEED DATA ────────────────────────────────────────────────────────────────
const EMOJI_CATEGORIES = [
  // ── Smileys & People ──────────────────────────────────────────────────────
  // Faces
  { label:"😀", emojis:[
    "😀","😃","😄","😁","😆","😅","🤣","😂","🙂","🙃","🫠","😉","😊","😇",
    "🥰","😍","🤩","😘","😗","☺️","😚","😙","🥲","😋","😛","😜","🤪","😝","🤑",
    "🤗","🤭","🫢","🫣","🤫","🤔","🫡","🤐","🤨","😐","😑","😶","🫥","😶‍🌫️",
    "😏","😒","🙄","😬","😮‍💨","🤥","🫨","🙂‍↔️","🙂‍↕️",
    "😌","😔","😪","🤤","😴","🥱",
    "😷","🤒","🤕","🤢","🤧","🥵","🥶","🥴","😵","😵‍💫","🤯",
    "🤠","🥳","🥸","😎","🤓","🧐",
    "😕","🫤","😟","🙁","☹️","😮","😯","😲","😳","🥺","🥹",
    "😦","😧","😨","😰","😥","😢","😭","😱","😖","😣","😞","😓","😩","😫",
    "😤","😠","😡","🤬","😈","👿","💀","☠️","💩","🤡","👹","👺","👻","👽","👾","🤖",
    "😺","😸","😹","😻","😼","😽","🙀","😿","😾",
    // Hand gestures & body
    "👋","🤚","🖐️","✋","🖖","🫱","🫲","🫳","🫴","🫷","🫸",
    "👌","🤌","🤏","✌️","🤞","🫰","🤟","🤘","🤙",
    "👈","👉","👆","🖕","👇","☝️","🫵",
    "👍","👎","✊","👊","🤛","🤜",
    "👏","🫶","🙌","👐","🤲","🤝","🙏",
    "✍️","💅","🤳","💪","🦾","🦿",
    "🦵","🦶","👂","🦻","👃",
    "🫀","🫁","🧠","🦷","🦴","👀","👁️","👅","👄","🫦","🫂",
    // People
    "👶","🧒","👦","👧","🧑","👱","👨","🧔","🧔‍♂️","🧔‍♀️",
    "👨‍🦰","👨‍🦱","👨‍🦳","👨‍🦲",
    "👩","👩‍🦰","🧑‍🦰","👩‍🦱","🧑‍🦱","👩‍🦳","🧑‍🦳","👩‍🦲","🧑‍🦲",
    "👱‍♀️","👱‍♂️","🧓","👴","👵",
    "🙍","🙍‍♂️","🙍‍♀️","🙎","🙎‍♂️","🙎‍♀️",
    "🙅","🙅‍♂️","🙅‍♀️","🙆","🙆‍♂️","🙆‍♀️",
    "💁","💁‍♂️","💁‍♀️","🙋","🙋‍♂️","🙋‍♀️",
    "🧏","🧏‍♂️","🧏‍♀️","🙇","🙇‍♂️","🙇‍♀️",
    "🤦","🤦‍♂️","🤦‍♀️","🤷","🤷‍♂️","🤷‍♀️",
    // People with roles
    "👮","👮‍♂️","👮‍♀️","🕵️","🕵️‍♂️","🕵️‍♀️",
    "💂","💂‍♂️","💂‍♀️","🥷","👷","👷‍♂️","👷‍♀️",
    "🫅","🤴","👸","👳","👳‍♂️","👳‍♀️","👲","🧕",
    "🤵","🤵‍♂️","🤵‍♀️","👰","👰‍♂️","👰‍♀️",
    "🤰","🫃","🫄","🤱","👩‍🍼","👨‍🍼","🧑‍🍼",
    "👼","🎅","🤶","🧑‍🎄",
    "🦸","🦸‍♂️","🦸‍♀️","🦹","🦹‍♂️","🦹‍♀️",
    "🧙","🧙‍♂️","🧙‍♀️","🧚","🧚‍♂️","🧚‍♀️",
    "🧛","🧛‍♂️","🧛‍♀️","🧜","🧜‍♂️","🧜‍♀️",
    "🧝","🧝‍♂️","🧝‍♀️","🧞","🧞‍♂️","🧞‍♀️","🧟","🧟‍♂️","🧟‍♀️","🧌",
    // People activities
    "💆","💆‍♂️","💆‍♀️","💇","💇‍♂️","💇‍♀️",
    "🚶","🚶‍♂️","🚶‍♀️","🚶‍♂️‍➡️","🚶‍➡️","🚶‍♀️‍➡️",
    "🧍","🧍‍♂️","🧍‍♀️","🧎","🧎‍♂️","🧎‍♀️","🧎‍♂️‍➡️","🧎‍➡️","🧎‍♀️‍➡️",
    "🧑‍🦯","👨‍🦯","👩‍🦯","🧑‍🦯‍➡️","👨‍🦯‍➡️","👩‍🦯‍➡️",
    "🧑‍🦼","👨‍🦼","👩‍🦼","🧑‍🦼‍➡️","👨‍🦼‍➡️","👩‍🦼‍➡️",
    "🧑‍🦽","👨‍🦽","👩‍🦽","🧑‍🦽‍➡️","👨‍🦽‍➡️","👩‍🦽‍➡️",
    "🏃","🏃‍♂️","🏃‍♀️","🏃‍♂️‍➡️","🏃‍➡️","🏃‍♀️‍➡️",
    "💃","🕺","🕴️","👯","👯‍♂️","👯‍♀️",
    "🧖","🧖‍♂️","🧖‍♀️",
    // Families & couples
    "👫","👬","👭","💏","👩‍❤️‍💋‍👨","👨‍❤️‍💋‍👨","👩‍❤️‍💋‍👩",
    "💑","👩‍❤️‍👨","👨‍❤️‍👨","👩‍❤️‍👩",
    "👪","👨‍👩‍👦","👨‍👩‍👧","👨‍👩‍👧‍👦","👨‍👩‍👦‍👦","👨‍👩‍👧‍👧",
    "👨‍👦","👨‍👦‍👦","👨‍👧","👨‍👧‍👦","👨‍👧‍👧",
    "👩‍👦","👩‍👦‍👦","👩‍👧","👩‍👧‍👦","👩‍👧‍👧",
  ]},
  // ── Animals & Nature ──────────────────────────────────────────────────────
  { label:"🐶", emojis:[
    // Animals – mammals
    "🐵","🐒","🦍","🦧","🐶","🐕","🦮","🐕‍🦺","🐩","🐺","🦊","🦝",
    "🐱","🐈","🐈‍⬛","🪶","🐓","🦃","🦤","🦚","🦜","🦢","🦩","🕊️",
    "🐇","🦝","🦨","🦡","🦫","🦦","🦥","🐁","🐀","🐿️","🦔",
    "🐾","🐉","🐲",
    "🐻","🐼","🐨","🐯","🦁","🐮","🐷","🐽","🐸","🐵",
    "🙈","🙉","🙊","🐔","🐧","🐦","🐦‍🔥","🐤","🐣","🐥","🦆","🦅","🦉","🦇",
    "🐺","🐗","🐴","🦄","🫎","🫏","🐝","🪱","🐛","🦋","🐌","🐞","🐜","🪲",
    "🦟","🦗","🪳","🕷️","🦂","🐢","🐍","🦎","🦖","🦕",
    "🐙","🦑","🦐","🦞","🦀","🪸","🐡","🐠","🐟","🐬","🐳","🐋","🦈","🦭",
    "🐊","🐅","🐆","🦓","🦍","🦧","🦣","🐘","🦛","🦏",
    "🐪","🐫","🦒","🦘","🦬","🐃","🐂","🐄","🐎","🐖","🐏","🐑","🦙","🐐","🦌",
    "🐕","🐩","🦮","🐕‍🦺","🐈","🐈‍⬛",
    // Plants & nature
    "🌵","🎄","🌲","🌳","🌴","🪵","🌱","🌿","☘️","🍀","🎍","🪴","🎋",
    "🍃","🍂","🍁","🪺","🪹","🪸","🍄","🍄‍🟫","🐚","🪨","🌾","💐",
    "🌷","🪷","🌹","🥀","🌺","🌸","🌼","🌻",
    // Sky & weather
    "🌞","🌝","🌛","🌜","🌚","🌕","🌖","🌗","🌘","🌑","🌒","🌓","🌔",
    "🌙","🌟","⭐","🌠","🌌","☀️","🌤️","⛅","🌥️","☁️","🌦️","🌧️",
    "⛈️","🌩️","🌨️","❄️","☃️","⛄","🌬️","🌀","🌈","🌂","☂️","☔","⛱️",
    "⚡","🔥","💧","💦","🫧","🌊","🌫️","💨","🌪️","🌡️","☄️",
  ]},
  // ── Food & Drink ──────────────────────────────────────────────────────────
  { label:"🍎", emojis:[
    // Fruit
    "🍇","🍈","🍉","🍊","🍋","🍋‍🟩","🍌","🍍","🥭","🍎","🍏","🍐","🍑","🍒",
    "🍓","🫐","🥝","🍅","🫒","🥥",
    // Vegetables
    "🥑","🍆","🥔","🍠","🫚","🫛","🥐","🥯","🍞","🥖","🥨","🧀","🥚","🍳",
    "🧈","🥞","🧇","🥓","🥩","🍗","🍖","🦴","🌭","🍔","🍟","🍕",
    "🫓","🥙","🧆","🌮","🌯","🫔","🥗","🥘","🫕","🍝","🍜","🍲","🍛",
    "🍣","🍱","🥟","🦪","🍤","🍙","🍘","🍥","🥮","🍢",
    "🧂","🥫",
    // Sweets & snacks
    "🍱","🍘","🍙","🍚","🍛","🍜","🍝","🍞","🍟","🍠",
    "🧁","🍰","🎂","🍮","🍭","🍬","🍫","🍿","🍩","🍪","🌰","🥜","🍯",
    // Drinks
    "🧃","🥤","🧋","🍵","☕","🫖","🍺","🍻","🥂","🍷","🥃","🍸","🍹","🧉","🍾","🧊",
    // Utensils
    "🥄","🍴","🍽️","🥣","🥡","🥢",
  ]},
  // ── Activity ──────────────────────────────────────────────────────────────
  { label:"⚽", emojis:[
    // Sports
    "⚽","🏀","🏈","⚾","🥎","🎾","🏐","🏉","🥏","🎱","🪀","🏓","🏸",
    "🏒","🏑","🥍","🏏","🪃","🥅","⛳","🪁","🛝","🎣","🤿","🎽","🎿","🛷","🥌",
    // Sports people
    "🧗","🧗‍♂️","🧗‍♀️","🤺","🏇","⛷️","🏂",
    "🏌️","🏌️‍♂️","🏌️‍♀️","🏄","🏄‍♂️","🏄‍♀️",
    "🚣","🚣‍♂️","🚣‍♀️","🧘","🧘‍♂️","🧘‍♀️",
    "🏊","🏊‍♂️","🏊‍♀️","⛹️","⛹️‍♂️","⛹️‍♀️",
    "🏋️","🏋️‍♂️","🏋️‍♀️","🚴","🚴‍♂️","🚴‍♀️","🚵","🚵‍♂️","🚵‍♀️",
    // Games & awards
    "🎯","🎳","🏆","🥇","🥈","🥉","🏅","🎖️","🏵️","🎗️","🎫","🎟️",
    // Arts & entertainment
    "🎪","🤹","🤹‍♂️","🤹‍♀️","🎭","🩰","🎨","🖼️","🎬","🎤","🎧","🎼",
    "🎹","🥁","🪘","🎷","🪗","🎺","🎸","🪕","🎻","🪈",
    // Games
    "🎲","♟️","🎮","🕹️","🧩","🪆","🪅","🎠","🎡","🎢",
  ]},
  // ── Travel & Places ───────────────────────────────────────────────────────
  { label:"✈️", emojis:[
    // Ground transport
    "🚗","🚕","🚙","🚌","🚎","🏎️","🚓","🚑","🚒","🚐","🛻","🚚","🚛","🚜",
    "🏍️","🛵","🛺","🚲","🛴","🛹","🛼","🚏","🛣️","🛤️","🛞","⛽",
    "🚨","🚥","🚦","🛑","🚧",
    // Water transport
    "⚓","🪝","⛵","🚤","🛥️","🛳️","⛴️","🚢",
    // Air & space
    "✈️","🛩️","🛫","🛬","🪂","💺","🚁","🚟","🚠","🚡","🛰️","🚀","🛸",
    // Rail
    "🚂","🚃","🚄","🚅","🚆","🚇","🚈","🚉","🚊","🚝","🚞","🚋",
    // Globe & map
    "🌍","🌎","🌏","🌐","🗺️","🧭","🪐","💫","⭐","🌟",
    // Places & landmarks
    "🏔️","⛰️","🌋","🗻","🏕️","🏖️","🏜️","🏝️","🏞️","🏟️","🏛️","🏗️",
    "🧱","🪨","🪵","🛖","🏘️","🏚️","🏠","🏡","🏢","🏣","🏤","🏥","🏦",
    "🏨","🏩","🏪","🏫","🏬","🏭","🏯","🏰","💒","🗼","🗽","⛪",
    "🕌","🛕","🕍","⛩️","🕋","⛲","⛺",
    // Scenes
    "🌁","🌃","🏙️","🌄","🌅","🌆","🌇","🌉","🌌","🌠","🎇","🎆","🗾",
  ]},
  // ── Objects ───────────────────────────────────────────────────────────────
  { label:"💡", emojis:[
    // Clothing & accessories (Apple puts these in Objects)
    "👓","🕶️","🥽","🌂","☂️",
    "👔","👕","👖","🧣","🧤","🧥","🥼","🦺","👗","👘","🥻","🩱","👙","🩲","🩳",
    "👚","🩴","👠","👡","🥿","👢","👞","👟","🥾","🧢","👒","🎩","🪖","⛑️",
    "💄","💍","💼","👛","👜","🎒","🧳",
    // Music & sound
    "🔇","🔈","🔉","🔊","📢","📣","📯","🔔","🔕",
    "🎵","🎶","🎙️","🎚️","🎛️","📻",
    // Tech
    "📱","📲","☎️","📞","📟","📠","🔋","🪫","🔌","💻","🖥️","🖨️","⌨️",
    "🖱️","🖲️","💽","💾","💿","📀","📼","📷","📸","📹","🎥","📽️","🎞️",
    "📺","📡","🔭","🔬",
    // Time
    "⏱️","⏲️","⏰","🕰️","⌛","⏳","🧭",
    // Light & tools
    "💡","🔦","🕯️","🪔","🧯","🛢️",
    "💰","💴","💵","💶","💷","💸","💳","🪙","💹",
    // Books & writing
    "📈","📉","📊","📋","📌","📍","✂️","🗂️","📁","📂","🗒️","🗓️",
    "📆","📅","📇","📃","📄","📑","🗃️","🗄️","🗑️",
    "📓","📔","📒","📕","📗","📘","📙","📚","📖","🔖","🏷️",
    "✉️","📧","📨","📩","📪","📫","📬","📭","📮","🗳️","📦","📯",
    "📝","✏️","✒️","🖊️","🖋️","🖌️","🖍️","📏","📐",
    // Locks & tools
    "🔑","🗝️","🔒","🔓","🔏","🔐",
    "🔨","🪓","⛏️","⚒️","🛠️","🗡️","⚔️","🔫","🪃","🛡️","🪚","🔧","🪛",
    "🔩","⚙️","🗜️","⚖️","🦯","🔗","⛓️","🪝","🧲","🪜","🧰","🪤",
    // Science & medical
    "⚗️","🔭","🔬","🩺","🩻","💉","🩸","💊","🩹","🩼",
    // Other objects
    "🚬","⚰️","🪦","⚱️","🗿","🪬","🧿","🪆","🪅",
    "💎","🔮","🪄","💈","🧸",
    "🪞","🪟","🛋️","🪑","🛏️","🚪","🪠","🧴","🧷","🧹","🧺","🪣",
    "🧻","🧼","🫧","🪥","🧽","🪒","🛒","🛍️",
  ]},
  // ── Symbols ───────────────────────────────────────────────────────────────
  { label:"❤️", emojis:[
    // Hearts
    "❤️","🧡","💛","💚","💙","🩵","💜","🖤","🩶","🤍","🤎","💔",
    "❤️‍🔥","❤️‍🩹","❣️","💕","💞","💓","💗","💖","💘","💝","💟",
    // Religious & zodiac
    "☮️","✝️","☯️","🕉️","✡️","🔯","🛐","⛎",
    "♈","♉","♊","♋","♌","♍","♎","♏","♐","♑","♒","♓",
    // Misc symbols
    "🆔","⚛️","🉑","☢️","☣️","📴","📳","🈶","🈚","🈸","🈺","🈷️","✴️",
    "🆚","💮","🉐","㊙️","㊗️","🈵","🈹","🈲",
    "🅰️","🅱️","🆎","🆑","🅾️","🆘",
    "❌","⭕","🛑","⛔","📛","🚫","💯","💢","♨️",
    "🚷","🚯","🚳","🚱","🔞","📵","🚭",
    "❗","❕","❓","❔","‼️","⁉️","🔅","🔆","〽️","⚠️","🚸",
    "🔱","⚜️","🔰","♻️","✅","🈯","💹","❎","🌐","💠","Ⓜ️","🌀","💤",
    "🏧","🚾","♿","🅿️","🛗","🈳","🈂️","🛂","🛃","🛄","🛅",
    "🚹","🚺","🚼","⚧️","🚻","🚮","🎦","📶","🈁","🔣","ℹ️",
    "🔤","🔡","🔠","🆖","🆗","🆙","🆒","🆕","🆓",
    "0️⃣","1️⃣","2️⃣","3️⃣","4️⃣","5️⃣","6️⃣","7️⃣","8️⃣","9️⃣","🔟","🔢","#️⃣","*️⃣",
    "⏏️","▶️","⏸️","⏹️","⏺️","⏭️","⏮️","⏩","⏪","⏫","⏬","◀️","🔼","🔽",
    "➡️","⬅️","⬆️","⬇️","↗️","↘️","↙️","↖️","↕️","↔️","↪️","↩️","⤴️","⤵️",
    "🔀","🔁","🔂","🔄","🔃",
    "➕","➖","➗","✖️","🟰","♾️","💲","💱","™️","©️","®️","〰️","➰","➿",
    "🔚","🔙","🔛","🔝","🔜","✔️","☑️",
    "🔘","🔴","🟠","🟡","🟢","🔵","🟣","⚫","⚪","🟤",
    "🔺","🔻","🔷","🔶","🔹","🔸","🔳","🔲",
    "▪️","▫️","◾","◽","◼️","◻️","🟥","🟧","🟨","🟩","🟦","🟪","⬛","⬜","🟫",
    "💬","💭","🗯️","💤","♠️","♣️","♥️","♦️","🃏","🀄","🎴",
  ]},
  // ── Flags ─────────────────────────────────────────────────────────────────
  { label:"🏁", emojis:[
    "🏁","🚩","🎌","🏴","🏳️","🏳️‍🌈","🏳️‍⚧️","🏴‍☠️",
    "🇦🇨","🇦🇩","🇦🇪","🇦🇫","🇦🇬","🇦🇮","🇦🇱","🇦🇲","🇦🇴","🇦🇶","🇦🇷","🇦🇸","🇦🇹","🇦🇺","🇦🇼","🇦🇽","🇦🇿",
    "🇧🇦","🇧🇧","🇧🇩","🇧🇪","🇧🇫","🇧🇬","🇧🇭","🇧🇮","🇧🇯","🇧🇱","🇧🇲","🇧🇳","🇧🇴","🇧🇶","🇧🇷","🇧🇸","🇧🇹","🇧🇻","🇧🇼","🇧🇾","🇧🇿",
    "🇨🇦","🇨🇨","🇨🇩","🇨🇫","🇨🇬","🇨🇭","🇨🇮","🇨🇰","🇨🇱","🇨🇲","🇨🇳","🇨🇴","🇨🇵","🇨🇷","🇨🇺","🇨🇻","🇨🇼","🇨🇽","🇨🇾","🇨🇿",
    "🇩🇪","🇩🇬","🇩🇯","🇩🇰","🇩🇲","🇩🇴","🇩🇿",
    "🇪🇦","🇪🇨","🇪🇪","🇪🇬","🇪🇭","🇪🇷","🇪🇸","🇪🇹","🇪🇺",
    "🇫🇮","🇫🇯","🇫🇰","🇫🇲","🇫🇴","🇫🇷",
    "🇬🇦","🇬🇧","🇬🇩","🇬🇪","🇬🇫","🇬🇬","🇬🇭","🇬🇮","🇬🇱","🇬🇲","🇬🇳","🇬🇵","🇬🇶","🇬🇷","🇬🇸","🇬🇹","🇬🇺","🇬🇼","🇬🇾",
    "🇭🇰","🇭🇲","🇭🇳","🇭🇷","🇭🇹","🇭🇺",
    "🇮🇨","🇮🇩","🇮🇪","🇮🇱","🇮🇲","🇮🇳","🇮🇴","🇮🇶","🇮🇷","🇮🇸","🇮🇹",
    "🇯🇪","🇯🇲","🇯🇴","🇯🇵",
    "🇰🇪","🇰🇬","🇰🇭","🇰🇮","🇰🇲","🇰🇳","🇰🇵","🇰🇷","🇰🇼","🇰🇾","🇰🇿",
    "🇱🇦","🇱🇧","🇱🇨","🇱🇮","🇱🇰","🇱🇷","🇱🇸","🇱🇹","🇱🇺","🇱🇻","🇱🇾",
    "🇲🇦","🇲🇨","🇲🇩","🇲🇪","🇲🇫","🇲🇬","🇲🇭","🇲🇰","🇲🇱","🇲🇲","🇲🇳","🇲🇴","🇲🇵","🇲🇶","🇲🇷","🇲🇸","🇲🇹","🇲🇺","🇲🇻","🇲🇼","🇲🇽","🇲🇾","🇲🇿",
    "🇳🇦","🇳🇨","🇳🇪","🇳🇫","🇳🇬","🇳🇮","🇳🇱","🇳🇴","🇳🇵","🇳🇷","🇳🇺","🇳🇿",
    "🇴🇲",
    "🇵🇦","🇵🇪","🇵🇫","🇵🇬","🇵🇭","🇵🇰","🇵🇱","🇵🇲","🇵🇳","🇵🇷","🇵🇸","🇵🇹","🇵🇼","🇵🇾",
    "🇶🇦",
    "🇷🇪","🇷🇴","🇷🇸","🇷🇺","🇷🇼",
    "🇸🇦","🇸🇧","🇸🇨","🇸🇩","🇸🇪","🇸🇬","🇸🇭","🇸🇮","🇸🇯","🇸🇰","🇸🇱","🇸🇲","🇸🇳","🇸🇴","🇸🇷","🇸🇸","🇸🇹","🇸🇻","🇸🇽","🇸🇾","🇸🇿",
    "🇹🇦","🇹🇨","🇹🇩","🇹🇫","🇹🇬","🇹🇭","🇹🇯","🇹🇰","🇹🇱","🇹🇲","🇹🇳","🇹🇴","🇹🇷","🇹🇹","🇹🇻","🇹🇼","🇹🇿",
    "🇺🇦","🇺🇬","🇺🇲","🇺🇳","🇺🇸","🇺🇾","🇺🇿",
    "🇻🇦","🇻🇨","🇻🇪","🇻🇬","🇻🇮","🇻🇳","🇻🇺",
    "🇼🇫","🇼🇸","🇽🇰",
    "🇾🇪","🇾🇹","🇿🇦","🇿🇲","🇿🇼",
    "🏴󠁧󠁢󠁥󠁮󠁧󠁿","🏴󠁧󠁢󠁳󠁣󠁴󠁿","🏴󠁧󠁢󠁷󠁬󠁳󠁿",
  ]},
];
const EMOJI_REACTIONS = ["👍","💪","🔥","❤️","😂","🎯","👏","🤯"];

// SVG icon paths for each emoji category tab (same order as EMOJI_CATEGORIES)
const EMOJI_CAT_ICONS = [
  // Smileys & People — smiley face
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><path d="M8.5 14s1 2 3.5 2 3.5-2 3.5-2"/><circle cx="9.5" cy="10" r="1" fill="currentColor" stroke="none"/><circle cx="14.5" cy="10" r="1" fill="currentColor" stroke="none"/>
  </svg>`,
  // Animals & Nature — paw print
  `<svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <ellipse cx="6" cy="7.5" rx="2" ry="2.8"/>
    <ellipse cx="11" cy="5" rx="2" ry="2.8"/>
    <ellipse cx="16" cy="5" rx="2" ry="2.8"/>
    <ellipse cx="20.5" cy="7.5" rx="2" ry="2.8"/>
    <path d="M13.5 10.5c-2.5-1.5-6.5-.5-7.5 3s1 6 3.5 7.5c1 .6 2 .5 3 0 .5-.3 1-.3 1.5 0 1 .5 2 .6 3 0 2.5-1.5 4.5-4.5 3.5-7.5s-4.5-4.5-7-3z"/>
  </svg>`,
  // Food & Drink — fork & knife
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 2v7c0 1.7 1.3 3 3 3s3-1.3 3-3V2"/><path d="M6 2v20"/><path d="M21 2v20"/><path d="M21 7c0-2.8-2-5-2-5v9h4V2s-2 2.2-2 5z"/>
  </svg>`,
  // Activity — trophy
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2h12v10a6 6 0 0 1-12 0V2z"/><path d="M6 7H3a2 2 0 0 0 0 4h3"/><path d="M18 7h3a2 2 0 0 1 0 4h-3"/><path d="M9 22h6"/><path d="M12 17v5"/>
  </svg>`,
  // Travel & Places — airplane
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.8 19.2L16 11l3.5-3.5A2 2 0 0 0 17 4.5L13.5 8 5 6l-1.5 1.5 7 3.5L8 13H5l-1 2 4 1 1 4 2-1V16l3.5-2.5 3.5 7L17.8 19.2z"/>
  </svg>`,
  // Objects — light bulb
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a7 7 0 0 1 7 7c0 2.4-1.2 4.5-3 5.7V17H8v-2.3A7 7 0 0 1 5 9a7 7 0 0 1 7-7z"/><path d="M9 21h6"/><path d="M10 17v4"/><path d="M14 17v4"/>
  </svg>`,
  // Symbols — heart
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/>
  </svg>`,
  // Flags — flag
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
  </svg>`,
];

// Searchable name index — [emoji, "space separated search terms"]
// Covers the most commonly searched emoji names
const EMOJI_NAMES = [
  ["😀","grinning face happy smile"],["😃","big smile happy"],["😄","grin smile happy"],["😁","beaming smile grin"],["😆","laughing grinning squinting"],["😅","sweat smile nervous laugh"],["🤣","rolling floor laughing rofl"],["😂","tears joy laugh crying"],["🙂","slightly smiling face"],["🙃","upside down face silly"],["🫠","melting face dissolve"],["😉","winking face"],["😊","blush smile happy"],["😇","angel halo innocent"],["🥰","smiling hearts love"],["😍","heart eyes love"],["🤩","star struck excited"],["😘","kiss face love"],["😗","kissing face"],["😚","kissing closed eyes"],["😙","kissing smiling eyes"],["🥲","smiling tear happy cry"],["😋","yum face tongue"],["😛","tongue face"],["😜","winking tongue face"],["🤪","zany crazy face"],["😝","squinting tongue"],["🤑","money mouth face"],["🤗","hugging face"],["🤔","thinking face"],["🫡","saluting face"],["🤫","shushing quiet"],["🤐","zipper mouth sealed"],["😐","neutral face"],["😑","expressionless face"],["😶","no mouth face"],["😶‍🌫️","face clouds"],["😏","smirking face"],["😒","unamused face"],["🙄","eye roll face"],["😬","grimacing face"],["🤥","lying face pinocchio"],["😌","relieved face"],["😔","pensive sad"],["😪","sleepy face"],["🤤","drooling face"],["😴","sleeping face zzz"],["🥱","yawning face tired"],["😷","mask sick medical"],["🤒","thermometer sick"],["🤕","head bandage hurt"],["🤢","nauseated face sick"],["🤧","sneezing face sick"],["🥵","hot face overheated"],["🥶","cold face frozen"],["🥴","woozy face drunk dizzy"],["😵","dizzy face"],["😵‍💫","spiral eyes dizzy"],["🤯","exploding head mind blown"],["🤠","cowboy hat face"],["🥳","party hat face celebrate"],["🥸","disguise face"],["😎","sunglasses cool"],["🤓","nerd glasses"],["🧐","monocle curious"],["😕","confused face"],["🫤","diagonal mouth"],["😟","worried face"],["🙁","slightly frowning"],["☹️","frowning face sad"],["😮","open mouth surprised"],["😯","hushed face surprised"],["😲","astonished face"],["😳","flushed face embarrassed"],["🥺","pleading face puppy eyes"],["🥹","holding back tears"],["😦","frowning open mouth"],["😧","anguished face"],["😨","fearful afraid scared"],["😰","anxious sweat nervous"],["😥","sad relieved"],["😢","crying face tear sad"],["😭","loudly crying sob"],["😱","screaming fear scared"],["😖","confounded face"],["😣","persevering face"],["😞","disappointed face sad"],["😓","downcast sweat"],["😩","weary face tired"],["😫","tired face exhausted"],["😤","steam nose angry"],["😠","angry face"],["😡","red angry face"],["🤬","swearing symbols face"],["😈","devil smile"],["👿","angry devil"],["💀","skull death"],["☠️","skull crossbones pirate"],["💩","poop brown"],["🤡","clown face"],["👹","ogre monster"],["👺","goblin red demon"],["👻","ghost boo"],["👽","alien extraterrestrial"],["👾","alien monster space"],["🤖","robot"],
  ["👋","wave hand hello goodbye"],["🤚","raised back hand"],["🖐️","hand spread fingers"],["✋","raised hand stop"],["🖖","vulcan salute spock"],["👌","ok hand"],["🤌","pinched fingers italian"],["🤏","pinching hand small"],["✌️","peace victory fingers"],["🤞","crossed fingers luck"],["🫰","hand index heart"],["🤟","love you gesture"],["🤘","sign horns rock metal"],["🤙","call me hang loose"],["👈","backhand left point"],["👉","backhand right point"],["👆","backhand up point"],["🖕","middle finger rude"],["👇","backhand down point"],["☝️","index up"],["🫵","point you"],["👍","thumbs up like good"],["👎","thumbs down dislike bad"],["✊","raised fist"],["👊","oncoming fist punch"],["🤛","left facing fist bump"],["🤜","right facing fist bump"],["👏","clapping hands applause"],["🫶","heart hands love"],["🙌","raising hands celebrate"],["👐","open hands"],["🤲","palms up together"],["🤝","handshake"],["🙏","folded hands pray thanks"],["✍️","writing hand"],["💅","nail polish"],["🤳","selfie"],["💪","flexed bicep strong muscle"],["🦾","mechanical arm"],["🦵","leg kick"],["🦶","foot"],["👂","ear listen"],["👃","nose smell"],["🧠","brain mind smart"],["👀","eyes look see"],["👅","tongue taste"],["👄","mouth lips"],["🫦","biting lip"],
  ["❤️","red heart love"],["🧡","orange heart"],["💛","yellow heart"],["💚","green heart"],["💙","blue heart"],["🩵","light blue heart"],["💜","purple heart"],["🖤","black heart"],["🩶","grey heart"],["🤍","white heart"],["🤎","brown heart"],["💔","broken heart"],["❤️‍🔥","heart on fire love"],["❤️‍🩹","mending heart healing"],["💕","two hearts"],["💞","revolving hearts"],["💓","beating heart"],["💗","growing heart"],["💖","sparkling heart"],["💘","heart arrow"],["💝","heart ribbon"],["💟","heart decoration"],
  ["🐶","dog puppy"],["🐱","cat kitten"],["🐭","mouse"],["🐹","hamster"],["🐰","rabbit bunny"],["🦊","fox"],["🐻","bear"],["🐼","panda"],["🐨","koala"],["🐯","tiger"],["🦁","lion"],["🐮","cow"],["🐷","pig"],["🐸","frog"],["🐵","monkey"],["🙈","see no evil monkey"],["🙉","hear no evil monkey"],["🙊","speak no evil monkey"],["🐔","chicken"],["🐧","penguin"],["🐦","bird"],["🦆","duck"],["🦅","eagle"],["🦉","owl"],["🦇","bat"],["🐺","wolf"],["🐗","boar pig wild"],["🐴","horse"],["🦄","unicorn"],["🐝","bee honeybee"],["🦋","butterfly"],["🐌","snail"],["🐞","ladybug lady beetle"],["🐜","ant"],["🦟","mosquito"],["🦗","cricket"],["🦂","scorpion"],["🐢","turtle"],["🐍","snake"],["🦎","lizard"],["🦕","sauropod dinosaur"],["🦖","t-rex dinosaur"],["🐙","octopus"],["🦑","squid"],["🦐","shrimp"],["🦞","lobster"],["🦀","crab"],["🐡","blowfish"],["🐠","tropical fish"],["🐟","fish"],["🐬","dolphin"],["🐳","whale"],["🦈","shark"],["🐊","crocodile"],["🐘","elephant"],["🦛","hippo hippopotamus"],["🦏","rhinoceros"],["🐪","camel"],["🦒","giraffe"],["🦘","kangaroo"],["🦬","bison buffalo"],["🐄","cow"],["🐎","horse racing"],["🐑","sheep"],["🐐","goat"],["🦌","deer"],["🐕","dog"],["🐩","poodle"],["🐈","cat"],["🐓","rooster"],["🦃","turkey"],["🦜","parrot"],["🕊️","dove peace"],["🐇","rabbit"],["🦔","hedgehog"],["🌵","cactus"],["🌲","evergreen tree"],["🌳","deciduous tree"],["🌴","palm tree"],["🌱","seedling plant"],["🌿","herb leaf"],["🍀","four leaf clover luck"],["🍃","leaves wind"],["🍂","fallen leaf autumn"],["🍁","maple leaf autumn"],["🍄","mushroom"],["🌾","sheaf rice wheat"],["💐","bouquet flowers"],["🌷","tulip flower"],["🌹","rose flower"],["🥀","wilted flower"],["🌺","hibiscus flower"],["🌸","cherry blossom flower"],["🌼","blossom daisy flower"],["🌻","sunflower"],["🌞","sun with face"],["🌙","crescent moon"],["⭐","star"],["🔥","fire flame hot"],["💧","droplet water"],["🌊","wave water ocean"],
  ["🍏","green apple"],["🍎","red apple"],["🍐","pear"],["🍊","tangerine orange"],["🍋","lemon"],["🍌","banana"],["🍉","watermelon"],["🍇","grapes"],["🍓","strawberry"],["🫐","blueberry"],["🍒","cherries"],["🍑","peach"],["🥭","mango"],["🍍","pineapple"],["🥥","coconut"],["🥝","kiwi"],["🍅","tomato"],["🍆","eggplant aubergine"],["🥑","avocado"],["🥦","broccoli"],["🥬","leafy greens"],["🥒","cucumber"],["🌶️","hot pepper chili"],["🧄","garlic"],["🧅","onion"],["🥔","potato"],["🍠","roasted sweet potato"],["🥐","croissant"],["🍞","bread"],["🥖","baguette"],["🧀","cheese"],["🥚","egg"],["🍳","cooking fried egg"],["🧈","butter"],["🥞","pancakes"],["🧇","waffle"],["🥓","bacon"],["🥩","meat steak"],["🍗","poultry leg chicken"],["🍖","meat bone"],["🌭","hot dog"],["🍔","burger hamburger"],["🍟","fries"],["🍕","pizza"],["🥙","stuffed flatbread"],["🌮","taco"],["🌯","burrito wrap"],["🥗","salad"],["🍝","spaghetti pasta"],["🍜","noodles ramen"],["🍛","curry rice"],["🍣","sushi"],["🥟","dumpling"],["🍔","burger"],["🧁","cupcake"],["🍰","cake slice"],["🎂","birthday cake"],["🍭","lollipop candy"],["🍬","candy"],["🍫","chocolate"],["🍩","donut doughnut"],["🍪","cookie"],["☕","coffee hot"],["🍵","tea hot"],["🍺","beer"],["🍻","beers clinking"],["🥂","champagne glasses"],["🍷","wine"],["🍸","cocktail"],["🍹","tropical drink"],["🍾","champagne bottle"],["🧊","ice cube"],
  ["⚽","soccer football"],["🏀","basketball"],["🏈","american football"],["⚾","baseball"],["🥎","softball"],["🎾","tennis"],["🏐","volleyball"],["🏉","rugby football"],["🎱","billiards pool 8ball"],["🏓","ping pong table tennis"],["🏸","badminton"],["🥊","boxing glove"],["🥋","martial arts"],["🎯","bullseye target dart"],["⛳","golf flag"],["🎮","video game controller"],["🎲","dice game"],["🏆","trophy winner"],["🥇","gold medal first"],["🥈","silver medal second"],["🥉","bronze medal third"],["🎸","guitar"],["🎹","piano keyboard"],["🎺","trumpet"],["🎻","violin"],["🥁","drums"],["🎷","saxophone"],["🎤","microphone"],["🎧","headphones"],["🎨","art palette"],["🎭","performing arts theater"],["🎬","clapper film"],["🎤","karaoke mic"],["🎡","ferris wheel"],["🎢","rollercoaster"],["🎠","carousel"],
  ["🚗","car automobile"],["🚕","taxi cab"],["🚙","suv car"],["🚌","bus"],["🚎","trolleybus"],["🏎️","racing car"],["🚓","police car"],["🚑","ambulance"],["🚒","fire engine truck"],["🚐","minibus"],["🛻","pickup truck"],["🚚","truck delivery"],["🚛","semi truck"],["🚜","tractor farm"],["🏍️","motorcycle motorbike"],["🛵","scooter moped"],["🚲","bicycle bike"],["🛴","kick scooter"],["✈️","airplane plane flight"],["🚀","rocket space"],["🛸","ufo flying saucer"],["🚁","helicopter"],["⛵","sailboat"],["🚢","ship cruise"],["🚂","train locomotive"],["🚇","metro subway"],["🚉","station train"],["🚊","tram"],["🏠","house home"],["🏢","office building"],["🏥","hospital"],["🏦","bank"],["🏨","hotel"],["🏪","convenience store"],["🏫","school"],["🏛️","classical building"],["🗼","tokyo tower"],["🗽","statue liberty"],["🌉","bridge night"],["🏔️","mountain snow"],["🌋","volcano"],["🏖️","beach"],["🏝️","island"],["🌅","sunrise"],["🌄","sunrise mountains"],
  ["💡","light bulb idea"],["🔦","flashlight torch"],["🕯️","candle"],["💻","laptop computer"],["📱","mobile phone"],["🖥️","desktop computer"],["⌨️","keyboard"],["🖱️","mouse computer"],["🖨️","printer"],["📷","camera photo"],["📸","camera flash"],["📹","video camera"],["🎥","movie camera"],["📺","television tv"],["📻","radio"],["📞","telephone call"],["☎️","phone telephone"],["📟","pager"],["🔋","battery"],["🔌","plug electric"],["💾","floppy disk"],["💿","cd disc"],["📀","dvd disc"],["📚","books stack"],["📖","open book read"],["📝","memo note write"],["✏️","pencil write"],["🖊️","pen write"],["📌","pushpin map"],["📍","round pushpin location"],["🔍","magnifying glass search"],["🔎","magnifying glass right"],["🔑","key lock"],["🗝️","key old"],["🔒","locked"],["🔓","unlocked"],["🔨","hammer"],["⚔️","crossed swords"],["🛡️","shield"],["🔧","wrench"],["🔩","nut bolt"],["⚙️","gear"],["🧲","magnet"],["💊","pill medicine"],["💉","syringe injection"],["🩺","stethoscope doctor"],["🔬","microscope science"],["🔭","telescope space"],["💰","money bag"],["💵","dollar banknote"],["💳","credit card"],["📈","chart increasing"],["📉","chart decreasing"],["📊","bar chart"],["📦","package box"],["📫","closed mailbox"],["📧","email"],["📬","open mailbox"],["📎","paperclip"],["✂️","scissors"],["🗑️","wastebasket trash"],
  ["🎉","party popper celebration"],["🎊","confetti celebration"],["🎈","balloon party"],["🎁","gift present wrapped"],["🎀","ribbon bow"],["🎆","fireworks"],["🎇","sparkler"],["✨","sparkles stars glitter"],["🧨","firecracker"],["🪄","magic wand"],["🎃","jack-o-lantern halloween pumpkin"],["🎄","christmas tree"],["🎋","tanabata tree"],["🎍","pine decoration"],["🎎","japanese dolls"],["🎑","moon viewing ceremony"],["🧧","red envelope"],["🏮","red lantern"],["🎐","wind chime"],["🪅","pinata"],
  ["🏁","chequered flag race"],["🚩","triangular flag"],["🎌","crossed flags japan"],["🏴","black flag"],["🏳️","white flag"],["🏳️‍🌈","rainbow flag pride"],["🏳️‍⚧️","transgender flag"],["🏴‍☠️","pirate flag jolly roger"],["🇺🇸","us usa america flag"],["🇬🇧","uk britain england flag"],["🇨🇦","canada flag"],["🇦🇺","australia flag"],["🇫🇷","france french flag"],["🇩🇪","germany german flag"],["🇮🇹","italy italian flag"],["🇯🇵","japan japanese flag"],["🇰🇷","korea korean flag"],["🇨🇳","china chinese flag"],["🇧🇷","brazil flag"],["🇮🇳","india flag"],["🇲🇽","mexico flag"],["🇷🇺","russia flag"],["🇪🇸","spain spanish flag"],["🇵🇹","portugal flag"],["🇸🇦","saudi arabia flag"],["🇮🇱","israel flag"],["🇺🇦","ukraine flag"],["🇵🇱","poland flag"],["🇿🇦","south africa flag"],["🇳🇬","nigeria flag"],["🇵🇭","philippines flag"],["🇮🇩","indonesia flag"],["🇵🇰","pakistan flag"],["🇧🇩","bangladesh flag"],["🇻🇳","vietnam flag"],["🇹🇷","turkey flag"],["🇪🇬","egypt flag"],["🇦🇷","argentina flag"],["🇨🇱","chile flag"],["🇨🇴","colombia flag"],["🇳🇿","new zealand flag"],["🇮🇪","ireland flag"],["🇸🇪","sweden flag"],["🇳🇴","norway flag"],["🇫🇮","finland flag"],["🇩🇰","denmark flag"],["🇨🇭","switzerland flag"],["🇦🇹","austria flag"],["🇧🇪","belgium flag"],["🇳🇱","netherlands flag"],["🇬🇷","greece flag"],["🇵🇱","poland flag"],["🇨🇿","czech republic flag"],["🇭🇺","hungary flag"],["🇷🇴","romania flag"],["🇧🇬","bulgaria flag"],["🇭🇷","croatia flag"],["🇸🇰","slovakia flag"],["🇸🇮","slovenia flag"],["🇱🇹","lithuania flag"],["🇱🇻","latvia flag"],["🇪🇪","estonia flag"],["🏴󠁧󠁢󠁥󠁮󠁧󠁿","england flag"],["🏴󠁧󠁢󠁳󠁣󠁴󠁿","scotland flag"],["🏴󠁧󠁢󠁷󠁬󠁳󠁿","wales flag"],
];

// ─── BACKEND DATA HOOKS ───────────────────────────────────────────────────────
//
// These hooks are the single integration points for the backend.
// When the backend is ready, replace the TODO block inside each hook
// with a real fetch call — the rest of the app needs no changes.
//
// COMMUNITY LIFT DATA
// Expected shape from GET /api/community/users:
// [
//   {
//     name: "BrotherName",           // display name / username
//     logs: {
//       "Bench Press": { 1: [{weight:225,ts:1700000000000}, ...],
//                        5: [...], 10: [...], 15: [...] },
//       "Squat":       { ... },
//       "Deadlift":    { ... },
//       // ...one key per exercise in EXERCISE_LIST
//     }
//   },
//   ...
// ]
function useCommunityPrs(currentUsername) {
  const [communityPrs, setCommunityPrs] = useState([]); // [{ name, prs: { exercise: value } }]
  const [myPrs, setMyPrs]               = useState({}); // { exercise: value }
  const [loading, setLoading]           = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.getCommunityPrs(), api.getMyPrs()])
      .then(([community, mine]) => {
        if (cancelled) return;
        setCommunityPrs(community);
        const myMap = {};
        for (const p of mine) myMap[p.exercise] = p.value;
        setMyPrs(myMap);
      })
      .catch(err => console.warn("useCommunityPrs:", err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentUsername]);

  usePolling(() => {
    Promise.all([api.getCommunityPrs(), api.getMyPrs()])
      .then(([community, mine]) => {
        setCommunityPrs(community);
        const myMap = {};
        for (const p of mine) myMap[p.exercise] = p.value;
        setMyPrs(myMap);
      }).catch(() => {});
  }, 45000);

  return { communityPrs, myPrs, loading };
}

// Number of messages fetched per page (initial load and each "scroll to top" load).
// The server clamps this to 100.
const CHAT_PAGE_SIZE = 50;

// Invisible marker rendered as the LAST child of a column-reverse message list
// (i.e. visually at the very top). When it scrolls into view it asks for the
// next page of older messages. `count` changes after each page loads, which
// re-arms the observer so a still-visible marker keeps loading until the
// viewport is filled or history runs out.
function LoadOlderSentinel({ hasMore, loading, count, onLoad }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !hasMore || loading) return;
    if (typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) onLoad();
    }, { rootMargin: "300px 0px 300px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, count, onLoad]);
  if (!hasMore && !loading) return null;
  return (
    <div ref={ref} style={{ minHeight: 24, textAlign: "center", color: "var(--muted)", fontSize: 11, padding: 6, flexShrink: 0 }}>
      {loading ? "Loading earlier messages…" : ""}
    </div>
  );
}

function useBoardMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [hasMore, setHasMore]   = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const latestTs = useRef(0);
  const loadingOlderRef = useRef(false);
  const messagesRef = useRef([]);
  messagesRef.current = messages;

  const fetchMessages = () => {
    setLoading(true);
    api.getMessages()
      .then(data => {
        setMessages(data);
        setHasMore(data.length >= CHAT_PAGE_SIZE);
        if (data.length > 0) latestTs.current = Math.max(...data.map(m => m.ts));
      })
      .catch(err => console.warn("useBoardMessages:", err))
      .finally(() => setLoading(false));
  };

  // Loads the next page of older messages (messages state is oldest-first)
  const loadOlder = useCallback(() => {
    if (loadingOlderRef.current) return;
    const oldest = messagesRef.current.length > 0 ? messagesRef.current[0].ts : 0;
    if (!oldest) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    api.getMessages(null, oldest)
      .then(older => {
        setHasMore(older.length >= CHAT_PAGE_SIZE);
        if (older.length === 0) return;
        setMessages(prev => {
          const ids = new Set(prev.map(m => m.id));
          const fresh = older.filter(m => !ids.has(m.id));
          return fresh.length > 0 ? [...fresh, ...prev] : prev;
        });
      })
      .catch(err => console.warn("loadOlder failed:", err))
      .finally(() => { loadingOlderRef.current = false; setLoadingOlder(false); });
  }, []);


  // Poll for new messages every 8 seconds using the since param
  usePolling(() => {
    if (latestTs.current === 0) return; // haven't loaded yet
    api.getMessages(latestTs.current)
      .then(newMsgs => {
        if (newMsgs.length === 0) return;
        setMessages(prev => {
          const ids = new Set(prev.map(m => m.id));
          const fresh = newMsgs.filter(m => !ids.has(m.id));
          if (fresh.length === 0) return prev;
          const updated = [...prev, ...fresh];
          latestTs.current = Math.max(...updated.map(m => m.ts));
          return updated;
        });
      })
      .catch(() => {});
  }, 8000);

  // Posts a new message to the backend; appends to state and updates latestTs
  const saveMessage = async (msg) => {
    try {
      const saved = await api.postMessage({
        text:       msg.text,
        media:      msg.media,
        mediaExtra: msg.mediaExtra ?? [],
      });
      setMessages(prev => [...prev, saved]);
      latestTs.current = Math.max(latestTs.current, saved.ts);
    } catch (err) {
      console.warn("saveMessage failed:", err);
    }
  };

  // Toggles a reaction; updates state from server response
  const saveReaction = async (messageId, emoji) => {
    try {
      const { reactions } = await api.postReaction({ messageId, emoji });
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, reactions } : m
      ));
    } catch (err) {
      console.warn("saveReaction failed:", err);
    }
  };

  const deleteMessage = async (id) => {
    try {
      await api.deleteMessage(id);
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      console.warn("deleteMessage failed:", err);
    }
  };

  return { messages, loading, fetchMessages, saveMessage, saveReaction, deleteMessage, hasMore, loadingOlder, loadOlder };
}



// ─── COMMUNITY USER SHAPE (for reference) ─────────────────────────────────────
// { name: string, logs: { [exercise]: { [repCat]: [{weight,ts}] } } }
// ─── LINK PREVIEW ────────────────────────────────────────────────────────────
const fmtChatTime = (ts) => {
  const diff = Date.now() - ts;
  if (diff < 60000)    return "just now";
  if (diff < 3600000)  return `${Math.floor(diff/60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
  return new Date(ts).toLocaleDateString("en-US", { month:"short", day:"numeric" });
};

// ─── VIDEO PLAYER ─────────────────────────────────────────────────────────────
function VideoPlayer({ src, mt }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    // Cloudinary or other http URLs — use directly
    if (src.startsWith("http") || src.startsWith("blob:")) {
      video.src = src;
      return;
    }

    // Legacy base64 dataURL — convert to blob for smooth playback
    try {
      const [header, data] = src.split(",");
      const mime = header.split(":")[1].split(";")[0];
      const bytes = atob(data);
      const ab = new ArrayBuffer(bytes.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < bytes.length; i++) ia[i] = bytes.charCodeAt(i);
      const blobUrl = URL.createObjectURL(new Blob([ab], { type: mime }));
      video.src = blobUrl;
      return () => URL.revokeObjectURL(blobUrl);
    } catch {
      video.src = src;
    }
  }, [src]);

  return (
    <video ref={videoRef} playsInline
      style={{ width:"100%", maxWidth:280, borderRadius:10, marginTop: mt || 0, display:"block", background:"#000", verticalAlign:"top" }} />
  );
}

// ─── CLOUDINARY CONFIG ────────────────────────────────────────────────────────
// Cloudinary uploads now go through the server (/api/upload) — no client-side keys needed

// ─── CLOUDINARY QUOTA MANAGEMENT ─────────────────────────────────────────────
const CHAT_MAX_FILE_BYTES  = 100 * 1024 * 1024;          // 100 MB per upload
const CHAT_STORAGE_CEILING = 20 * 1024 * 1024 * 1024;    // 20 GB — prune before hitting 25 GB free limit

// ─── AUDIO LIGHTBOX ───────────────────────────────────────────────────────────
// Must be module-level (not inside BoardPage) so React doesn't remount on each render
function AudioLightbox({ src, onClose }) {
  const audioRef    = useRef(null);
  const canvasRef   = useRef(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const sourceRef   = useRef(null);
  const [playing,  setPlaying]  = useState(false);
  const [elapsed,  setElapsed]  = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onMeta  = () => setDuration(audio.duration || 0);
    const onTime  = () => setElapsed(audio.currentTime || 0);
    const onEnded = () => setPlaying(false);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("timeupdate",     onTime);
    audio.addEventListener("ended",          onEnded);
    return () => {
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("timeupdate",     onTime);
      audio.removeEventListener("ended",          onEnded);
      audio.pause();
      if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    };
  }, []);

  // Keep a ref mirroring playing state so the draw loop always sees current value
  const playingRef = useRef(false);
  useEffect(() => { playingRef.current = playing; }, [playing]);

  // Waveform — flat idle line immediately on open; live oscilloscope when playing.
  // useLayoutEffect (not useEffect) so canvasRef.current is guaranteed attached
  // even inside a createPortal before the browser has painted.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let rafId;
    const ctx2d = canvas.getContext("2d");

    // Draw the idle line once synchronously before the first RAF tick
    const w = canvas.width, h = canvas.height;
    ctx2d.clearRect(0, 0, w, h);
    ctx2d.strokeStyle = "rgba(0,255,140,0.85)";
    ctx2d.lineWidth = 1.5;
    ctx2d.beginPath();
    ctx2d.moveTo(0, h / 2);
    ctx2d.lineTo(w, h / 2);
    ctx2d.stroke();

    const draw = () => {
      rafId = requestAnimationFrame(draw);
      const w = canvas.width, h = canvas.height;
      ctx2d.clearRect(0, 0, w, h);
      ctx2d.lineWidth = 1.5;
      ctx2d.beginPath();
      if (analyserRef.current && playingRef.current) {
        // Live oscilloscope
        analyserRef.current.fftSize = 2048;
        const bufLen = analyserRef.current.fftSize;
        const data   = new Uint8Array(bufLen);
        analyserRef.current.getByteTimeDomainData(data);
        ctx2d.strokeStyle = "rgba(0,255,140,0.85)";
        const sliceW = w / bufLen;
        let x = 0;
        for (let i = 0; i < bufLen; i++) {
          const v = data[i] / 128.0;
          const y = (v * h) / 2;
          i === 0 ? ctx2d.moveTo(x, y) : ctx2d.lineTo(x, y);
          x += sliceW;
        }
      } else {
        // Idle — flat centre line, same color as active
        ctx2d.strokeStyle = "rgba(0,255,140,0.85)";
        ctx2d.moveTo(0, h / 2);
        ctx2d.lineTo(w, h / 2);
      }
      ctx2d.stroke();
    };
    draw();
    return () => cancelAnimationFrame(rafId);
  }, []);

  const initAudioContext = () => {
    if (!audioCtxRef.current) {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      // createMediaElementSource must only be called once per element
      if (!sourceRef.current) {
        sourceRef.current = audioCtx.createMediaElementSource(audioRef.current);
      }
      sourceRef.current.connect(analyser);
      analyser.connect(audioCtx.destination);
      analyserRef.current = analyser;
    }
    // Must resume inside user gesture on iOS
    if (audioCtxRef.current.state === "suspended") audioCtxRef.current.resume();
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    try { initAudioContext(); } catch(e) { /* play without analyser if CORS blocked */ }
    if (audio.paused) { audio.play().catch(e => console.warn("play():", e)); setPlaying(true); }
    else              { audio.pause(); setPlaying(false); }
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect   = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    audio.currentTime = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * duration;
  };

  // Drag-to-scrub — native touch listeners with passive:false for iOS
  const scrubBarRef  = useRef(null);
  const isDragging   = useRef(false);
  const durationRef  = useRef(duration);
  useEffect(() => { durationRef.current = duration; }, [duration]);

  const seekFromClientX = useCallback((clientX) => {
    const audio = audioRef.current;
    const dur   = durationRef.current;
    if (!audio || !dur || !scrubBarRef.current) return;
    const rect = scrubBarRef.current.getBoundingClientRect();
    audio.currentTime = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * dur;
  }, []); // stable — reads via refs, never stale

  // Attach once on mount; seekFromClientX always reads latest duration via ref
  useEffect(() => {
    const el = scrubBarRef.current;
    if (!el) return;
    const onTouchStart = (e) => {
      e.preventDefault(); // claim gesture immediately — prevents iOS scroll takeover
      e.stopPropagation();
      isDragging.current = true;
      seekFromClientX(e.touches[0].clientX);
    };
    const onTouchMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      seekFromClientX(e.touches[0].clientX);
    };
    const onTouchEnd = () => { isDragging.current = false; };
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd,   { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
    };
  }, [seekFromClientX]); // stable — runs once

  // Mouse scrub (desktop)
  const onScrubMouseDown = (e) => { isDragging.current = true;  seekFromClientX(e.clientX); };
  const onScrubMouseMove = (e) => { if (isDragging.current) seekFromClientX(e.clientX); };
  const onScrubMouseUp   = ()  => { isDragging.current = false; };

  const fmt = (s) => !s || isNaN(s) ? "0:00"
    : `${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,"0")}`;
  const pct = duration > 0 ? (elapsed / duration) * 100 : 0;

  const PlayIcon  = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="#00ff99"><polygon points="5,3 17,10 5,17"/></svg>;
  const PauseIcon = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="#00ff99"><rect x="4" y="3" width="4" height="14" rx="1"/><rect x="12" y="3" width="4" height="14" rx="1"/></svg>;

  return createPortal(
    <div onClick={onClose} style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.97)",
      zIndex:9999, display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center", padding:"0 24px",
    }}>
      <button onClick={e => { e.stopPropagation(); onClose(); }} style={{
        position:"absolute", top:16, right:16,
        background:"rgba(0,0,0,0.7)", border:"1px solid rgba(255,255,255,0.3)",
        borderRadius:"50%", width:36, height:36, color:"#fff",
        fontSize:20, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
      }}>✕</button>

      <div onClick={e => { e.stopPropagation(); try { initAudioContext(); } catch(_){} }} style={{ width:"100%", maxWidth:420, display:"flex", flexDirection:"column", alignItems:"center", gap:28 }}
           onMouseMove={onScrubMouseMove} onMouseUp={onScrubMouseUp}>
        <canvas ref={canvasRef} width={360} height={100} style={{ width:"100%", height:100 }} />

        <div style={{ width:"100%", display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:11, color:"rgba(0,255,140,0.7)", fontFamily:"'Orbitron',sans-serif", width:34, textAlign:"right" }}>{fmt(elapsed)}</span>
          <div ref={scrubBarRef}
               onMouseDown={onScrubMouseDown}
               style={{ flex:1, height:44, display:"flex", alignItems:"center", cursor:"pointer", position:"relative" }}>
            {/* Track */}
            <div style={{ position:"absolute", left:0, right:0, height:5, background:"rgba(0,255,140,0.1)", borderRadius:3 }}>
              <div style={{ width:`${pct}%`, height:"100%", background:"linear-gradient(90deg,#00cc77,#00ff99)", borderRadius:3, transition:"width 0.1s linear" }}/>
            </div>
            {/* Thumb */}
            <div style={{ position:"absolute", top:"50%", left:`${pct}%`, transform:"translate(-50%,-50%)", width:13, height:13, borderRadius:"50%", background:"#00ff99", boxShadow:"0 0 8px #00ff99", zIndex:1 }}/>
          </div>
          <span style={{ fontSize:11, color:"rgba(0,255,140,0.7)", fontFamily:"'Orbitron',sans-serif", width:34 }}>{fmt(duration)}</span>
        </div>

        <button onClick={togglePlay} style={{
          width:62, height:62, borderRadius:"50%",
          background:"none", border:"2px solid rgba(0,255,140,0.55)",
          cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
          filter:"drop-shadow(0 0 10px rgba(0,255,140,0.7))", transition:"all 0.15s",
        }}>
          {playing ? <PauseIcon/> : <PlayIcon/>}
        </button>
      </div>

      {/* crossOrigin="anonymous" required for Web Audio API with Cloudinary CORS */}
      <audio ref={audioRef} src={src} preload="metadata" crossOrigin="anonymous" />
    </div>,
    document.body
  );
}

// ─── LINK PREVIEW ────────────────────────────────────────────────────────────
const URL_REGEX = /https?:\/\/[^\s<>"']+/g;

// Returns up to 2 initials from a display name — first letter of first word
// and first letter of last word (if different). e.g. "John Doe" → "JD", "John" → "JO"
function initials(name) {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function extractUrls(text) {
  return text ? [...new Set(text.match(URL_REGEX) || [])] : [];
}

function LinkPreview({ url, onLoad }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = api.getToken();
    fetch(`${API_BASE}/api/link-preview?url=${encodeURIComponent(url)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(d => {
        if (!cancelled && d.title) { setData(d); }
        else if (!cancelled) setFailed(true);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [url]);

  // Scroll to bottom whenever preview card appears (text-only, no image)
  useEffect(() => {
    if (data && !data.image && onLoad) onLoad();
  }, [data]);

  if (failed || !data) return null;

  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      style={{ display:"block", marginTop:8, textDecoration:"none",
        background:"rgba(0,0,0,0.45)", border:"1px solid rgba(136,255,0,0.2)",
        borderLeft:"3px solid rgba(136,255,0,0.6)", borderRadius:4,
        overflow:"hidden", color:"inherit", cursor:"pointer" }}
      onClick={e => e.stopPropagation()}>
      {data.image && (
        <img src={data.image} alt="" style={{ width:"100%", maxHeight:140, objectFit:"cover", display:"block" }}
          onLoad={() => { if (onLoad) onLoad(); }}
          onError={e => { e.target.style.display = "none"; if (onLoad) onLoad(); }} />
      )}
      <div style={{ padding:"8px 10px" }}>
        {data.siteName && (
          <div style={{ fontSize:10, color:"rgba(136,255,0,0.7)", fontFamily:"'Orbitron',sans-serif",
            letterSpacing:1, marginBottom:3, textTransform:"uppercase" }}>
            {data.siteName}
          </div>
        )}
        <div style={{ fontSize:13, fontWeight:700, color:"#e8f8e8", lineHeight:1.3, marginBottom:3 }}>
          {data.title.length > 80 ? data.title.slice(0, 80) + "…" : data.title}
        </div>
        {data.description && (
          <div style={{ fontSize:11, color:"rgba(200,220,200,0.7)", lineHeight:1.4 }}>
            {data.description.length > 120 ? data.description.slice(0, 120) + "…" : data.description}
          </div>
        )}
        <div style={{ fontSize:10, color:"rgba(136,255,0,0.45)", marginTop:4 }}>{data.domain}</div>
      </div>
    </a>
  );
}

// ─── BOARD PAGE ───────────────────────────────────────────────────────────────
function BoardPage({ username, currentUser, mobileScreen, onHasChapter }) {
  const isMobile = useIsMobile();
  const { messages, fetchMessages, saveMessage, saveReaction, deleteMessage, hasMore, loadingOlder, loadOlder } = useBoardMessages();
  // On mobile, chatTab is derived directly from mobileScreen to avoid render-cycle lag.
  // On desktop, it's driven by the internal tab buttons via chatTabInternal.
  const [chatTabInternal, setChatTabInternal] = useState("global");
  const chatTab = (isMobile && mobileScreen !== null)
    ? (mobileScreen === "chapter" ? "chapter" : "global")
    : chatTabInternal;
  const setChatTab = (val) => { if (!isMobile) setChatTabInternal(val); };
  const [chapterMembership, setChapterMembership] = useState(null);
  const [chapterMessages, setChapterMessages]     = useState([]);
  const [chapterMsgLoading, setChapterMsgLoading] = useState(false);
  const chapterLatestTs = useRef(0);
  const [chapterHasMore, setChapterHasMore]         = useState(false);
  const [chapterLoadingOlder, setChapterLoadingOlder] = useState(false);
  const chapterLoadingOlderRef = useRef(false);
  const chapterMessagesRef = useRef([]);
  chapterMessagesRef.current = chapterMessages;

  // Loads the next page of older chapter messages (chapterMessages is newest-first,
  // so the oldest loaded message is the LAST element and older pages append to the end)
  const loadOlderChapter = useCallback(() => {
    if (chapterLoadingOlderRef.current || !chapterMembership) return;
    const list = chapterMessagesRef.current;
    const oldest = list.length > 0 ? list[list.length - 1].ts : 0;
    if (!oldest) return;
    chapterLoadingOlderRef.current = true;
    setChapterLoadingOlder(true);
    api.getChapterMessages(chapterMembership.chapter_id, null, oldest)
      .then(older => {
        setChapterHasMore(older.length >= CHAT_PAGE_SIZE);
        if (older.length === 0) return;
        setChapterMessages(prev => {
          const ids = new Set(prev.map(m => m.id));
          const fresh = older.filter(m => !ids.has(m.id));
          return fresh.length > 0 ? [...prev, ...fresh] : prev;
        });
      })
      .catch(() => {})
      .finally(() => { chapterLoadingOlderRef.current = false; setChapterLoadingOlder(false); });
  }, [chapterMembership]);

  useEffect(() => {
    api.getMyMembership().then(m => {
      if (m && m.status === "approved") {
        setChapterMembership(m);
        if (onHasChapter) onHasChapter(true);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (chatTab !== "chapter" || !chapterMembership) return;
    chapterLatestTs.current = 0;
    setChapterMsgLoading(true);
    api.getChapterMessages(chapterMembership.chapter_id)
      .then(msgs => {
        setChapterMessages(msgs);
        setChapterHasMore(msgs.length >= CHAT_PAGE_SIZE);
        if (msgs.length > 0) chapterLatestTs.current = Math.max(...msgs.map(m => m.ts));
      })
      .catch(() => {})
      .finally(() => setChapterMsgLoading(false));
    // Clear chapter-chat notifications
    const token = api.getToken();
    if (token) {
      fetch(`${API_BASE}/api/badge/clear`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ types: ["chapter-chat"] }),
      }).catch(() => {});
    }
  }, [mobileScreen, chatTabInternal, chapterMembership]);

  // Poll chapter chat every 8s when on chapter tab
  usePolling(() => {
    if (!chapterMembership || chapterLatestTs.current === 0) return;
    api.getChapterMessages(chapterMembership.chapter_id, chapterLatestTs.current)
      .then(newMsgs => {
        if (newMsgs.length === 0) return;
        const maxTs = Math.max(...newMsgs.map(m => m.ts));
        chapterLatestTs.current = Math.max(chapterLatestTs.current, maxTs);
        setChapterMessages(prev => {
          const ids = new Set(prev.map(m => m.id));
          const fresh = newMsgs.filter(m => !ids.has(m.id));
          return fresh.length > 0 ? [...fresh, ...prev] : prev;
        });
      })
      .catch(() => {});
  }, 8000, chatTab === "chapter" && !!chapterMembership);
  const [text, setText]                 = useState("");
  const [mediaFiles, setMediaFiles]     = useState([]);
  const [attachWarning, setAttachWarning] = useState(false);
  const attachWarningTimer = useRef(null);
  const showAttachWarning = () => {
    setAttachWarning(true);
    clearTimeout(attachWarningTimer.current);
    attachWarningTimer.current = setTimeout(() => setAttachWarning(false), 3000);
  };
  useEffect(() => () => clearTimeout(attachWarningTimer.current), []);
  const [uploading, setUploading]       = useState(false);
  const [emojiPickerFor, setEmojiPickerFor] = useState(null); // msgId of open quick bar
  const [showFullPicker, setShowFullPicker] = useState(null);  // msgId of open full picker
  const [pickerClosing, setPickerClosing]   = useState(false); // drives slide-out animation
  const [emojiCatIdx, setEmojiCatIdx]       = useState(0);
  const [emojiSearch, setEmojiSearch]       = useState("");
  const [reactionTooltip, setReactionTooltip] = useState(null); // { msgId, emoji, users, x, y }
  const [deleteHover, setDeleteHover] = useState(null); // msgId shown delete button
  const lastTapRef = useRef({}); // { [msgId]: timestamp } for double-tap detection
  const isArchAdmin = currentUser?.role === "arch_admin";
  const isChapterAdmin = chapterMembership?.role === "admin";
  const canDelete = (msg) => {
    if (isArchAdmin) return true;
    if (isChapterAdmin && msg.chapterId) return true;
    return false;
  };

  // Arch-admin: fetch full user list to resolve real names behind aliases
  const [allUsersMap, setAllUsersMap] = useState({});
  useEffect(() => {
    if (!isArchAdmin) return;
    api.get("/api/admin/users").then(users => {
      if (!Array.isArray(users)) return;
      const map = {};
      for (const u of users) map[u.id] = u.displayName;
      setAllUsersMap(map);
    }).catch(() => {});
  }, [isArchAdmin]);

  // ── Most-used emoji (localStorage per user) ────────────────────────────
  const MOST_USED_KEY = `emoji_usage_${username}`;
  const getMostUsed = () => {
    try { const r = localStorage.getItem(MOST_USED_KEY); return r ? JSON.parse(r) : {}; }
    catch { return {}; }
  };
  const recordEmojiUse = (emoji) => {
    try {
      const c = getMostUsed(); c[emoji] = (c[emoji] || 0) + 1;
      localStorage.setItem(MOST_USED_KEY, JSON.stringify(c));
    } catch {}
  };
  // Quick-bar row: top N most-used, padded with defaults until 11 slots filled
  const QUICK_DEFAULTS = ["👍","👎","👌","😢","🤔","🫡","💀","👀","🙏","🔥","💯"];
  const quickBarEmojis = useMemo(() => {
    const counts = getMostUsed();
    const used = Object.entries(counts).sort((a,b)=>b[1]-a[1]).map(([e])=>e);
    const row = [...used];
    for (const d of QUICK_DEFAULTS) { if (row.length >= 11) break; if (!row.includes(d)) row.push(d); }
    return row.slice(0, 11);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emojiPickerFor, showFullPicker]);
  const [lightboxSrc, setLightboxSrc]   = useState(null); // fullscreen media viewer
  const [lightboxType, setLightboxType] = useState(null); // "image" | "video"
  const scrollContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const fileRef   = useRef(null);
  const emojiInputRef = useRef(null);   // unused - kept for compat
  const emojiTargetRef = useRef(null);  // message id currently being reacted to

  useEffect(() => { fetchMessages(); }, []);

  // Close delete button on any click/tap outside the bubble
  useEffect(() => {
    if (!deleteHover) return;
    const close = () => setDeleteHover(null);
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [deleteHover]);

  // Close reaction tooltip on any click or touch outside
  useEffect(() => {
    if (!reactionTooltip) return;
    const close = () => setReactionTooltip(null);
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [reactionTooltip]);
  // Only listen to mousedown (not touchstart) — on mobile, iOS fires a synthetic
  // touchstart on the document when the keyboard opens, which would close the picker.
  // The mobile full picker is dismissed via its backdrop overlay instead.
  useEffect(() => {
    if (!emojiPickerFor && !showFullPicker) return;
    const close = (e) => { setEmojiPickerFor(null); dismissFullPicker(); setReactionTooltip(null); };
    document.addEventListener("mousedown", close);
    // touchstart only for the quick bar (not full picker — handled by backdrop on mobile)
    const closeTouchQuickBar = (e) => { if (!showFullPicker) close(); };
    document.addEventListener("touchstart", closeTouchQuickBar);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", closeTouchQuickBar);
    };
  }, [emojiPickerFor, showFullPicker]);

  // Auto-grow textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 140) + "px";
  }, [text]);

  // ── Visual viewport resize: keeps chat above keyboard on all mobile browsers ──
  const chatRootRef = useRef(null);
  const showFullPickerRef = useRef(null);
  const chatInputFocusedRef = useRef(false); // true only while chat textarea is focused
  useEffect(() => {
    if (!isMobile) return;
    const vv = window.visualViewport;
    if (!vv) return;

    let rafId = null;
    const apply = () => {
      if (!chatRootRef.current) return;
      const el = chatRootRef.current;
      el.style.height = vv.height + "px";
      // When keyboard closes, vv.height returns to full screen height.
      // iOS may have scrolled the layout viewport while the keyboard was open.
      // Reset that scroll so the next keyboard open doesn't cause a zoom/jump.
      if (vv.height > window.innerHeight * 0.7) {
        // Keyboard is likely closed — reset any layout viewport scroll
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
      // Only scroll chat to bottom when the chat's own textarea triggered the keyboard —
      // not when the emoji picker search input opened/closed the keyboard.
      if (scrollContainerRef.current && chatInputFocusedRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    };

    const onResize = () => {
      // Cancel any pending frame to avoid stacking
      if (rafId) cancelAnimationFrame(rafId);
      // Apply immediately for responsiveness, then again next frame for accuracy
      apply();
      rafId = requestAnimationFrame(apply);
    };

    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    onResize();
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isMobile]);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${API_BASE}/api/upload`, {
      method:  "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body:    formData,
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try { msg = (await res.json()).error || msg; } catch {}
      throw new Error(msg);
    }
    const { url, publicId, bytes } = await res.json();
    return { url, publicId, bytes };
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    if (mediaFiles.length >= 3) {
      showAttachWarning();
      return;
    }
    if (file.size > CHAT_MAX_FILE_BYTES) {
      alert(`File too large — the maximum upload size is 100 MB.\nYour file is ${(file.size / 1024 / 1024).toFixed(1)} MB.`);
      return;
    }
    const id = Date.now() + Math.random();
    const blobUrl = URL.createObjectURL(file);
    setMediaFiles(prev => [...prev, { id, blobUrl, type: file.type, cloudUrl: null, uploading: true, progress: 0 }]);
    setUploading(true);
    try {
      const { url: cloudUrl, bytes, publicId } = await uploadToCloudinary(file);
      setMediaFiles(prev => prev.map(f => f.id === id ? { ...f, cloudUrl, bytes, publicId, uploading: false } : f));
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
      setMediaFiles(prev => prev.filter(f => f.id !== id));
    } finally {
      setUploading(false);
    }
  };

  const sendMessage = async () => {
    if (!text.trim() && mediaFiles.length === 0) return;
    if (uploading) return;
    const allMedia = mediaFiles.map(f => ({
      dataUrl:  f.cloudUrl,
      type:     f.type,
      bytes:    f.bytes ?? 0,
      publicId: f.publicId ?? "",
      isVideo:  f.type?.startsWith("video/"),
    }));
    const msg = {
      text:       text.trim(),
      media:      allMedia.length > 0 ? allMedia[0] : null,
      mediaExtra: allMedia.length > 1 ? allMedia.slice(1) : [],
    };
    if (chatTab === "chapter" && chapterMembership) {
      try {
        const saved = await api.postChapterMessage(chapterMembership.chapter_id, msg);
        setChapterMessages(prev => [saved, ...prev]);
      } catch (err) { console.warn("chapter message failed:", err); }
    } else {
      await saveMessage(msg);
    }
    setText("");
    setMediaFiles([]);
  };

  const dismissFullPicker = () => {
    if (!showFullPicker) return;
    setPickerClosing(true);
    setTimeout(() => {
      setShowFullPicker(null); showFullPickerRef.current = null;
      setPickerClosing(false);
      setEmojiSearch("");
    }, 240);
  };

  const pickEmoji = (msgId, emoji) => {
    const msg = messages.find(m => m.id === msgId);
    const alreadyHas = msg?.reactions[emoji]?.includes(username);
    if (!alreadyHas) recordEmojiUse(emoji);
    saveReaction(msgId, alreadyHas ? null : emoji);
    setEmojiPickerFor(null);
    dismissFullPicker();
  };
  // kept as alias for reaction bar taps
  const toggleReaction = pickEmoji;

  // ── Lightbox (fullscreen media viewer) ──────────────────────────────────
  const openLightbox = (src, type) => { setLightboxSrc(src); setLightboxType(type); };
  const closeLightbox = () => { setLightboxSrc(null); setLightboxType(null); };

  // VideoLightbox: closes itself when the browser's native fullscreen/pip player exits
  const VideoLightbox = ({ src, onClose }) => {
    const videoRef = useRef(null);
    useEffect(() => {
      const vid = videoRef.current;
      if (!vid) return;
      // iOS Safari fires webkitendfullscreen when the native player is dismissed
      const onWebkitEnd = () => onClose();
      // Standard browsers fire fullscreenchange on document when exiting fullscreen
      const onFsChange = () => { if (!document.fullscreenElement) onClose(); };
      vid.addEventListener("webkitendfullscreen", onWebkitEnd);
      document.addEventListener("fullscreenchange", onFsChange);
      return () => {
        vid.removeEventListener("webkitendfullscreen", onWebkitEnd);
        document.removeEventListener("fullscreenchange", onFsChange);
      };
    }, [onClose]);
    return createPortal(
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.97)",
        zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <button onClick={e => { e.stopPropagation(); onClose(); }} style={{
          position: "absolute", top: 16, right: 16,
          background: "rgba(0,0,0,0.7)", border: "1px solid rgba(255,255,255,0.3)",
          borderRadius: "50%", width: 36, height: 36, color: "#fff",
          fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        }}>✕</button>
        <video ref={videoRef} src={src} controls autoPlay
          style={{ maxWidth: "95vw", maxHeight: "90svh", borderRadius: 4 }}
          onClick={e => e.stopPropagation()} />
      </div>,
      document.body
    );
  };

  const lightbox = lightboxSrc && (
    lightboxType === "audio"
      ? <AudioLightbox src={lightboxSrc} onClose={closeLightbox} />
      : lightboxType === "video"
      ? <VideoLightbox src={lightboxSrc} onClose={closeLightbox} />
      : createPortal(
          <div onClick={closeLightbox} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.97)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <button onClick={e => { e.stopPropagation(); closeLightbox(); }} style={{
              position: "absolute", top: 16, right: 16,
              background: "rgba(0,0,0,0.7)", border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "50%", width: 36, height: 36, color: "#fff",
              fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}>✕</button>
            <img src={lightboxSrc} alt="fullscreen" style={{ maxWidth: "95vw", maxHeight: "90svh", borderRadius: 4, objectFit: "contain" }} onClick={e => e.stopPropagation()} />
          </div>,
          document.body
        )
  );

  // ── Message list ────────────────────────────────────────────────────────
  // Rendered into a column-reverse container, so we reverse the array here
  // to keep chronological order (oldest top → newest bottom) visually correct.
  function buildMessageList(msgs) { return msgs.map((msg, i, arr) => {
    const isMe = msg.userId ? msg.userId === currentUser?.id : msg.author === username;
    const prevMsg = arr[i + 1];
    const nextMsg = arr[i - 1];
    const sameDay = (a, b) => {
      const da = new Date(a), db = new Date(b);
      return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
    };
    const grouped = prevMsg && prevMsg.author === msg.author && sameDay(msg.ts, prevMsg.ts);
    const isLastInGroup = !nextMsg || nextMsg.author !== msg.author || !sameDay(msg.ts, nextMsg.ts);
    const reactionEntries = Object.entries(msg.reactions).filter(([,users]) => users.length > 0);

    if (msg.isSystem) return (
      <div key={msg.id} style={{ textAlign:"center", margin:"12px 0", padding:"7px 18px",
        background:"rgba(255,51,68,0.08)", border:"1px solid rgba(255,51,68,0.25)",
        borderRadius:4, fontSize:11, color:"var(--muted)", letterSpacing:0.3 }}>
        {msg.text}
      </div>
    );

    return (
      <div key={msg.id}
        style={{ display:"flex", flexDirection: isMe ? "row-reverse" : "row", gap:8, alignItems:"flex-end", marginTop: grouped ? 0 : 8, position:"relative" }}
        onMouseLeave={() => setEmojiPickerFor(null)}>

        {!isMe && (
          <div style={{ width:48, flexShrink:0 }}>
            {isLastInGroup && (
              msg.avatarUrl
                ? <AvatarPhoto src={msg.avatarUrl} alt={msg.author} />
                : <div className="avatar sm" style={{ background:"linear-gradient(135deg,#001a10,#002e1a)", color:"#88ff00" }}>
                    {initials(msg.author)}
                    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"conic-gradient(from 198deg at 34% 24%, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.7) 22deg, rgba(255,255,255,0) 44deg, rgba(255,255,255,0) 360deg)",WebkitMaskImage:"radial-gradient(circle at 50% 50%,transparent 51%,black 57%,transparent 65%)",maskImage:"radial-gradient(circle at 50% 50%,transparent 51%,black 57%,transparent 65%)",pointerEvents:"none",zIndex:3}} />
                    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 40% 28% at 33% 22%,rgba(255,255,255,0.22) 0%,transparent 62%)",pointerEvents:"none",zIndex:3}} />
                    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 11% 8% at 31% 18%,rgba(255,255,255,1) 0%,transparent 100%)",pointerEvents:"none",zIndex:3}} />
                    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 6% 4% at 44% 29%,rgba(200,230,255,0.6) 0%,transparent 100%)",pointerEvents:"none",zIndex:3}} />
                    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 60% 18% at 50% 100%,rgba(0,0,0,0.38) 0%,transparent 65%)",pointerEvents:"none",zIndex:3}} />
                  </div>
            )}
          </div>
        )}

        <div style={{ maxWidth:"75%", display:"flex", flexDirection:"column", alignItems: isMe ? "flex-end" : "flex-start" }}>
          {/* Arch-admin real name reveal — shown above alias on hover */}
          {isArchAdmin && deleteHover === msg.id && msg.userId && allUsersMap[msg.userId] && allUsersMap[msg.userId] !== msg.author && (
            <div style={{ fontSize:9, color:"#ffcc00", fontFamily:"'Orbitron',sans-serif", letterSpacing:0.5, marginBottom:2, paddingLeft: isMe ? 0 : 2, paddingRight: isMe ? 2 : 0 }}>
              {allUsersMap[msg.userId]}
            </div>
          )}
          {!grouped && (
            <div style={{ fontSize:11, color:"var(--muted)", marginBottom:3, paddingLeft: isMe ? 0 : 2, paddingRight: isMe ? 2 : 0, fontFamily:"'Orbitron',sans-serif", letterSpacing:0.5 }}>
              <span style={{ fontWeight:700, color: isMe ? "var(--accent)" : "var(--chrome)" }}>{msg.author}</span>
              <span style={{ marginLeft:8 }}>{fmtChatTime(msg.ts)}</span>
            </div>
          )}

          <div
            className="msg-bubble"
            onMouseEnter={() => { if (canDelete(msg) && !isMobile) setDeleteHover(msg.id); }}
            onMouseLeave={() => { if (canDelete(msg) && !isMobile) setDeleteHover(null); }}
            onMouseDown={e => { if (canDelete(msg) && deleteHover === msg.id) e.stopPropagation(); }}
            onTouchStart={(e) => {
              if (!canDelete(msg)) return;
              e.preventDefault(); // suppress native text selection on long-press
              const id = msg.id;
              lastTapRef.current[id] = setTimeout(() => {
                setDeleteHover(prev => prev === id ? null : id);
              }, 600);
            }}
            onTouchEnd={() => {
              if (!canDelete(msg)) return;
              clearTimeout(lastTapRef.current[msg.id]);
            }}
            onTouchMove={() => {
              if (!canDelete(msg)) return;
              clearTimeout(lastTapRef.current[msg.id]);
            }}
            style={{
            position: "relative",
            userSelect: canDelete(msg) ? "none" : "text",
            WebkitUserSelect: canDelete(msg) ? "none" : "text",
            background: isMe
              ? `radial-gradient(ellipse 90% 50% at 50% -10%, rgba(255,255,255,0.35) 0%, transparent 60%),
                 radial-gradient(ellipse 60% 40% at 80% 90%, rgba(0,255,200,0.15) 0%, transparent 70%),
                 linear-gradient(160deg, rgba(0,180,140,0.22) 0%, rgba(0,80,60,0.12) 100%)`
              : `radial-gradient(ellipse 90% 50% at 50% -10%, rgba(255,255,255,0.35) 0%, transparent 55%),
                 radial-gradient(ellipse 60% 40% at 80% 90%, rgba(80,200,0,0.15) 0%, transparent 70%),
                 linear-gradient(160deg, rgba(100,180,0,0.22) 0%, rgba(60,120,0,0.12) 100%)`,
            border: emojiPickerFor === msg.id
              ? "1px solid rgba(136,255,0,0.75)"
              : isMe
                ? "1px solid rgba(0,255,204,0.25)"
                : "1px solid rgba(120,220,0,0.25)",
            borderTop: emojiPickerFor === msg.id
              ? "1px solid rgba(136,255,0,0.75)"
              : isMe ? "1.5px solid rgba(0,255,204,0.6)" : "1.5px solid rgba(160,255,60,0.5)",
            borderLeft: isMe ? "1px solid rgba(0,255,204,0.35)" : "1px solid rgba(120,220,0,0.2)",
            borderRadius: isMe ? "18px 3px 18px 18px" : "3px 18px 18px 18px",
            padding: msg.text ? "10px 14px" : (!msg.text && (msg.media || (msg.mediaExtra||[]).length > 0)) ? "0" : "4px",
            overflow: (!msg.text && (msg.media || (msg.mediaExtra||[]).length > 0)) ? "hidden" : "visible",
            fontSize:14, lineHeight:1.5, wordBreak:"break-word",
            boxShadow: emojiPickerFor === msg.id
              ? "0 0 0 2px rgba(136,255,0,0.15), 0 2px 16px rgba(136,255,0,0.2)"
              : isMe
                ? "inset 0 2px 0 rgba(0,255,204,0.15), inset 0 -3px 8px rgba(0,60,50,0.4), 0 12px 32px rgba(0,0,0,0.65), 0 3px 8px rgba(0,60,40,0.4), 0 0 0 0.5px rgba(0,0,0,0.6)"
                : "inset 0 2px 0 rgba(160,255,80,0.2), inset 0 -3px 8px rgba(20,60,0,0.25), 0 12px 32px rgba(0,0,0,0.65), 0 3px 8px rgba(20,60,0,0.3), 0 0 0 0.5px rgba(0,0,0,0.55)",
            color: isMe ? "#b0fff0" : "#d8ffaa",
            transition: "border 0.15s, box-shadow 0.15s",
          }}>
            {msg.text && <div style={{whiteSpace:"pre-wrap"}}>{msg.text}</div>}
            {msg.text && extractUrls(msg.text).map(url => (
              <LinkPreview key={url} url={url} />
            ))}
            {[msg.media, ...(msg.mediaExtra||[])].filter(Boolean).map((m, mi) => (
              <div key={mi} style={{ marginTop: (mi === 0 && msg.text) ? 8 : mi > 0 ? 6 : 0 }}>
                {m.type?.startsWith("image/") && (
                  <div style={{ width: isMobile ? "60vw" : 220 }}>
                    <img src={m.dataUrl} alt="attachment"
                      onClick={() => openLightbox(m.dataUrl, "image")}
                      style={{ display:"block", cursor:"pointer", width:"100%", height:"auto", borderRadius: msg.text ? 4 : "inherit" }} />
                  </div>
                )}
                {m.type?.startsWith("video/") && (
                  <div onClick={() => openLightbox(m.dataUrl, "video")} style={{ cursor:"pointer", position:"relative", lineHeight:0, width: isMobile ? "60vw" : 220 }}>
                    <VideoPlayer src={m.dataUrl} mt={0} />
                    <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="24" cy="24" r="24" fill="rgba(0,0,0,0.45)"/>
                        <polygon points="19,14 37,24 19,34" fill="rgba(255,255,255,0.92)"/>
                      </svg>
                    </div>
                  </div>
                )}
                {m.type?.startsWith("audio/") && (
                  <div onClick={() => openLightbox(m.dataUrl, "audio")}
                    style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer",
                    background:"rgba(0,255,204,0.06)", border:"1px solid var(--border)", borderRadius:4, padding:"8px 12px", minWidth:180 }}>
                    {/* Waveform SVG icon */}
                    <svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{flexShrink:0}}>
                      <rect x="0"  y="6" width="2" height="4"  rx="1" fill="rgba(0,255,140,0.7)"/>
                      <rect x="3"  y="3" width="2" height="10" rx="1" fill="rgba(0,255,140,0.85)"/>
                      <rect x="6"  y="0" width="2" height="16" rx="1" fill="#00ff99"/>
                      <rect x="9"  y="4" width="2" height="8"  rx="1" fill="rgba(0,255,140,0.85)"/>
                      <rect x="12" y="1" width="2" height="14" rx="1" fill="#00ff99"/>
                      <rect x="15" y="5" width="2" height="6"  rx="1" fill="rgba(0,255,140,0.85)"/>
                      <rect x="18" y="7" width="2" height="2"  rx="1" fill="rgba(0,255,140,0.6)"/>
                      <rect x="20" y="6" width="2" height="4"  rx="1" fill="rgba(0,255,140,0.4)"/>
                    </svg>
                    <span style={{ fontSize:12, color:"var(--accent)", fontFamily:"'Orbitron',sans-serif", letterSpacing:1 }}>TAP TO PLAY</span>
                  </div>
                )}
              </div>
            ))}
            {/* Arch-admin delete overlay — desktop: appears on bubble hover via CSS;
                mobile: appears after long-press sets deleteHover */}
            {canDelete(msg) && (
              <div
                className="delete-overlay"
                style={{
                  display: deleteHover === msg.id ? "flex" : "none",
                  position:"absolute", top:6, right:6,
                  alignItems:"center", gap:6,
                  zIndex: 10,
                }}>
                <button
                  onMouseDown={e => e.stopPropagation()}
                  onTouchStart={e => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this message?")) deleteMessage(msg.id); }}
                  style={{
                    background:"rgba(180,20,20,0.85)", border:"1px solid rgba(255,80,80,0.6)",
                    borderRadius:"50%", width:28, height:28, display:"flex", alignItems:"center",
                    justifyContent:"center", cursor:"pointer", color:"#fff", fontSize:14,
                    boxShadow:"0 2px 8px rgba(0,0,0,0.6)", lineHeight:1, padding:0,
                  }}
                  title="Delete message">
                  ✕
                </button>
              </div>
            )}
          </div>

          {reactionEntries.length > 0 && (
            <div style={{ display:"flex", gap:4, marginTop:4, flexWrap:"wrap", justifyContent: isMe ? "flex-end" : "flex-start" }}>
              {reactionEntries.map(([emoji, users]) => {
                const isMine = users.includes(username);
                const tooltipKey = `${msg.id}-${emoji}`;
                const isOpen = reactionTooltip?.key === tooltipKey;
                return (
                  <div key={emoji} style={{ position:"relative" }}>
                    <div
                      onClick={() => {
                        if (isMine) {
                          // Own reaction — toggle it off
                          toggleReaction(msg.id, emoji);
                        } else {
                          // Others' reaction — show who reacted
                          setReactionTooltip(isOpen ? null : { key: tooltipKey, users });
                        }
                      }}
                      style={{ display:"flex", alignItems:"center", gap:3,
                        background: isMine ? "rgba(140,255,0,0.12)" : "rgba(0,20,50,0.8)",
                        border:`1px solid ${isMine ? "var(--accent)" : "var(--border)"}`,
                        borderRadius:2, padding:"2px 8px", cursor:"pointer", fontSize:12, fontWeight:700,
                        color: isMine ? "var(--accent)" : "var(--muted)",
                        fontFamily:"'Orbitron',sans-serif", letterSpacing:1,
                        boxShadow: isMine ? "var(--glow-sm)" : "none",
                        transition:"all 0.15s" }}>
                      {emoji} {users.length}
                    </div>
                    {/* Reactor names tooltip */}
                    {isOpen && (
                      <div
                        onClick={e => { e.stopPropagation(); setReactionTooltip(null); }}
                        style={{
                          position:"absolute", bottom:"calc(100% + 6px)",
                          [isMe ? "right" : "left"]: 0,
                          background:"#0a1a0e", border:"1px solid rgba(136,255,0,0.25)",
                          borderRadius:6, padding:"6px 10px", zIndex:250, whiteSpace:"nowrap",
                          boxShadow:"0 4px 20px rgba(0,0,0,0.8)",
                          fontSize:11, color:"var(--chrome)", fontFamily:"'Orbitron',sans-serif",
                          letterSpacing:0.5, lineHeight:1.8,
                        }}>
                        {users.map(u => (
                          <div key={u} style={{ color: u === username ? "var(--accent)" : "var(--chrome)" }}>
                            {u === username ? "You" : u}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ position:"relative", display:"flex", alignItems:"center", gap:2 }}>
            {/* Quick emoji button */}
            <button
              onMouseDown={e => e.preventDefault()}
              onClick={() => {
                if (emojiPickerFor === msg.id) { setEmojiPickerFor(null); return; }
                setEmojiPickerFor(msg.id);
                setShowFullPicker(null);
                setEmojiSearch("");
              }}
              style={{ background:"none", border:"none", cursor:"pointer", color:"var(--muted)", fontSize:12, padding:"3px 6px", opacity:0.5, marginTop:2 }}>
              😊 +
            </button>

            {/* ── Quick-bar: single row of most-used + + button ── */}
            {emojiPickerFor === msg.id && (() => {
              const bar = (
                <div
                  onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                  onTouchStart={e => e.stopPropagation()}
                  style={isMobile ? {
                    position:"fixed", left:"50%", transform:"translateX(-50%)",
                    bottom: 90,
                    background:"#0a1a0e", border:"1px solid rgba(136,255,0,0.25)",
                    borderRadius:24, zIndex:500,
                    boxShadow:"0 4px 24px rgba(0,0,0,0.8), 0 0 16px rgba(136,255,0,0.08)",
                    display:"flex", alignItems:"center", padding:"4px 6px", gap:0,
                    overflowX:"auto", scrollbarWidth:"none",
                    maxWidth:"calc(100vw - 24px)",
                  } : {
                    position:"absolute", bottom:"calc(100% + 4px)", [isMe?"right":"left"]:0,
                    background:"#0a1a0e", border:"1px solid rgba(136,255,0,0.25)",
                    borderRadius:24, zIndex:200,
                    boxShadow:"0 4px 24px rgba(0,0,0,0.8), 0 0 16px rgba(136,255,0,0.08)",
                    display:"flex", alignItems:"center", padding:"4px 6px", gap:0,
                    overflowX:"auto", scrollbarWidth:"none",
                  }}>
                  {quickBarEmojis.map(e => (
                    <button key={e}
                      onMouseDown={ev => ev.preventDefault()}
                      onClick={() => pickEmoji(msg.id, e)}
                      style={{
                        background: (msg.reactions[e]||[]).includes(username) ? "rgba(136,255,0,0.2)" : "none",
                        border:"none", cursor:"pointer", fontSize:22,
                        borderRadius:"50%", width:36, height:36, lineHeight:1,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        transition:"background 0.1s", flexShrink:0,
                      }}>
                      {e}
                    </button>
                  ))}
                  {/* + opens full picker */}
                  <button
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => { setEmojiPickerFor(null); setShowFullPicker(msg.id); showFullPickerRef.current = msg.id; setEmojiCatIdx(0); setEmojiSearch(""); }}
                    style={{
                      background:"rgba(136,255,0,0.1)", border:"1px solid rgba(136,255,0,0.3)",
                      cursor:"pointer", color:"rgba(136,255,0,0.9)", fontSize:16, fontWeight:700,
                      borderRadius:"50%", width:30, height:30, lineHeight:1, marginLeft:4,
                      display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                    }}>
                    +
                  </button>
                </div>
              );
              return isMobile ? createPortal(bar, document.body) : bar;
            })()}

            {/* ── Full picker — portal so mobile can be a bottom sheet ── */}
            {showFullPicker === msg.id && (() => {
              const filteredEmojis = emojiSearch.trim()
                ? EMOJI_NAMES.filter(([,n]) => n.includes(emojiSearch.toLowerCase())).map(([e])=>e)
                : EMOJI_CATEGORIES[emojiCatIdx]?.emojis ?? [];

              const pickerContent = (() => {
                let swipeStartY = null;
                let swipeStartX = null;
                const HANDLE_ZONE = 44; // px from top of picker — only swipe from handle area
                const onSwipeTouchStart = (e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const touchY = e.touches[0].clientY;
                  if (touchY - rect.top > HANDLE_ZONE) return; // outside handle zone
                  swipeStartY = touchY;
                  swipeStartX = e.touches[0].clientX;
                };
                const onSwipeTouchEnd = (e) => {
                  if (swipeStartY === null) return;
                  const dy = e.changedTouches[0].clientY - swipeStartY;
                  const dx = Math.abs(e.changedTouches[0].clientX - swipeStartX);
                  swipeStartY = null; swipeStartX = null;
                  // Only dismiss if clearly downward and not a horizontal scroll
                  if (dy > 50 && dy > dx * 1.5) { dismissFullPicker(); }
                };
                return (
                <div
                  onMouseDown={e => e.stopPropagation()}
                  onTouchStart={e => { e.stopPropagation(); onSwipeTouchStart(e); }}
                  onTouchEnd={onSwipeTouchEnd}
                  style={isMobile ? {
                    position:"fixed", left:0, right:0, bottom:0,
                    background:"#0a1a0e", borderTop:"1px solid rgba(136,255,0,0.25)",
                    borderRadius:"16px 16px 0 0",
                    boxShadow:"0 -8px 40px rgba(0,0,0,0.9)",
                    zIndex:1000, display:"flex", flexDirection:"column",
                    maxHeight:"62vh",
                    animation: `${pickerClosing ? "emojiPickerSlideOut" : "emojiPickerSlideIn"} 0.24s cubic-bezier(0.32,0,0.67,0) both`,
                  } : {
                    position:"absolute", bottom:"calc(100% + 4px)", [isMe?"right":"left"]:0,
                    background:"#0a1a0e", border:"1px solid rgba(136,255,0,0.25)",
                    borderRadius:12, zIndex:300,
                    boxShadow:"0 8px 40px rgba(0,0,0,0.9), 0 0 24px rgba(136,255,0,0.08)",
                    width:300, display:"flex", flexDirection:"column",
                    maxHeight:420,
                  }}>
                  {/* Mobile drag handle */}
                  {isMobile && (
                    <div style={{ display:"flex", justifyContent:"center", padding:"10px 0 4px" }}>
                      <div style={{ width:36, height:4, borderRadius:2, background:"rgba(255,255,255,0.2)" }} />
                    </div>
                  )}
                  {/* Search bar — font-size 16px prevents iOS zoom */}
                  <div style={{ padding:"8px 10px 6px", borderBottom:"1px solid rgba(136,255,0,0.12)" }}>
                    <input
                      type="text"
                      placeholder="Search emoji…"
                      value={emojiSearch}
                      onChange={e => setEmojiSearch(e.target.value)}
                      onMouseDown={e => e.stopPropagation()}
                      onTouchStart={e => e.stopPropagation()}
                      autoComplete="off"
                      style={{
                        width:"100%", boxSizing:"border-box",
                        background:"rgba(255,255,255,0.07)", border:"1px solid rgba(136,255,0,0.2)",
                        borderRadius:8, padding:"7px 10px", color:"#fff",
                        fontSize:16, outline:"none",
                      }}
                    />
                  </div>
                  {/* Category tab bar — icons evenly spaced across full width */}
                  {!emojiSearch.trim() && (
                    <div style={{ display:"flex", borderBottom:"1px solid rgba(136,255,0,0.12)", background:"rgba(0,0,0,0.25)", flexShrink:0 }}>
                      {EMOJI_CATEGORIES.map((cat, ci) => (
                        <button key={ci}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => setEmojiCatIdx(ci)}
                          style={{
                            flex:1, border:"none", background:"none", cursor:"pointer",
                            padding:"8px 0", display:"flex", alignItems:"center", justifyContent:"center",
                            borderBottom: ci === emojiCatIdx ? "2px solid rgba(136,255,0,0.8)" : "2px solid transparent",
                            opacity: ci === emojiCatIdx ? 1 : 0.4,
                            color: ci === emojiCatIdx ? "rgba(136,255,0,0.9)" : "#fff",
                            transition:"opacity 0.15s, color 0.15s",
                          }}>
                          <span style={{ width:18, height:18, display:"block" }}
                            dangerouslySetInnerHTML={{ __html: EMOJI_CAT_ICONS[ci] }} />
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Emoji grid — key on container forces full remount on category change,
                      preventing emoji DOM nodes from being reused across categories */}
                  <div
                    key={emojiSearch.trim() ? "search" : `cat-${emojiCatIdx}`}
                    style={{ display:"flex", flexWrap:"wrap", overflowY:"auto", padding:6, gap:0, height: isMobile ? 260 : 200, alignContent:"flex-start" }}>
                    {filteredEmojis.length === 0
                      ? <div style={{ color:"var(--muted)", fontSize:13, padding:12 }}>No results</div>
                      : filteredEmojis.map((e, ei) => (
                        <button key={`${emojiSearch.trim() ? "s" : emojiCatIdx}-${ei}`}
                          onMouseDown={ev => ev.preventDefault()}
                          onClick={() => pickEmoji(msg.id, e)}
                          style={{
                            background: (msg.reactions[e]||[]).includes(username) ? "rgba(136,255,0,0.15)" : "none",
                            border:"none", cursor:"pointer", fontSize:isMobile?26:22,
                            borderRadius:6, padding:isMobile?"7px":"5px", lineHeight:1,
                            transition:"background 0.1s",
                            minWidth: isMobile ? 44 : 36,
                          }}>
                          {e}
                        </button>
                      ))
                    }
                  </div>
                </div>
                );
              })();

              return isMobile
                ? createPortal(
                    <>
                      {/* Backdrop */}
                      <div onClick={() => dismissFullPicker()}
                        style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:999 }} />
                      {pickerContent}
                    </>,
                    document.body
                  )
                : pickerContent;
            })()}
          </div>
        </div>
      </div>
    );
  }); }
  const globalMessageList = buildMessageList([...messages].reverse());
  const chapterMessageList = chapterMembership ? buildMessageList(chapterMessages) : [];
  // Desktop uses global list (or chapter list if on chapter tab); mobile carousel uses both
  const messageList = chatTab === "chapter" && chapterMembership ? chapterMessageList : globalMessageList;

  // ── Input bar (shared desktop + mobile) ─────────────────────────────────
  const inputBar = (
    <>
      {attachWarning && (
        <div style={{
          padding: "8px 12px", marginBottom: 6,
          background: "rgba(255,60,60,0.15)", border: "1px solid rgba(255,60,60,0.4)",
          borderRadius: 4, color: "#ff6060", fontSize: 12,
          fontFamily: "'Orbitron',sans-serif", letterSpacing: 0.5,
        }}>
          You cannot attach more than three files to a message.
        </div>
      )}

      {/* File previews */}
      {mediaFiles.length > 0 && (
        <div style={{ display:"flex", gap:8, marginBottom:6, flexWrap:"wrap" }}>
          {mediaFiles.map(f => (
            <div key={f.id} style={{ position:"relative", height:60, borderRadius:4, overflow:"hidden", background:"#000", minWidth:52, flexShrink:0 }}>
              {f.type?.startsWith("video/")
                ? <video src={f.blobUrl} style={{ height:60, objectFit:"cover" }} muted />
                : f.type?.startsWith("audio/")
                ? <div style={{ height:60, width:80, display:"flex", alignItems:"center", justifyContent:"center", background:"var(--surface2)", border:"1px solid var(--border)" }}>
                    <span style={{ fontSize:20 }}>🎵</span>
                  </div>
                : <img src={f.blobUrl} alt="preview" style={{ height:60, objectFit:"cover" }} />
              }
              {f.uploading && (
                <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <div style={{ fontSize:10, color:"#fff", fontWeight:700, fontFamily:"'Orbitron',sans-serif" }}>{f.progress ?? 0}%</div>
                </div>
              )}
              {!f.uploading && f.cloudUrl && (
                <div style={{ position:"absolute", bottom:2, right:4, fontSize:9, color:"var(--accent)", fontWeight:700 }}>✓</div>
              )}
              <button onClick={() => setMediaFiles(prev => prev.filter(x => x.id !== f.id))}
                style={{ position:"absolute", top:2, right:2, background:"rgba(0,0,0,0.75)", border:"none", borderRadius:"50%", width:16, height:16, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#fff", fontSize:9, lineHeight:1, padding:0 }}>✕</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
        <input ref={fileRef} type="file" accept="image/*,video/*,audio/*" style={{ display:"none" }} onChange={handleFile} />
        <button onClick={() => { if (mediaFiles.length >= 3) { showAttachWarning(); } else fileRef.current?.click(); }}
          style={{ background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:2, padding:"0 10px", height:40,
            cursor:"pointer", color: uploading ? "var(--accent)" : "var(--muted)", fontSize:16, flexShrink:0, transition:"all 0.15s" }}
          title="Attach photo, video, or audio">📎</button>
        <textarea
          ref={textareaRef}
          placeholder="Send a message..."
          value={text}
          onChange={e => setText(e.target.value)}
          onFocus={() => { chatInputFocusedRef.current = true; }}
          onBlur={() => { chatInputFocusedRef.current = false; }}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          rows={1}
          style={{ flex:1, resize:"none", borderRadius:2, padding:"10px 14px", fontSize:16, minHeight:40, maxHeight:140, overflowY:"auto", lineHeight:1.5, opacity:1 }}
        />
        <button onClick={sendMessage} className="btn btn-primary"
          style={{ flexShrink:0, padding:"0 16px", height:40, borderRadius:2, opacity: uploading ? 0.5 : 1 }}
          disabled={(!text.trim() && mediaFiles.length === 0) || uploading}>
          {uploading ? "…" : "SEND"}
        </button>
      </div>
    </>
  );

  // ── Mobile layout: fixed full-screen flex column ─────────────────────────
  if (isMobile) {
    const showChapter = chatTab === "chapter";
    // Callback ref — always points to the active scroll container
    const setActiveScrollRef = (el) => { scrollContainerRef.current = el; };
    return (
      <>
        {lightbox}
        <div ref={chatRootRef} className="chat-mobile-root">
          {chapterMembership && (
            <div className="tab-pill">
              {["global","chapter"].map(tab => (
                <div key={tab} style={{
                  width: chatTab===tab ? 18 : 6,
                  height:6, borderRadius:3, flexShrink:0,
                  background: chatTab===tab ? "#88ff00" : "rgba(136,255,0,0.25)",
                  transition:"all 0.25s ease",
                }} />
              ))}
              <PillScrollText label={showChapter ? chapterMembership.chapter_name.toUpperCase() : "GLOBAL"} />
            </div>
          )}
          {/* Carousel track — slides between global (0%) and chapter (-50%) */}
          <div style={{flex:1, overflow:"hidden", minHeight:0}}>
            <div style={{
              display:"flex", height:"100%",
              width: chapterMembership ? "200%" : "100%",
              transform: showChapter && chapterMembership ? "translateX(-50%)" : "translateX(0)",
              transition: chapterMembership ? "transform 0.32s cubic-bezier(0.4,0,0.2,1)" : "none",
            }}>
              {/* Global panel — always renders global messages */}
              <div style={{width: chapterMembership ? "50%" : "100%", flexShrink:0, display:"flex", flexDirection:"column", minHeight:0}}>
                <div
                  ref={!showChapter ? setActiveScrollRef : undefined}
                  className="chat-mobile-messages"
                  style={{ padding: "60px 14px 20px", display: "flex", flexDirection: "column-reverse", willChange: "transform" }}>
                  {globalMessageList}
                  <LoadOlderSentinel hasMore={hasMore} loading={loadingOlder} count={messages.length} onLoad={loadOlder} />
                </div>
              </div>
              {/* Chapter panel — always renders chapter messages */}
              {chapterMembership && (
                <div style={{width:"50%", flexShrink:0, display:"flex", flexDirection:"column", minHeight:0}}>
                  <div
                    ref={showChapter ? setActiveScrollRef : undefined}
                    className="chat-mobile-messages"
                    style={{ padding: "60px 14px 20px", display: "flex", flexDirection: "column-reverse", willChange: "transform" }}>
                    {chapterMsgLoading
                      ? <div style={{color:"var(--muted)",fontSize:13,textAlign:"center",padding:20}}>Loading…</div>
                      : <>
                          {chapterMessageList}
                          <LoadOlderSentinel hasMore={chapterHasMore} loading={chapterLoadingOlder} count={chapterMessages.length} onLoad={loadOlderChapter} />
                        </>}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="chat-mobile-input">
            {inputBar}
          </div>
        </div>
      </>
    );
  }

  // ── Desktop layout: same as before ──────────────────────────────────────
  return (
    <>
      {lightbox}
      <div style={{ display:"flex", flexDirection:"column", height:"calc(100vh - 0px)", overflow:"hidden", position:"relative" }}>
        {chapterMembership && (
          <div style={{display:"flex",justifyContent:"flex-end",borderBottom:"1px solid rgba(136,255,0,0.12)",
            background:"rgba(0,8,4,0.98)",flexShrink:0,zIndex:5}}>
            {["global","chapter"].map(tab => (
              <button key={tab} onClick={()=>setChatTab(tab)}
                style={{padding:"12px 24px",border:"none",background:"none",cursor:"pointer",
                  fontFamily:"'Orbitron',sans-serif",fontSize:10,letterSpacing:2,textTransform:"uppercase",
                  color: chatTab===tab ? "#88ff00" : "var(--muted)",
                  borderBottom: chatTab===tab ? "2px solid #88ff00" : "2px solid transparent",
                  transition:"all 0.2s"}}>
                {tab === "global" ? "Global" : chapterMembership.chapter_name}
              </button>
            ))}
          </div>
        )}
        <div ref={scrollContainerRef} style={{ flex:1, overflowY:"scroll", overscrollBehavior:"none", WebkitOverflowScrolling:"auto", padding:"120px 28px 130px", display:"flex", flexDirection:"column-reverse", gap:4, willChange:"transform" }}>
          {chapterMsgLoading ? (
            <div style={{color:"var(--muted)",fontSize:13,textAlign:"center",padding:20}}>Loading…</div>
          ) : (
            <>
              {messageList}
              {chatTab === "chapter" && chapterMembership
                ? <LoadOlderSentinel hasMore={chapterHasMore} loading={chapterLoadingOlder} count={chapterMessages.length} onLoad={loadOlderChapter} />
                : <LoadOlderSentinel hasMore={hasMore} loading={loadingOlder} count={messages.length} onLoad={loadOlder} />}
            </>
          )}
        </div>

        {/* Desktop attach warning */}
        {attachWarning && (
          <div style={{
            position:"fixed", left:360, right:0, zIndex:12,
            bottom:150, margin:"0 28px", padding:"10px 16px",
            background:"rgba(255,60,60,0.15)", border:"1px solid rgba(255,60,60,0.4)",
            borderRadius:4, color:"#ff6060", fontSize:13,
            fontFamily:"'Orbitron',sans-serif", letterSpacing:0.5,
          }}>
            You cannot attach more than three files to a message.
          </div>
        )}

        {/* Desktop file previews */}
        {mediaFiles.length > 0 && (
          <div style={{ position:"fixed", bottom:78, left:360, right:0, padding:"8px 28px", display:"flex", gap:10, zIndex:11 }}>
            {mediaFiles.map(f => (
              <div key={f.id} style={{ position:"relative", height:72, borderRadius:4, overflow:"hidden", background:"#000", minWidth:64, flexShrink:0 }}>
                {f.type?.startsWith("video/") ? <video src={f.blobUrl} style={{ height:72, objectFit:"cover" }} muted />
                  : f.type?.startsWith("audio/") ? <div style={{ height:72, width:100, display:"flex", alignItems:"center", justifyContent:"center", background:"var(--surface2)", border:"1px solid var(--border)" }}><span style={{ fontSize:24 }}>🎵</span></div>
                  : <img src={f.blobUrl} alt="preview" style={{ height:72, objectFit:"cover" }} />
                }
                {f.uploading && <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:4 }}><div style={{ fontSize:10, color:"#fff", fontWeight:700, fontFamily:"'Orbitron',sans-serif" }}>{f.progress ?? 0}%</div></div>}
                {!f.uploading && f.cloudUrl && <div style={{ position:"absolute", bottom:3, right:5, fontSize:10, color:"var(--accent)", fontWeight:700 }}>✓</div>}
                <button onClick={() => setMediaFiles(prev => prev.filter(x => x.id !== f.id))}
                  style={{ position:"absolute", top:3, right:3, background:"rgba(0,0,0,0.75)", border:"none", borderRadius:"50%", width:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#fff", fontSize:10, lineHeight:1, padding:0 }}>✕</button>
              </div>
            ))}
          </div>
        )}

        {/* Desktop input bar */}
        <div style={{ position:"fixed", bottom:0, left:360, right:0, padding:"12px 28px", borderTop:"1px solid var(--border)", background:"rgba(0,8,4,0.45)", display:"flex", gap:10, alignItems:"stretch", zIndex:10 }}>
          <input ref={fileRef} type="file" accept="image/*,video/*,audio/*" style={{ display:"none" }} onChange={handleFile} />
          <button onClick={() => { if (mediaFiles.length >= 3) { showAttachWarning(); } else fileRef.current?.click(); }}
            style={{ background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:2, padding:"0 12px", cursor:"pointer", color: uploading ? "var(--accent)" : "var(--muted)", fontSize:16, flexShrink:0, transition:"all 0.15s" }}
            title="Attach photo, video, or audio">📎</button>
          <textarea
            ref={textareaRef}
            placeholder="Send a message..."
            value={text}
            onChange={e => setText(e.target.value)}
            onFocus={() => { chatInputFocusedRef.current = true; }}
            onBlur={() => { chatInputFocusedRef.current = false; }}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            rows={1}
            style={{ flex:1, resize:"none", borderRadius:2, padding:"10px 14px", fontSize:14, minHeight:42, maxHeight:120, overflowY:"auto", lineHeight:1.5, opacity:1 }}
          />
          <button onClick={sendMessage} className="btn btn-primary"
            style={{ flexShrink:0, padding:"0 20px", borderRadius:2, opacity: uploading ? 0.5 : 1 }}
            disabled={(!text.trim() && mediaFiles.length === 0) || uploading}>
            {uploading ? "UPLOADING…" : "SEND"}
          </button>
        </div>
      </div>
    </>
  );
}


// ─── EXERCISE DEFINITIONS ─────────────────────────────────────────────────────
const EXERCISES = [
  // Chest
  { name:"Bench Press",       group:"Chest",     type:"weighted"   },
  { name:"Incline Bench Press",group:"Chest",    type:"weighted"   },
  { name:"Dumbbell Press",    group:"Chest",     type:"weighted"   },
  { name:"Push-up",           group:"Chest",     type:"bodyweight" },
  // Back
  { name:"Pull-up",           group:"Back",      type:"bodyweight" },
  { name:"Weighted Pull-up",  group:"Back",      type:"weighted"   },
  { name:"Lat Pulldown",      group:"Back",      type:"weighted"   },
  { name:"Cable Row",         group:"Back",      type:"weighted"   },
  // Shoulders
  { name:"Overhead Press",    group:"Shoulders", type:"weighted"   },
  { name:"Lateral Raise",     group:"Shoulders", type:"weighted"   },
  // Arms
  { name:"Curl",              group:"Arms",      type:"weighted"   },
  { name:"Dips",              group:"Arms",      type:"bodyweight" },
  { name:"Weighted Dips",     group:"Arms",      type:"weighted"   },
  { name:"Chin-up",           group:"Arms",      type:"bodyweight" },
  { name:"Chin-up",           group:"Back",      type:"bodyweight" },
  // Legs
  { name:"Squat",             group:"Legs",      type:"weighted"   },
  { name:"Deadlift",          group:"Legs",      type:"weighted"   },
  { name:"Hex-Bar Deadlift",  group:"Legs",      type:"weighted"   },
  { name:"Leg Press",         group:"Legs",      type:"weighted"   },
  { name:"Leg Curl",          group:"Legs",      type:"weighted"   },
  { name:"Leg Extension",     group:"Legs",      type:"weighted"   },
  { name:"Bulgarian Split Squat", group:"Legs",  type:"weighted"   },
  // Core
  { name:"Plank",             group:"Core",      type:"duration"   },
  { name:"Reverse Hyper-extension",  group:"Core", type:"bodyweight" },
  { name:"Back Extension",           group:"Core", type:"bodyweight" },
  { name:"Weighted Back Extension",  group:"Core", type:"weighted"   },
];
const EXERCISE_GROUPS = ["Chest","Back","Shoulders","Arms","Legs","Core"];
const EXERCISE_MAP    = Object.fromEntries(EXERCISES.map(e => [e.name, e]));
const EXERCISE_LIST   = EXERCISES.map(e => e.name); // backward compat

// ── Body-weight-adjusted exercises ────────────────────────────────────────────
// For these weighted exercises part of the lifter's own body weight is moved along
// with the added weight, so 1RM estimates must include it. The value is the
// fraction of body weight that counts as load (approximate): pull-ups and dips
// move essentially all of it. A back extension moves only the head/arms/trunk above
// the hips (~60-68% of body mass), but that mass sits closer to the hip than a plate
// held at the chest, so in plate-equivalent terms (torque about the hip) it counts
// as ~0.45. Keep in sync with BW_FRACTIONS in server.js.
const BW_FRACTIONS = {
  "Weighted Pull-up":        1.0,
  "Weighted Dips":           1.0,
  "Weighted Back Extension": 0.45,
};
// Set by Top Charts' "add body weight" button; the Workout page scrolls to the body weight panel on load
let bodyWeightScrollPending = false;
const isBwExercise = (name) => Object.prototype.hasOwnProperty.call(BW_FRACTIONS, name);

// Estimated 1RM expressed as added weight (same formula as the server's computePrValue).
// Epley is run on the total load, then the body weight share is subtracted back out.
function estAddedWeight1RM(exercise, weight, reps, bodyWeight) {
  const r = reps || 1;
  const bwLoad = BW_FRACTIONS[exercise] * bodyWeight;
  const total  = bwLoad + (Number(weight) || 0);
  const est    = r === 1 ? total : total * (1 + r / 30);
  return Math.round(est - bwLoad);
}

// Body weight that applies at timestamp `ts`: the most recent weigh-in on or before
// it; sets that predate the first weigh-in use the first weigh-in.
// `entries` is [{ weight, loggedAt }] sorted oldest first.
function bodyWeightAt(entries, ts) {
  if (!entries || entries.length === 0) return null;
  let bw = entries[0].weight;
  for (const e of entries) {
    if (e.loggedAt <= ts) bw = e.weight; else break;
  }
  return bw;
}

// Normalize date strings — old system used "Mar 8", new uses "Mar 8, 2026"
// normDate: normalises the session date string for use as a display/grouping key.
// Old sessions: "Jan 15" (no year — stored without year, pre-fix)
// New sessions: "2025-01-15" (ISO, unambiguous)
// Returns the date as-is for grouping — the rawTs field carries the real timestamp.
const normDate = (d) => d ? d.trim() : d;

// ─── STYLES ───────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  button { -webkit-appearance: none; appearance: none; }

  :root {
    --bg:        #020800;
    --surface:   #030a00;
    --surface2:  #061200;
    --border:    #0d2200;
    --accent:    #88ff00;
    --accent2:   #66cc00;
    --lime:      #88ff00;
    --teal:      #44cc00;
    --cyan:      #aaff44;
    --chrome:    #f4ffe8;
    --text:      #d8f5c0;
    --muted:     #5a8a30;
    --danger:    #ff3344;
    --radius:    2px;
    --glow:      0 0 20px #88ff00bb, 0 0 60px #88ff0044, 0 0 100px #88ff0011;
    --glow-sm:   0 0 8px #88ff0099, 0 0 24px #88ff0033;
    --glow-lime: 0 0 10px #88ff0099, 0 0 28px #88ff0033;
    --glow-cyan: 0 0 10px #aaff4499, 0 0 28px #aaff4433;
    --chrome-grad: linear-gradient(155deg, #ffffff 0%, #eeffcc 30%, #aaff44 60%, #66dd00 85%, #338800 100%);
    --energy-grad: linear-gradient(135deg, #88ff00 0%, #aaff44 40%, #ccff88 80%, #88ff00 100%);
    --depth-grad:  linear-gradient(135deg, #88ff00 0%, #66cc00 50%, #aaff44 100%);
  }

  /* ── KEYFRAMES ── */
  @keyframes gridBreath {
    0%,100% { opacity: 1; }
    50%     { opacity: 0.7; }
  }
  @keyframes scanline {
    0%   { background-position: 0 0; }
    100% { background-position: 0 8px; }
  }
  @keyframes logoBeam {
    0%   { letter-spacing: 32px; opacity: 0; filter: blur(12px) brightness(3); }
    55%  { letter-spacing: 9px;  opacity: 0.9; filter: blur(2px) brightness(1.5); }
    100% { letter-spacing: 6px;  opacity: 1;   filter: blur(0) brightness(1); }
  }
  @keyframes trace {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @keyframes energyBeat {
    0%,100% { box-shadow: 0 0 20px rgba(136,255,0,0.2), inset 0 0 40px rgba(0,255,180,0.03); }
    50%     { box-shadow: 0 0 40px rgba(0,255,180,0.45), inset 0 0 60px rgba(0,255,180,0.07); }
  }
  @keyframes float {
    0%,100% { transform: translateY(0px); }
    50%     { transform: translateY(-4px); }
  }
  @keyframes sheen {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes voidPulse {
    0%,100% { opacity: 1; }
    50%     { opacity: 0.6; }
  }
  @keyframes depthSweep {
    0%   { background-position: 50% 0%; }
    100% { background-position: 50% 100%; }
  }
  @keyframes crossGlitter {
    0%   { opacity: 0.82; filter: brightness(1.0) drop-shadow(0 0 8px rgba(220,235,255,0.7)); }
    50%  { opacity: 1.0;  filter: brightness(1.3) drop-shadow(0 0 20px rgba(255,255,255,0.9)); }
    100% { opacity: 0.88; filter: brightness(1.1) drop-shadow(0 0 12px rgba(200,220,255,0.75)); }
  }
  @keyframes emojiPickerSlideIn {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
  @keyframes emojiPickerSlideOut {
    from { transform: translateY(0); }
    to   { transform: translateY(100%); }
  }
  @keyframes rankGlow {
    0%,100% { box-shadow: 0 0 20px rgba(136,255,0,0.2), inset 0 0 40px rgba(136,255,0,0.03); }
    50%     { box-shadow: 0 0 40px rgba(136,255,0,0.45), inset 0 0 60px rgba(136,255,0,0.07); }
  }
  @keyframes slideInFromRight {
    from { transform: translateX(100%); }
    to   { transform: translateX(0); }
  }
  @keyframes slideInFromLeft {
    from { transform: translateX(-100%); }
    to   { transform: translateX(0); }
  }
  @keyframes slideOutToLeft {
    from { transform: translateX(0); }
    to   { transform: translateX(-100%); }
  }
  @keyframes slideOutToRight {
    from { transform: translateX(0); }
    to   { transform: translateX(100%); }
  }
  .slide-in-right  { animation: slideInFromRight 0.32s cubic-bezier(0.4,0,0.2,1) both; }
  .slide-in-left   { animation: slideInFromLeft  0.32s cubic-bezier(0.4,0,0.2,1) both; }
  .slide-out-left  { animation: slideOutToLeft   0.32s cubic-bezier(0.4,0,0.2,1) both; }
  .slide-out-right { animation: slideOutToRight  0.32s cubic-bezier(0.4,0,0.2,1) both; }
  .tab-pill {
    position: fixed;
    top: 10px;
    right: 14px;
    z-index: 200;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 12px;
    background: rgba(0,10,5,0.4);
    border: 1px solid rgba(136,255,0,0.35);
    border-radius: 999px;
    box-shadow: 0 0 12px rgba(136,255,0,0.2), inset 0 0 8px rgba(136,255,0,0.04);
    pointer-events: none;
    overflow: hidden;
  }
  .tab-pill-text {
    position: relative;
    overflow-x: hidden;
    overflow-y: visible;
    width: 48px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
  .tab-pill-text::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 12px;
    background: linear-gradient(to right, rgba(0,10,5,0.9), transparent);
    z-index: 1;
    opacity: 0;
    transition: opacity 0.3s;
  }
  .tab-pill-text::after {
    content: '';
    position: absolute;
    right: 0; top: 0; bottom: 0;
    width: 12px;
    background: linear-gradient(to left, rgba(0,10,5,0.9), transparent);
    z-index: 1;
  }
  .tab-pill-text.scrolling::before { opacity: 1; }
  @keyframes pillMarquee {
    from { transform: translateX(var(--pill-marquee-from, 48px)); }
    to   { transform: translateX(var(--pill-marquee-to, -200px)); }
  }
  .tab-pill-marquee {
    display: inline-block;
    white-space: nowrap;
    font-size: 9px;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
    color: rgba(136,255,0,0.7);
    animation: pillMarquee var(--pill-duration, 8s) linear 0s infinite;
  }
  .tab-pill-inner {
    display: inline-block;
    white-space: nowrap;
    font-size: 9px;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
    color: rgba(136,255,0,0.7);
  }

  /* ── Portrait lock — block landscape on mobile ── */
  @media (orientation: landscape) and (max-width: 900px) and (pointer: coarse) {
    .landscape-block {
      display: flex !important;
    }
  }

  /* ── BASE ── */
  body {
    background: var(--bg); color: var(--text);
    font-family: 'Rajdhani', sans-serif; font-size: 15px; line-height: 1.5;
    min-height: 100vh; overflow-x: hidden; overscroll-behavior: none;
  }

  /* CRT scanlines */
  body::before {
    content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 9999;
    background: repeating-linear-gradient(
      0deg,
      rgba(0,0,0,0.07) 0px, rgba(0,0,0,0.07) 1px,
      transparent 1px, transparent 5px
    );
    animation: scanline 0.08s steps(1) infinite;
  }

  /* ── 3D PERSPECTIVE GRID ── */
  /* The whole trick: two pseudo-elements stacked.
     .grid-floor  = the receding floor plane (perspective-transformed flat grid)
     .grid-fog    = depth fog that fades out the far end
     .app-bg      = the atmospheric color wash + stars */

  .app-bg {
    position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background:
      radial-gradient(ellipse 60% 55% at 50% 100%, rgba(20,60,0,0.55)  0%, transparent 65%),
      radial-gradient(ellipse 40% 35% at 50%  60%, rgba(10,40,0,0.4)   0%, transparent 60%),
      radial-gradient(ellipse 80% 30% at 50%   0%, rgba(8,30,0,0.5)    0%, transparent 80%),
      radial-gradient(ellipse 30% 60% at  0%  50%, rgba(0,10,20,0.15)  0%, transparent 55%),
      radial-gradient(ellipse 30% 60% at 100% 50%, rgba(0,5,15,0.15)   0%, transparent 55%),
      #020800;
    overflow: hidden;
  }

  /* Perspective grid floor */
  .app-bg::before {
    content: ''; position: absolute;
    left: -50%; right: -50%;
    top: 0%; bottom: -5%;
    background-image:
      linear-gradient(rgba(136,255,0,0.38) 1px, transparent 1px),
      linear-gradient(90deg, rgba(136,255,0,0.38) 1px, transparent 1px);
    background-size: 80px 80px;
    background-position: 50% 0%;
    transform: perspective(600px) rotateX(82deg) translateY(35%);
    transform-origin: 50% 50%;
    animation: depthSweep 3.6s linear infinite;
    mask-image: linear-gradient(180deg, transparent 0%, black 15%, black 100%);
  }

  /* Depth fog */
  .app-bg::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg,
      #020800 0%,
      rgba(2,8,0,0.0) 28%,
      rgba(2,8,0,0.0) 100%
    );
  }

  /* Star-field */
  .grid-stars {
    position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image:
      radial-gradient(1px 1px at  8%  12%, rgba(136,255,0,0.5) 0%, transparent 100%),
      radial-gradient(1px 1px at 22%  78%, rgba(170,255,50,0.4) 0%, transparent 100%),
      radial-gradient(1px 1px at 37%  31%, rgba(180,255,50,0.4) 0%, transparent 100%),
      radial-gradient(1px 1px at 55%  88%, rgba(136,255,0,0.5) 0%, transparent 100%),
      radial-gradient(1px 1px at 67%  19%, rgba(160,255,30,0.4) 0%, transparent 100%),
      radial-gradient(1px 1px at 80%  54%, rgba(150,255,30,0.4) 0%, transparent 100%),
      radial-gradient(1px 1px at 91%  7%,  rgba(136,255,0,0.5) 0%, transparent 100%),
      radial-gradient(1px 1px at 14%  44%, rgba(120,230,0,0.3) 0%, transparent 100%),
      radial-gradient(1px 1px at 46%  66%, rgba(140,255,20,0.3) 0%, transparent 100%),
      radial-gradient(1px 1px at 73%  92%, rgba(136,255,0,0.4) 0%, transparent 100%),
      radial-gradient(1.5px 1.5px at 30% 5%,  rgba(136,255,0,0.6) 0%, transparent 100%),
      radial-gradient(1.5px 1.5px at 85% 38%, rgba(160,255,20,0.6) 0%, transparent 100%),
      radial-gradient(1.5px 1.5px at 60% 55%, rgba(180,255,30,0.5) 0%, transparent 100%);
    animation: gridBreath 6s ease-in-out infinite;
  }

  .app { display: flex; min-height: 100vh; position: relative; z-index: 1; overflow: visible; }

  /* ── SIDEBAR — orb + blade nav ── */
  .sidebar {
    width: 280px; min-width: 280px;
    background: transparent;
    display: flex; flex-direction: column;
    padding: 0; position: sticky; top: 0; height: 100vh;
    overflow: visible; justify-content: center;
    align-items: flex-start;
    z-index: 10;
    transition: width 0.4s cubic-bezier(0.4,0,0.2,1), min-width 0.4s cubic-bezier(0.4,0,0.2,1);
  }
  .sidebar.nav-collapsed {
    width: 90px; min-width: 90px;
  }
  .sidebar::after { display: none; }

  /* Orb container */
  .xbox-orb-wrap {
    position: absolute;
    left: -60px;
    top: calc(50% - 25.5px);
    transform: translateY(-50%);
    width: 220px; height: 220px;
    pointer-events: all;
    cursor: pointer;
    z-index: 4;
  }
  .xbox-orb {
    position: absolute; inset: 0; border-radius: 50%;
    background: radial-gradient(circle at 38% 35%,
      #eeff88 0%, #aaff00 15%, #66dd00 35%, #009900 60%, #001a00 100%
    );
    box-shadow:
      0 0 30px #aaff00cc,
      0 0 60px #88ff0099,
      0 0 100px #44cc0066,
      0 0 160px #22880033,
      inset 0 0 40px rgba(255,255,255,0.25),
      inset -15px -15px 50px rgba(0,0,0,0.4);
    animation: orbPulse 3s ease-in-out infinite;
  }
  .xbox-orb::after { display: none; }
  .xbox-bubble {
    position: absolute; border-radius: 50%;
    z-index: 4;
    background: radial-gradient(circle at 35% 30%, rgba(180,255,80,0.7), rgba(0,180,0,0.3) 60%, transparent);
    border: 1px solid rgba(136,255,0,0.4);
    box-shadow: 0 0 12px rgba(136,255,0,0.3);
    animation: bubbleFloat 4s ease-in-out infinite;
  }
  .xbox-bubble:nth-child(2) { width:38px; height:38px; top:8%;  left:62%; animation-delay:0s;    animation-duration:3.8s; z-index:5; }
  .xbox-bubble:nth-child(3) { width:24px; height:24px; top:22%; left:80%; animation-delay:0.7s;  animation-duration:4.5s; }
  .xbox-bubble:nth-child(4) { width:18px; height:18px; top:55%; left:84%; animation-delay:1.4s;  animation-duration:3.2s; }
  .xbox-bubble:nth-child(5) { width:30px; height:30px; top:72%; left:68%; animation-delay:0.3s;  animation-duration:5s;   }
  .xbox-bubble:nth-child(6) { width:14px; height:14px; top:80%; left:82%; animation-delay:1.9s;  animation-duration:4.1s; }
  @keyframes orbPulse {
    0%,100% { box-shadow: 0 0 30px #aaff00cc, 0 0 60px #88ff0099, 0 0 100px #44cc0066, 0 0 160px #22880033, inset 0 0 40px rgba(255,255,255,0.25), inset -15px -15px 50px rgba(0,0,0,0.4); }
    50%      { box-shadow: 0 0 50px #ccff00ee, 0 0 90px #aaff00bb, 0 0 140px #66ee0088, 0 0 200px #33990044, inset 0 0 55px rgba(255,255,255,0.35), inset -15px -15px 50px rgba(0,0,0,0.4); }
  }
  @keyframes bubbleFloat {
    0%,100% { transform: translateY(0) scale(1); opacity: 0.8; }
    50%      { transform: translateY(-6px) scale(1.06); opacity: 1; }
  }

  /* Nav wrap — retractable blade container */
  .nav-wrap {
    position: absolute;
    left: 150px;
    top: calc(50% - 25.5px);
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    gap: 5px;
    z-index: 3;
    pointer-events: all;
    transition: opacity 0.35s ease, transform 0.35s cubic-bezier(0.4,0,0.2,1);
  }
  .nav-wrap.retracted {
    opacity: 0;
    pointer-events: none;
    transform: translateY(-50%) translateX(-55px);
  }

  /* Xbox blade nav items */
  .nav-item {
    position: relative;
    width: 220px;
    height: 46px;
    padding: 0 20px;
    cursor: pointer;
    pointer-events: none;
    font-family: 'Orbitron', sans-serif;
    font-size: 11px; font-weight: 900;
    letter-spacing: 3px; text-transform: uppercase;
    transition: all 0.15s ease;
    border: 1px solid rgba(136,255,0,0.15);
    background: rgba(0,20,0,0.55);
    color: rgba(136,255,120,0.55);
    display: flex; align-items: center;
    clip-path: polygon(
      0.00% 84.00%,
      1.58% 100.00%,
      9.47% 100.00%,
      10.69% 84.00%,
      100.00% 84.00%,
      100.00% 20.00%,
      97.37%  0.00%,
      1.58%   0.00%,
      0.00%  12.29%
    );
    animation: navIdle 8s ease-in-out infinite;
  }
  .nav-item::after {
    content: '';
    position: absolute; inset: 0;
    backdrop-filter: blur(4px);
    pointer-events: none;
    z-index: -1;
  }
  .nav-item:nth-child(1) { animation-delay:  0.0s; }
  .nav-item:nth-child(2) { animation-delay: -1.6s; }
  .nav-item:nth-child(3) { animation-delay: -3.2s; }
  .nav-item:nth-child(4) { animation-delay: -4.8s; }
  .nav-item:nth-child(5) { animation-delay: -5.6s; }
  .nav-item:nth-child(6) { animation-delay: -2.4s; }
  @keyframes navIdle {
    0%,100% { transform: translate( 0.0px,  0.0px); }
    33%      { transform: translate( 1.2px, -1.8px); }
    66%      { transform: translate(-0.8px,  1.2px); }
  }
  .nav-item-wrap {
    position: relative;
    width: 220px;
    height: 46px;
    cursor: pointer;
    flex-shrink: 0;
    transition: width 0.15s ease;
    background: transparent;
    pointer-events: all;
  }
  .nav-item-wrap.active-wrap {
    width: 270px;
  }
  .nav-item-wrap.active-wrap .nav-item {
    background: linear-gradient(90deg, #aaee00 0%, #88cc00 60%, #669900 100%);
    color: #001a00;
    border-color: #ccff00;
    box-shadow: 0 0 18px #88ff0088, 0 0 40px #44cc0044, inset 0 1px 0 rgba(255,255,255,0.3);
    text-shadow: none;
    transform: translateX(6px) scaleY(1.06);
    font-size: 12px;
    width: 100%;
  }
  .nav-item-wrap .nav-item {
    pointer-events: none;
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
                box-shadow 0.15s ease, transform 0.15s ease, font-size 0.15s ease, width 0.15s ease;
  }
  .nav-item-wrap:hover .nav-item {
    background: rgba(0,60,0,0.7);
    color: rgba(200,255,150,0.85);
    border-color: rgba(136,255,0,0.4);
  }
  .nav-item-wrap:nth-child(1) .nav-item { animation-delay:  0.0s; }
  .nav-item-wrap:nth-child(2) .nav-item { animation-delay: -1.6s; }
  .nav-item-wrap:nth-child(3) .nav-item { animation-delay: -3.2s; }
  .nav-item-wrap:nth-child(4) .nav-item { animation-delay: -4.8s; }
  .nav-item-wrap:nth-child(5) .nav-item { animation-delay: -5.6s; }
  .nav-item-wrap:nth-child(6) .nav-item { animation-delay: -2.4s; }
  .nav-item::before { display: none; }
  .nav-item:hover {
    background: rgba(0,60,0,0.7);
    color: rgba(200,255,150,0.85);
    border-color: rgba(136,255,0,0.4);
  }
  .nav-item.active::before { display: none; }
  .xbox-orb-wrap { z-index: 4 !important; }

  /* Logo */
  .logo {
    position: absolute;
    top: 22px; left: 22px;
    font-family: 'Orbitron', sans-serif; font-weight: 900;
    text-transform: uppercase;
    background: var(--chrome-grad);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: drop-shadow(0 0 14px rgba(136,255,0,0.5));
    animation: logoBeam 1.4s cubic-bezier(0.16,1,0.3,1) both;
    line-height: 1.15;
    pointer-events: none;
    transition: font-size 0.4s cubic-bezier(0.4,0,0.2,1);
    white-space: nowrap;
  }
  .logo-l1 {
    display: block; font-size: inherit; letter-spacing: 5px;
    transition: letter-spacing 0.4s cubic-bezier(0.4,0,0.2,1);
  }
  .logo-l2 {
    display: block; font-size: inherit; letter-spacing: 10.5px;
    transition: letter-spacing 0.4s cubic-bezier(0.4,0,0.2,1);
  }
  .logo span { -webkit-text-fill-color: transparent; }

  .user-badge {
    margin-top: auto; padding: 16px 22px;
    display: flex; align-items: center; gap: 10px;
    position: relative;
  }
  .user-badge::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: var(--energy-grad); background-size: 200% 100%;
    animation: trace 4s linear infinite;
    opacity: 0.5;
  }

  .avatar {
    width: 36px; height: 36px; border-radius: 50%;
    background: linear-gradient(135deg, #001a0e, #002d18);
    color: var(--accent); display: flex; align-items: center; justify-content: center;
    font-weight: 900; font-size: 11px; font-family: 'Orbitron', sans-serif;
    flex-shrink: 0; position: relative; overflow: hidden;
    border: 1px solid rgba(136,255,0,0.35);
    box-shadow:
      0 0 0 1px rgba(0,0,0,0.55),
      0 10px 20px rgba(0,0,0,0.85),
      0 20px 40px rgba(0,0,0,0.45),
      0 0 14px rgba(136,255,0,0.3),
      0 0 32px rgba(136,255,0,0.12);
    filter: drop-shadow(0 0 6px rgba(136,255,0,0.5)) drop-shadow(0 0 18px rgba(136,255,0,0.25));
    animation: energyBeat 5s ease-in-out infinite;
  }
  .avatar.sm { width: 45px; height: 45px; font-size: 12px; }
  .avatar.lg { width: 48px; height: 48px; font-size: 14px; }
  .avatar::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: radial-gradient(
      circle at 50% 50%,
      transparent 50%,
      rgba(0,0,0,0.4) 68%,
      rgba(0,0,0,0.85) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
  .avatar::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: linear-gradient(155deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.06) 25%, transparent 42%);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.35), inset 1px 0 0 rgba(255,255,255,0.1);
    pointer-events: none;
    z-index: 2;
  }

  /* Avatar with photo — circular with PS Vita-style glass sphere effect */
  .avatar-photo {
    position: relative;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    border: 1px solid rgba(136,255,0,0.35);
    box-shadow:
      0 0 0 1px rgba(0,0,0,0.55),
      0 10px 20px rgba(0,0,0,0.85),
      0 20px 40px rgba(0,0,0,0.45),
      0 0 14px rgba(136,255,0,0.3),
      0 0 32px rgba(136,255,0,0.12);
    filter: drop-shadow(0 0 6px rgba(136,255,0,0.5)) drop-shadow(0 0 18px rgba(136,255,0,0.25));
    animation: energyBeat 5s ease-in-out infinite;
  }
  .avatar-photo img {
    width: 100%; height: 100%; object-fit: cover; display: block;
  }
  /* Minimal rim darkening only — just the very edge, not the body of the image */
  .avatar-photo::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: radial-gradient(
      circle at 50% 50%,
      transparent 50%,
      rgba(0,0,0,0.4) 68%,
      rgba(0,0,0,0.85) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
  /* Specular highlight only — no bottom shadow overlay on the image */
  .avatar-photo::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background:
      linear-gradient(155deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.06) 25%, transparent 42%);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.35), inset 1px 0 0 rgba(255,255,255,0.1);
    pointer-events: none;
    z-index: 2;
  }
  /* Additional specular layers via a third pseudo — use a wrapper div instead */
  .avatar-photo.sm { width: 45px; height: 45px; }
  .avatar-photo.lg { width: 48px; height: 48px; }

  /* ── MAIN ── */
  .main { flex: 1; overflow-y: auto; position: relative; padding-bottom: 10px; z-index: 20; margin-left: 80px; }
  .mobile-only { display: none !important; }

  .page { padding: 40px 48px; max-width: 1000px; margin: 0 auto; }
  .app { display: flex; min-height: 100vh; position: relative; z-index: 1; overflow: visible; }
  .page > *:not(:first-child) { position: relative; z-index: 1; }
  .page-title { position: relative; z-index: 1; }

  .page-title {
    font-family: 'Orbitron', sans-serif; font-size: 28px; font-weight: 900;
    letter-spacing: 6px; margin-bottom: 6px; text-transform: uppercase;
    background: var(--chrome-grad);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    filter: drop-shadow(0 0 20px rgba(136,255,0,0.7)) drop-shadow(0 0 60px rgba(136,255,0,0.2));
    animation: float 7s ease-in-out infinite;
  }
  .page-sub {
    color: var(--muted); font-size: 12px; margin-bottom: 36px;
    letter-spacing: 2px; text-transform: uppercase; font-family: 'Orbitron', sans-serif;
  }
  .accentText {
    background: var(--energy-grad); background-size: 300% 300%;
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    animation: sheen 3s ease-in-out infinite;
  }

  /* ── CARDS ── */
  .card {
    background: linear-gradient(160deg, rgba(0,22,13,0.55) 0%, rgba(0,7,4,0.38) 50%, rgba(0,3,2,0.45) 100%);
    border: 1px solid rgba(6,51,34,0.9);
    border-top: 1px solid rgba(136,255,0,0.18);
    border-left: 1px solid rgba(136,255,0,0.10);
    border-radius: var(--radius); padding: 24px; margin-bottom: 16px;
    position: relative; overflow: hidden; z-index: 1;
    box-shadow:
      0 2px 0 rgba(136,255,0,0.08),
      0 8px 24px rgba(0,0,0,0.55),
      0 24px 64px rgba(0,0,0,0.5),
      0 48px 80px rgba(0,0,0,0.3),
      inset 0 1px 0 rgba(136,255,0,0.12),
      inset 0 0 60px rgba(136,255,0,0.02);
  }
  /* Animated cyan trace across top */
  .card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg,
      transparent 0%, transparent 30%,
      rgba(136,255,0,0.0) 38%,
      rgba(136,255,0,0.95) 47%,
      rgba(170,255,30,0.95) 53%,
      rgba(136,255,0,0.0) 62%,
      transparent 70%, transparent 100%
    );
    background-size: 300% 100%;
    animation: trace 3.5s linear infinite;
  }
  /* Corner bracket — top-left in lime */
  .card::after {
    content: ''; position: absolute; top: 0; left: 0;
    width: 14px; height: 14px;
    border-top: 1px solid rgba(136,255,0,0.6);
    border-left: 1px solid rgba(136,255,0,0.6);
  }
  .card-title {
    font-family: 'Orbitron', sans-serif; font-weight: 700; font-size: 11px;
    margin-bottom: 20px; display: flex; align-items: center; gap: 10px;
    letter-spacing: 2px; text-transform: uppercase; color: var(--accent);
    text-shadow: 0 0 12px #88ff00aa, 0 0 30px #88ff0033;
  }
  .card-title::before {
    content: '◆'; font-size: 8px; color: var(--lime);
    filter: drop-shadow(0 0 6px #88ff00cc);
    animation: voidPulse 3s ease-in-out infinite;
  }

  /* ── BUTTONS ── */
  .btn {
    display: inline-flex; align-items: center; gap: 8px; padding: 10px 24px;
    border-radius: var(--radius);
    font-family: 'Orbitron', sans-serif; font-weight: 700;
    font-size: 11px; letter-spacing: 2px; text-transform: uppercase;
    cursor: pointer; border: none; transition: all 0.2s;
    position: relative; overflow: hidden;
  }
  .btn::after {
    content: ''; position: absolute; top: 0; left: -120%; width: 80%; height: 100%;
    background: linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 60%, transparent 70%);
    transition: left 0.5s cubic-bezier(0.4,0,0.2,1);
    pointer-events: none;
  }
  .btn:hover::after { left: 140%; }
  .btn-primary {
    background: linear-gradient(180deg,
      rgba(0,160,110,0.95) 0%,
      rgba(0,90,65,0.98)  45%,
      rgba(0,40,30,1)     100%
    );
    color: #fff;
    border: 1px solid rgba(0,255,180,0.35);
    box-shadow: var(--glow-sm),
                inset 0 1px 0 rgba(255,255,255,0.18),
                inset 0 0 30px rgba(136,255,0,0.06);
  }
  .btn-primary:hover {
    background: linear-gradient(180deg, #00ddaa 0%, #008866 45%, #004433 100%);
    box-shadow: var(--glow), inset 0 1px 0 rgba(255,255,255,0.25);
    transform: translateY(-1px);
  }
  .btn-ghost {
    background: transparent; color: var(--muted);
    border: 1px solid rgba(13,34,0,0.9);
  }
  .btn-ghost:hover {
    color: var(--accent); border-color: rgba(136,255,0,0.4);
    box-shadow: var(--glow-sm); transform: translateY(-1px);
  }
  .btn-sm { padding: 5px 14px; font-size: 10px; letter-spacing: 1.5px; }

  /* ── INPUTS ── */
  input, textarea, select {
    background: rgba(0,10,6,0.92);
    border: 1px solid rgba(6,51,34,0.9);
    border-radius: var(--radius); color: var(--text);
    font-family: 'Rajdhani', sans-serif; font-size: 15px; font-weight: 500; line-height: 1.4;
    padding: 10px 14px; width: 100%; outline: none; transition: all 0.25s;
    box-shadow: inset 0 0 30px rgba(0,0,0,0.6);
  }
  input:focus, textarea:focus, select:focus {
    border-color: rgba(136,255,0,0.5);
    box-shadow: var(--glow-sm), inset 0 0 30px rgba(136,255,0,0.03);
    background: rgba(0,16,9,0.96);
  }
  textarea { resize: vertical; min-height: 80px; }
  .form-label {
    font-size: 11px; color: var(--muted); margin-bottom: 6px;
    font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
    font-family: 'Orbitron', sans-serif;
  }

  /* ── TABLE ── */
  .log-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .log-table th {
    text-align: left; color: var(--accent); font-weight: 700; font-size: 11px;
    text-transform: uppercase; letter-spacing: 1.5px; padding: 8px 12px;
    border-bottom: 1px solid rgba(6,51,34,0.9);
    font-family: 'Orbitron', sans-serif;
  }
  .log-table td { padding: 10px 12px; border-bottom: 1px solid rgba(6,51,34,0.4); line-height: 1.4; }
  .log-table tr:last-child td { border-bottom: none; }
  .log-table tr:hover td { background: rgba(136,255,0,0.02); }

  .badge {
    display: inline-block; padding: 2px 10px; border-radius: 1px; font-size: 11px;
    font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
    background: rgba(136,255,0,0.06); color: var(--accent);
    border: 1px solid rgba(136,255,0,0.2); font-family: 'Orbitron', sans-serif;
  }

  /* ── TABS ── */
  .tab-row { display: flex; gap: 5px; margin-bottom: 20px; flex-wrap: wrap; }
  .tab {
    padding: 6px 14px; border-radius: var(--radius); font-size: 11px; font-weight: 700;
    cursor: pointer; border: 1px solid rgba(6,51,34,0.9); color: var(--muted);
    transition: all 0.2s; letter-spacing: 1px; text-transform: uppercase;
    font-family: 'Orbitron', sans-serif;
  }
  .tab:hover { border-color: rgba(136,255,0,0.35); color: var(--chrome); }
  .tab.active {
    background: linear-gradient(180deg, rgba(0,100,70,0.85), rgba(0,50,35,0.95));
    color: #fff; border-color: rgba(136,255,0,0.45);
    box-shadow: var(--glow-sm);
    text-shadow: 0 0 10px rgba(136,255,0,0.7);
  }

  /* ── AUDIO PLAYER ── */
  .player-bar {
    position: fixed;
    bottom: calc(25vh - 145px);
    left: 5px;
    width: 365px;
    z-index: 1000;
    background: none;
    border: none;
    border-radius: 0;
    padding: 10px 0 10px 22px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow: visible;
    box-shadow: none;
    pointer-events: none;
    transition: transform 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.35s ease;
  }
  .player-bar.retracted {
    transform: translateX(calc(-100% - 5px));
    opacity: 0;
    pointer-events: none;
  }
  .player-bar::before { display: none; }
  .player-bar::after  { display: none; }
  .track-info { min-width: 0; position: relative; z-index: 1; overflow: hidden; text-align: left; pointer-events: all; }
  .track-title {
    font-weight: 700; font-size: 10px; letter-spacing: 2px; text-transform: uppercase;
    font-family: 'Orbitron', sans-serif;
    color: #00ff99;
    -webkit-text-fill-color: #00ff99;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    filter: drop-shadow(0 0 6px rgba(0,255,140,0.9)) drop-shadow(0 0 14px rgba(0,255,100,0.6));
  }
  .track-artist {
    font-size: 10px; letter-spacing: 1px; margin-top: 2px; text-align: left;
    color: rgba(0,255,140,0.6);
    filter: drop-shadow(0 0 5px rgba(0,255,140,0.7));
  }
  .player-controls { display: flex; align-items: center; gap: 8px; position: relative; z-index: 1; pointer-events: all; }
  .ctrl-btn {
    background: none;
    border: 1px solid rgba(0,255,140,0.25);
    border-radius: 50%;
    width: 30px; height: 30px;
    color: rgba(0,255,140,0.75); cursor: pointer;
    font-size: 12px;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
    filter: drop-shadow(0 0 4px rgba(0,255,140,0.5));
    -webkit-appearance: none;
    appearance: none;
  }
  .ctrl-btn:hover {
    color: #88ff00;
    border-color: rgba(136,255,0,0.6);
    filter: drop-shadow(0 0 10px rgba(136,255,0,0.8)) drop-shadow(0 0 20px rgba(136,255,0,0.4));
    transform: scale(1.15);
  }
  .ctrl-btn:disabled { opacity: 0.25; cursor: default; transform: none; filter: none; }
  .play-btn {
    background: none;
    color: #00ff99; border: 1px solid rgba(0,255,140,0.5);
    border-radius: 50%; width: 44px; height: 44px; font-size: 15px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    filter:
      drop-shadow(0 0 8px rgba(0,255,140,0.9))
      drop-shadow(0 0 20px rgba(0,255,100,0.6))
      drop-shadow(0 0 40px rgba(0,200,80,0.3));
    transition: all 0.2s;
    animation: energyBeat 3.5s ease-in-out infinite;
    position: relative; z-index: 1;
    -webkit-appearance: none;
    appearance: none;
  }
  .play-btn:hover {
    filter:
      drop-shadow(0 0 16px #00ff88)
      drop-shadow(0 0 40px rgba(0,255,136,0.7))
      drop-shadow(0 0 70px rgba(0,255,100,0.4));
    transform: scale(1.1);
  }
  .play-btn:disabled { opacity: 0.25; cursor: default; transform: none; animation: none; filter: none; }
  .progress-wrap {
    display: flex; align-items: center; gap: 8px; font-size: 9px;
    color: rgba(0,255,140,0.6); font-family: 'Orbitron', sans-serif; letter-spacing: 0.5px;
    position: relative; z-index: 1;
    filter: drop-shadow(0 0 4px rgba(0,255,140,0.6));
    pointer-events: all;
  }
  .progress-bar {
    flex: 1; height: 44px;
    background: transparent;
    border-radius: 999px;
    cursor: pointer; position: relative;
    display: flex; align-items: center;
  }
  .progress-bar::before {
    content: ''; position: absolute; left: 0; right: 0;
    height: 3px; border-radius: 999px;
    background: rgba(0,255,140,0.12);
  }
  .progress-fill {
    position: absolute; left: 0; height: 3px; border-radius: 999px;
    background: linear-gradient(90deg, #00ff99, #88ff00);
    box-shadow: 0 0 8px #00ff88, 0 0 18px rgba(0,255,140,0.5);
    transition: width 0.25s linear;
  }

  /* ── TRACK LIST ── */
  .track-row {
    pointer-events: all;
    display: flex; align-items: center; gap: 14px; padding: 11px 14px;
    border-radius: var(--radius); cursor: pointer; transition: all 0.2s;
    border: 1px solid transparent; position: relative; overflow: hidden;
  }
  .track-row::before {
    content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 0;
    background: linear-gradient(90deg, rgba(136,255,0,0.07), rgba(170,255,30,0.04), transparent);
    transition: width 0.25s;
  }
  .track-row:hover::before { width: 100%; }
  .track-row:hover { border-color: rgba(136,255,0,0.08); }
  .track-row.playing { border-color: rgba(136,255,0,0.18); background: rgba(136,255,0,0.025); }
  .track-row.playing .track-title {
    color: var(--accent) !important;
    -webkit-text-fill-color: var(--accent) !important;
    text-shadow: 0 0 12px #88ff00aa !important;
  }
  .track-num { width: 22px; text-align: center; font-size: 12px; color: var(--muted); font-family: 'Orbitron', sans-serif; }

  /* ── STAT TILES ── */
  .stat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 28px; }
  .stat-tile {
    background: linear-gradient(160deg, rgba(0,22,13,0.55) 0%, rgba(0,10,5,0.35) 50%, rgba(0,3,2,0.42) 100%);
    border: 1px solid rgba(6,51,34,0.9);
    border-top: 1px solid rgba(136,255,0,0.18);
    border-left: 1px solid rgba(136,255,0,0.10);
    border-radius: var(--radius); padding: 22px; position: relative; overflow: hidden;
    animation: float 7s ease-in-out infinite;
    box-shadow:
      0 2px 0 rgba(136,255,0,0.08),
      0 8px 20px rgba(0,0,0,0.55),
      0 20px 48px rgba(0,0,0,0.45),
      inset 0 1px 0 rgba(136,255,0,0.12),
      inset 0 0 40px rgba(136,255,0,0.02);
  }
  .stat-tile:nth-child(2) { animation-delay: -2.3s; }
  .stat-tile:nth-child(3) { animation-delay: -4.6s; }
  .stat-tile::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg,
      transparent 0%, transparent 35%,
      rgba(136,255,0,0.85) 47%,
      rgba(136,255,0,0.85)  53%,
      transparent 65%, transparent 100%
    );
    background-size: 300% 100%;
    animation: trace 4.5s linear infinite;
  }
  /* Corner bracket — bottom-right in cyan */
  .stat-tile::after {
    content: ''; position: absolute; bottom: 0; right: 0;
    width: 10px; height: 10px;
    border-bottom: 1px solid rgba(170,255,30,0.5);
    border-right: 1px solid rgba(170,255,30,0.5);
  }
  .stat-num {
    font-family: 'Orbitron', sans-serif; font-size: 28px; font-weight: 900;
    line-height: 1; letter-spacing: 2px;
    background: linear-gradient(135deg, #ffffff 0%, #aaffee 30%, #88ff00 60%, #88ff00 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    filter: drop-shadow(0 0 16px rgba(136,255,0,0.8));
  }
  .stat-label {
    font-size: 11px; color: var(--muted); margin-top: 10px;
    letter-spacing: 2px; text-transform: uppercase; font-family: 'Orbitron', sans-serif;
  }

  /* ── MODAL ── */
  .modal-bg {
    position: fixed; inset: 0; z-index: 100;
    background: rgba(0,4,2,0.90);
    backdrop-filter: blur(12px) saturate(1.8);
    display: flex; align-items: center; justify-content: center;
  }
  .modal {
    background: linear-gradient(155deg, rgba(0,12,7,0.99) 0%, rgba(0,5,3,1) 100%);
    border: 1px solid rgba(6,51,34,0.9);
    border-radius: var(--radius); padding: 32px; width: 500px; max-width: 95vw;
    box-shadow: 0 0 100px rgba(0,0,0,0.95), var(--glow), var(--glow-lime);
    position: relative; overflow: hidden;
  }
  .modal::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: var(--energy-grad); background-size: 300% 300%;
    animation: sheen 2.5s ease-in-out infinite;
  }
  .modal::after {
    content: ''; position: absolute; inset: 0; pointer-events: none;
    background:
      radial-gradient(ellipse 50% 40% at 0%   0%,   rgba(170,255,30,0.06),  transparent),
      radial-gradient(ellipse 50% 40% at 100% 100%, rgba(136,255,0,0.05),  transparent),
      radial-gradient(ellipse 40% 30% at 100%  0%,   rgba(136,255,0,0.04),  transparent);
  }
  .modal-title {
    font-family: 'Orbitron', sans-serif; font-size: 19px; font-weight: 900;
    letter-spacing: 5px; margin-bottom: 24px; text-transform: uppercase;
    background: var(--chrome-grad);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    filter: drop-shadow(0 0 12px rgba(136,255,0,0.6));
    position: relative; z-index: 1;
  }
  .flex-end { display: flex; justify-content: flex-end; gap: 10px; margin-top: 18px; }

  /* ─────────────────────────────────────────────────────────────────────────
     MOBILE LAYOUT  (≤768px) — desktop CSS above is completely untouched
     ───────────────────────────────────────────────────────────────────────── */
  @media (max-width: 768px) {

    /* ── Remove sidebar from flex flow so .main can take full width ── */
    .app {
      display: block !important;
      min-height: 100vh;
      overflow-x: hidden;
    }

    /* ── Sidebar: fixed overlay, never takes layout space ── */
    .sidebar {
      position: fixed !important;
      left: 0; top: 0; bottom: 0;
      width: 80px !important; min-width: 80px !important;
      height: 100vh;
      z-index: 300;
      justify-content: center;
      align-items: flex-start;
      overflow: visible;
      transition: none !important;
    }
    .sidebar.nav-collapsed {
      /* Shrink sidebar to only cover the small orb footprint — prevents phantom taps */
      width: 24px !important; min-width: 24px !important;
      pointer-events: none;  /* sidebar shell is inert; orb-wrap re-enables itself */
    }
    /* Re-enable pointer events only on the orb itself when collapsed */
    .sidebar.nav-collapsed .xbox-orb-wrap {
      pointer-events: all !important;
    }

    /* ── Nav tabs: completely inert when retracted (fix phantom taps) ── */
    .nav-wrap.retracted,
    .nav-wrap.retracted * {
      pointer-events: none !important;
      user-select: none !important;
    }

    /* ── Logo: single line, slides fully off left when nav collapses ── */
    .logo {
      font-size: 5.5vw !important;
      top: 14px; left: 10px;
      line-height: 1.2;
      letter-spacing: 0.12em !important;
      opacity: 1 !important;
      transform: translateX(0) !important;
      transition: opacity 0.35s ease, transform 0.35s cubic-bezier(0.4,0,0.2,1) !important;
      animation: none !important;
    }
    .logo-l1 { letter-spacing: 0.12em !important; }
    .logo-l2 { letter-spacing: 0.12em !important; }
    .sidebar.nav-collapsed .logo {
      opacity: 0 !important;
      transform: translateX(-40px) !important;
      pointer-events: none !important;
    }

    /* ── Orb: full size when nav open, scales down + shifts right when collapsed ── */
    .xbox-orb-wrap {
      position: absolute;
      left: -60px !important;
      top: 62px;
      transform: scale(1) !important;
      transform-origin: left center;
      width: 220px !important; height: 220px !important;
      transition: transform 0.4s cubic-bezier(0.4,0,0.2,1), left 0.4s cubic-bezier(0.4,0,0.2,1) !important;
    }
    /* Collapsed: scale down and shift right so it's easier to tap */
    .sidebar.nav-collapsed .xbox-orb-wrap {
      transform: scale(0.35) !important;
      left: -42px !important;
    }

    /* ── Nav tabs: fixed overlay, retractable ── */
    .nav-wrap {
      position: fixed !important;
      left: 64px !important;
      top: 62px !important;
      transform: none !important;
      z-index: 299 !important;
      pointer-events: all;
    }
    .nav-wrap.retracted {
      opacity: 0 !important;
      transform: translateX(-40px) !important;
      pointer-events: none !important;
    }
    .nav-item-wrap .nav-item {
      transition: transform 0.1s ease, font-size 0.1s ease !important;
    }
    /* No hover state on mobile — only selected or unselected */
    .nav-item-wrap:hover .nav-item,
    .nav-item:hover {
      background: transparent !important;
      color: var(--muted) !important;
      border-color: rgba(136,255,0,0.12) !important;
    }
    /* Active tab: hover must be identical to active state so it never visually changes */
    .nav-item-wrap.active-wrap:hover .nav-item,
    .nav-item.active:hover {
      background: linear-gradient(90deg, #aaee00 0%, #88cc00 60%, #669900 100%) !important;
      color: #001a00 !important;
      border-color: #ccff00 !important;
      box-shadow: 0 0 18px #88ff0088, 0 0 40px #44cc0044, inset 0 1px 0 rgba(255,255,255,0.3) !important;
    }

    /* ── Denser grid plane on mobile — double the line frequency ── */
    /* ── Slower scroll + raised front end + thinner lines ── */
    .app-bg::before {
      background-image:
        linear-gradient(rgba(136,255,0,0.38) 0.5px, transparent 0.5px),
        linear-gradient(90deg, rgba(136,255,0,0.38) 0.5px, transparent 0.5px) !important;
      background-size: 20px 20px !important;
      animation-duration: 14s !important;
      transform: perspective(600px) rotateX(88deg) translateY(35%) !important;
    }
    .onboard-grid-floor {
      background-image:
        linear-gradient(rgba(136,255,0,0.38) 0.5px, transparent 0.5px),
        linear-gradient(90deg, rgba(136,255,0,0.38) 0.5px, transparent 0.5px) !important;
      background-size: 20px 20px !important;
      animation-duration: 14s !important;
      transform: perspective(600px) rotateX(88deg) translateY(35%) !important;
    }

    /* ── Main: full viewport, slides in from right when nav retracts ── */
    .main {
      display: block !important;
      width: 100vw !important;
      height: 100vh;
      overflow-y: auto;
      margin-left: 0 !important;
      padding-left: 0 !important;
      padding-bottom: 0 !important;
      box-sizing: border-box;
      z-index: 50;
      position: relative;
      transform: translateX(100vw);
      transition: transform 0.38s cubic-bezier(0.4,0,0.2,1) !important;
    }
    .main.nav-open  { transform: translateX(100vw) !important; }
    .main.nav-closed { transform: translateX(0)    !important; }
    .main.in-call { overflow: hidden !important; touch-action: none; }
    .mobile-only { display: flex !important; }

    /* Prevent iOS Safari zoom on input focus — requires font-size >= 16px */
    input, textarea, select { font-size: 16px !important; }

    /* ── Page: compact padding, smaller title ── */
    .page { padding: 20px 14px !important; }
    .page-title { font-size: 20px !important; margin-bottom: 4px !important; }
    .page-sub   { font-size: 11px !important; margin-bottom: 16px !important; }

    /* ── Stat tiles: 3 equal columns, smaller text ── */
    .stat-grid  { grid-template-columns: repeat(3,1fr) !important; gap: 8px !important; margin-bottom: 16px !important; }
    .stat-num   { font-size: 22px !important; }
    .stat-label { font-size: 9px !important; letter-spacing: 1px !important; }

    /* ── Cards ── */
    .card       { padding: 14px 12px !important; margin-bottom: 12px !important; }
    .card-title { font-size: 11px !important; margin-bottom: 10px !important; }
    .pr-badge   { top:10px !important; right:10px !important; padding:2px 6px !important; gap:3px !important; }
    .pr-badge .pr-badge-label { display:none !important; }
    .pr-badge .pr-badge-value { font-size:9px !important; }

    /* ── Log table: smaller, contained ── */
    .log-table              { font-size: 11px !important; width: 100% !important; }
    .log-table th,
    .log-table td           { padding: 6px 4px !important; }
    .badge                  { font-size: 9px !important; padding: 2px 5px !important; }

    /* ── Exercise tab pills ── */
    .tab-row { gap: 4px !important; margin-bottom: 12px !important; flex-wrap: wrap !important; }
    .tab     { font-size: 9px !important; padding: 5px 8px !important; }

    /* ── Bible page: stack columns ── */
    .bible-grid { grid-template-columns: 1fr !important; }

    /* ── Workout: 2-col chart grid → 1 col ── */
    .workout-chart-grid { grid-template-columns: 1fr !important; }

    /* ── Audio player: transparent background, same as desktop ── */
    .player-bar {
      position: fixed !important;
      bottom: 0 !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      border-radius: 0 !important;
      padding: 8px 14px 18px !important;
      background: none !important;
      border: none !important;
      box-shadow: none !important;
      z-index: 400 !important;
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 6px !important;
    }
    .player-bar.retracted {
      transform: translateY(110%) !important;
      opacity: 0 !important;
    }

    /* ── Remove ALL borders from audio control buttons (the "rounded squares") ── */
    .ctrl-btn, .play-btn {
      border: none !important;
      font-variant-emoji: text !important;
      -webkit-font-smoothing: antialiased !important;
    }
    .ctrl-btn {
      width: 28px !important; height: 28px !important;
    }

    /* ── Modal sits ABOVE sidebar (300) and nav tabs (299) ──
       Without this, tapping inputs fires nav tap events instead */
    .modal-bg { z-index: 500 !important; }

    /* ── Chat page: fixed full-screen column, keyboard-safe ── */
    /* 
       The fundamental problem with height-based approaches (svh/dvh/vh) is that
       .main has a CSS transform, which creates a new stacking context and causes
       any position:fixed children to fix relative to .main, not the viewport.
       But we also can't use position:fixed on chat-mobile-root for the same reason.
       
       Solution: use position:absolute inside .main (which is already
       positioned), and listen to window.visualViewport resize events in JS
       to update the height dynamically when the keyboard opens/closes.
       The visualViewport API is the only truly cross-platform reliable way.
    */
    /* Arch-admin delete overlay on bubble */
    .msg-bubble { position: relative; }

    .chat-mobile-root {
      position: absolute;
      top: 0; left: 0; right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      /* top and height are overridden by visualViewport JS — this is just the initial state */
      will-change: height, top;
    }
    .chat-mobile-messages {
      flex: 1;
      min-height: 0;
      overflow-y: scroll;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
    }
    .chat-mobile-input {
      flex-shrink: 0;
      border-top: 1px solid var(--border);
      background: rgba(0,8,4,0.96);
      padding: 8px 10px;
      padding-bottom: max(8px, env(safe-area-inset-bottom));
    }
    /* When chat is active, .main must be a positioned full-height container */
    .main.nav-closed.chat-active,
    .main.nav-open.chat-active {
      position: fixed !important;
      top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important;
      height: auto !important;
      overflow: hidden !important;
    }
    .main.nav-closed.chat-active > div,
    .main.nav-open.chat-active > div {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
    }
  }


  /* ── ONBOARDING ── */
  .onboard-wrap {
    min-height: 100vh; display: flex; align-items: flex-end; justify-content: center;
    background: #020800;
    position: relative; overflow: hidden;
    padding-bottom: 0;
  }
  .onboard-grid-floor {
    position: absolute; pointer-events: none; z-index: 0;
    left: -50%; right: -50%;
    top: 0%; bottom: -5%;
    background-image:
      linear-gradient(rgba(136,255,0,0.38) 1px, transparent 1px),
      linear-gradient(90deg, rgba(136,255,0,0.38) 1px, transparent 1px);
    background-size: 80px 80px;
    background-position: 50% 0%;
    transform: perspective(600px) rotateX(82deg) translateY(35%);
    transform-origin: 50% 50%;
    animation: depthSweep 3.6s linear infinite;
    mask-image: linear-gradient(180deg, transparent 0%, black 15%, black 100%);
  }
  .onboard-grid-fog {
    position: absolute; inset: 0; pointer-events: none; z-index: 1;
    background: linear-gradient(180deg, #020800 0%, rgba(2,8,0,0.0) 38%, rgba(2,8,0,0.0) 100%);
  }
  .onboard-title {
    position: absolute; top: 28px; left: 0; right: 0; z-index: 3;
    text-align: center; pointer-events: none;
  }
  .onboard-scroll {
    position: absolute; top: 44%; left: 0; right: 0; bottom: 0; z-index: 2;
    overflow-y: auto; display: flex; flex-direction: column; align-items: center;
    padding: 0 20px 40px;
  }
  .onboard-scroll-bar {
    position: absolute; top: 44%; left: 50%; transform: translateX(-50%);
    width: calc(100% - 40px); max-width: 400px; height: 2px; z-index: 3; pointer-events: none;
    background: rgba(136,255,0,0.9);
    box-shadow:
      0 0 4px  rgba(136,255,0,1),
      0 0 12px rgba(136,255,0,0.9),
      0 0 28px rgba(136,255,0,0.7),
      0 0 55px rgba(136,255,0,0.4),
      0 0 90px rgba(136,255,0,0.2);
  }

  /* ── MISC ── */
  .action-btn {
    display: flex; align-items: center; gap: 5px; font-size: 11px; color: var(--muted);
    cursor: pointer; padding: 4px 9px; border-radius: var(--radius); transition: all 0.15s;
    font-family: 'Orbitron', sans-serif; letter-spacing: 1px; text-transform: uppercase;
  }
  .action-btn:hover { color: var(--accent); text-shadow: var(--glow-sm); }

  ::-webkit-scrollbar { width: 3px; height: 3px; }
  ::-webkit-scrollbar-track { background: var(--bg); }
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #aaff44, #88ff00, #88ff00);
    border-radius: 0;
  }

  ::selection { background: rgba(136,255,0,0.3); color: #fff; }

  * { cursor: crosshair; }
  button, a, [role="button"], .nav-item, .tab, .track-row, select { cursor: pointer; }
`;

// ─── UTILS ────────────────────────────────────────────────────────────────────
const fmtTime = (s) => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,"0")}`;

// ─── FIGURE BACKDROP ──────────────────────────────────────────────────────────
// ─── PRE-RENDER PATH ──────────────────────────────────────────────────────────
const PR = "/Pre-Render/";

// Detect whether this browser needs the canvas+MOV approach for correct alpha.
// Safari (and Safari-engine browsers) support HEVC (hvc1) — Chrome and Firefox don't.
// Safari's <video> element renders WebM alpha incorrectly even though it technically
// supports VP9. The fix: serve MOV and draw frames via canvas instead.
const needsCanvasForAlpha = (() => {
  const v = document.createElement("video");
  return v.canPlayType('video/mp4; codecs="hvc1"') !== "";
})();

// Renders the correct single source for the current browser.
function VideoSource({ name }) {
  return needsCanvasForAlpha
    ? <source src={PR + name + ".mov"} type='video/mp4; codecs="hvc1"' />
    : <source src={PR + name + ".webm"} type="video/webm" />;
}

// For Safari (MOV path): drives a canvas that draws from an opacity:0 video element.
// The video must be visible in the DOM (opacity:0, not display:none) for Safari to
// continue decoding frames. The canvas receives the correctly composited output.
// For non-Safari (WebM path): no-op — the video element renders correctly on its own.
function useVideoCanvas(videoRef, canvasRef, { circular = false, visibleRef = null } = {}) {
  useEffect(() => {
    if (!needsCanvasForAlpha) return;
    let cancelled = false;
    let rafId;
    let sizeSet = false;

    const drawFrame = () => {
      if (cancelled) return;
      const vid = videoRef.current;
      const cvs = canvasRef.current;
      if (!vid || !cvs || vid.readyState < 2) return;
      if (visibleRef && !visibleRef.current) {
        if (vid.requestVideoFrameCallback) vid.requestVideoFrameCallback(drawFrame);
        return;
      }
      const ctx = cvs.getContext("2d");
      if (!sizeSet || cvs.width !== vid.videoWidth || cvs.height !== vid.videoHeight) {
        cvs.width = vid.videoWidth;
        cvs.height = vid.videoHeight;
        sizeSet = true;
      }
      ctx.clearRect(0, 0, cvs.width, cvs.height);
      if (circular) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(cvs.width/2, cvs.height/2, Math.min(cvs.width, cvs.height)/2, 0, Math.PI*2);
        ctx.clip();
      }
      ctx.drawImage(vid, 0, 0, cvs.width, cvs.height);
      if (circular) ctx.restore();
      if (vid.requestVideoFrameCallback) vid.requestVideoFrameCallback(drawFrame);
    };

    const start = () => {
      if (cancelled) return;
      const vid = videoRef.current;
      const cvs = canvasRef.current;
      if (!vid || !cvs) { rafId = requestAnimationFrame(start); return; }
      if (vid.requestVideoFrameCallback) {
        vid.requestVideoFrameCallback(drawFrame);
      } else {
        const rafLoop = () => {
          if (cancelled) return;
          rafId = requestAnimationFrame(rafLoop);
          drawFrame();
        };
        rafLoop();
      }
    };
    rafId = requestAnimationFrame(start);
    return () => { cancelled = true; cancelAnimationFrame(rafId); };
  }, []);
}

// Shared video backdrop. Positions a <video> by projecting world-space coordinates
// through the exact same Three.js camera the original GLB component used.
function VideoBackdrop({ visible, isMobile, fullH = false, src, worldX, worldY, worldZ,
                         scale, containerStyle, extraStyle, videoRef: extVideoRef, children,
                         onMetadata }) {
  const [opacity, setOpacity] = useState(0);
  const [pos, setPos]         = useState(null);
  const internalRef           = useRef(null);
  const videoRef              = extVideoRef || internalRef;
  const canvasRef             = useRef(null);
  const visibleRef            = useRef(visible);

  useVideoCanvas(videoRef, canvasRef, { visibleRef });

  useEffect(() => {
    visibleRef.current = visible;
    if (!visible) { setOpacity(0); return; }
    const t = setTimeout(() => setOpacity(0.85), 250);
    return () => clearTimeout(t);
  }, [visible]);

  // Restart from frame 0 each time the page opens, pause when it closes
  useEffect(() => {
    let wasVisible = visibleRef.current;
    let rafId;
    const tick = () => {
      rafId = requestAnimationFrame(tick);
      const isVisible = visibleRef.current;
      if (isVisible && !wasVisible) {
        const vid = videoRef.current;
        if (vid) { vid.currentTime = 0; vid.play().catch(() => {}); }
      } else if (!isVisible && wasVisible) {
        const vid = videoRef.current;
        if (vid) vid.pause();
      }
      wasVisible = isVisible;
    };
    tick();
    return () => cancelAnimationFrame(rafId);
  }, []);

  const calcPos = useCallback(() => {
    const vid = videoRef.current;
    const aspect = (vid?.videoWidth && vid?.videoHeight)
      ? vid.videoWidth / vid.videoHeight : 1;
    projectWithThree(worldX, worldY, worldZ, 300, scale, isMobile, fullH).then(r => {
      const h = r.projH;
      const w = h * aspect;
      setPos({ left: r.cssLeft - w/2, top: r.cssTop - h/2, width: w, height: h });
    });
  }, [worldX, worldY, worldZ, scale, isMobile, fullH]);

  useEffect(() => {
    calcPos();
    window.addEventListener("resize", calcPos);
    return () => window.removeEventListener("resize", calcPos);
  }, [calcPos]);

  // Shared style for both the video (opacity:0 in Safari) and the canvas overlay
  const elStyle = pos ? {
    position: "absolute",
    left: pos.left, top: pos.top, width: pos.width, height: pos.height,
    ...extraStyle,
  } : null;

  return (
    <div style={{
      position: "fixed",
      pointerEvents: "none",
      zIndex: -1,
      opacity,
      transition: "opacity 0.5s ease",
      ...containerStyle,
    }}>
      {elStyle && (<>
        <video
          ref={videoRef}
          autoPlay muted loop playsInline
          onLoadedMetadata={() => { calcPos(); onMetadata?.(); }}
          style={{
            ...elStyle,
            // Safari: keep in DOM for frame decoding but invisible — canvas draws instead
            opacity: !needsCanvasForAlpha ? 1 : 0,
          }}
        >
          <VideoSource name={src.replace(PR,"").replace(".webm","")} />
        </video>
        {/* Safari canvas overlay — draws video frames with correct alpha */}
        {!!needsCanvasForAlpha && (
          <canvas ref={canvasRef} style={{...elStyle, pointerEvents:"none"}} />
        )}
      </>)}
      {children}
    </div>
  );
}
// This is the only way to guarantee the result matches the original Three.js rendering.
// Three.js is already in the bundle (used by the cross and original GLB components),
// so the lazy import just retrieves the cached module — no extra download.
let _threeCache = null;
function projectWithThree(worldX, worldY, worldZ, worldH, scale, isMobile, fullH) {
  const load = _threeCache
    ? Promise.resolve(_threeCache)
    : import("three").then(m => { _threeCache = m; return m; });

  return load.then(THREE => {
    // Canvas dimensions — identical to what each original component used
    const W = isMobile ? window.innerWidth : (window.innerWidth - 224);
    const H = isMobile
      ? window.innerHeight
      : (fullH ? window.innerHeight : window.innerHeight - 70);

    const camX = isMobile ? 0 : (-W * 0.32);
    const camZ = isMobile ? 1200 : 660;

    const cam = new THREE.PerspectiveCamera(40, W / H, 0.1, 5000);
    cam.position.set(camX, 160, camZ);
    cam.lookAt(new THREE.Vector3(0, 160, 0));
    cam.updateMatrixWorld();
    cam.updateProjectionMatrix();

    // Project the center point
    const center = new THREE.Vector3(worldX, worldY, worldZ).project(cam);
    const cssLeft = (center.x + 1) / 2 * W;
    const cssTop  = (1 - center.y) / 2 * H;

    // Project a point half-height above center to get projected height in pixels
    const topPt = new THREE.Vector3(worldX, worldY + (300 * scale) / 2, worldZ).project(cam);
    const topY  = (1 - topPt.y) / 2 * H;
    const projH = 2 * Math.abs(cssTop - topY);

    return { cssLeft, cssTop, projH, W, H };
  });
}

const FIGURE_CONTAINER = (isMobile) => ({ left: isMobile ? 0 : 224, top: 0, right: 0, bottom: isMobile ? 0 : 70 });

function FigureBackdropPR({ variant = "boards", visible = false, isMobile = false }) {
  const src = {
    boards:    PR+"Talking_On_A_Cell_Phone.webm",
    meet:      PR+"Talking.webm",
    topcharts: PR+"Warming_Up.webm",
  }[variant];
  return (
    <VideoBackdrop
      visible={visible} isMobile={isMobile}
      src={src}
      worldX={110} worldY={146} worldZ={0} scale={2.75}
      containerStyle={FIGURE_CONTAINER(isMobile)}
    />
  );
}


// ─── AUDIO FIGURE BACKDROP ──────────────────────────────────────────────────────────────────────────────

// ─── RULE PAGE ────────────────────────────────────────────────────────────────
const RULE_DEFAULTS = {
  introduction: `<p>The Bros. of St. Hyacinth constitutes a holy order of alarmingly right-wing young men aiming to pursue virtue and excellence through the process of becoming yolked.</p>
<p>The soul is, after all, the form of the body. The average modern man's body takes the form of an amorphous blob. It is no wonder, then, that we observe the rapidly deteriorating character of men's souls. Therefore, to improve the soul, we must improve the form of the body.</p>
<p>Such a pursuit of excellence requires the engagement of all man's faculties. No one can expect to profit from weightlifting if in other respects he remains degenerate, uncivilized, undisciplined, impure, intemperate, or if he in any other respect maintains the vicious barbarism to which Adam's sin inclines him. Strength in body avails nothing without strength in virtue.</p>
<blockquote>"But avoid foolish and old wives' fables: and exercise thyself unto godliness. For bodily exercise is profitable to little: but godliness is profitable to all things, having promise of the life that now is, and of that which is to come."<br/>— I Timothy IV: VII–VIII</blockquote>
<p>Accordingly, the Bros of St. Hyacinth, not content with anything only "profitable to little," seek to sanctify and elevate the act of physical exercise by the grace of God. We will exercise ourselves unto Godliness.</p>
<p>Bros of St. Hyacinth must continually and at all times contemplate the meaning of the word <em>excellence</em>. Each member will set unrealistic expectations for himself and then surpass those expectations. He will come to despise mediocrity with an implacable rage. He will seek to guide his actions by the following virtues, in addition to all others:</p>
<ul><li>Charity</li><li>Humility</li><li>Discipline</li><li>Magnanimity</li><li>Courage</li><li>Sexism</li><li>Assertiveness</li><li>Prudence</li></ul>
<p>Finally, any member who is not 🅱️ased will submit to verbal harassment and creative disciplinary correction from his brothers until he amends his ways.</p>`,
  guiding_principles: `<p>The Bros of St. Hyacinth will operate by three general guiding principles. These are the tenets of Neo-Gastonism. They go as follows:</p>
<ol><li>Women aren't allowed to read</li><li>Eat lots of raw eggs</li><li>Beat up furries</li></ol>`,
  exercise_plan: `<p>No man can seek holy gains on his own merits. He cannot boast of his strength, for all his strength comes from God.</p>
<blockquote>"I can do all these things in Him who strengtheneth me." (Philippians 4:13)</blockquote>
<blockquote>"The Lord is my strength and my praise: and he is become salvation to me. He is my God and I will glorify him: the God of my father, and I will exalt him." (Exodus 15:2)</blockquote>
<p>Therefore, Bros of St. Hyacinth lift not in vain glory, but pray constantly to the Lord, that He will strengthen us in our endeavors.</p>
<p>While in the gym, Bros of St. Hyacinth will listen to readings of Holy Scripture, so that the soul may suffer purification, and exercise be made into a holy act, rather than a worldly one.</p>`,
  prayer: `<p><strong>Opening Prayer</strong></p>
<ul><li>Pater Noster</li><li>Ave Maria</li><li>Gloria Patri</li><li>Invoke the Intercession of St. Hyacinth of Poland</li></ul>
<p><strong>Prayer Before Each Set of Reps</strong></p>
<ul><li>Pray "Non nobis, Domine, non nobis, sed nomini tuo da gloriam"</li><li>Pray for the intercession of:<ul><li>St. Hyacinth of Poland, patron saint of weight lifters</li><li>St. Christopher, a great pioneer of hallowed swolness</li><li>St. Joseph, in order that you might develop in your strength as a man of virtue</li></ul></li></ul>
<p><strong>Daily Rosary</strong></p>
<ul><li>At some time outside of the meeting, pray the Holy Rosary. Bonus points if it's in Latin.</li></ul>`,
  discipline: `<ul><li>Wake up every day at 6:15</li><li>Only take cold showers (as cold as the water will get)</li><li>Never waste time on the internet</li><li>Drink raw eggs every day (creatine recommended)</li><li>Prefer hunger to unhealthy food</li><li>Weekly confession</li><li>Daily Mass</li><li>Daily Rosary</li></ul>`,
  monastic_habit: `<ul><li>Repeal 19 Shirt</li><li>Cargo Shorts</li><li>Hawaiian Shirt</li><li>Pit Vipers</li></ul>`,
};

const DEFAULT_SECTION_INDEX = [
  { id: "introduction",       label: "Introduction" },
  { id: "guiding_principles", label: "Guiding Principles" },
  { id: "exercise_plan",      label: "Exercise Plan" },
  { id: "prayer",             label: "Prayer" },
  { id: "discipline",         label: "Discipline" },
  { id: "monastic_habit",     label: "Monastic Habit" },
];

function RulePage({ user }) {
  const isArchAdmin = user?.role === "arch_admin";
  const [sectionIndex, setSectionIndex] = useState([]); // ordered list of {id, label} — populated from server
  const [contents, setContents]         = useState({});          // { id: html } — populated from server
  const [loaded, setLoaded]             = useState(false); // true once /api/rule has responded
  const [editMode, setEditMode]         = useState(false);
  const [saving, setSaving]             = useState(false);
  const editorRefs   = useRef({});
  const fileInputRef = useRef(null);
  const [imgTarget, setImgTarget]       = useState(null);
  const [renamingId, setRenamingId]     = useState(null); // id of section being renamed
  const [renameVal, setRenameVal]       = useState("");

  // ── Load all data from server ───────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    fetch(`${API_BASE}/api/rule`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        // __index__ is a special key storing the section list as JSON.
        // Its absence means this install has never saved a rule page yet —
        // that's the only case where we fall back to the built-in starter text.
        const isFreshInstall = !data.__index__;
        if (isFreshInstall) {
          setSectionIndex(DEFAULT_SECTION_INDEX);
        } else {
          try { setSectionIndex(JSON.parse(data.__index__)); }
          catch (_) { setSectionIndex(DEFAULT_SECTION_INDEX); }
        }
        const merged = isFreshInstall ? { ...RULE_DEFAULTS } : {};
        // Always take the server's value for a section, even an empty string —
        // otherwise cleared-out content would keep falling back to the
        // hardcoded defaults above.
        Object.entries(data).forEach(([k, v]) => { if (k !== "__index__") merged[k] = v; });
        setContents(merged);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  // Populate editors when entering edit mode
  useEffect(() => {
    if (editMode) {
      sectionIndex.forEach(sec => {
        const el = editorRefs.current[sec.id];
        if (el) el.innerHTML = contents[sec.id] ?? "";
      });
    }
  }, [editMode]);

  // ── Persist helpers ─────────────────────────────────────────────────────────
  const apiPut = async (id, content) => {
    const token = localStorage.getItem("auth_token");
    await fetch(`${API_BASE}/api/rule/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content }),
    });
  };

  const saveAll = async () => {
    setSaving(true);
    // Save section index
    await apiPut("__index__", JSON.stringify(sectionIndex));
    // Save each section content
    for (const sec of sectionIndex) {
      const html = editorRefs.current[sec.id]?.innerHTML ?? contents[sec.id] ?? "";
      await apiPut(sec.id, html);
      setContents(prev => ({ ...prev, [sec.id]: html }));
    }
    setSaving(false);
  };

  // ── Section management ──────────────────────────────────────────────────────
  const addSection = () => {
    const id          = "section_" + Date.now();
    const label       = "New Section";
    const initialHtml = "<p>Enter text here.</p>";
    setSectionIndex(prev => [...prev, { id, label }]);
    setContents(prev => ({ ...prev, [id]: initialHtml }));
    // The new editor div isn't populated by the edit-mode effect (that only
    // runs when editMode itself changes), so seed it directly once it mounts,
    // then scroll to it.
    setTimeout(() => {
      const el = editorRefs.current[id];
      if (el) {
        el.innerHTML = initialHtml;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  const removeSection = async (id) => {
    if (!window.confirm("Delete this section? This cannot be undone.")) return;
    setSectionIndex(prev => prev.filter(s => s.id !== id));
    setContents(prev => { const n = { ...prev }; delete n[id]; return n; });
    // Also remove from DB
    await apiPut(id, "");
    await apiPut("__index__", JSON.stringify(sectionIndex.filter(s => s.id !== id)));
  };

  const moveSection = (id, dir) => {
    setSectionIndex(prev => {
      const idx  = prev.findIndex(s => s.id === id);
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr;
    });
  };

  const startRename = (id, label) => { setRenamingId(id); setRenameVal(label); };
  const commitRename = () => {
    if (!renameVal.trim()) { setRenamingId(null); return; }
    setSectionIndex(prev => prev.map(s => s.id === renamingId ? { ...s, label: renameVal.trim() } : s));
    setRenamingId(null);
  };

  // ── execCommand wrapper ─────────────────────────────────────────────────────
  const exec = (cmd, val = null) => document.execCommand(cmd, false, val);

  // ── Image insertion ─────────────────────────────────────────────────────────
  const insertImage = (e) => {
    const file = e.target.files?.[0];
    if (!file || !imgTarget) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const el = editorRefs.current[imgTarget];
      if (!el) return;
      el.focus();
      exec("insertImage", ev.target.result);
      el.querySelectorAll("img").forEach(img => {
        img.style.cssText = "max-width:100%;border-radius:4px;margin:8px 0;display:block;";
      });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
    setImgTarget(null);
  };

  // ── Toolbar ─────────────────────────────────────────────────────────────────
  const TB = {
    cursor:"pointer", background:"rgba(136,255,0,0.07)", border:"1px solid rgba(136,255,0,0.2)",
    color:"var(--chrome)", borderRadius:3, padding:"3px 8px", fontSize:12,
    fontFamily:"'Orbitron',sans-serif", letterSpacing:0.5, transition:"all 0.15s",
  };

  const Toolbar = ({ secId }) => (
    <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:8,
      padding:"8px", background:"rgba(0,0,0,0.3)",
      border:"1px solid rgba(136,255,0,0.15)", borderRadius:"4px 4px 0 0" }}>
      <button style={TB} title="Bold"          onMouseDown={e=>{e.preventDefault();exec("bold");}}>𝗕</button>
      <button style={TB} title="Italic"        onMouseDown={e=>{e.preventDefault();exec("italic");}}>𝘐</button>
      <button style={TB} title="Underline"     onMouseDown={e=>{e.preventDefault();exec("underline");}}>U̲</button>
      <button style={TB} title="Strikethrough" onMouseDown={e=>{e.preventDefault();exec("strikeThrough");}}>S̶</button>
      <div style={{width:1,background:"rgba(136,255,0,0.2)",margin:"0 2px"}}/>
      <button style={TB} title="Paragraph"     onMouseDown={e=>{e.preventDefault();exec("formatBlock","<p>");}}>¶</button>
      <button style={TB} title="Heading 1"     onMouseDown={e=>{e.preventDefault();exec("formatBlock","<h2>");}}>H1</button>
      <button style={TB} title="Heading 2"     onMouseDown={e=>{e.preventDefault();exec("formatBlock","<h3>");}}>H2</button>
      <button style={TB} title="Blockquote"    onMouseDown={e=>{e.preventDefault();exec("formatBlock","<blockquote>");}}>&ldquo;</button>
      <div style={{width:1,background:"rgba(136,255,0,0.2)",margin:"0 2px"}}/>
      <button style={TB} title="Bullet list"   onMouseDown={e=>{e.preventDefault();exec("insertUnorderedList");}}>• —</button>
      <button style={TB} title="Numbered list" onMouseDown={e=>{e.preventDefault();exec("insertOrderedList");}}>1. —</button>
      <button style={TB} title="Indent"        onMouseDown={e=>{e.preventDefault();exec("indent");}}>→</button>
      <button style={TB} title="Outdent"       onMouseDown={e=>{e.preventDefault();exec("outdent");}}>←</button>
      <div style={{width:1,background:"rgba(136,255,0,0.2)",margin:"0 2px"}}/>
      <button style={TB} title="Insert image"
        onMouseDown={e=>{e.preventDefault();setImgTarget(secId);fileInputRef.current.click();}}>🖼</button>
      <button style={TB} title="Horizontal rule" onMouseDown={e=>{e.preventDefault();exec("insertHorizontalRule");}}>—</button>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="page" style={{ position:"relative" }}>
      <div className="page-title">RULE OF THE <span className="accentText">BROS</span></div>
      <div className="page-sub">&ldquo;Idleness is the enemy of the soul.&rdquo; &mdash; St. Benedict</div>

      <input ref={fileInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={insertImage} />

      {!loaded && (
        <div className="card" style={{ fontFamily:"'Orbitron',sans-serif", fontSize:12, letterSpacing:1, color:"var(--muted)" }}>
          Loading…
        </div>
      )}

      {loaded && sectionIndex.map((sec, idx) => (
        <div key={sec.id} className="card" style={{ overflow: editMode && isArchAdmin ? "visible" : "hidden" }}>

          {/* Section header */}
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom: editMode && isArchAdmin ? 4 : 16, flexWrap:"wrap" }}>
            {/* Label — editable inline when renaming */}
            {isArchAdmin && editMode && renamingId === sec.id ? (
              <input
                value={renameVal}
                onChange={e => setRenameVal(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenamingId(null); }}
                onBlur={commitRename}
                autoFocus
                style={{ flex:1, background:"rgba(0,0,0,0.4)", border:"1px solid rgba(136,255,0,0.5)",
                  color:"var(--accent)", fontFamily:"'Orbitron',sans-serif", fontSize:11, fontWeight:700,
                  letterSpacing:2, textTransform:"uppercase", padding:"4px 8px", borderRadius:3, outline:"none" }}
              />
            ) : (
              <div className="card-title" style={{ marginBottom:0, flex:1,
                cursor: isArchAdmin && editMode ? "pointer" : "default" }}
                onDoubleClick={() => isArchAdmin && editMode && startRename(sec.id, sec.label)}
                title={isArchAdmin && editMode ? "Double-click to rename" : undefined}>
                {sec.label}
                {isArchAdmin && editMode && (
                  <span style={{ marginLeft:8, fontSize:9, color:"var(--muted)", fontWeight:400, letterSpacing:1 }}>
                    dbl-click to rename
                  </span>
                )}
              </div>
            )}

            {/* Section controls in edit mode */}
            {isArchAdmin && editMode && (
              <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                <button style={{...TB, padding:"2px 7px", fontSize:11}} title="Move up"
                  onMouseDown={e=>{e.preventDefault();moveSection(sec.id,-1);}}>↑</button>
                <button style={{...TB, padding:"2px 7px", fontSize:11}} title="Move down"
                  onMouseDown={e=>{e.preventDefault();moveSection(sec.id,1);}}>↓</button>
                <button style={{...TB, padding:"2px 7px", fontSize:11,
                  color:"rgba(255,80,80,0.8)", borderColor:"rgba(255,80,80,0.3)"}}
                  title="Delete section"
                  onMouseDown={e=>{e.preventDefault();removeSection(sec.id);}}>✕</button>
              </div>
            )}
          </div>

          {isArchAdmin && editMode && <Toolbar secId={sec.id} />}

          <div
            ref={el => { editorRefs.current[sec.id] = el; }}
            contentEditable={isArchAdmin && editMode && !saving}
            suppressContentEditableWarning
            className={(!editMode || !isArchAdmin) ? "rule-card" : ""}
            style={{
              fontSize:13, lineHeight:1.75, color:"#f4ffe8",
              fontFamily:"'Orbitron',sans-serif", letterSpacing:"0.3px",
              outline:"none",
              padding: isArchAdmin && editMode ? "10px 12px" : "0",
              borderRadius: isArchAdmin && editMode ? "0 0 4px 4px" : 3,
              border: isArchAdmin && editMode ? "1px solid rgba(136,255,0,0.2)" : "none",
              borderTop:"none",
              minHeight: isArchAdmin && editMode ? 80 : 0,
              background: isArchAdmin && editMode ? "rgba(0,0,0,0.15)" : "transparent",
              userSelect: (!isArchAdmin || !editMode) ? "none" : "text",
              WebkitUserSelect: (!isArchAdmin || !editMode) ? "none" : "text",
            }}
            {...(!editMode || !isArchAdmin
              ? { dangerouslySetInnerHTML: { __html: contents[sec.id] ?? "" } }
              : {})}
          />
        </div>
      ))}

      <style>{`
        [contenteditable="true"], .rule-card { color:#f4ffe8 !important; }
        [contenteditable="true"] p, .rule-card p { margin:0 0 10px; color:#f4ffe8 !important; }
        [contenteditable="true"] h2, .rule-card h2 { font-size:16px; font-weight:700; color:#88ff00 !important; margin:14px 0 8px; letter-spacing:2px; text-transform:uppercase; }
        [contenteditable="true"] h3, .rule-card h3 { font-size:13px; font-weight:700; color:#f4ffe8 !important; margin:10px 0 6px; letter-spacing:1px; }
        [contenteditable="true"] ul, [contenteditable="true"] ol, .rule-card ul, .rule-card ol { margin:0 0 10px !important; padding-left:28px !important; }
        [contenteditable="true"] ul, .rule-card ul { list-style-type:disc !important; }
        [contenteditable="true"] ol, .rule-card ol { list-style-type:decimal !important; }
        [contenteditable="true"] li, .rule-card li { margin-bottom:4px !important; display:list-item !important; color:#f4ffe8 !important; }
        [contenteditable="true"] ul ul, .rule-card ul ul { list-style-type:circle !important; padding-left:22px !important; }
        [contenteditable="true"] ol ol, .rule-card ol ol { list-style-type:lower-alpha !important; padding-left:22px !important; }
        [contenteditable="true"] blockquote, .rule-card blockquote { margin:12px 0; padding:10px 16px; border-left:2px solid rgba(136,255,0,0.4); background:rgba(136,255,0,0.04); color:#f4ffe8 !important; font-style:italic; border-radius:0 3px 3px 0; }
        [contenteditable="true"] hr, .rule-card hr { border:none; border-top:1px solid rgba(136,255,0,0.2); margin:12px 0; }
        [contenteditable="true"] img, .rule-card img { max-width:100%; border-radius:4px; margin:8px 0; display:block; }
        [contenteditable="true"] strong, .rule-card strong { color:#88ff00 !important; }
        [contenteditable="true"] em, .rule-card em { color:#f4ffe8 !important; font-style:italic; }
      `}</style>

      {/* Bottom toolbar */}
      {isArchAdmin && (
        <div style={{ display:"flex", gap:10, marginTop:8, marginBottom:8, alignItems:"center", flexWrap:"wrap" }}>
          <button className="btn btn-primary btn-sm" disabled={saving}
            onClick={async () => {
              if (editMode) { await saveAll(); setEditMode(false); }
              else { setEditMode(true); }
            }}>
            {editMode ? (saving ? "Saving…" : "💾 Save All & Exit") : "✏️ Edit Rule"}
          </button>
          {editMode && (
            <>
              <button className="btn btn-sm"
                style={{ fontSize:10, padding:"6px 14px", background:"rgba(136,255,0,0.08)",
                  border:"1px solid rgba(136,255,0,0.3)", color:"var(--accent)",
                  fontFamily:"'Orbitron',sans-serif", letterSpacing:1, cursor:"pointer", borderRadius:3 }}
                onClick={addSection}>
                + Add Section
              </button>
              <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Orbitron',sans-serif", letterSpacing:1 }}>
                EDIT MODE ACTIVE
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}


function RuleBackdropPR({ visible = false, isMobile = false }) {
  const [opacity, setOpacity] = useState(0);
  const [pos, setPos]         = useState(null);
  const containerRef          = useRef(null);
  const videoRef              = useRef(null);
  const bubbleMountRef        = useRef(null);
  const visibleRef            = useRef(visible);

  useEffect(() => {
    visibleRef.current = visible;
    if (!visible) { setOpacity(0); return; }
    const t = setTimeout(() => setOpacity(0.85), 250);
    return () => clearTimeout(t);
  }, [visible]);

  // Restart statue video from frame 0 on page re-open, pause on close
  useEffect(() => {
    let wasVisible = visibleRef.current;
    let rafId;
    const tick = () => {
      rafId = requestAnimationFrame(tick);
      const isVisible = visibleRef.current;
      if (isVisible && !wasVisible) {
        const vid = videoRef.current;
        if (vid) { vid.currentTime = 0; vid.play().catch(() => {}); }
      } else if (!isVisible && wasVisible) {
        const vid = videoRef.current;
        if (vid) vid.pause();
      }
      wasVisible = isVisible;
    };
    tick();
    return () => cancelAnimationFrame(rafId);
  }, []);

  const calcPos = useCallback(() => {
    const vid = videoRef.current;
    const aspect = (vid?.videoWidth && vid?.videoHeight) ? vid.videoWidth / vid.videoHeight : 1;
    projectWithThree(110, 146, 0, 300, 2.75, isMobile, true).then(r => {
      const h = r.projH, w = h * aspect;
      setPos({ left: r.cssLeft - w/2, top: r.cssTop - h/2, width: w, height: h });
    });
  }, [isMobile]);

  useEffect(() => {
    calcPos();
    window.addEventListener("resize", calcPos);
    return () => window.removeEventListener("resize", calcPos);
  }, [calcPos]);

  // Bubble canvas — Three.js scene on top of the video (Option A)
  useEffect(() => {
    const el = bubbleMountRef.current;
    if (!el) return;
    let animId = null;
    let rendererInst = null;
    let cancelled = false;

    Promise.all([
      import("three"),
      import("three/examples/jsm/environments/RoomEnvironment"),
    ]).then(([THREE, { RoomEnvironment }]) => {
      if (cancelled) return;

      const w = isMobile ? window.innerWidth : (window.innerWidth - 224);
      const h = window.innerHeight;

      const scene  = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 5000);
      camera.position.set(isMobile ? 0 : (-w * 0.32), 160, isMobile ? 1200 : 660);
      camera.lookAt(0, 160, 0);

      const renderer = new THREE.WebGLRenderer({ canvas: el, alpha: true, antialias: false });
      renderer.setPixelRatio(1);
      renderer.setSize(w, h);
      renderer.setClearColor(0x000000, 0);
      rendererInst = renderer;

      const makeCausticLayer = (phaseOffset, speedMult) => {
        const SIZE = 256;
        const cvs  = document.createElement("canvas");
        cvs.width = cvs.height = SIZE;
        const ctx  = cvs.getContext("2d");
        const tex  = new THREE.CanvasTexture(cvs);
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(2.2, 2.2);
        const update = (t) => {
          ctx.clearRect(0, 0, SIZE, SIZE);
          const tt = t * speedMult;
          for (let i = 0; i < 10; i++) {
            const phase = (i / 10) * Math.PI * 2 + phaseOffset;
            const cx = SIZE * (0.5 + 0.4  * Math.sin(tt * 0.65 + phase) * Math.cos(tt * 0.28 + phase * 0.6));
            const cy = SIZE * (0.5 + 0.25 * Math.cos(tt * 0.48 + phase * 1.2));
            const rx = Math.max(1, 16 + 13 * Math.sin(tt * 0.85 + phase * 0.55));
            const ry = Math.max(0.5, 7 + 5 * Math.cos(tt * 0.58 + phase * 0.75));
            const al = 0.28 + 0.16 * Math.sin(tt * 1.05 + phase);
            const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, rx);
            grd.addColorStop(0,   `rgba(200,230,255,${al})`);
            grd.addColorStop(0.4, `rgba(160,210,255,${al * 0.55})`);
            grd.addColorStop(1,   "rgba(0,0,0,0)");
            ctx.save(); ctx.translate(cx, cy); ctx.scale(1, ry / rx); ctx.translate(-cx, -cy);
            ctx.beginPath(); ctx.arc(cx, cy, rx, 0, Math.PI * 2);
            ctx.fillStyle = grd; ctx.fill(); ctx.restore();
          }
          tex.needsUpdate = true;
        };
        const mat  = new THREE.MeshBasicMaterial({
          map: tex, transparent: true, opacity: 0.13,
          blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(700, 700), mat);
        mesh.rotation.x = -Math.PI / 2;
        mesh.position.y = 420;
        scene.add(mesh);
        return { update, mat, mesh };
      };
      const causticA = makeCausticLayer(0, 1.0);
      const causticB = makeCausticLayer(Math.PI * 0.7, 0.65);
      causticB.mesh.rotation.z = Math.PI * 0.08;

      const bubbleLightDir = new THREE.Vector3(-0.4, 0.8, 0.6).normalize();
      const bubbleVert = `
        varying vec3 vNormal;
        varying vec3 vViewDir;
        void main() {
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vNormal   = normalize(normalMatrix * normal);
          vViewDir  = normalize(cameraPosition - worldPos.xyz);
          gl_Position = projectionMatrix * viewMatrix * worldPos;
        }
      `;
      const bubbleFrag = `
        varying vec3 vNormal;
        varying vec3 vViewDir;
        uniform float uTime;
        uniform float uPhase;
        uniform vec3  uLightDir;
        vec3 iridescence(float f) {
          float h = 0.33 + 0.21 * fract(f * 1.4 + uPhase * 0.12);
          vec3 c = clamp(abs(mod(h*6.0 + vec3(0,4,2), 6.0)-3.0)-1.0, 0.0, 1.0);
          return c;
        }
        void main() {
          float NdotV   = max(dot(vNormal, vViewDir), 0.0);
          float fresnel = pow(1.0 - NdotV, 3.5);
          vec3 halfV    = normalize(uLightDir + vViewDir);
          float spec    = pow(max(dot(vNormal, halfV), 0.0), 80.0);
          vec3  specColor  = vec3(0.88, 1.0, 0.96) * spec * 2.5;
          float spec2      = pow(max(dot(vNormal, halfV), 0.0), 18.0) * 0.35;
          vec3  specColor2 = vec3(0.72, 1.0, 0.88) * spec2;
          vec3  iriColor   = iridescence(fresnel) * fresnel * 1.4;
          float shadow     = pow(max(-dot(vNormal, uLightDir), 0.0), 2.0) * 0.25;
          vec3  color  = specColor + specColor2 + iriColor * 0.9;
          float alpha  = fresnel * 0.55 + spec * 0.8 + spec2 * 0.3 + shadow * 0.15;
          alpha = clamp(alpha, 0.0, 0.92);
          gl_FragColor = vec4(color, alpha);
        }
      `;

      const bubbleGeo = new THREE.SphereGeometry(1, 32, 24);
      const bubbles = [];
      for (let i = 0; i < 14; i++) {
        const r   = 6 + Math.random() * 18;
        const mat = new THREE.ShaderMaterial({
          vertexShader: bubbleVert, fragmentShader: bubbleFrag,
          uniforms: {
            uTime:     { value: 0 },
            uPhase:    { value: Math.random() * Math.PI * 2 },
            uLightDir: { value: bubbleLightDir },
          },
          transparent: true, depthWrite: false, side: THREE.FrontSide,
        });
        const mesh = new THREE.Mesh(bubbleGeo, mat);
        mesh.scale.setScalar(r);
        const zBase = 20 + Math.random() * 140;
        mesh.position.set(
          40 + (Math.random() - 0.5) * 420,
          -80 + Math.random() * 710,
          zBase
        );
        mesh.userData.speed  = 8  + Math.random() * 12;
        mesh.userData.driftX = (Math.random() - 0.5) * 0.9;
        mesh.userData.driftZ = (Math.random() - 0.5) * 0.5;
        mesh.userData.wobble = 0.3 + Math.random() * 0.5;
        mesh.userData.phase  = Math.random() * Math.PI * 2;
        mesh.userData.phaseZ = Math.random() * Math.PI * 2;
        mesh.userData.resetY = -90  - Math.random() * 20;
        mesh.userData.topY   = 640  + Math.random() * 80;
        scene.add(mesh);
        bubbles.push(mesh);
      }

      const clock    = new THREE.Clock();
      const FRAME_MS = 1000 / 30;
      const FADE_MS  = 500;
      let lastFrame  = 0;
      let hiddenAt   = visibleRef.current ? Infinity : 0;
      let wasVisible = visibleRef.current;

      const animate = (now) => {
        animId = requestAnimationFrame(animate);
        const isVisible = visibleRef.current;
        if (isVisible && !wasVisible) { lastFrame = now; hiddenAt = Infinity; }
        if (!isVisible && wasVisible) hiddenAt = now;
        wasVisible = isVisible;
        const fadingOut = !isVisible && (now - hiddenAt < FADE_MS);
        if (!isVisible && !fadingOut) return;
        if (now - lastFrame < FRAME_MS) return;
        lastFrame = now - ((now - lastFrame) % FRAME_MS);

        const t  = clock.getElapsedTime();
        const dt = clock.getDelta();

        causticA.update(t);
        causticB.update(t);
        const baseOp = 0.11 + 0.05 * Math.sin(t * 0.75);
        causticA.mat.opacity = baseOp;
        causticB.mat.opacity = baseOp * 0.85;
        causticB.mesh.rotation.z = Math.PI * 0.08 + t * 0.015;

        for (const b of bubbles) {
          b.material.uniforms.uTime.value = t;
          b.position.y += b.userData.speed * dt
                        + Math.sin(t * 0.4  + b.userData.phase)  * b.userData.wobble * 0.3
                        + Math.sin(t * 0.9  + b.userData.phaseZ) * b.userData.wobble * 0.15;
          b.position.x += Math.sin(t * 0.22 + b.userData.phase)  * b.userData.driftX
                        + Math.cos(t * 0.37 + b.userData.phase)  * b.userData.wobble * 0.35
                        + Math.sin(t * 0.61 + b.userData.phaseZ) * b.userData.driftX * 0.2;
          b.position.z += Math.cos(t * 0.18 + b.userData.phaseZ) * b.userData.driftZ
                        + Math.sin(t * 0.31 + b.userData.phaseZ) * b.userData.wobble * 0.2;
          if (b.position.y > b.userData.topY) {
            b.position.y = b.userData.resetY;
            b.userData.phase  = Math.random() * Math.PI * 2;
            b.userData.phaseZ = Math.random() * Math.PI * 2;
          }
        }

        renderer.render(scene, camera);
      };
      animate(0);
    }).catch(e => console.warn("RuleBackdrop bubble error:", e));

    return () => {
      cancelled = true;
      cancelAnimationFrame(animId);
      if (rendererInst) rendererInst.dispose();
    };
  }, []);

  const statueCvsRef = useRef(null);
  useVideoCanvas(videoRef, statueCvsRef, { visibleRef });

  return (
    <div ref={containerRef} style={{
      position: "fixed", left: isMobile ? 0 : 224, top: 0, right: 0, bottom: 0,
      pointerEvents: "none", zIndex: -1, opacity, transition: "opacity 0.5s ease",
    }}>
      {pos && <>
        <img src={PR+"statue_halo.png"} alt=""
          style={{ position:"absolute", left:pos.left, top:pos.top, width:pos.width, height:pos.height, zIndex:1, pointerEvents:"none" }} />
        <video ref={videoRef} autoPlay muted loop playsInline onLoadedMetadata={calcPos}
          style={{ position:"absolute", left:pos.left, top:pos.top, width:pos.width, height:pos.height, zIndex:2, opacity: !needsCanvasForAlpha ? 1 : 0 }}>
          <VideoSource name="Mary_Statue" />
        </video>
        {!!needsCanvasForAlpha && (
          <canvas ref={statueCvsRef}
            style={{ position:"absolute", left:pos.left, top:pos.top, width:pos.width, height:pos.height, zIndex:2, pointerEvents:"none" }} />
        )}
      </>}
      <canvas ref={bubbleMountRef} style={{ position:"absolute", inset:0, zIndex:3, pointerEvents:"none" }} />
    </div>
  );
}

function AudioFigureBackdropPR({ visible = false, isMobile = false }) {
  const crossMountRef = useRef(null);
  const containerDivRef = useRef(null);
  const visibleRef    = useRef(visible);
  const [opacity, setOpacity] = useState(0);
  const [pos, setPos] = useState(null);
  // Use refs for playback state — mirrors the original 3D wasVisible pattern,
  // avoiding React re-render races that caused "already on loop" / "revert flicker" bugs.
  const introRef      = useRef(null);
  const idleRef       = useRef(null);
  const swappedRef    = useRef(false); // true once intro has finished and loop is active

  // Only opacity is driven by React — playback is driven by the visibleRef + refs pattern
  useEffect(() => {
    visibleRef.current = visible;
    if (!visible) { setOpacity(0); return; }
    const t = setTimeout(() => setOpacity(0.85), 250);
    return () => clearTimeout(t);
  }, [visible]);

  // Mirror original's wasVisible pattern: when page re-opens, restart intro; pause when it closes
  useEffect(() => {
    let wasVisible = visibleRef.current;
    let rafId;
    const tick = () => {
      rafId = requestAnimationFrame(tick);
      const isVisible = visibleRef.current;
      if (isVisible && !wasVisible) {
        // Page just opened — restart from intro
        swappedRef.current = false;
        if (introRef.current) {
          introRef.current.style.display = "block";
          introRef.current.currentTime = 0;
          introRef.current.play().catch(() => {});
        }
        if (idleRef.current) {
          idleRef.current.style.display = "none";
          idleRef.current.pause();
          idleRef.current.currentTime = 0;
        }
      } else if (!isVisible && wasVisible) {
        // Page closed — pause both to stop decoding
        if (introRef.current) introRef.current.pause();
        if (idleRef.current) idleRef.current.pause();
      }
      wasVisible = isVisible;
    };
    tick();
    return () => cancelAnimationFrame(rafId);
  }, []);


  // Cross — Three.js only runs on Safari (GLB path). On WebM path the cross PNG handles it.
  useEffect(() => {
    if (!needsCanvasForAlpha) return;
    if (!crossMountRef.current) return;
    const crossEl = crossMountRef.current;
    let animId = null;
    let crossRendererInst = null;
    let cancelled = false;

    Promise.all([import("three"), import("three/examples/jsm/loaders/GLTFLoader")])
      .then(([THREE, { GLTFLoader }]) => {
      if (cancelled) return;
      const w = isMobile ? window.innerWidth : (window.innerWidth - 224);
      const h = isMobile ? window.innerHeight : (window.innerHeight - 70);

      const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 5000);
      camera.position.set(isMobile ? 0 : (-w * 0.32), 160, isMobile ? 1200 : 660);
      camera.lookAt(0, 160, 0);

      const crossScene    = new THREE.Scene();
      const crossRenderer = new THREE.WebGLRenderer({ canvas: crossEl, alpha: true, antialias: false });
      crossRenderer.setPixelRatio(1);
      crossRenderer.setSize(w, h);
      crossRenderer.setClearColor(0x000000, 0);
      crossRendererInst = crossRenderer;

      const glitterCanvas = document.createElement("canvas");
      glitterCanvas.width = glitterCanvas.height = 128;
      const gCtx = glitterCanvas.getContext("2d");
      const glitterTex = new THREE.CanvasTexture(glitterCanvas);

      const updateGlitter = (time) => {
        gCtx.clearRect(0, 0, 128, 128);
        const grd = gCtx.createLinearGradient(0, 0, 128, 128);
        grd.addColorStop(0,   "#e8eef2");
        grd.addColorStop(0.4, "#ffffff");
        grd.addColorStop(0.7, "#d4e4f0");
        grd.addColorStop(1,   "#f0f4f8");
        gCtx.fillStyle = grd;
        gCtx.fillRect(0, 0, 128, 128);
        const rng = (s) => { let x = Math.sin(s) * 43758.5453; return x - Math.floor(x); };
        for (let i = 0; i < 60; i++) {
          const tx = time * 0.7 + i * 1.3;
          const x  = rng(tx) * 128;
          const y  = rng(tx + 99) * 128;
          const r  = rng(tx + 17) * 3 + 0.5;
          const br = Math.sin(time * (2 + rng(i) * 4) + i) * 0.5 + 0.5;
          const a  = br * 0.9 + 0.1;
          const spark = gCtx.createRadialGradient(x, y, 0, x, y, r * 4);
          spark.addColorStop(0,   `rgba(255,255,255,${a})`);
          spark.addColorStop(0.3, `rgba(210,230,245,${a * 0.5})`);
          spark.addColorStop(1,   "rgba(255,255,255,0)");
          gCtx.fillStyle = spark;
          gCtx.beginPath(); gCtx.arc(x, y, r * 4, 0, Math.PI * 2); gCtx.fill();
        }
        glitterTex.needsUpdate = true;
      };

      const crossMat = new THREE.MeshBasicMaterial({
        map: glitterTex, transparent: true, opacity: 1.0,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });
      const crossGroup = new THREE.Group();
      const crossH = isMobile ? 279 : 230, crossW = isMobile ? 153 : 125, barThick = isMobile ? 30 : 23;
      const vBar = new THREE.Mesh(new THREE.BoxGeometry(barThick, crossH, barThick), crossMat);
      vBar.position.y = crossH / 2;
      const hBar = new THREE.Mesh(new THREE.BoxGeometry(crossW, barThick, barThick), crossMat.clone());
      hBar.position.y = crossH * 0.70;
      crossGroup.add(vBar, hBar);
      const crossCamDist    = isMobile ? 1200 : 660;
      const crossFovRad     = (40 * Math.PI) / 180;
      const crossWorldPerPx = 2 * Math.tan(crossFovRad / 2) * crossCamDist / w;
      const crossCenterX    = isMobile ? 0 : (-160 * crossWorldPerPx);
      crossGroup.position.set(crossCenterX, 0, 0);
      // Load the Praying GLB to get the exact world Y the original code used for the cross.
      // Original formula: crossGroup.position.y = obj.position.y + 180,
      // where obj.position.y = -box2.min.y - extraH + 70.
      new GLTFLoader().load("/Praying.glb", (gltf) => {
        if (cancelled) return;
        const obj = gltf.scene;
        const box = new THREE.Box3().setFromObject(obj);
        const size = box.getSize(new THREE.Vector3());
        const fovRad = (40 * Math.PI) / 180;
        const worldH2 = 2 * Math.tan(fovRad / 2) * 600;
        const s1 = (worldH2 * 0.65) / size.y;
        const s2 = s1 * 1.495;
        obj.scale.setScalar(s2);
        const box2 = new THREE.Box3().setFromObject(obj);
        const extraH = size.y * (s2 - s1);
        crossGroup.position.y = (-box2.min.y - extraH + 70) + 180;
      }, undefined, () => {
        // Fallback if GLB unavailable
        crossGroup.position.y = 110;
      });
      crossScene.add(crossGroup);

      let lastFrame = 0;
      const FRAME_MS = 1000 / 30;
      let elapsed = 0;
      let lastTime = performance.now();
      const animate = (now) => {
        animId = requestAnimationFrame(animate);
        if (!visibleRef.current) return;
        const dt = Math.min((now - lastTime) / 1000, 0.05);
        lastTime = now;
        elapsed += dt;
        if (now - lastFrame < FRAME_MS) return;
        lastFrame = now - ((now - lastFrame) % FRAME_MS);
        const toCamX = camera.position.x - crossGroup.position.x;
        const toCamZ = camera.position.z - crossGroup.position.z;
        crossGroup.rotation.set(0, Math.atan2(toCamX, toCamZ), 0);
        updateGlitter(elapsed);
        crossMat.opacity = 0.88 + Math.sin(elapsed * 4.1) * 0.08 + Math.sin(elapsed * 11.3) * 0.04;
        crossRenderer.render(crossScene, camera);
      };
      animate(performance.now());
    }).catch(e => console.warn("AudioFigure cross error:", e));

    return () => {
      cancelled = true;
      cancelAnimationFrame(animId);
      if (crossRendererInst) crossRendererInst.dispose();
    };
  }, []);

  // wasVisible RAF loop — detects page re-entry and restarts intro
  useEffect(() => {
    let wasVisible = visibleRef.current;
    let rafId;
    const tick = () => {
      rafId = requestAnimationFrame(tick);
      const isVisible = visibleRef.current;
      if (isVisible && !wasVisible) {
        swappedRef.current = false;
        if (introRef.current) {
          introRef.current.style.display = "block";
          introRef.current.currentTime = 0;
          introRef.current.play().catch(() => {});
        }
        if (idleRef.current) {
          idleRef.current.style.display = "none";
          idleRef.current.pause();
          idleRef.current.currentTime = 0;
        }
      }
      wasVisible = isVisible;
    };
    tick();
    return () => cancelAnimationFrame(rafId);
  }, []);

  const calcPos = useCallback(() => {
    const vid = introRef.current;
    const aspect = (vid?.videoWidth && vid?.videoHeight) ? vid.videoWidth / vid.videoHeight : 1;
    projectWithThree(110, 146, 0, 300, 2.75, isMobile, false).then(r => {
      const h = r.projH, w = h * aspect;
      setPos({ left: r.cssLeft - w/2, top: r.cssTop - h/2, width: w, height: h });
    });
  }, [isMobile]);

  useEffect(() => {
    calcPos();
    window.addEventListener("resize", calcPos);
    return () => window.removeEventListener("resize", calcPos);
  }, [calcPos]);

  const handleIntroEnded = useCallback(() => {
    if (swappedRef.current) return;
    swappedRef.current = true;
    if (introRef.current) introRef.current.style.display = "none";
    if (idleRef.current) {
      idleRef.current.style.display = "block";
      idleRef.current.currentTime = 0;
      idleRef.current.play().catch(() => {});
    }
  }, []);

  const AUDIO_VID_STYLE = pos ? {
    position: "absolute",
    left: pos.left, top: pos.top, width: pos.width, height: pos.height,
    zIndex: 2,
  } : null;

  // Cross PNG — positioned like a billboard, flickering drop-shadow simulates glitter
  const crossImgRef = useRef(null);
  const [crossPos, setCrossPos] = useState(null);

  const calcCrossPos = useCallback(() => {
    const img = crossImgRef.current;
    const aspect = (img?.naturalWidth && img?.naturalHeight) ? img.naturalWidth / img.naturalHeight : 1;
    const cx = isMobile ? 1 : -102;
    const cy = isMobile ? 161 : 153;
    const sc = isMobile ? 2.9 : 2.4;
    projectWithThree(cx, cy, 0, 300, sc, isMobile, false).then(r => {
      const h = r.projH, w = h * aspect;
      setCrossPos({ left: r.cssLeft - w/2, top: r.cssTop - h/2, width: w, height: h });
    });
  }, [isMobile]);

  useEffect(() => {
    calcCrossPos();
    window.addEventListener("resize", calcCrossPos);
    return () => window.removeEventListener("resize", calcCrossPos);
  }, [calcCrossPos]);

  // Flickering drop-shadow — mirrors the original glitter opacity animation.
  // Fully suspends (no RAF) when page is not visible, restarts on re-entry.
  useEffect(() => {
    let rafId = null;
    let start = null;
    let running = false;

    const tick = (now) => {
      if (!visibleRef.current) { rafId = null; running = false; return; }
      rafId = requestAnimationFrame(tick);
      if (!start) start = now;
      const t = (now - start) / 1000;
      const intensity = 0.88
        + Math.sin(t * 4.1)  * 0.08
        + Math.sin(t * 11.3) * 0.04
        + Math.sin(t * 23.7) * 0.02;
      const a1 = Math.max(0, Math.min(1, intensity));
      const a2 = a1 * 0.6;
      const el = crossImgRef.current;
      if (el) {
        el.style.filter = [
          `drop-shadow(0 0 10px rgba(180,220,255,${a1.toFixed(2)}))`,
          `drop-shadow(0 0 3px rgba(255,255,255,${a2.toFixed(2)}))`,
          `drop-shadow(0 0 24px rgba(180,220,255,${(a1 * 0.4).toFixed(2)}))`,
        ].join(' ');
      }
    };

    // Start/stop based on page visibility via wasVisible pattern
    let wasVisible = visibleRef.current;
    const watchId = requestAnimationFrame(function watch(now) {
      requestAnimationFrame(watch);
      const isVisible = visibleRef.current;
      if (isVisible && !wasVisible && !running) {
        // Page just opened — start the flicker loop
        running = true;
        start = null;
        rafId = requestAnimationFrame(tick);
      }
      if (isVisible && !running && !rafId) {
        // Initial start if page is already visible on mount
        running = true;
        start = null;
        rafId = requestAnimationFrame(tick);
      }
      wasVisible = isVisible;
    });

    return () => {
      cancelAnimationFrame(watchId);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Safari canvas — draws from whichever video is currently active
  const audioCvsRef = useRef(null);
  useEffect(() => {
    if (!needsCanvasForAlpha) return;
    let cancelled = false, rafId, sizeSet = false;

    const drawFrame = () => {
      if (cancelled) return;
      const cvs = audioCvsRef.current;
      const vid = swappedRef.current ? idleRef.current : introRef.current;
      if (!cvs || !vid || vid.readyState < 2) return;
      if (!visibleRef.current) { if (vid.requestVideoFrameCallback) vid.requestVideoFrameCallback(drawFrame); return; }
      const ctx = cvs.getContext("2d");
      if (!sizeSet || cvs.width !== vid.videoWidth || cvs.height !== vid.videoHeight) {
        cvs.width = vid.videoWidth; cvs.height = vid.videoHeight; sizeSet = true;
      }
      ctx.clearRect(0, 0, cvs.width, cvs.height);
      ctx.drawImage(vid, 0, 0, cvs.width, cvs.height);
      if (vid.requestVideoFrameCallback) vid.requestVideoFrameCallback(drawFrame);
    };

    const start = () => {
      if (cancelled) return;
      const vid = swappedRef.current ? idleRef.current : introRef.current;
      const cvs = audioCvsRef.current;
      if (!vid || !cvs) { rafId = requestAnimationFrame(start); return; }
      if (vid.requestVideoFrameCallback) { vid.requestVideoFrameCallback(drawFrame); }
      else { const loop = () => { if (!cancelled) { rafId = requestAnimationFrame(loop); drawFrame(); } }; loop(); }
    };
    rafId = requestAnimationFrame(start);
    return () => { cancelled = true; cancelAnimationFrame(rafId); };
  }, []);

  return (
    <div ref={containerDivRef} style={{
      position: "fixed", left: isMobile ? 0 : 224, top: 0, right: 0, bottom: isMobile ? 0 : 70,
      pointerEvents: "none", zIndex: -1, opacity, transition: "opacity 0.5s ease",
    }}>
      {/* Pre-rendered cross PNG — zIndex 1, behind the praying figure */}
      {crossPos && (
        <img ref={crossImgRef} src={PR+"Cross.png"} alt=""
          onLoad={calcCrossPos}
          style={{
            position: "absolute",
            left: crossPos.left, top: crossPos.top,
            width: crossPos.width, height: crossPos.height,
            zIndex: 1, pointerEvents: "none",
          }} />
      )}
      {AUDIO_VID_STYLE && <>
        <video ref={introRef} autoPlay muted playsInline
          onLoadedMetadata={calcPos} onEnded={handleIntroEnded}
          style={{...AUDIO_VID_STYLE, display: "block", opacity: !needsCanvasForAlpha ? 1 : 0}}>
          <VideoSource name="Praying_Intro" />
        </video>
        <video ref={idleRef} muted loop playsInline
          onLoadedMetadata={calcPos}
          style={{...AUDIO_VID_STYLE, display: "none", opacity: !needsCanvasForAlpha ? 1 : 0}}>
          <VideoSource name="Praying_Idle" />
        </video>
        {!!needsCanvasForAlpha && (
          <canvas ref={audioCvsRef} style={{...AUDIO_VID_STYLE, pointerEvents:"none"}} />
        )}
      </>}
    </div>
  );
}


// ─── WORKOUT FIGURE BACKDROP ──────────────────────────────────────────────────
function WorkoutFigureBackdropPR({ visible = false, isMobile = false }) {
  const [opacity, setOpacity] = useState(0);
  const [pos, setPos]         = useState(null);
  const visibleRef            = useRef(visible);
  const introRef              = useRef(null);
  const loopRef               = useRef(null);
  const swappedRef            = useRef(false);

  useEffect(() => {
    visibleRef.current = visible;
    if (!visible) { setOpacity(0); return; }
    const t = setTimeout(() => setOpacity(0.85), 250);
    return () => clearTimeout(t);
  }, [visible]);

  // Mirror original wasVisible pattern — restart intro on page re-entry, pause on close
  useEffect(() => {
    let wasVisible = visibleRef.current;
    let rafId;
    const tick = () => {
      rafId = requestAnimationFrame(tick);
      const isVisible = visibleRef.current;
      if (isVisible && !wasVisible) {
        swappedRef.current = false;
        if (introRef.current) {
          introRef.current.style.display = "block";
          introRef.current.currentTime = 0;
          introRef.current.play().catch(() => {});
        }
        if (loopRef.current) {
          loopRef.current.style.display = "none";
          loopRef.current.pause();
          loopRef.current.currentTime = 0;
        }
      } else if (!isVisible && wasVisible) {
        if (introRef.current) introRef.current.pause();
        if (loopRef.current) loopRef.current.pause();
      }
      wasVisible = isVisible;
    };
    tick();
    return () => cancelAnimationFrame(rafId);
  }, []);

  const calcPos = useCallback(() => {
    const vid = introRef.current;
    const aspect = (vid?.videoWidth && vid?.videoHeight) ? vid.videoWidth / vid.videoHeight : 1;
    projectWithThree(110, 146, 0, 300, 2.75, isMobile, true).then(r => {
      const h = r.projH, w = h * aspect;
      setPos({ left: r.cssLeft - w/2, top: r.cssTop - h/2, width: w, height: h });
    });
  }, [isMobile]);

  useEffect(() => {
    calcPos();
    window.addEventListener("resize", calcPos);
    return () => window.removeEventListener("resize", calcPos);
  }, [calcPos]);

  const handleIntroEnded = useCallback(() => {
    if (swappedRef.current) return;
    swappedRef.current = true;
    if (introRef.current) introRef.current.style.display = "none";
    if (loopRef.current) {
      loopRef.current.style.display = "block";
      loopRef.current.currentTime = 0;
      loopRef.current.play().catch(() => {});
    }
  }, []);

  const VID_STYLE = pos ? {
    position: "absolute",
    left: pos.left, top: pos.top, width: pos.width, height: pos.height,
    zIndex: 1,
  } : null;

  // Safari canvas — draws from whichever video is currently active
  const workoutCvsRef = useRef(null);
  useEffect(() => {
    if (!needsCanvasForAlpha) return;
    let cancelled = false, rafId, sizeSet = false;

    const drawFrame = () => {
      if (cancelled) return;
      const cvs = workoutCvsRef.current;
      const vid = swappedRef.current ? loopRef.current : introRef.current;
      if (!cvs || !vid || vid.readyState < 2) return;
      if (!visibleRef.current) { if (vid.requestVideoFrameCallback) vid.requestVideoFrameCallback(drawFrame); return; }
      const ctx = cvs.getContext("2d");
      if (!sizeSet || cvs.width !== vid.videoWidth || cvs.height !== vid.videoHeight) {
        cvs.width = vid.videoWidth; cvs.height = vid.videoHeight; sizeSet = true;
      }
      ctx.clearRect(0, 0, cvs.width, cvs.height);
      ctx.drawImage(vid, 0, 0, cvs.width, cvs.height);
      if (vid.requestVideoFrameCallback) vid.requestVideoFrameCallback(drawFrame);
    };

    const start = () => {
      if (cancelled) return;
      const vid = swappedRef.current ? loopRef.current : introRef.current;
      const cvs = workoutCvsRef.current;
      if (!vid || !cvs) { rafId = requestAnimationFrame(start); return; }
      if (vid.requestVideoFrameCallback) { vid.requestVideoFrameCallback(drawFrame); }
      else { const loop = () => { if (!cancelled) { rafId = requestAnimationFrame(loop); drawFrame(); } }; loop(); }
    };
    rafId = requestAnimationFrame(start);
    return () => { cancelled = true; cancelAnimationFrame(rafId); };
  }, []);

  return (
    <div style={{
      position: "fixed", left: isMobile ? 0 : 224, top: 0, right: 0, bottom: 0,
      pointerEvents: "none", zIndex: -1, opacity, transition: "opacity 0.5s ease",
    }}>
      {VID_STYLE && <>
        <video ref={introRef} autoPlay muted playsInline
          onLoadedMetadata={calcPos} onEnded={handleIntroEnded}
          style={{...VID_STYLE, display: "block", opacity: !needsCanvasForAlpha ? 1 : 0}}>
          <VideoSource name="Idle_To_Push_Up" />
        </video>
        <video ref={loopRef} muted loop playsInline
          onLoadedMetadata={calcPos}
          style={{...VID_STYLE, display: "none", opacity: !needsCanvasForAlpha ? 1 : 0}}>
          <VideoSource name="Push_Up" />
        </video>
        {!!needsCanvasForAlpha && (
          <canvas ref={workoutCvsRef} style={{...VID_STYLE, pointerEvents:"none"}} />
        )}
      </>}
    </div>
  );
}



const BACKDROP_MODELS_GLB = {
  boards:    "/Talking_On_A_Cell_Phone.glb",
  meet:      "/Talking.glb",
  workout:   null,
  audio:     "/Talking_On_A_Cell_Phone.glb",
  topcharts: "/Warming_Up.glb",
};

function FigureBackdropGLB({ variant = "workout", visible = false, isMobile = false }) {
  const mountRef  = useRef(null);
  const fbxFile   = BACKDROP_MODELS_GLB[variant];
  const visibleRef = useRef(visible);

  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    visibleRef.current = visible;
    if (!visible) { setOpacity(0); return; }
    const t = setTimeout(() => setOpacity(0.85), 250);
    return () => clearTimeout(t);
  }, [visible]);

  useEffect(() => {
    if (!fbxFile || !mountRef.current) return;
    const el = mountRef.current;
    let animId = null;
    let rendererInst = null;
    let cancelled = false;

    Promise.all([
      import("three"),
      import("three/examples/jsm/loaders/GLTFLoader"),
    ]).then(([THREE, { GLTFLoader }]) => {
      if (cancelled) return;
      const w = isMobile ? window.innerWidth : (window.innerWidth - 224);
      const h = isMobile ? window.innerHeight : (window.innerHeight - 70);

      const scene  = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 2000);
      // Desktop: camera offset left so figure at x=110 appears at screen-right-third
      // Mobile: camera centered, pulled back for portrait aspect
      camera.position.set(isMobile ? 0 : (-w * 0.32), 160, isMobile ? 1200 : 660);
      camera.lookAt(0, 160, 0);

      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
      renderer.setPixelRatio(1);
      renderer.setSize(w, h);
      renderer.setClearColor(0x000000, 0);
      renderer.domElement.style.cssText="position:absolute;top:0;left:0;"; el.appendChild(renderer.domElement);
      rendererInst = renderer;

      const wireMat = new THREE.MeshBasicMaterial({
        color: 0x00ffcc, wireframe: true,
        transparent: true, opacity: 0.32,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });

      let mixer = null;
      const clock = new THREE.Clock();

      new GLTFLoader().load(fbxFile, (gltf) => {
        const obj = gltf.scene;
        obj.animations = gltf.animations;
        if (cancelled) return;
        obj.traverse(c => {
          if (c.isMesh) { c.material = wireMat; c.castShadow = c.receiveShadow = false; }
        });
        const box    = new THREE.Box3().setFromObject(obj);
        const size   = box.getSize(new THREE.Vector3());
        const fovRad = (40 * Math.PI) / 180;
        const worldH = 2 * Math.tan(fovRad / 2) * 600;
        const scale  = (worldH * 0.65) / size.y;
        const newScale = scale * 1.495;
        obj.scale.setScalar(newScale);
        const box2   = new THREE.Box3().setFromObject(obj);
        const extraH = size.y * (newScale - scale);
        // Same x position on mobile as desktop — camera centered so 110 puts figure right-of-center
        obj.position.set(110, -box2.min.y - extraH + 70, 0);
        obj.rotation.y = isMobile ? (-Math.PI * 5 / 180) : -Math.PI / 6;  // mobile: -5°, desktop: -30°
        scene.add(obj);
        if (obj.animations?.length) {
          mixer = new THREE.AnimationMixer(obj);
          const a = mixer.clipAction(obj.animations[0]);
          a.setLoop(THREE.LoopRepeat, Infinity);
          a.play();
        }

        let wasVisible = visibleRef.current;
        let hiddenAt = wasVisible ? Infinity : 0;
        const FADE_MS = 500;
        const FRAME_MS = 1000 / 30;
        let lastFrame = 0;
        const animate = (now) => {
          animId = requestAnimationFrame(animate);
          const isVisible = visibleRef.current;
          if (isVisible && !wasVisible && mixer && obj.animations?.length) {
            mixer.stopAllAction();
            const a = mixer.clipAction(obj.animations[0]);
            a.setLoop(THREE.LoopRepeat, Infinity);
            a.time = 0;
            a.play();
            clock.start();
            lastFrame = now;
            hiddenAt = Infinity;
          }
          if (!isVisible && wasVisible) hiddenAt = now;
          wasVisible = isVisible;
          const fadingOut = !isVisible && (now - hiddenAt < FADE_MS);
          if (!isVisible && !fadingOut) return;
          if (now - lastFrame < FRAME_MS) return;
          lastFrame = now - ((now - lastFrame) % FRAME_MS);
          if (mixer) mixer.update(clock.getDelta());
          renderer.render(scene, camera);
        };
        animate(0);
      }, undefined, e => console.warn("FBX load error:", e));
    }).catch(e => console.warn("Three.js import error:", e));

    return () => {
      cancelled = true;
      cancelAnimationFrame(animId);
      if (rendererInst) {
        rendererInst.dispose();
        if (el.contains(rendererInst.domElement)) el.removeChild(rendererInst.domElement);
      }
    };
  }, [fbxFile]);

  return (
    <div ref={mountRef} style={{
      position: "fixed",
      left: isMobile ? 0 : 224,
      top: 0,
      right: 0,
      bottom: isMobile ? 0 : 70,
      pointerEvents: "none",
      zIndex: -1,
      opacity: fbxFile ? opacity : 0,
      transition: "opacity 0.5s ease",
      }} />
  );
}


function RuleBackdropGLB({ visible = false, isMobile = false }) {
  const mountRef   = useRef(null);
  const visibleRef = useRef(visible);
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    visibleRef.current = visible;
    if (!visible) { setOpacity(0); return; }
    const t = setTimeout(() => setOpacity(0.85), 250);
    return () => clearTimeout(t);
  }, [visible]);

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;
    let animId = null;
    let rendererInst = null;
    let cancelled = false;

    Promise.all([
      import("three"),
      import("three/examples/jsm/loaders/GLTFLoader"),
      import("three/examples/jsm/environments/RoomEnvironment"),
    ]).then(([THREE, { GLTFLoader }, { RoomEnvironment }]) => {
      if (cancelled) return;

      const w = isMobile ? window.innerWidth : (window.innerWidth - 224);
      const h = window.innerHeight; // full height so bubbles exit at screen bottom

      const scene  = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 5000);
      camera.position.set(isMobile ? 0 : (-w * 0.32), 160, isMobile ? 1200 : 660);
      camera.lookAt(0, 160, 0);

      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
      renderer.setPixelRatio(1);
      renderer.setSize(w, h);
      renderer.setClearColor(0x000000, 0);
      renderer.domElement.style.cssText="position:absolute;top:0;left:0;"; el.appendChild(renderer.domElement);
      rendererInst = renderer;

      const pmrem  = new THREE.PMREMGenerator(renderer);
      pmrem.compileEquirectangularShader();
      const envTex = pmrem.fromScene(new RoomEnvironment()).texture;
      scene.environment = envTex;
      pmrem.dispose();

      // ── Lighting ────────────────────────────────────────────────────────────
      const keyLight = new THREE.DirectionalLight(0x99ccff, 4.5);
      keyLight.position.set(0, 800, 300);
      scene.add(keyLight);
      const keyLight2 = new THREE.DirectionalLight(0xaaddff, 3.0);
      keyLight2.position.set(200, 600, 100);
      scene.add(keyLight2);
      const fillLight = new THREE.DirectionalLight(0x4488cc, 1.2);
      fillLight.position.set(-400, 200, 400);
      scene.add(fillLight);
      const rimLight = new THREE.DirectionalLight(0x002244, 0.4);
      rimLight.position.set(200, 100, -500);
      scene.add(rimLight);
      scene.add(new THREE.AmbientLight(0x112233, 0.8));

      // ── Dual caustic planes ──────────────────────────────────────────────────
      const makeCausticLayer = (phaseOffset, speedMult) => {
        const SIZE = 256;
        const cvs  = document.createElement("canvas");
        cvs.width = cvs.height = SIZE;
        const ctx  = cvs.getContext("2d");
        const tex  = new THREE.CanvasTexture(cvs);
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(2.2, 2.2);
        const update = (t) => {
          ctx.clearRect(0, 0, SIZE, SIZE);
          const tt = t * speedMult;
          for (let i = 0; i < 10; i++) {
            const phase = (i / 10) * Math.PI * 2 + phaseOffset;
            const cx = SIZE * (0.5 + 0.4  * Math.sin(tt * 0.65 + phase) * Math.cos(tt * 0.28 + phase * 0.6));
            const cy = SIZE * (0.5 + 0.25 * Math.cos(tt * 0.48 + phase * 1.2));
            const rx = Math.max(1, 16 + 13 * Math.sin(tt * 0.85 + phase * 0.55));
            const ry = Math.max(0.5, 7 + 5 * Math.cos(tt * 0.58 + phase * 0.75));
            const al = 0.28 + 0.16 * Math.sin(tt * 1.05 + phase);
            const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, rx);
            grd.addColorStop(0,   `rgba(200,230,255,${al})`);
            grd.addColorStop(0.4, `rgba(160,210,255,${al * 0.55})`);
            grd.addColorStop(1,   "rgba(0,0,0,0)");
            ctx.save();
            ctx.translate(cx, cy);
            ctx.scale(1, ry / rx);
            ctx.translate(-cx, -cy);
            ctx.beginPath();
            ctx.arc(cx, cy, rx, 0, Math.PI * 2);
            ctx.fillStyle = grd;
            ctx.fill();
            ctx.restore();
          }
          tex.needsUpdate = true;
        };
        const mat  = new THREE.MeshBasicMaterial({
          map: tex, transparent: true, opacity: 0.13,
          blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(700, 700), mat);
        mesh.rotation.x = -Math.PI / 2;
        mesh.position.y = 420;
        scene.add(mesh);
        return { update, mat, mesh };
      };
      const causticA = makeCausticLayer(0, 1.0);
      const causticB = makeCausticLayer(Math.PI * 0.7, 0.65);
      causticB.mesh.rotation.z = Math.PI * 0.08;

      // ── Bubbles — custom shader: Frutiger Aero soap bubble look ─────────────
      // MeshPhysicalMaterial transmission needs a background to refract, which we
      // don't have (transparent canvas). Instead we fake the classic bubble look
      // with a ShaderMaterial: bright specular highlight, iridescent rim, inner shadow.
      const bubbleVert = `
        varying vec3 vNormal;
        varying vec3 vViewDir;
        void main() {
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vNormal   = normalize(normalMatrix * normal);
          vViewDir  = normalize(cameraPosition - worldPos.xyz);
          gl_Position = projectionMatrix * viewMatrix * worldPos;
        }
      `;
      const bubbleFrag = `
        varying vec3 vNormal;
        varying vec3 vViewDir;
        uniform float uTime;
        uniform float uPhase;
        uniform vec3  uLightDir;

        // Iridescence biased to app colors: cyan (#00ffcc) to green (#88ff00)
        vec3 iridescence(float f) {
          // Map f to 0.33–0.54 hue range (green → cyan), oscillating
          float h = 0.33 + 0.21 * fract(f * 1.4 + uPhase * 0.12);
          // hue to rgb
          vec3 c = clamp(abs(mod(h*6.0 + vec3(0,4,2), 6.0)-3.0)-1.0, 0.0, 1.0);
          return c;
        }

        void main() {
          float NdotV  = max(dot(vNormal, vViewDir), 0.0);
          float fresnel = pow(1.0 - NdotV, 3.5); // strong at edges

          // Primary specular highlight — sun spot
          vec3 halfV = normalize(uLightDir + vViewDir);
          float spec = pow(max(dot(vNormal, halfV), 0.0), 80.0);
          vec3  specColor = vec3(0.88, 1.0, 0.96) * spec * 2.5; // cyan-white

          // Secondary softer highlight
          float spec2 = pow(max(dot(vNormal, halfV), 0.0), 18.0) * 0.35;
          vec3 specColor2 = vec3(0.72, 1.0, 0.88) * spec2; // green-cyan tint

          // Iridescent rim — rainbow at edges
          vec3 iriColor = iridescence(fresnel) * fresnel * 1.4;

          // Inner shadow crescent (opposite to light)
          float shadow = pow(max(-dot(vNormal, uLightDir), 0.0), 2.0) * 0.25;

          // Combine: mostly transparent, rim + specular visible
          vec3  color   = specColor + specColor2 + iriColor * 0.9;
          float alpha   = fresnel * 0.55 + spec * 0.8 + spec2 * 0.3 + shadow * 0.15;
          alpha = clamp(alpha, 0.0, 0.92);

          gl_FragColor = vec4(color, alpha);
        }
      `;

      const bubbleGeo = new THREE.SphereGeometry(1, 32, 24);
      const bubbles = [];
      const bubbleLightDir = new THREE.Vector3(-0.4, 0.8, 0.6).normalize();

      for (let i = 0; i < 14; i++) {
        const r      = 6 + Math.random() * 18;
        const behind = i >= 7;
        const mat = new THREE.ShaderMaterial({
          vertexShader:   bubbleVert,
          fragmentShader: bubbleFrag,
          uniforms: {
            uTime:     { value: 0 },
            uPhase:    { value: Math.random() * Math.PI * 2 },
            uLightDir: { value: bubbleLightDir },
          },
          transparent: true,
          depthWrite:  false,
          side:        THREE.FrontSide,
        });
        const mesh = new THREE.Mesh(bubbleGeo, mat);
        mesh.scale.setScalar(r);
        const zBase = behind ? -(80 + Math.random() * 160) : (20 + Math.random() * 140);
        // Spawn across the full vertical range including off-screen above and below
        const spawnY = -80 + Math.random() * 710; // -80 (screen bottom) to 630 (above)
        mesh.position.set(
          40 + (Math.random() - 0.5) * 420,
          spawnY,
          zBase
        );
        mesh.userData.speed   = 8 + Math.random() * 12;
        mesh.userData.driftX  = (Math.random() - 0.5) * 0.9;
        mesh.userData.driftZ  = (Math.random() - 0.5) * 0.5;
        mesh.userData.wobble  = 0.3 + Math.random() * 0.5;
        mesh.userData.phase   = Math.random() * Math.PI * 2;
        mesh.userData.phaseZ  = Math.random() * Math.PI * 2;
        mesh.userData.resetY  = -90 - Math.random() * 20;  // reset at screen bottom
        mesh.userData.topY    = 640 + Math.random() * 80;  // disappear well above screen
        scene.add(mesh);
        bubbles.push(mesh);
      }

      // ── Statue ───────────────────────────────────────────────────────────────
      const glassMat = new THREE.MeshPhysicalMaterial({
        color:            new THREE.Color(0x00ffcc),
        metalness:        0.0,
        roughness:        0.04,
        transmission:     0.98,
        thickness:        4.0,
        envMap:           envTex,
        envMapIntensity:  4.0,
        transparent:      true,
        opacity:          0.97,
        emissive:         new THREE.Color(0x00ffcc),
        emissiveIntensity: 0.06,
        side:             THREE.FrontSide,
      });

      new GLTFLoader().load("/Mary_Statue.glb", (gltf) => {
        const obj = gltf.scene;
        if (cancelled) return;
        obj.traverse(c => {
          if (c.isMesh) { c.material = glassMat; c.castShadow = c.receiveShadow = false; }
        });
        const box    = new THREE.Box3().setFromObject(obj);
        const size   = box.getSize(new THREE.Vector3());
        const fovRad = (40 * Math.PI) / 180;
        const worldH = 2 * Math.tan(fovRad / 2) * 600;
        const scale  = (worldH * 0.65) / size.y;
        const newScale = scale * 1.495;
        obj.scale.setScalar(newScale);
        const box2  = new THREE.Box3().setFromObject(obj);
        const extraH = size.y * (newScale - scale);
        obj.position.set(110, -box2.min.y - extraH + 70, 0);
        obj.rotation.y = isMobile ? -(Math.PI * 10 / 180) : -(Math.PI * 30 / 180);
        scene.add(obj);

        const clock    = new THREE.Clock();
        const FRAME_MS = 1000 / 30;
        let lastFrame  = 0;
        let wasVisible = visibleRef.current;
        let hiddenAt   = wasVisible ? Infinity : 0;
        const FADE_MS  = 500;

        const animate = (now) => {
          animId = requestAnimationFrame(animate);
          const isVisible = visibleRef.current;
          if (isVisible && !wasVisible) { lastFrame = now; hiddenAt = Infinity; }
          if (!isVisible && wasVisible) hiddenAt = now;
          wasVisible = isVisible;
          const fadingOut = !isVisible && (now - hiddenAt < FADE_MS);
          if (!isVisible && !fadingOut) return;
          if (now - lastFrame < FRAME_MS) return;
          lastFrame = now - ((now - lastFrame) % FRAME_MS);

          const t  = clock.getElapsedTime();
          const dt = clock.getDelta();

          causticA.update(t);
          causticB.update(t);
          const baseOp = 0.11 + 0.05 * Math.sin(t * 0.75);
          causticA.mat.opacity = baseOp;
          causticB.mat.opacity = baseOp * 0.85;
          causticB.mesh.rotation.z = Math.PI * 0.08 + t * 0.015;

          // Dancing light — faster frequencies, wider swings
          keyLight.position.x  = Math.sin(t * 0.4) * 450 + Math.sin(t * 1.1) * 120;
          keyLight.position.z  = 300 + Math.cos(t * 0.35) * 300 + Math.cos(t * 0.9) * 80;
          // Math.abs() ensures each term only adds light, never subtracts — no dark flicker
          keyLight.intensity    = 2.5
                                + 5.0 * Math.abs(Math.sin(t * 1.1))
                                + 4.5 * Math.abs(Math.sin(t * 3.7))
                                + 4.0 * Math.abs(Math.sin(t * 7.3))
                                + 3.5 * Math.abs(Math.sin(t * 13.1))
                                + 3.0 * Math.abs(Math.sin(t * 23.7))
                                + 2.5 * Math.abs(Math.sin(t * 41.9))
                                + 2.0 * Math.abs(Math.sin(t * 67.3))
                                + 1.5 * Math.abs(Math.sin(t * 103.7))
                                + 1.1 * Math.abs(Math.sin(t * 157.3))
                                + 0.8 * Math.abs(Math.sin(t * 229.1))
                                + 0.5 * Math.abs(Math.sin(t * 331.7))
                                + 0.3 * Math.abs(Math.sin(t * 479.3))
                                + 0.2 * Math.abs(Math.sin(t * 691.1))
                                + 0.1 * Math.abs(Math.sin(t * 997.3))
                                + 0.05 * Math.abs(Math.sin(t * 1439.7));
          keyLight2.position.x = Math.sin(t * 0.38 + 1.4) * 380 + Math.cos(t * 1.0) * 100;
          keyLight2.position.z = 100 + Math.cos(t * 0.31 + 0.8) * 280 + Math.sin(t * 0.85) * 70;
          keyLight2.intensity   = 2.0
                                + 4.8 * Math.abs(Math.cos(t * 0.95 + 0.6))
                                + 4.2 * Math.abs(Math.cos(t * 4.2  + 0.9))
                                + 3.7 * Math.abs(Math.sin(t * 8.7  + 0.4))
                                + 3.2 * Math.abs(Math.sin(t * 16.4 + 1.1))
                                + 2.7 * Math.abs(Math.sin(t * 29.8 + 0.7))
                                + 2.2 * Math.abs(Math.cos(t * 53.1 + 1.5))
                                + 1.7 * Math.abs(Math.sin(t * 89.4 + 0.3))
                                + 1.3 * Math.abs(Math.cos(t * 131.7 + 0.9))
                                + 1.0 * Math.abs(Math.sin(t * 199.3 + 1.7))
                                + 0.7 * Math.abs(Math.cos(t * 283.1 + 0.5))
                                + 0.5 * Math.abs(Math.sin(t * 409.7 + 1.3))
                                + 0.3 * Math.abs(Math.cos(t * 593.3 + 0.8))
                                + 0.2 * Math.abs(Math.sin(t * 857.9 + 2.1))
                                + 0.1 * Math.abs(Math.cos(t * 1237.1 + 1.6))
                                + 0.05 * Math.abs(Math.sin(t * 1783.3 + 0.4));
          fillLight.intensity   = 1.2
                                + 2.0 * Math.abs(Math.sin(t * 2.3  + 0.5))
                                + 1.8 * Math.abs(Math.sin(t * 7.8  + 1.8))
                                + 1.5 * Math.abs(Math.sin(t * 19.2 + 0.9))
                                + 1.2 * Math.abs(Math.cos(t * 38.5 + 1.2))
                                + 0.9 * Math.abs(Math.sin(t * 71.3 + 0.4))
                                + 0.6 * Math.abs(Math.cos(t * 113.9 + 2.1))
                                + 0.4 * Math.abs(Math.sin(t * 173.7 + 0.7))
                                + 0.2 * Math.abs(Math.cos(t * 251.3 + 1.4))
                                + 0.1 * Math.abs(Math.sin(t * 367.1 + 0.9));

          // Update bubble shader time + animate positions
          for (const b of bubbles) {
            b.material.uniforms.uTime.value = t;
            b.position.y += b.userData.speed * dt
                          + Math.sin(t * 0.4  + b.userData.phase)  * b.userData.wobble * 0.3
                          + Math.sin(t * 0.9  + b.userData.phaseZ) * b.userData.wobble * 0.15;
            b.position.x += Math.sin(t * 0.22 + b.userData.phase)   * b.userData.driftX
                          + Math.cos(t * 0.37 + b.userData.phase)   * b.userData.wobble * 0.35
                          + Math.sin(t * 0.61 + b.userData.phaseZ)  * b.userData.driftX * 0.2;
            b.position.z += Math.cos(t * 0.18 + b.userData.phaseZ) * b.userData.driftZ
                          + Math.sin(t * 0.31 + b.userData.phaseZ)  * b.userData.wobble * 0.2;
            if (b.position.y > b.userData.topY) {
              b.position.y = b.userData.resetY; // reset below screen, not mid-view
              // Randomise X/Z on reset so they don't retrace the same path
              b.userData.phase  = Math.random() * Math.PI * 2;
              b.userData.phaseZ = Math.random() * Math.PI * 2;
            }
          }

          renderer.render(scene, camera);
        };
        animate(0);
      }, undefined, e => console.warn("RuleBackdrop GLB error:", e));
    }).catch(e => console.warn("RuleBackdrop import error:", e));

    return () => {
      cancelled = true;
      cancelAnimationFrame(animId);
      if (rendererInst) {
        rendererInst.dispose();
        if (el.contains(rendererInst.domElement)) el.removeChild(rendererInst.domElement);
      }
    };
  }, []);

  return (
    <div ref={mountRef} style={{
      position: "fixed",
      left: isMobile ? 0 : 224,
      top: 0, right: 0,
      bottom: 0,
      pointerEvents: "none",
      zIndex: -1,
      opacity,
      transition: "opacity 0.5s ease",
      filter: [
        "drop-shadow(0 0 8px rgba(0,255,204,0.6))",
        "drop-shadow(0 0 22px rgba(0,255,204,0.38))",
        "drop-shadow(0 0 50px rgba(0,220,180,0.22))",
        "drop-shadow(0 0 100px rgba(0,180,150,0.1))",
      ].join(" "),
    }} />
  );
}


function AudioFigureBackdropGLB({ visible = false, isMobile = false }) {
  const crossMountRef  = useRef(null);
  const figureMountRef = useRef(null);
  const visibleRef     = useRef(visible);
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    visibleRef.current = visible;
    if (!visible) { setOpacity(0); return; }
    const t = setTimeout(() => setOpacity(0.85), 250);
    return () => clearTimeout(t);
  }, [visible]);

  useEffect(() => {
    if (!crossMountRef.current || !figureMountRef.current) return;
    const crossEl  = crossMountRef.current;
    const figureEl = figureMountRef.current;
    let animId = null;
    let crossRendererInst  = null;
    let figureRendererInst = null;
    let cancelled = false;

    Promise.all([
      import("three"),
      import("three/examples/jsm/loaders/GLTFLoader"),
    ]).then(([THREE, { GLTFLoader }]) => {
      if (cancelled) return;
      const w = isMobile ? window.innerWidth : (window.innerWidth - 224);
      const h = isMobile ? window.innerHeight : (window.innerHeight - 70);

      const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 5000);
      camera.position.set(isMobile ? 0 : (-w * 0.32), 160, isMobile ? 1200 : 660);
      camera.lookAt(0, 160, 0);

      const crossScene    = new THREE.Scene();
      const crossRenderer = new THREE.WebGLRenderer({ canvas: crossEl, alpha: true, antialias: false });
      crossRenderer.setPixelRatio(1);
      crossRenderer.setSize(w, h);
      crossRenderer.setClearColor(0x000000, 0);
      crossRendererInst = crossRenderer;

      const glitterCanvas = document.createElement("canvas");
      glitterCanvas.width = glitterCanvas.height = 128;
      const gCtx = glitterCanvas.getContext("2d");
      const glitterTex = new THREE.CanvasTexture(glitterCanvas);

      const updateGlitter = (time) => {
        gCtx.clearRect(0, 0, 128, 128);
        const grd = gCtx.createLinearGradient(0, 0, 128, 128);
        grd.addColorStop(0,   "#e8eef2");
        grd.addColorStop(0.4, "#ffffff");
        grd.addColorStop(0.7, "#d4e4f0");
        grd.addColorStop(1,   "#f0f4f8");
        gCtx.fillStyle = grd;
        gCtx.fillRect(0, 0, 128, 128);
        const rng = (s) => { let x = Math.sin(s) * 43758.5453; return x - Math.floor(x); };
        for (let i = 0; i < 60; i++) {
          const tx = time * 0.7 + i * 1.3;
          const x  = rng(tx) * 128;
          const y  = rng(tx + 99) * 128;
          const r  = rng(tx + 17) * 3 + 0.5;
          const br = Math.sin(time * (2 + rng(i) * 4) + i) * 0.5 + 0.5;
          const a  = br * 0.9 + 0.1;
          const spark = gCtx.createRadialGradient(x, y, 0, x, y, r * 4);
          spark.addColorStop(0,   `rgba(255,255,255,${a})`);
          spark.addColorStop(0.3, `rgba(210,230,245,${a * 0.5})`);
          spark.addColorStop(1,   "rgba(255,255,255,0)");
          gCtx.fillStyle = spark;
          gCtx.beginPath(); gCtx.arc(x, y, r * 4, 0, Math.PI * 2); gCtx.fill();
        }
        glitterTex.needsUpdate = true;
      };

      const crossMat = new THREE.MeshBasicMaterial({
        map: glitterTex, transparent: true, opacity: 1.0,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });
      const crossGroup = new THREE.Group();
      const crossH = isMobile ? 279 : 230, crossW = isMobile ? 153 : 125, barThick = isMobile ? 30 : 23;
      const vBar = new THREE.Mesh(new THREE.BoxGeometry(barThick, crossH, barThick), crossMat);
      vBar.position.y = crossH / 2;
      const hBar = new THREE.Mesh(new THREE.BoxGeometry(crossW, barThick, barThick), crossMat.clone());
      hBar.position.y = crossH * 0.70;
      crossGroup.add(vBar, hBar);
      const crossCamDist    = isMobile ? 1200 : 660;
      const crossFovRad     = (40 * Math.PI) / 180;
      const crossWorldPerPx = 2 * Math.tan(crossFovRad / 2) * crossCamDist / w;
      const crossCenterX    = isMobile ? 0 : (-160 * crossWorldPerPx);
      crossGroup.position.set(crossCenterX, 0, 0);
      crossScene.add(crossGroup);

      const figureScene    = new THREE.Scene();
      const figureRenderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
      figureRenderer.setPixelRatio(1);
      figureRenderer.setSize(w, h);
      figureRenderer.setClearColor(0x000000, 0);
      figureEl.appendChild(figureRenderer.domElement);
      figureRendererInst = figureRenderer;

      const wireMat = new THREE.MeshBasicMaterial({
        color: 0x00ffcc, wireframe: true,
        transparent: true, opacity: 0.32,
        blending: THREE.AdditiveBlending, depthWrite: false,
        depthTest: !isMobile,
      });

      let mixer = null;
      const clock = new THREE.Clock();

      new GLTFLoader().load("/Praying.glb", (gltf) => {
        const obj = gltf.scene;
        obj.animations = gltf.animations;
        if (cancelled) return;
        obj.traverse(c => {
          if (c.isMesh) { c.material = wireMat; c.castShadow = c.receiveShadow = false; }
        });
        const box    = new THREE.Box3().setFromObject(obj);
        const size   = box.getSize(new THREE.Vector3());
        const fovRad = (40 * Math.PI) / 180;
        const worldH = 2 * Math.tan(fovRad / 2) * 600;
        const scale  = (worldH * 0.65) / size.y;
        const newScale = scale * 1.495;
        obj.scale.setScalar(newScale);
        const box2   = new THREE.Box3().setFromObject(obj);
        const extraH = size.y * (newScale - scale);
        // Same x position as desktop — camera centered on mobile so 110 places figure right-of-center
        obj.position.set(110, -box2.min.y - extraH + 70, 0);
        // Same rotation as desktop adjusted 20° CCW on mobile
        obj.rotation.y = isMobile ? -(Math.PI * 170) / 180 : -(Math.PI * 195) / 180;
        figureScene.add(obj);

        // Align cross base to figure's ground level.
        // On mobile, crossH was reduced by 31 (310→279), so shift y up by 31 to keep top at same position.
        crossGroup.position.y = -box2.min.y - extraH + 70 + 180 + (isMobile ? 0 : 0);

        if (obj.animations?.length) {
          const clip = obj.animations[0];
          const fps  = 30;
          mixer = new THREE.AnimationMixer(obj);

          // Intro: play frames 0–45 once at 0.4x speed
          const intro = THREE.AnimationUtils.subclip(clip, "intro", 0, 45, fps);
          const introAction = mixer.clipAction(intro);
          introAction.setLoop(THREE.LoopOnce, 1);
          introAction.clampWhenFinished = true;
          introAction.timeScale = 0.5;
          introAction.play();

          // Phase tracking: "intro" → "bounce" → "breathing"
          let phase = "intro";
          let breathTime = 0;
          let bounceTime = 0;

          // Physics bounce — upward kick, 1.5x faster than before
          const restY     = obj.position.y;
          let   vel       = 0;
          let   disp      = 0;
          const stiffness = 90;
          const damping   = 18.0;
          const kickVel   = 44;   // upward, modest

          // Collect foot bones
          const footBones = [];
          obj.traverse(c => {
            if (c.isBone && (c.name.toLowerCase().includes("foot") ||
                             c.name.toLowerCase().includes("toe") ||
                             c.name.toLowerCase().includes("ankle"))) {
              footBones.push({ bone: c, worldPos: new THREE.Vector3() });
            }
          });
          let footPositionsLocked = false;

          // Collect torso/thigh bones for forward lean
          const leanBones = [];
          obj.traverse(c => {
            if (c.isBone && (c.name.toLowerCase().includes("spine") ||
                             c.name.toLowerCase().includes("chest") ||
                             c.name.toLowerCase().includes("thigh") ||
                             c.name.toLowerCase().includes("upleg"))) {
              leanBones.push({ bone: c, baseRot: c.quaternion.clone() });
            }
          });

          // Collect arm bones for ragdoll
          const armBones = [];
          obj.traverse(c => {
            if (c.isBone && (c.name.toLowerCase().includes("arm") ||
                             c.name.toLowerCase().includes("forearm") ||
                             c.name.toLowerCase().includes("shoulder") ||
                             c.name.toLowerCase().includes("elbow") ||
                             c.name.toLowerCase().includes("hand"))) {
              armBones.push({ bone: c, baseRot: null, dispX: 0, dispY: 0, velX: 0, velY: 0 });
            }
          });

          // Estimate bounce duration for normalizing t (stiffness/damping gives ~settle time)
          const bounceDuration = 1.2; // seconds estimate

          const triggerBounce = () => {
            if (phase !== "intro") return;
            phase = "bounce";
            vel   = kickVel;
            disp  = 0;
            bounceTime = 0;
            footBones.forEach(fb => {
              fb.bone.updateWorldMatrix(true, false);
              fb.worldPos = new THREE.Vector3();
              fb.bone.getWorldPosition(fb.worldPos);
            });
            footPositionsLocked = true;
            leanBones.forEach(lb => { lb.baseRot = lb.bone.quaternion.clone(); });
            armBones.forEach(ab => {
              ab.baseRot = ab.bone.quaternion.clone();
              ab.dispX = 0; ab.dispY = 0; ab.velX = 0; ab.velY = 0;
            });
          };

          mixer.addEventListener("finished", () => { triggerBounce(); });

          const breathBones = [];
          obj.traverse(c => {
            if (c.isBone && (c.name === "mixamorig:Spine" ||
                             c.name === "mixamorig:Spine1" ||
                             c.name === "mixamorig:Spine2" ||
                             c.name === "mixamorig:Neck")) {
              breathBones.push({ bone: c, baseScale: c.scale.clone() });
            }
          });

          // Idle sway — upper body bones only (no knees/feet/ankles/toes)
          const idleBones = [];
          obj.traverse(c => {
            if (!c.isBone) return;
            const n = c.name.toLowerCase();
            const isUpper = n.includes("spine") || n.includes("neck") ||
                            n.includes("head")  || n.includes("shoulder") ||
                            n.includes("arm")   || n.includes("forearm") ||
                            n.includes("hand")  || n.includes("clavicle");
            const isLower = n.includes("knee") || n.includes("foot") ||
                            n.includes("toe")  || n.includes("ankle") ||
                            n.includes("leg")  || n.includes("thigh") || n.includes("upleg");
            if (isUpper && !isLower) {
              // Each bone gets unique phase offsets for organic, non-repeating feel
              idleBones.push({
                bone:   c,
                baseRot: null, // snapshotted when breathing starts
                // Sway frequencies and phases — all slightly irrational for aperiodic motion
                phX1: Math.random() * Math.PI * 2, frX1: 0.31 + Math.random() * 0.18,
                phX2: Math.random() * Math.PI * 2, frX2: 0.71 + Math.random() * 0.22,
                phZ1: Math.random() * Math.PI * 2, frZ1: 0.27 + Math.random() * 0.15,
                phZ2: Math.random() * Math.PI * 2, frZ2: 0.63 + Math.random() * 0.19,
                ampX: 0.008 + Math.random() * 0.006,
                ampZ: 0.006 + Math.random() * 0.005,
              });
            }
          });

          // Lower leg / foot bones to keep locked during idle
          const lowerLockBones = [];
          obj.traverse(c => {
            if (!c.isBone) return;
            const n = c.name.toLowerCase();
            if (n.includes("knee") || n.includes("foot") ||
                n.includes("toe")  || n.includes("ankle")) {
              lowerLockBones.push({ bone: c, worldPos: null });
            }
          });

          // introDuration: subclip is 45 frames at 30fps = 1.5s, played at 0.5x = 3s wall time
          const introDuration = (45 / fps) / 0.5;

          const tmpQ  = new THREE.Quaternion();
          const leanQ = new THREE.Quaternion();

          // Restart function: resets mixer and all phase state to beginning
          const restartAnimation = () => {
            mixer.stopAllAction();
            const restartIntro = THREE.AnimationUtils.subclip(clip, "intro", 0, 45, fps);
            const restartAction = mixer.clipAction(restartIntro);
            restartAction.setLoop(THREE.LoopOnce, 1);
            restartAction.clampWhenFinished = true;
            restartAction.timeScale = 0.5;
            restartAction.reset().play();
            phase = "intro";
            vel = 0; disp = 0; bounceTime = 0; breathTime = 0;
            introElapsed = 0;
            footPositionsLocked = false;
            obj.position.y = restY;
            clock.start();
          };

          const FRAME_MS = 1000 / 30;
          const FADE_MS = 500;
          let lastFrame = 0;
          let introElapsed = 0;
          let hiddenAt = visibleRef.current ? Infinity : 0;
          let wasVisible = visibleRef.current;
          const animateWithBreath = (now) => {
            animId = requestAnimationFrame(animateWithBreath);
            const isVisible = visibleRef.current;
            if (isVisible && !wasVisible) { restartAnimation(); lastFrame = now; introElapsed = 0; hiddenAt = Infinity; }
            if (!isVisible && wasVisible) hiddenAt = now;
            wasVisible = isVisible;
            const fadingOut = !isVisible && (now - hiddenAt < FADE_MS);
            if (!isVisible && !fadingOut) return;
            if (now - lastFrame < FRAME_MS) return;
            lastFrame = now - ((now - lastFrame) % FRAME_MS);
            const dt = Math.min(clock.getDelta(), 0.05);
            if (!isVisible) { if (mixer) mixer.update(dt); crossRenderer.render(crossScene, camera); figureRenderer.render(figureScene, camera); return; }
            if (mixer) mixer.update(dt);

            // Manual intro end detection as fallback in case finished event doesn't fire
            if (phase === "intro") {
              introElapsed += dt;
              if (introElapsed >= introDuration) triggerBounce();
            }

            const toCamX = camera.position.x - crossGroup.position.x;
            const toCamZ = camera.position.z - crossGroup.position.z;
            const camAngle = Math.atan2(toCamX, toCamZ);
            crossGroup.rotation.set(0, camAngle, 0);
            updateGlitter(clock.elapsedTime);
            crossMat.opacity = 0.88 + Math.sin(clock.elapsedTime * 4.1) * 0.08 + Math.sin(clock.elapsedTime * 11.3) * 0.04;

            if (phase === "bounce") {
              bounceTime += dt;
              const force = -stiffness * disp - damping * vel;
              vel  += force * dt;
              disp += vel   * dt;
              obj.position.y = restY + disp;

              // t: 0→1 over bounce duration, clamped, used to blend back to base
              const t = Math.min(bounceTime / bounceDuration, 1.0);

              // Torso/thigh: slight forward lean peaking at bounce apex, fades with t
              // Max lean ~4° forward (positive x rotation in local space)
              const leanAmt = (disp / kickVel) * 0.07 * (1 - t * t);
              leanBones.forEach(lb => {
                leanQ.setFromAxisAngle(new THREE.Vector3(1, 0, 0), leanAmt);
                lb.bone.quaternion.copy(lb.baseRot).multiply(leanQ);
              });

              // Arm ragdoll: each arm bone driven by figure's vertical acceleration
              const armStiff = 8, armDamp = 2.5;
              const accel = force; // reuse spring force as proxy for acceleration
              armBones.forEach(ab => {
                if (!ab.baseRot) return;
                const extForce = accel * 0.004;
                const fx = -armStiff * ab.dispX - armDamp * ab.velX + extForce;
                const fy = -armStiff * ab.dispY - armDamp * ab.velY + extForce * 0.5;
                ab.velX += fx * dt; ab.dispX += ab.velX * dt;
                ab.velY += fy * dt; ab.dispY += ab.velY * dt;
                const blend = 1 - t;
                const rx = ab.dispX * blend * 0.35;
                const ry = ab.dispY * blend * 0.20;
                tmpQ.setFromEuler(new THREE.Euler(rx, ry, 0));
                ab.bone.quaternion.copy(ab.baseRot).multiply(tmpQ);
              });

              // Foot locking
              if (footPositionsLocked) {
                footBones.forEach(fb => {
                  if (!fb.worldPos) return;
                  const parent = fb.bone.parent;
                  if (parent) {
                    parent.updateWorldMatrix(true, false);
                    const invParent = new THREE.Matrix4().copy(parent.matrixWorld).invert();
                    const localTarget = fb.worldPos.clone().applyMatrix4(invParent);
                    fb.bone.position.copy(localTarget);
                  }
                });
              }

              if (Math.abs(disp) < 0.8 && Math.abs(vel) < 0.8) {
                obj.position.y = restY;

                // Snapshot every idle bone's current world quaternion BEFORE any resets
                // so idle starts exactly where bounce left off
                idleBones.forEach(ib => {
                  ib.bone.updateWorldMatrix(true, false);
                  // Store world-space quat; we'll apply relative to parent each frame
                  const worldQ = new THREE.Quaternion();
                  ib.bone.getWorldQuaternion(worldQ);
                  ib.worldBaseQ = worldQ;
                  // Also store local quat as-is right now
                  ib.baseRot = ib.bone.quaternion.clone();
                });

                // Snapshot lower-leg world positions before any movement
                lowerLockBones.forEach(lb => {
                  lb.bone.updateWorldMatrix(true, false);
                  lb.worldPos = new THREE.Vector3();
                  lb.bone.getWorldPosition(lb.worldPos);
                });

                leanBones.forEach(lb => { lb.bone.quaternion.copy(lb.baseRot); });
                // Arms stay where they landed — no reset
                disp = 0; vel = 0;
                breathTime = 0;
                phase = "breathing";
              }
            }

            if (phase === "breathing") {
              breathTime += dt;

              // Subtle upper-body idle sway — aperiodic, zero displacement at t=0
              const eq = new THREE.Euler();
              const qq = new THREE.Quaternion();
              idleBones.forEach(ib => {
                if (!ib.baseRot) return;
                // Subtract the t=0 value so displacement starts exactly at zero
                const rx = (Math.sin(breathTime * ib.frX1 * Math.PI * 2 + ib.phX1) - Math.sin(ib.phX1)) * ib.ampX
                         + (Math.sin(breathTime * ib.frX2 * Math.PI * 2 + ib.phX2) - Math.sin(ib.phX2)) * ib.ampX * 0.5;
                const rz = (Math.sin(breathTime * ib.frZ1 * Math.PI * 2 + ib.phZ1) - Math.sin(ib.phZ1)) * ib.ampZ
                         + (Math.sin(breathTime * ib.frZ2 * Math.PI * 2 + ib.phZ2) - Math.sin(ib.phZ2)) * ib.ampZ * 0.5;
                eq.set(rx, 0, rz);
                qq.setFromEuler(eq);
                // Apply on top of snapshotted pose — starts exactly where bounce ended
                ib.bone.quaternion.copy(ib.baseRot).multiply(qq);
              });

              // Keep knees and feet world-locked
              lowerLockBones.forEach(lb => {
                if (!lb.worldPos) return;
                const parent = lb.bone.parent;
                if (parent) {
                  parent.updateWorldMatrix(true, false);
                  const inv = new THREE.Matrix4().copy(parent.matrixWorld).invert();
                  lb.bone.position.copy(lb.worldPos.clone().applyMatrix4(inv));
                }
              });
            }

            crossRenderer.render(crossScene, camera);
            figureRenderer.render(figureScene, camera);
          };
          animId = requestAnimationFrame(animateWithBreath);
        }
      }, undefined, e => console.warn("Audio FBX load error:", e));

      // Default fallback animate — only runs if FBX fails to load
      setTimeout(() => {
        if (!animId) {
          const animate = () => {
            animId = requestAnimationFrame(animate);
            const toCamX = camera.position.x - crossGroup.position.x;
            const toCamZ = camera.position.z - crossGroup.position.z;
            crossGroup.rotation.y = Math.atan2(toCamX, toCamZ);
            crossRenderer.render(crossScene, camera);
            figureRenderer.render(figureScene, camera);
          };
          animate();
        }
      }, 3000);
    }).catch(e => console.warn("Three.js import error:", e));

    return () => {
      cancelled = true;
      cancelAnimationFrame(animId);
      if (crossRendererInst) {
        crossRendererInst.dispose();
      }
      if (figureRendererInst) {
        figureRendererInst.dispose();
        if (figureEl.contains(figureRendererInst.domElement)) figureEl.removeChild(figureRendererInst.domElement);
      }
    };
  }, []);

  return (
    <div style={{
      position: "fixed", left: isMobile ? 0 : 224, top: 0, right: 0, bottom: isMobile ? 0 : 70,
      pointerEvents: "none", zIndex: -1, opacity,
      transition: "opacity 0.5s ease",
    }}>
      {needsCanvasForAlpha && (
        <canvas ref={crossMountRef} style={{ position: "absolute", inset: 0, zIndex: 1, filter: "drop-shadow(0 0 10px rgba(180,220,255,0.88)) drop-shadow(0 0 3px rgba(255,255,255,0.75))" }} />
      )}
      <div ref={figureMountRef} style={{ position: "absolute", inset: 0, zIndex: 2 }} />
    </div>
  );
}
// ─── WORKOUT FIGURE BACKDROP ──────────────────────────────────────────────────
function WorkoutFigureBackdropGLB({ visible = false, isMobile = false }) {
  const mountRef   = useRef(null);
  const visibleRef = useRef(visible);
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    visibleRef.current = visible;
    if (!visible) { setOpacity(0); return; }
    const t = setTimeout(() => setOpacity(0.85), 250);
    return () => clearTimeout(t);
  }, [visible]);

  useEffect(() => {
    if (!mountRef.current) return;
    const el = mountRef.current;
    let animId = null;
    let rendererInst = null;
    let cancelled = false;

    Promise.all([
      import("three"),
      import("three/examples/jsm/loaders/GLTFLoader"),
    ]).then(([THREE, { GLTFLoader }]) => {
      if (cancelled) return;
      const w = isMobile ? window.innerWidth : (window.innerWidth - 224);
      const h = isMobile ? window.innerHeight : window.innerHeight; // full height — prevents figure clipping at bottom

      const scene  = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 2000);
      camera.position.set(isMobile ? 0 : (-w * 0.32), 160, isMobile ? 1200 : 660);
      camera.lookAt(0, 160, 0);

      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
      renderer.setPixelRatio(1);
      renderer.setSize(w, h);
      renderer.setClearColor(0x000000, 0);
      renderer.domElement.style.cssText="position:absolute;top:0;left:0;"; el.appendChild(renderer.domElement);
      rendererInst = renderer;

      const wireMat = new THREE.MeshBasicMaterial({
        color: 0x00ffcc, wireframe: true,
        transparent: true, opacity: 0.32,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });

      const clock = new THREE.Clock();
      // Desktop: -(30° + 20°) = -50°, matching reference. Mobile: -25°
      const ROTATION_Y = isMobile ? -(Math.PI * 25 / 180) : -(Math.PI / 6) - (20 * Math.PI / 180);

      const applyTransform = (THREE, obj) => {
        obj.traverse(c => {
          if (c.isMesh) { c.material = wireMat; c.castShadow = c.receiveShadow = false; }
        });
        const box    = new THREE.Box3().setFromObject(obj);
        const size   = box.getSize(new THREE.Vector3());
        const fovRad = (40 * Math.PI) / 180;
        // Use hardcoded 600 (desktop camera Z) so scale matches the other two backdrops
        const worldH   = 2 * Math.tan(fovRad / 2) * 600;
        const scale    = (worldH * 0.65) / size.y;
        const newScale = scale * 1.495;
        obj.scale.setScalar(newScale);
        const box2   = new THREE.Box3().setFromObject(obj);
        const extraH = size.y * (newScale - scale);
        obj.position.set(110, -box2.min.y - extraH + 70, 0);
        obj.rotation.y = ROTATION_Y;
        return { savedScale: obj.scale.clone(), savedPos: obj.position.clone() };
      };

      // Preload both FBX files simultaneously so loop is ready instantly
      Promise.all([
        new Promise((res, rej) => new GLTFLoader().load("/Idle_To_Push_Up.glb", res, undefined, rej)),
        new Promise((res, rej) => new GLTFLoader().load("/Push_Up.glb",         res, undefined, rej)),
      ]).then(([introGltf, loopGltf]) => {
        if (cancelled) return;
        const introObj = introGltf.scene; introObj.animations = introGltf.animations;
        const loopObj  = loopGltf.scene;  loopObj.animations  = loopGltf.animations;

        const { savedScale, savedPos } = applyTransform(THREE, introObj);
        applyTransform(THREE, loopObj);

        // Keep loop hidden until needed
        loopObj.visible = false;
        scene.add(introObj);
        scene.add(loopObj);

        let activeMixer = new THREE.AnimationMixer(introObj);
        let loopMixer   = null;

        // Pre-build loop mixer so it's ready with no load delay
        if (loopObj.animations?.length) {
          loopMixer = new THREE.AnimationMixer(loopObj);
          const loopAction = loopMixer.clipAction(loopObj.animations[0]);
          loopAction.setLoop(THREE.LoopRepeat, Infinity);
          loopAction.play();
          loopMixer.update(0); // prime first frame
        }

        if (introObj.animations?.length) {
          const introAction = activeMixer.clipAction(introObj.animations[0]);
          introAction.setLoop(THREE.LoopOnce, 1);
          introAction.clampWhenFinished = true;
          introAction.play();
        }

        let swapped = false;
        activeMixer.addEventListener("finished", () => {
          if (cancelled || swapped) return;
          swapped = true;

          // Reset loop to frame 0 first so bone positions are at start pose
          if (loopMixer && loopObj.animations?.length) {
            loopMixer.stopAllAction();
            const loopAction = loopMixer.clipAction(loopObj.animations[0]);
            loopAction.setLoop(THREE.LoopRepeat, Infinity);
            loopAction.time = 0;
            loopAction.play();
            loopMixer.update(0);
          }

          // Pin intro to its exact last frame before measuring — prevents overshoot
          // from clock.getDelta() causing a different bone position each time
          if (introObj.animations?.length) {
            const introAction = activeMixer.clipAction(introObj.animations[0]);
            introAction.time = introObj.animations[0].duration;
            activeMixer.update(0);
          }

          // Find the root/hips bone in each skeleton
          let introBone = null, loopBone = null;
          const rootNames = ["hips", "pelvis", "root", "spine"];
          introObj.traverse(c => {
            if (!introBone && c.isBone) {
              const n = c.name.toLowerCase();
              if (rootNames.some(r => n.includes(r))) introBone = c;
            }
          });
          loopObj.traverse(c => {
            if (!loopBone && c.isBone) {
              const n = c.name.toLowerCase();
              if (rootNames.some(r => n.includes(r))) loopBone = c;
            }
          });
          // Fallback: use first bone found
          if (!introBone) introObj.traverse(c => { if (!introBone && c.isBone) introBone = c; });
          if (!loopBone)  loopObj.traverse(c  => { if (!loopBone  && c.isBone) loopBone  = c; });

          if (introBone && loopBone) {
            // Get world positions of both root bones
            introBone.updateWorldMatrix(true, false);
            loopBone.updateWorldMatrix(true, false);
            const introWorldPos = new THREE.Vector3();
            const loopWorldPos  = new THREE.Vector3();
            introBone.getWorldPosition(introWorldPos);
            loopBone.getWorldPosition(loopWorldPos);

            // Shift loop object so its root bone lands exactly where intro's root bone is
            loopObj.position.x += introWorldPos.x - loopWorldPos.x;
            loopObj.position.y += introWorldPos.y - loopWorldPos.y;
            loopObj.position.z += introWorldPos.z - loopWorldPos.z;
          } else {
            // Fallback: match bounding box bottoms
            const introBox = new THREE.Box3().setFromObject(introObj);
            const loopBox  = new THREE.Box3().setFromObject(loopObj);
            loopObj.position.y += introBox.min.y - loopBox.min.y;
          }

          // Swap visibility
          introObj.visible = false;
          loopObj.visible  = true;
          activeMixer      = loopMixer;
        });

        const FRAME_MS = 1000 / 30;
        const FADE_MS = 500;
        let lastFrame = 0;
        let hiddenAt = visibleRef.current ? Infinity : 0;
        let wasVisible = visibleRef.current;
        const animate = (now) => {
          animId = requestAnimationFrame(animate);
          const isVisible = visibleRef.current;
          if (isVisible && !wasVisible) {
            // Restart: reset to intro animation from frame 0
            swapped = false;
            introObj.visible = true;
            loopObj.visible  = false;
            activeMixer = new THREE.AnimationMixer(introObj);
            if (introObj.animations?.length) {
              const introAction = activeMixer.clipAction(introObj.animations[0]);
              introAction.setLoop(THREE.LoopOnce, 1);
              introAction.clampWhenFinished = true;
              introAction.reset().play();
            }
            activeMixer.addEventListener("finished", () => {
              if (cancelled || swapped) return;
              swapped = true;
              if (loopMixer && loopObj.animations?.length) {
                loopMixer.stopAllAction();
                const loopAction = loopMixer.clipAction(loopObj.animations[0]);
                loopAction.setLoop(THREE.LoopRepeat, Infinity);
                loopAction.time = 0;
                loopAction.play();
                loopMixer.update(0);
              }
              introObj.visible = false;
              loopObj.visible  = true;
              activeMixer      = loopMixer;
            });
            clock.start();
            lastFrame = now;
            hiddenAt = Infinity;
          }
          if (!isVisible && wasVisible) hiddenAt = now;
          wasVisible = isVisible;
          const fadingOut = !isVisible && (now - hiddenAt < FADE_MS);
          if (!isVisible && !fadingOut) return;
          if (now - lastFrame < FRAME_MS) return;
          lastFrame = now - ((now - lastFrame) % FRAME_MS);
          const dt = Math.min(clock.getDelta(), 0.05);
          if (activeMixer) activeMixer.update(dt);
          renderer.render(scene, camera);
        };
        animate(0);
      }).catch(e => console.warn("Workout FBX load error:", e));
    }).catch(e => console.warn("Three.js import error:", e));

    return () => {
      cancelled = true;
      cancelAnimationFrame(animId);
      if (rendererInst) {
        rendererInst.dispose();
        if (el.contains(rendererInst.domElement)) el.removeChild(rendererInst.domElement);
      }
    };
  }, []);

  return (
    <div ref={mountRef} style={{
      position: "fixed",
      left: isMobile ? 0 : 224, top: 0, right: 0, bottom: 0,
      pointerEvents: "none", zIndex: -1,
      opacity, transition: "opacity 0.5s ease",
      }} />
  );
}


// ─── BACKDROP SWITCHERS ────────────────────────────────────────────────────────
// needsCanvasForAlpha = true means Safari (no WebM alpha support).
// Safari gets original 3D GLB rendering — same as before.
// Everyone else gets the pre-rendered WebM video — lighter and faster.

function FigureBackdrop(props) {
  return needsCanvasForAlpha
    ? <FigureBackdropGLB {...props} />
    : <FigureBackdropPR {...props} />;
}

function RuleBackdrop(props) {
  return needsCanvasForAlpha
    ? <RuleBackdropGLB {...props} />
    : <RuleBackdropPR {...props} />;
}

function AudioFigureBackdrop(props) {
  return needsCanvasForAlpha
    ? <AudioFigureBackdropGLB {...props} />
    : <AudioFigureBackdropPR {...props} />;
}

function WorkoutFigureBackdrop(props) {
  return needsCanvasForAlpha
    ? <WorkoutFigureBackdropGLB {...props} />
    : <WorkoutFigureBackdropPR {...props} />;
}


// ─── AVATAR PHOTO ──────────────────────────────────────────────────────────────
// Single source of truth for the glossy sphere avatar effect.
// Use everywhere a user photo appears.
function AvatarPhoto({ src, alt = "", size = 45, className = "sm" }) {
  return (
    <div className={`avatar-photo ${className}`} style={{width:size,height:size,flexShrink:0,position:"relative"}}>
      <img src={src} alt={alt} crossOrigin="anonymous" />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(circle at 50% 50%,transparent 50%,rgba(0,0,0,0.4) 68%,rgba(0,0,0,0.85) 100%)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"linear-gradient(155deg,rgba(255,255,255,0.28) 0%,rgba(255,255,255,0.06) 25%,transparent 42%)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",boxShadow:"inset 0 1px 0 rgba(255,255,255,0.35),inset 1px 0 0 rgba(255,255,255,0.1)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"conic-gradient(from 198deg at 34% 24%, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.7) 22deg, rgba(255,255,255,0) 44deg, rgba(255,255,255,0) 360deg)",WebkitMaskImage:"radial-gradient(circle at 50% 50%,transparent 51%,black 57%,transparent 65%)",maskImage:"radial-gradient(circle at 50% 50%,transparent 51%,black 57%,transparent 65%)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 40% 28% at 33% 22%,rgba(255,255,255,0.22) 0%,transparent 62%)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 11% 8% at 31% 18%,rgba(255,255,255,1) 0%,transparent 100%)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 6% 4% at 44% 29%,rgba(200,230,255,0.6) 0%,transparent 100%)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 60% 18% at 50% 100%,rgba(0,0,0,0.38) 0%,transparent 65%)",pointerEvents:"none"}} />
    </div>
  );
}

// Initials avatar with the same glossy overlays as AvatarPhoto.
// bg defaults to var(--surface); pass "var(--accent)" for the current user.
function AvatarInitials({ name, size = 45, bg = "var(--surface)", color = "var(--text)", fontSize }) {
  const fs = fontSize || Math.round(size * 0.31);
  return (
    <div className="avatar sm" style={{width:size,height:size,flexShrink:0,fontSize:fs,background:bg,color,position:"relative"}}>
      {initials(name)}
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(circle at 50% 50%,transparent 50%,rgba(0,0,0,0.4) 68%,rgba(0,0,0,0.85) 100%)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"linear-gradient(155deg,rgba(255,255,255,0.18) 0%,rgba(255,255,255,0.04) 25%,transparent 42%)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",boxShadow:"inset 0 1px 0 rgba(255,255,255,0.25),inset 1px 0 0 rgba(255,255,255,0.08)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"conic-gradient(from 198deg at 34% 24%, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.5) 22deg, rgba(255,255,255,0) 44deg, rgba(255,255,255,0) 360deg)",WebkitMaskImage:"radial-gradient(circle at 50% 50%,transparent 51%,black 57%,transparent 65%)",maskImage:"radial-gradient(circle at 50% 50%,transparent 51%,black 57%,transparent 65%)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 11% 8% at 31% 18%,rgba(255,255,255,0.8) 0%,transparent 100%)",pointerEvents:"none"}} />
      <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 60% 18% at 50% 100%,rgba(0,0,0,0.3) 0%,transparent 65%)",pointerEvents:"none"}} />
    </div>
  );
}

// ─── RANK CARD ─────────────────────────────────────────────────────────────────
// Unified rank card using PR data. myPr and communityPrs are plain numbers.
function RankCard({ username, exercise, myPr, communityPrs = [], needsBodyWeight = false, onGoToWorkout }) {
  const exDef = EXERCISE_MAP[exercise] || { type: "weighted" };

  const leaderboard = useMemo(() => {
    const entries = [];
    if (myPr != null && myPr > 0) entries.push({ name: username, value: myPr, isMe: true, avatarUrl: null });
    communityPrs.forEach(u => {
      const v = u.prs?.[exercise];
      if (v != null && v > 0) entries.push({ name: u.name, value: v, isMe: false, avatarUrl: u.avatarUrl || null });
    });
    return entries.sort((a, b) => b.value - a.value);
  }, [username, exercise, myPr, communityPrs]);

  const myEntry    = leaderboard.find(e => e.isMe);
  const rank       = myEntry ? leaderboard.findIndex(e => e.isMe) + 1 : null;
  const total      = leaderboard.length;
  const ordinal    = n => { const s = ["th","st","nd","rd"], v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };
  const percentile = rank && total > 1 ? Math.round(((total - rank) / (total - 1)) * 100) : rank === 1 && total === 1 ? 100 : null;
  const MEDALS = [
    { color: "#ffdd00", label: "GOLD",   shadow: "0 0 16px #ffdd0099" },
    { color: "#c8d4de", label: "SILVER", shadow: "0 0 12px #c8d4de55" },
    { color: "#ff9944", label: "BRONZE", shadow: "0 0 12px #ff994455" },
  ];
  const medal   = rank && rank <= 3 ? MEDALS[rank - 1] : null;
  const barPct  = percentile !== null ? Math.max(2, percentile) : 0;
  const numSize = !rank ? 68 : rank >= 100 ? 46 : rank >= 10 ? 56 : 68;
  const valueLabel = myEntry
    ? exDef.type === "weighted"   ? `${myEntry.value} lbs est. 1RM`
    : exDef.type === "bodyweight" ? `${myEntry.value} reps`
    : formatDuration(myEntry.value)
    : null;
  const subtitle = exDef.type === "weighted"   ? "Est. 1RM"
                 : exDef.type === "bodyweight" ? "Max Reps"
                 : "Best Duration";

  return (
    <div style={{
      background: "linear-gradient(160deg,rgba(0,22,13,.55),rgba(0,12,7,.38) 50%,rgba(0,4,2,.45))",
      border: "1px solid rgba(136,255,0,.28)", borderTop: "1px solid rgba(136,255,0,.30)",
      borderLeft: "1px solid rgba(136,255,0,.18)",
      boxShadow: "0 2px 0 rgba(136,255,0,0.10), 0 8px 24px rgba(0,0,0,0.6), 0 28px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(136,255,0,0.15), inset 0 0 60px rgba(136,255,0,0.025)",
      borderRadius: "var(--radius)", padding: "26px 30px 24px", marginBottom: 24,
      position: "relative", overflow: "hidden", animation: "rankGlow 4s ease-in-out infinite",
    }}>
      <div style={{ position:"absolute", top:0, left:0, width:18, height:18, borderTop:"2px solid var(--lime)", borderLeft:"2px solid var(--lime)" }} />
      <div style={{ position:"absolute", bottom:0, right:0, width:18, height:18, borderBottom:"2px solid var(--cyan)", borderRight:"2px solid var(--cyan)" }} />
      <div style={{ position:"absolute", top:0, left:0, right:0, height:1, background:"var(--energy-grad)", backgroundSize:"300% 300%", animation:"sheen 2s ease-in-out infinite" }} />
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:22 }}>
        <div style={{ fontFamily:"'Orbitron',sans-serif", fontWeight:700, fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"var(--accent)", display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ color:"var(--lime)" }}>◆</span> Your Rank
        </div>
        <div style={{ fontFamily:"'Orbitron',sans-serif", fontSize:10, color:"var(--muted)", letterSpacing:1.5, textTransform:"uppercase", background:"rgba(136,255,0,.05)", border:"1px solid var(--border)", padding:"3px 10px", borderRadius:2 }}>
          {exercise} · {subtitle}
        </div>
      </div>
      {!myEntry && needsBodyWeight ? (
        <div style={{ textAlign:"center", padding:"20px 8px" }}>
          <div style={{ color:"var(--muted)", fontSize:12, fontFamily:"'Orbitron',sans-serif", letterSpacing:2, textTransform:"uppercase", marginBottom:10 }}>
            Body weight needed to rank
          </div>
          <div style={{ color:"var(--muted)", fontSize:13, lineHeight:1.6, maxWidth:420, margin:"0 auto 14px" }}>
            {exercise} includes your own body weight, so log yours on the Workout page to calculate your rank.
          </div>
          {onGoToWorkout && (
            <button className="btn btn-primary" onClick={onGoToWorkout} style={{ padding:"9px 20px", fontSize:12, letterSpacing:1 }}>
              LOG BODY WEIGHT
            </button>
          )}
        </div>
      ) : !myEntry ? (
        <div style={{ textAlign:"center", padding:"28px 0", color:"var(--muted)", fontSize:12, fontFamily:"'Orbitron',sans-serif", letterSpacing:2, textTransform:"uppercase" }}>
          Log a {exercise} set to see your rank
        </div>
      ) : (
        <div style={{ display:"flex", alignItems:"center", gap:28, flexWrap:"wrap" }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, flexShrink:0 }}>
            <div style={{ fontFamily:"'Orbitron',sans-serif", fontWeight:900, fontSize:numSize, lineHeight:1, letterSpacing:-2, background:"var(--chrome-grad)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", filter: medal ? "drop-shadow(0 0 28px "+medal.color+"cc)" : "drop-shadow(0 0 22px rgba(136,255,0,.9))" }}>
              {ordinal(rank)}
            </div>
            <div style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Orbitron',sans-serif", letterSpacing:1.5, textTransform:"uppercase" }}>of {total} athlete{total !== 1 ? "s" : ""}</div>
            {medal && <div style={{ fontSize:10, fontFamily:"'Orbitron',sans-serif", fontWeight:700, letterSpacing:2, textTransform:"uppercase", padding:"3px 10px", borderRadius:2, color:medal.color, border:"1px solid "+medal.color+"55", background:medal.color+"10", boxShadow:medal.shadow }}>{medal.label}</div>}
          </div>
          <div style={{ width:1, height:64, flexShrink:0, background:"linear-gradient(180deg,transparent,rgba(136,255,0,.25),transparent)" }} />
          <div style={{ display:"flex", flexDirection:"column", gap:16, flex:1, minWidth:190 }}>
            <div>
              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Orbitron',sans-serif", letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>Your best</div>
              <div style={{ fontFamily:"'Orbitron',sans-serif", fontWeight:700, fontSize:16, color:"var(--accent)", textShadow:"var(--glow-sm)" }}>
                {valueLabel}
              </div>
            </div>
            <div>
              <div style={{ fontSize:10, color:"var(--muted)", fontFamily:"'Orbitron',sans-serif", letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>Percentile rank</div>
              <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:6 }}>
                <span style={{ fontFamily:"'Orbitron',sans-serif", fontWeight:900, fontSize:22, color: percentile >= 90 ? "var(--lime)" : percentile >= 50 ? "var(--accent)" : "var(--muted)", textShadow: percentile >= 90 ? "var(--glow-lime)" : percentile >= 50 ? "var(--glow-sm)" : "none" }}>
                  {percentile !== null ? percentile+"th" : "—"}
                </span>
                <span style={{ fontSize:11, color:"var(--muted)", fontFamily:"'Orbitron',sans-serif", letterSpacing:1 }}>percentile</span>
              </div>
              <div style={{ height:5, background:"rgba(136,255,0,.06)", border:"1px solid rgba(136,255,0,.1)", overflow:"hidden", marginBottom:4 }}>
                <div style={{ height:"100%", width:barPct+"%", background:"var(--energy-grad)", backgroundSize:"300% 300%", animation:"sheen 2s ease-in-out infinite", boxShadow:"0 0 10px #88ff0077", transition:"width 1s cubic-bezier(.16,1,.3,1)" }} />
              </div>
              <div style={{ fontSize:11, color:"var(--muted)" }}>{percentile !== null ? `Ahead of ${percentile}% of logged athletes` : ""}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── WORKOUT PAGE ─────────────────────────────────────────────────────────────
function WorkoutPage({ username, isNewUser }) {
  const isMobile = useIsMobile();

  // ── Session state ──────────────────────────────────────────────────────────
  const [session, setSession]     = useState(null);
  const [sets, setSets]           = useState([]);
  const [prs, setPrs]             = useState({});     // { exercise: value }
  const [bodyWeights, setBodyWeights] = useState([]); // [{ id, weight, loggedAt }] oldest first
  const [newPrEx, setNewPrEx]     = useState(null);   // flash exercise name on new PR
  const [loading, setLoading]     = useState(true);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [group, setGroup]         = useState("Chest");
  const [exercise, setExercise]   = useState("Bench Press");
  const [weight, setWeight]       = useState("");
  const [reps, setReps]           = useState("");
  const [duration, setDuration]   = useState(""); // seconds
  const [setCount, setSetCount]   = useState("1");
  const [adding, setAdding]       = useState(false);

  // ── History + chart state ──────────────────────────────────────────────────
  const [history, setHistory]     = useState([]);
  const [chartView, setChartView] = useState("volume"); // "volume" | "pr"
  const [chartPeriod, setChartPeriod] = useState("all"); // "1m" | "3m" | "1y" | "all"

  const exDef = EXERCISE_MAP[exercise] || EXERCISES[0];
  const chartExDef = EXERCISE_MAP[exercise] || EXERCISES[0]; // chart follows Today's Session exercise
  // Body-weight-adjusted exercises can't show progress until a body weight is on file
  const needsBw = isBwExercise(exercise);
  const awaitingBw = needsBw && bodyWeights.length === 0;

  // ── Load on mount ──────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      api.getTodaySession(),
      api.getMyPrs(),
      api.getWorkoutHistory(),
      api.getBodyWeights().catch(() => ({ entries: [] })),
    ]).then(([todayData, prData, histData, bwData]) => {
      setSession(todayData.session);
      setSets(todayData.sets);
      const prMap = {};
      for (const p of prData) prMap[p.exercise] = p.value;
      setPrs(prMap);
      setHistory(histData);
      setBodyWeights(bwData.entries || []);
    }).catch(err => console.warn("WorkoutPage load:", err))
      .finally(() => setLoading(false));
  }, [username]);

  // Coming from Top Charts' "body weight needed" prompt: scroll to the body weight panel once loaded
  useEffect(() => {
    if (loading || !bodyWeightScrollPending) return;
    bodyWeightScrollPending = false;
    setTimeout(() => document.getElementById("body-weight-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }, [loading]);

  // ── Body weight log / delete ────────────────────────────────────────────────
  // The server recomputes the affected PRs, so refresh them here too.
  const refreshPrs = () => api.getMyPrs().then(pd => {
    const prMap = {};
    for (const p of pd) prMap[p.exercise] = p.value;
    setPrs(prMap);
  }).catch(() => {});
  const logBodyWeight = async (w, loggedAt) => {
    const data = await api.addBodyWeight(w, loggedAt);
    setBodyWeights(data.entries || []);
    refreshPrs();
  };
  const removeBodyWeight = async (id) => {
    const data = await api.deleteBodyWeight(id);
    setBodyWeights(data.entries || []);
    refreshPrs();
  };

  // Poll for today's sets every 30s
  usePolling(() => {
    if (!session) return;
    api.getTodaySession().then(d => setSets(d.sets)).catch(() => {});
  }, 30000);

  // ── Add a set ─────────────────────────────────────────────────────────────
  const addSet = async () => {
    if (!session) return;
    if (exDef.type === "weighted"   && ((!weight && !needsBw) || !reps)) return;
    if (exDef.type === "bodyweight" && !reps) return;
    if (exDef.type === "duration"   && !duration) return;
    const n = Math.max(1, Math.min(50, parseInt(setCount) || 1));
    setAdding(true);
    try {
      const body = {
        exercise,
        setType: exDef.type,
        weight:          exDef.type === "weighted"   ? (Number(weight) || 0) : null,
        reps:            exDef.type !== "duration"   ? Number(reps)     : null,
        durationSeconds: exDef.type === "duration"   ? Number(duration) : null,
      };
      let latestIsNewPr = false;
      const newSets = [];
      for (let i = 0; i < n; i++) {
        const { set, isNewPr } = await api.addSet(session.id, body);
        newSets.push(set);
        if (isNewPr) latestIsNewPr = true;
      }
      setSets(prev => [...prev, ...newSets]);
      if (latestIsNewPr) {
        const prData = await api.getMyPrs();
        const prMap = {};
        for (const p of prData) prMap[p.exercise] = p.value;
        setPrs(prMap);
        setNewPrEx(exercise);
        setTimeout(() => setNewPrEx(null), 3000);
      }
      api.getWorkoutHistory().then(setHistory).catch(() => {});
      setWeight(""); setReps(""); setDuration(""); setSetCount("1");
    } catch (err) { console.warn("addSet:", err); }
    finally { setAdding(false); }
  };

  // ── Delete a set ──────────────────────────────────────────────────────────
  const deleteSet = async (setId) => {
    try {
      await api.deleteSet(setId);
      setSets(prev => prev.filter(s => s.id !== setId));
      // Refresh both history and PRs since deleting a best set lowers the PR
      const [histData, prData] = await Promise.all([
        api.getWorkoutHistory(),
        api.getMyPrs(),
      ]);
      setHistory(histData);
      const prMap = {};
      for (const p of prData) prMap[p.exercise] = p.value;
      setPrs(prMap);
    } catch (err) { console.warn("deleteSet:", err); }
  };

  // ── Chart data ────────────────────────────────────────────────────────────
  // Parse session date robustly — old sessions stored as "Jan 15" (no year),
  // new sessions as "2025-01-15" (ISO). Add current year to yearless strings.
  const parseSessionDate = (d) => {
    if (!d) return 0;
    const withYear = /^[A-Za-z]{3}\s+\d{1,2}$/.test(d.trim())
      ? `${d.trim()}, ${new Date().getFullYear()}`
      : d;
    const ts = new Date(withYear).getTime();
    return isNaN(ts) ? 0 : ts;
  };

  const buildVolumeData = (ex) => {
    const byDate = {};
    for (const sess of history) {
      const dateKey = normDate(sess.date);
      if (!byDate[dateKey]) byDate[dateKey] = { date: dateKey, rawTs: parseSessionDate(sess.date), sets: [] };
      for (const s of sess.sets) {
        if (s.exercise !== ex) continue;
        byDate[dateKey].sets.push(s);
      }
    }
    return Object.values(byDate).map(({ date, rawTs, sets }) => {
      if (sets.length === 0) return null;
      let vol = 0;
      for (const s of sets) {
        if (s.setType === "weighted")   vol += (s.weight || 0) * (s.reps || 0);
        if (s.setType === "bodyweight") vol += s.reps || 0;
        if (s.setType === "duration")   vol += s.durationSeconds || 0;
      }
      if (vol === 0) return null;
      return { date, rawTs, value: vol };
    }).filter(Boolean).sort((a, b) => {
      const ia = history.findIndex(s => normDate(s.date) === a.date);
      const ib = history.findIndex(s => normDate(s.date) === b.date);
      return ia - ib;
    });
  };

  const buildPrData = (ex, def) => {
    const byDate = {};
    for (const sess of history) {
      const dateKey = normDate(sess.date);
      if (!byDate[dateKey]) byDate[dateKey] = { sets: [], rawTs: parseSessionDate(sess.date) };
      for (const s of sess.sets) {
        if (s.exercise !== ex) continue;
        byDate[dateKey].sets.push(s);
      }
    }
    const dates = history
      .map(s => normDate(s.date))
      .filter((d, i, a) => a.indexOf(d) === i);
    let best = null;
    return dates.map(date => {
      const entry = byDate[date] || { sets: [], rawTs: 0 };
      if (entry.sets.length === 0) return null;
      let dayBest = null;
      for (const s of entry.sets) {
        let v;
        if (def.type === "weighted" && isBwExercise(ex)) {
          const bw = bodyWeightAt(bodyWeights, s.createdAt);
          if (bw == null) continue;
          v = estAddedWeight1RM(ex, s.weight, s.reps, bw);
        } else {
          v = def.type === "weighted"
            ? (s.reps === 1 ? s.weight : Math.round(s.weight * (1 + s.reps / 30)))
            : def.type === "duration" ? s.durationSeconds : s.reps;
        }
        if (dayBest === null || v > dayBest) dayBest = v;
      }
      if (dayBest === null) return null;
      // Plot this session's best directly — allows chart to show regression,
      // not just the running all-time max. All-time PR is unchanged elsewhere.
      return { date, rawTs: entry.rawTs, value: dayBest };
    }).filter(Boolean);
  };

  const chartData = useMemo(() => {
    const raw = chartView === "volume"
      ? buildVolumeData(exercise)
      : buildPrData(exercise, chartExDef);

    const cutoffDays = { "1m": 30, "3m": 91, "1y": 365 }[chartPeriod];
    const cutoffTs = cutoffDays ? Date.now() - cutoffDays * 24 * 60 * 60 * 1000 : 0;

    return raw
      .filter(d => d.rawTs >= cutoffTs)
      .map(d => ({ ...d, ts: d.rawTs }));
  }, [chartView, exercise, chartPeriod, history, bodyWeights]);

  // ── Stat tiles ────────────────────────────────────────────────────────────
  // Count unique days that have at least one set logged
  // (visiting the page creates a session row even with no sets, so we exclude empty sessions)
  const totalSessions = new Set(
    history
      .filter(s => s.sets && s.sets.length > 0)
      .map(s => {
        const d = s.date;
        if (!d) return null;
        if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
        const parsed = new Date(`${d}, ${new Date().getFullYear()}`);
        return isNaN(parsed) ? d : parsed.toISOString().slice(0, 10);
      })
      .filter(Boolean)
  ).size;
  const totalSetsCount = history.reduce((n, s) => n + s.sets.length, 0);
  const prCount        = Object.keys(prs).length;

  // Current PR label for selected exercise
  const currentPr = prs[exercise];
  const prLabel = currentPr != null
    ? exDef.type === "weighted"   ? `${currentPr} lbs est. 1RM`
    : exDef.type === "bodyweight" ? `${currentPr} reps`
    : formatDuration(currentPr)
    : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="page">
      <div className="page-title">WORKOUT <span className="accentText">TRACKER</span></div>
      <div className="page-sub">&ldquo;The Lord is my strength and my praise: and he is become my salvation.&rdquo; &mdash; Psalms 117:14</div>

      {isNewUser && (
        <div className="card" style={{ borderColor:"rgba(255,136,0,0.3)", marginBottom:16 }}>
          <div style={{ fontFamily:"'Orbitron',sans-serif", fontSize:12, letterSpacing:1, color:"#ff8800", fontWeight:700, marginBottom:4 }}>
            ACCOUNT PENDING APPROVAL
          </div>
          <div style={{ fontSize:12, color:"var(--muted)", lineHeight:1.6 }}>
            An admin needs to approve your account before you can access Top Charts, Chat, Meet, or the Rule page.
            You can still log your workouts here in the meantime.
          </div>
        </div>
      )}

      {/* Stat tiles */}
      <div className="stat-grid">
        <div className="stat-tile"><div className="stat-num">{totalSessions}</div><div className="stat-label">Sessions</div></div>
        <div className="stat-tile"><div className="stat-num">{totalSetsCount}</div><div className="stat-label">Total Sets</div></div>
        <div className="stat-tile"><div className="stat-num">{prCount}</div><div className="stat-label">PRs Held</div></div>
      </div>

      {/* ── Today's Session ── */}
      <div className="card">
        <div className="card-title">
          <span style={{fontFamily:"'Orbitron',sans-serif",fontSize:10,color:"var(--accent)",letterSpacing:2,marginRight:8}}>◈</span>
          Today's Session
        </div>

        {/* Exercise group selector */}
        <div className="form-label" style={{marginBottom:6}}>Muscle Group</div>
        <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch",marginBottom:14,paddingBottom:4}}>
          <div className="tab-row" style={{flexWrap:"nowrap",minWidth:"max-content"}}>
            {EXERCISE_GROUPS.map(g => (
              <div key={g} className={`tab ${group===g?"active":""}`}
                onClick={() => {
                  setGroup(g);
                  const first = EXERCISES.find(e => e.group === g);
                  if (first) setExercise(first.name);
                  setWeight(""); setReps(""); setDuration(""); setSetCount("1");
                }}>{g}</div>
            ))}
          </div>
        </div>

        {/* Exercise selector */}
        <div className="form-label" style={{marginBottom:6}}>Exercise</div>
        <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch",marginBottom:14,paddingBottom:4}}>
          <div className="tab-row" style={{flexWrap:"nowrap",minWidth:"max-content"}}>
            {EXERCISES.filter(e => e.group === group).map(e => (
              <div key={e.name} className={`tab ${exercise===e.name?"active":""}`}
                onClick={() => { setExercise(e.name); setWeight(""); setReps(""); setDuration(""); setSetCount("1"); }}>
                {e.name}
              </div>
            ))}
          </div>
        </div>

        {/* Current PR hint */}
        {prLabel && (
          <div style={{fontSize:12,color:"var(--muted)",marginBottom:10}}>
            Your PR: <span style={{color:"var(--accent)",fontWeight:700}}>{prLabel}</span>
          </div>
        )}

        {/* New PR flash */}
        {newPrEx && (
          <div style={{background:"rgba(136,255,0,0.1)",border:"1px solid rgba(136,255,0,0.4)",borderRadius:4,
            padding:"8px 14px",marginBottom:12,fontSize:13,fontWeight:700,color:"#88ff00",
            fontFamily:"'Orbitron',sans-serif",letterSpacing:1,display:"flex",alignItems:"center",gap:8}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 3H18V13C18 16.3137 15.3137 19 12 19C8.68629 19 6 16.3137 6 13V3Z" stroke="#88ff00" strokeWidth="1.8" strokeLinejoin="round"/>
              <path d="M6 6H3C3 6 2 11 6 11" stroke="#88ff00" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M18 6H21C21 6 22 11 18 11" stroke="#88ff00" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 19V22" stroke="#88ff00" strokeWidth="1.8" strokeLinecap="round"/>
              <path d="M8 22H16" stroke="#88ff00" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            NEW PR — {newPrEx}
          </div>
        )}

        {/* Input fields — grid layout keeps everything on the same baseline */}
        <div style={{
          display:"grid",
          gridTemplateColumns: exDef.type === "weighted" ? "1fr 1fr 1fr auto" : "1fr 1fr auto",
          gap:8, alignItems:"end", marginBottom:12,
        }}>
          {exDef.type === "weighted" && (
            <div>
              <div className="form-label" style={{marginBottom:4}}>{needsBw ? "Added Weight (lbs)" : "Weight (lbs)"}</div>
              <input type="number" value={weight} onChange={e => setWeight(e.target.value)}
                placeholder={needsBw ? "0" : "225"} style={{width:"100%",boxSizing:"border-box"}}
                onKeyDown={e => e.key==="Enter" && addSet()} />
            </div>
          )}
          {exDef.type !== "duration" && (
            <div>
              <div className="form-label" style={{marginBottom:4}}>Reps</div>
              <input type="number" value={reps} onChange={e => setReps(e.target.value)}
                placeholder={exDef.type === "weighted" ? "5" : "10"} style={{width:"100%",boxSizing:"border-box"}}
                onKeyDown={e => e.key==="Enter" && addSet()} />
            </div>
          )}
          {exDef.type === "duration" && (
            <div>
              <div className="form-label" style={{marginBottom:4}}>Duration (sec)</div>
              <input type="number" value={duration} onChange={e => setDuration(e.target.value)}
                placeholder="60" style={{width:"100%",boxSizing:"border-box"}}
                onKeyDown={e => e.key==="Enter" && addSet()} />
            </div>
          )}
          <div>
            <div className="form-label" style={{marginBottom:4}}>Sets</div>
            <input type="number" value={setCount} onChange={e => setSetCount(e.target.value)}
              min="1" max="50" placeholder="1" style={{width:"100%",boxSizing:"border-box"}}
              onKeyDown={e => e.key==="Enter" && addSet()} />
          </div>
          <button className="btn btn-primary" onClick={addSet} disabled={adding}
            style={{height:40,padding:"0 16px",fontSize:13,whiteSpace:"nowrap"}}>
            {adding ? "…" : "Log"}
          </button>
        </div>

        {/* Recent sets — last 4 logged, always deletable */}
        {(() => {
          const allSets = history
            .flatMap(sess => sess.sets.map(s => ({ ...s, sessionDate: sess.date })))
            .filter(s => !s.migrated)
            .sort((a, b) => b.createdAt - a.createdAt)
            .slice(0, 4);
          if (allSets.length === 0 && !loading) return (
            <div style={{color:"var(--muted)",fontSize:13,padding:"12px 0"}}>
              No sets logged yet. Start above!
            </div>
          );
          return allSets.length > 0 ? (
            <div style={{marginTop:16}}>
              <div className="form-label" style={{marginBottom:8}}>Recent Sets</div>
              {allSets.map((s, i) => (
                <div key={s.id} style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",padding:"6px 0",
                  borderBottom:"1px solid rgba(136,255,0,0.05)"}}>
                  <span style={{fontSize:12,color:"var(--muted)",flex:1}}>{s.exercise}</span>
                  <span style={{fontSize:12,color:"var(--muted)",marginRight:12}}>{s.sessionDate}</span>
                  <span style={{fontSize:13,fontWeight:600,marginRight:8}}>
                    {s.setType === "weighted"   && `${s.weight} lbs × ${s.reps}`}
                    {s.setType === "bodyweight" && `${s.reps} reps`}
                    {s.setType === "duration"   && formatDuration(s.durationSeconds)}
                  </span>
                  <button onClick={() => deleteSet(s.id)}
                    style={{background:"none",border:"none",color:"rgba(255,68,85,0.6)",
                      cursor:"pointer",fontSize:16,padding:"4px 10px",
                      minWidth:36,minHeight:36,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                </div>
              ))}
            </div>
          ) : null;
        })()}
      </div>

      {/* ── Progress Charts ── */}
      <div className="card" style={{position:"relative"}}>
        <div className="card-title">
          <span style={{fontFamily:"'Orbitron',sans-serif",fontSize:10,color:"var(--accent)",letterSpacing:2,marginRight:8}}>▲▲▲</span>
          Progress
        </div>

        {/* All-time PR badge — top-right corner, only on PR Trend view */}
        {chartView === "pr" && currentPr != null && (
          <div className="pr-badge" style={{
            position:"absolute", top:14, right:16,
            display:"inline-flex", alignItems:"center", gap:5,
            border:"1px solid rgba(136,255,0,0.35)", borderRadius:4,
            padding:"3px 8px", background:"rgba(136,255,0,0.05)",
            fontFamily:"'Orbitron',sans-serif", pointerEvents:"none",
          }}>
            <svg width="8" height="10" viewBox="0 0 9 11" style={{flexShrink:0}}>
              <polygon points="4.5,0 9,11 0,11" fill="none" stroke="rgba(136,255,0,0.6)" strokeWidth="1.2"/>
              <polygon points="4.5,2.5 7.5,9.5 1.5,9.5" fill="rgba(136,255,0,0.2)"/>
            </svg>
            <span className="pr-badge-label" style={{fontSize:8,letterSpacing:1.5,color:"var(--muted)"}}>ALL-TIME</span>
            <span className="pr-badge-value" style={{fontSize:10,fontWeight:700,color:"var(--accent)",letterSpacing:1}}>{prLabel}</span>
          </div>
        )}

        {awaitingBw ? (
          <div style={{textAlign:"center",padding:"28px 12px"}}>
            <div style={{fontFamily:"'Orbitron',sans-serif",fontSize:11,letterSpacing:2,color:"var(--accent)",marginBottom:10}}>
              BODY WEIGHT NEEDED
            </div>
            <div style={{fontSize:13,color:"var(--muted)",lineHeight:1.6,maxWidth:420,margin:"0 auto 16px"}}>
              {exercise} moves part of your own body weight, so we need it to estimate your strength and track progress.
              Log your body weight to see your progress here. Your sets are still being saved.
            </div>
            <BodyWeightQuickEntry onLog={logBodyWeight} />
          </div>
        ) : (
          <>
        {/* Chart type toggle */}
        <div style={{display:"flex",gap:8,marginBottom:14}}>
          {[["volume","Volume"],["pr","PR Trend"]].map(([v,l]) => (
            <button key={v} onClick={() => setChartView(v)}
              style={{padding:"6px 14px",border:`1px solid ${chartView===v?"var(--accent)":"var(--border)"}`,
                background: chartView===v ? "rgba(136,255,0,0.08)" : "var(--surface2)",
                color: chartView===v ? "var(--accent)" : "var(--muted)",
                borderRadius:4,cursor:"pointer",fontSize:12,fontFamily:"'Orbitron',sans-serif",
                letterSpacing:1}}>
              {l}
            </button>
          ))}
        </div>

        {/* Period filter */}
        <div style={{display:"flex",gap:6,marginBottom:14}}>
          {[["1m","1M"],["3m","3M"],["1y","1Y"],["all","All"]].map(([v,l]) => (
            <button key={v} onClick={() => setChartPeriod(v)}
              style={{padding:"5px 12px",border:`1px solid ${chartPeriod===v?"var(--accent)":"var(--border)"}`,
                background: chartPeriod===v ? "rgba(136,255,0,0.08)" : "var(--surface2)",
                color: chartPeriod===v ? "var(--accent)" : "var(--muted)",
                borderRadius:4,cursor:"pointer",fontSize:11,fontFamily:"'Orbitron',sans-serif",
                letterSpacing:1}}>
              {l}
            </button>
          ))}
        </div>

        {chartData.length > 1 ? (
          <div style={{height:220}}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{top:4,right:8,bottom:36,left:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a32" />
                <XAxis
                  dataKey="ts" type="number" scale="time"
                  domain={["dataMin","dataMax"]}
                  stroke="#7a7a8a" tick={{fontSize:9}} angle={-30} textAnchor="end" height={36}
                  tickCount={Math.min(chartData.length, 7)}
                  tickFormatter={ts => {
                    const d = new Date(ts);
                    return `${d.getMonth()+1}/${d.getDate()}`;
                  }}
                />
                <YAxis stroke="#7a7a8a" tick={{fontSize:10}} width={42}
                  tickFormatter={v => chartExDef.type === "duration" ? formatDuration(v) : v} />
                <Tooltip
                  contentStyle={{background:"#141417",border:"1px solid #2a2a32",borderRadius:8,fontSize:11}}
                  labelFormatter={ts => new Date(ts).toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"})}
                  formatter={v => [chartExDef.type === "duration" ? formatDuration(v)
                    : chartView === "volume" && chartExDef.type === "weighted" ? `${v} lbs`
                    : chartView === "volume" && chartExDef.type === "bodyweight" ? `${v} reps`
                    : `${v}`, chartView === "volume" ? "Volume" : "Est. PR"]} />
                <Line type="monotone" dataKey="value" stroke="var(--accent)" strokeWidth={2.5}
                  dot={{fill:"var(--accent)",r:3}} activeDot={{r:5}} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{textAlign:"center",padding:"32px 0",color:"var(--muted)",fontSize:13}}>
            {chartData.length === 1 ? "Log one more session to see your trend." : `Log some ${exercise} sets to see your progress.`}
          </div>
        )}
          </>
        )}
      </div>

      {/* ── Body weight (also feeds the weighted pull-up / dip / back extension estimates) ── */}
      <BodyWeightPanel entries={loading ? null : bodyWeights} onLog={logBodyWeight} onDelete={removeBodyWeight} />
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return "0s";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}


// ─── PERMANENT TRACK LIST ─────────────────────────────────────────────────────
// Audio files are served from /public/audio/.
// To add a track: drop the .mp3 into public/audio/ and add an entry here.
const PERMANENT_TRACKS = [
  { id: 1,  title: "Genesis",               artist: "Holy Bible", src: "/audio/Genesis.mp3" },
  { id: 2,  title: "Exodus",                artist: "Holy Bible", src: "/audio/Exodus.mp3" },
  { id: 3,  title: "Leviticus",             artist: "Holy Bible", src: "/audio/Leviticus.mp3" },
  { id: 4,  title: "Numbers",               artist: "Holy Bible", src: "/audio/Numbers.mp3" },
  { id: 5,  title: "Deuteronomy",           artist: "Holy Bible", src: "/audio/Deuteronomy.mp3" },
  { id: 6,  title: "Josue",                 artist: "Holy Bible", src: "/audio/Josue.mp3" },
  { id: 7,  title: "Judges",                artist: "Holy Bible", src: "/audio/Judges.mp3" },
  { id: 8,  title: "Ruth",                  artist: "Holy Bible", src: "/audio/Ruth.mp3" },
  { id: 9,  title: "I Kings",               artist: "Holy Bible", src: "/audio/I-Kings.mp3" },
  { id: 10, title: "II Kings",              artist: "Holy Bible", src: "/audio/II-Kings.mp3" },
  { id: 11, title: "III Kings",             artist: "Holy Bible", src: "/audio/III-Kings.mp3" },
  { id: 12, title: "IV Kings",              artist: "Holy Bible", src: "/audio/IV-Kings.mp3" },
  { id: 13, title: "I Paralipomenon",       artist: "Holy Bible", src: "/audio/I-Paralipomenon.mp3" },
  { id: 14, title: "II Paralipomenon",      artist: "Holy Bible", src: "/audio/II-Paralipomenon.mp3" },
  { id: 15, title: "I Esdras",              artist: "Holy Bible", src: "/audio/I-Esdras.mp3" },
  { id: 16, title: "II Esdras",             artist: "Holy Bible", src: "/audio/II-Esdras.mp3" },
  { id: 17, title: "Tobias",                artist: "Holy Bible", src: "/audio/Tobias.mp3" },
  { id: 18, title: "Judith",                artist: "Holy Bible", src: "/audio/Judith.mp3" },
  { id: 19, title: "Esther",                artist: "Holy Bible", src: "/audio/Esther.mp3" },
  { id: 20, title: "Job",                   artist: "Holy Bible", src: "/audio/Job.mp3" },
  { id: 21, title: "Psalms",                artist: "Holy Bible", src: "/audio/Psalms.mp3" },
  { id: 22, title: "Proverbs",              artist: "Holy Bible", src: "/audio/Proverbs.mp3" },
  { id: 23, title: "Ecclesiastes",          artist: "Holy Bible", src: "/audio/Ecclesiastes.mp3" },
  { id: 24, title: "Canticle of Canticles", artist: "Holy Bible", src: "/audio/Canticle-of-Canticles.mp3" },
  { id: 25, title: "Wisdom",                artist: "Holy Bible", src: "/audio/Wisdom.mp3" },
  { id: 26, title: "Ecclesiasticus",        artist: "Holy Bible", src: "/audio/Ecclesiasticus.mp3" },
  { id: 27, title: "Isaias",                artist: "Holy Bible", src: "/audio/Isaias.mp3" },
  { id: 28, title: "Jeremias",              artist: "Holy Bible", src: "/audio/Jeremias.mp3" },
  { id: 29, title: "Lamentations",          artist: "Holy Bible", src: "/audio/Lamentations.mp3" },
  { id: 30, title: "Baruch",                artist: "Holy Bible", src: "/audio/Baruch.mp3" },
  { id: 31, title: "Ezekiel",               artist: "Holy Bible", src: "/audio/Ezekiel.mp3" },
  { id: 32, title: "Daniel",                artist: "Holy Bible", src: "/audio/Daniel.mp3" },
  { id: 33, title: "Osee",                  artist: "Holy Bible", src: "/audio/Osee.mp3" },
  { id: 34, title: "Joel",                  artist: "Holy Bible", src: "/audio/Joel.mp3" },
  { id: 35, title: "Amos",                  artist: "Holy Bible", src: "/audio/Amos.mp3" },
  { id: 36, title: "Abdias",                artist: "Holy Bible", src: "/audio/Abdias.mp3" },
  { id: 37, title: "Jonas",                 artist: "Holy Bible", src: "/audio/Jonas.mp3" },
  { id: 38, title: "Micheas",               artist: "Holy Bible", src: "/audio/Micheas.mp3" },
  { id: 39, title: "Nahum",                 artist: "Holy Bible", src: "/audio/Nahum.mp3" },
  { id: 40, title: "Habacuc",               artist: "Holy Bible", src: "/audio/Habacuc.mp3" },
  { id: 41, title: "Sophonias",             artist: "Holy Bible", src: "/audio/Sophonias.mp3" },
  { id: 42, title: "Aggeus",                artist: "Holy Bible", src: "/audio/Aggeus.mp3" },
  { id: 43, title: "Zacharias",             artist: "Holy Bible", src: "/audio/Zacharias.mp3" },
  { id: 44, title: "Malachias",             artist: "Holy Bible", src: "/audio/Malachias.mp3" },
  { id: 45, title: "I Machabees",           artist: "Holy Bible", src: "/audio/I-Machabees.mp3" },
  { id: 46, title: "II Machabees",          artist: "Holy Bible", src: "/audio/II-Machabees.mp3" },
  { id: 47, title: "Matthew",               artist: "Holy Bible", src: "/audio/Matthew.mp3" },
  { id: 48, title: "Mark",                  artist: "Holy Bible", src: "/audio/Mark.mp3" },
  { id: 49, title: "Luke",                  artist: "Holy Bible", src: "/audio/Luke.mp3" },
  { id: 50, title: "John",                  artist: "Holy Bible", src: "/audio/John.mp3" },
  { id: 51, title: "Acts",                  artist: "Holy Bible", src: "/audio/Acts.mp3" },
  { id: 52, title: "Romans",                artist: "Holy Bible", src: "/audio/Romans.mp3" },
  { id: 53, title: "I Corinthians",         artist: "Holy Bible", src: "/audio/I-Corinthians.mp3" },
  { id: 54, title: "II Corinthians",        artist: "Holy Bible", src: "/audio/II-Corinthians.mp3" },
  { id: 55, title: "Galatians",             artist: "Holy Bible", src: "/audio/Galatians.mp3" },
  { id: 56, title: "Ephesians",             artist: "Holy Bible", src: "/audio/Ephesians.mp3" },
  { id: 57, title: "Philippians",           artist: "Holy Bible", src: "/audio/Philippians.mp3" },
  { id: 58, title: "Colossians",            artist: "Holy Bible", src: "/audio/Colossians.mp3" },
  { id: 59, title: "I Thessalonians",       artist: "Holy Bible", src: "/audio/I-Thessalonians.mp3" },
  { id: 60, title: "II Thessalonians",      artist: "Holy Bible", src: "/audio/II-Thessalonians.mp3" },
  { id: 61, title: "I Timothy",             artist: "Holy Bible", src: "/audio/I-Timothy.mp3" },
  { id: 62, title: "II Timothy",            artist: "Holy Bible", src: "/audio/II-Timothy.mp3" },
  { id: 63, title: "Titus",                 artist: "Holy Bible", src: "/audio/Titus.mp3" },
  { id: 64, title: "Philemon",              artist: "Holy Bible", src: "/audio/Philemon.mp3" },
  { id: 65, title: "Hebrews",               artist: "Holy Bible", src: "/audio/Hebrews.mp3" },
  { id: 66, title: "James",                 artist: "Holy Bible", src: "/audio/James.mp3" },
  { id: 67, title: "I Peter",               artist: "Holy Bible", src: "/audio/I-Peter.mp3" },
  { id: 68, title: "II Peter",              artist: "Holy Bible", src: "/audio/II-Peter.mp3" },
  { id: 69, title: "I John",                artist: "Holy Bible", src: "/audio/I-John.mp3" },
  { id: 70, title: "II John",               artist: "Holy Bible", src: "/audio/II-John.mp3" },
  { id: 71, title: "III John",              artist: "Holy Bible", src: "/audio/III-John.mp3" },
  { id: 72, title: "Jude",                  artist: "Holy Bible", src: "/audio/Jude.mp3" },
  { id: 73, title: "Apocalypse",            artist: "Holy Bible", src: "/audio/Apocalypse.mp3" },
];

// ─── AUDIO PAGE ───────────────────────────────────────────────────────────────
function AudioPage({ currentTrack, setCurrentTrack, isPlaying, setIsPlaying }) {
  const tracks = PERMANENT_TRACKS;

  const play = (track) => {
    if (currentTrack?.id === track.id) setIsPlaying(!isPlaying);
    else { setCurrentTrack(track); setIsPlaying(true); }
  };

  return (
    <div className="page" style={{position:"relative"}}>
      <div className="page-title">HOLY <span className="accentText">BIBLE</span></div>
      <div className="page-sub">&ldquo;Ignorance of Scripture is ignorance of Christ.&rdquo; &mdash; St. Jerome</div>

      <div className="bible-grid" style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:20}}>

        <div className="card" style={{background:"linear-gradient(160deg,rgba(0,22,13,0.55) 0%,rgba(0,7,4,0.35) 50%,rgba(0,3,2,0.42) 100%)"}}>
          <div style={{marginBottom:12}}>
            <div className="card-title" style={{marginBottom:0}}>Old Testament</div>
          </div>
          {tracks.filter(t => t.id <= 46).map((t, i) => {
            const active = currentTrack?.id === t.id;
            return (
              <div key={t.id} className={`track-row ${active ? "playing" : ""}`}
                style={{display:"flex", alignItems:"center", cursor:"pointer"}}
                onClick={() => play(t)}>
                <div className="track-num" style={{color: active && isPlaying ? "var(--accent)" : "var(--muted)"}}>
                  {active && isPlaying
                    ? <svg width="13" height="13" viewBox="0 0 13 13" style={{display:"block",filter:"drop-shadow(0 0 4px #00ff99)"}}>
                        <polygon points="2,1 12,6.5 2,12" fill="#00ff99"/>
                      </svg>
                    : i + 1}
                </div>
                <div style={{flex:1}}>
                  <div className="track-title" style={{fontSize:14, fontWeight:600}}>{t.title}</div>
                </div>
                {active && (
                  <div style={{fontSize:11, color:"var(--accent)", fontWeight:600, marginRight:8}}>
                    {isPlaying ? "NOW PLAYING" : "PAUSED"}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="card" style={{background:"linear-gradient(160deg,rgba(0,22,13,0.55) 0%,rgba(0,7,4,0.35) 50%,rgba(0,3,2,0.42) 100%)"}}>
          <div style={{marginBottom:12}}>
            <div className="card-title" style={{marginBottom:0}}>New Testament</div>
          </div>
          {tracks.filter(t => t.id >= 47).map((t, i) => {
            const active = currentTrack?.id === t.id;
            return (
              <div key={t.id} className={`track-row ${active ? "playing" : ""}`}
                style={{display:"flex", alignItems:"center", cursor:"pointer"}}
                onClick={() => play(t)}>
                <div className="track-num" style={{color: active && isPlaying ? "var(--accent)" : "var(--muted)"}}>
                  {active && isPlaying
                    ? <svg width="13" height="13" viewBox="0 0 13 13" style={{display:"block",filter:"drop-shadow(0 0 4px #00ff99)"}}>
                        <polygon points="2,1 12,6.5 2,12" fill="#00ff99"/>
                      </svg>
                    : i + 47}
                </div>
                <div style={{flex:1}}>
                  <div className="track-title" style={{fontSize:14, fontWeight:600}}>{t.title}</div>
                </div>
                {active && (
                  <div style={{fontSize:11, color:"var(--accent)", fontWeight:600, marginRight:8}}>
                    {isPlaying ? "NOW PLAYING" : "PAUSED"}
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </div>

      {/* Attribution footnote */}
      <div style={{
        marginTop:32, paddingTop:16, borderTop:"1px solid rgba(136,255,0,0.07)",
        color:"var(--muted)", fontSize:10, lineHeight:1.7, letterSpacing:0.3,
      }}>
        <div style={{marginBottom:6, fontFamily:"'Orbitron',sans-serif", fontSize:9, letterSpacing:2, textTransform:"uppercase"}}>
          Attributions
        </div>
        <div><span style={{color:"var(--text)"}}>Audio recordings:</span>{" "}
          <a href="https://www.youtube.com/@TraditionalCatholicAudiobooks" target="_blank" rel="noreferrer"
            style={{color:"var(--muted)",textDecoration:"underline"}}>Traditional Catholic Audiobooks</a>
          {" · "}
          <a href="https://www.youtube.com/@MultiBurtons" target="_blank" rel="noreferrer"
            style={{color:"var(--muted)",textDecoration:"underline"}}>MultiBurtons</a>
          {" · "}
          <a href="https://www.youtube.com/@alfredus_magnus" target="_blank" rel="noreferrer"
            style={{color:"var(--muted)",textDecoration:"underline"}}>alfredus magnus</a>
        </div>
        <div><span style={{color:"var(--text)"}}>3D models:</span>{" "}
          <a href="https://free3d.com/3d-model/male-base-mesh-arshlevon-sizes-22492.html" target="_blank" rel="noreferrer"
            style={{color:"var(--muted)",textDecoration:"underline"}}>Male Base Mesh by arshlevon — free3d.com</a>
        </div>
      </div>

    </div>
  );
}
function PlayerBar({ track, isPlaying, setIsPlaying, tracks, setTrack, navExpanded }) {
  const audioRef     = useRef(null);
  const scrubbing    = useRef(false);
  const [elapsed, setElapsed]   = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume]     = useState(1);
  const [speed, setSpeed]       = useState(1);
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  // Load track + wire events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setElapsed(0);
    setDuration(0);
    if (!track?.src) { audio.src = ""; return; }

    // Restore saved position after metadata loads
    const onMeta = async () => {
      setDuration(isFinite(audio.duration) ? audio.duration : 0);
      try {
        const saved = await store.get(`progress:${track.id}`);
        if (saved && parseFloat(saved) > 0 && parseFloat(saved) < audio.duration - 5) {
          audio.currentTime = parseFloat(saved);
        }
      } catch {}
    };

    const onCanPlay = () => { if (isPlayingRef.current) audio.play().catch(() => setIsPlaying(false)); };

    // Save position every 5 seconds while playing
    const onTime = () => {
      if (!scrubbing.current) {
        setElapsed(audio.currentTime);
        if (Math.round(audio.currentTime) % 5 === 0 && audio.currentTime > 0) {
          store.set(`progress:${track.id}`, String(audio.currentTime));
        }
      }
    };

    const onEnded = () => {
      // Clear saved position when track finishes
      store.set(`progress:${track.id}`, "0");
      if (!tracks.length) return;
      const idx  = tracks.findIndex(t => t.id === track.id);
      const next = tracks[(idx + 1) % tracks.length];
      setTrack(next); setIsPlaying(true);
    };

    audio.src = track.src;
    audio.load();
    audio.addEventListener("canplay",        onCanPlay);
    audio.addEventListener("timeupdate",     onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended",          onEnded);
    return () => {
      // Save position when unmounting / switching tracks
      if (audio.currentTime > 0) {
        store.set(`progress:${track.id}`, String(audio.currentTime));
      }
      audio.removeEventListener("canplay",        onCanPlay);
      audio.removeEventListener("timeupdate",     onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended",          onEnded);
    };
  }, [track]);

  // Play/pause
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !track?.src) return;
    if (isPlaying) audio.play().catch(() => setIsPlaying(false));
    else           audio.pause();
  }, [isPlaying]); // eslint-disable-line react-hooks/exhaustive-deps

  // Volume
  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume]);

  // Playback speed
  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = speed; }, [speed]);

  // Skip to track prev/next
  const skipTrack = (dir) => {
    if (!tracks.length) return;
    const idx = tracks.findIndex(t => t.id === track?.id);
    setTrack(tracks[(idx + dir + tracks.length) % tracks.length]);
    setIsPlaying(true);
  };

  // Skip ±15 seconds within current track
  const nudge = (secs) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + secs));
    setElapsed(audio.currentTime);
  };

  // Progress bar: click to seek + drag to scrub (mouse + touch)
  const barRef = useRef(null);
  const durationRef = useRef(duration);
  useEffect(() => { durationRef.current = duration; }, [duration]);
  const seekTo = (clientX) => {
    const audio = audioRef.current;
    const dur = durationRef.current;
    if (!audio || !dur || !barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const pct  = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const t    = pct * dur;
    audio.currentTime = t;
    setElapsed(t);
  };
  const onBarMouseDown = (e) => {
    scrubbing.current = true;
    seekTo(e.clientX);
    const onMove = (ev) => seekTo(ev.clientX);
    const onUp   = ()   => { scrubbing.current = false; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
  };
  // Native touch listeners on the bar — passive:false on touchstart claims the
  // gesture immediately so iOS cannot reassign it to page scroll
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const onTouchStart = (e) => {
      e.preventDefault();
      scrubbing.current = true;
      seekTo(e.touches[0].clientX);
    };
    const onTouchMove = (e) => {
      if (!scrubbing.current) return;
      e.preventDefault();
      seekTo(e.touches[0].clientX);
    };
    const onTouchEnd = () => { scrubbing.current = false; };
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd,   { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
    };
  // seekTo reads via durationRef so it's stable; re-run only if barRef changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = duration > 0 ? (elapsed / duration) * 100 : 0;
  const noSrc    = !track?.src;
  const SPEEDS   = [1, 1.25, 1.5, 1.75, 2];

  return (
    <div className={`player-bar${navExpanded ? "" : " retracted"}`}>
      <audio ref={audioRef} preload="auto" />

      {/* Track info */}
      <div className="track-info" style={{width:"100%"}}>
        <div className="track-title">{track?.title ?? "—"}</div>
        <div className="track-artist" style={{color: noSrc ? "var(--danger)" : undefined}}>
          {noSrc ? "No file linked" : track?.artist}
        </div>
      </div>

      {/* Progress + controls + speed — all aligned to timestamp bounds */}
      <div style={{display:"flex", flexDirection:"column", gap:6, position:"relative", zIndex:1, width:"100%"}}>

        {/* Progress row */}
        <div className="progress-wrap">
          <span style={{width:36, flexShrink:0, textAlign:"right"}}>{fmtTime(elapsed)}</span>
          <div ref={barRef} className="progress-bar"
            onMouseDown={onBarMouseDown}
            style={{cursor: noSrc ? "default" : "pointer", userSelect:"none"}}>
            <div className="progress-fill" style={{width:`${progress}%`, transition: scrubbing.current ? "none" : "width 0.25s linear"}} />
            {!noSrc && (
              <div style={{
                position:"absolute", top:"50%", left:`${progress}%`,
                transform:"translate(-50%,-50%)",
                width:16, height:16, borderRadius:"50%",
                background:"#00ff99", boxShadow:"0 0 8px #00ff99, 0 0 16px rgba(0,255,140,0.6)",
                pointerEvents:"none",
              }} />
            )}
          </div>
          <span style={{width:36, flexShrink:0, textAlign:"left"}}>{fmtTime(duration)}</span>
        </div>

        {/* Controls — bounded by timestamp widths */}
        <div style={{display:"flex", alignItems:"center", paddingLeft:44, paddingRight:44}}>
          <div className="player-controls" style={{flex:1, justifyContent:"space-between"}}>
            <button className="ctrl-btn" onClick={() => skipTrack(-1)} disabled={noSrc} title="Previous">{"⏮\uFE0E"}</button>
            <button className="ctrl-btn" onClick={() => nudge(-15)} disabled={noSrc} title="−15s"
              style={{fontSize:9, fontFamily:"'Orbitron',sans-serif", letterSpacing:0}}>−15</button>
            <button className="play-btn" onClick={() => setIsPlaying(!isPlaying)} disabled={noSrc}>
              {isPlaying
                ? <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="2" width="4" height="14" rx="1.5" fill="#00ff99"/>
                    <rect x="11" y="2" width="4" height="14" rx="1.5" fill="#00ff99"/>
                  </svg>
                : <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polygon points="4,2 16,9 4,16" fill="#00ff99"/>
                  </svg>
              }
            </button>
            <button className="ctrl-btn" onClick={() => nudge(15)} disabled={noSrc} title="+15s"
              style={{fontSize:9, fontFamily:"'Orbitron',sans-serif", letterSpacing:0}}>+15</button>
            <button className="ctrl-btn" onClick={() => skipTrack(1)} disabled={noSrc} title="Next">{"⏭\uFE0E"}</button>
          </div>
        </div>

        {/* Speed selector — bounded by timestamp widths */}
        <div style={{display:"flex", alignItems:"center", paddingLeft:44, paddingRight:44, pointerEvents:"all"}}>
          <div style={{flex:1, display:"flex", justifyContent:"space-between"}}>
            {SPEEDS.map(s => (
              <button key={s} onClick={() => setSpeed(s)} disabled={noSrc}
                style={{
                  background: "none",
                  color:       speed === s ? "#88ff00" : "rgba(0,255,140,0.35)",
                  border:      speed === s ? "1px solid rgba(136,255,0,0.6)" : "1px solid rgba(0,255,140,0.15)",
                  borderRadius: 999, padding:"3px 8px",
                  fontSize:9, fontFamily:"'Orbitron',sans-serif", fontWeight:700,
                  cursor: noSrc ? "default" : "pointer", letterSpacing:0.5,
                  transition:"all 0.12s",
                  filter: speed === s
                    ? "drop-shadow(0 0 6px rgba(136,255,0,0.8)) drop-shadow(0 0 14px rgba(136,255,0,0.4))"
                    : "drop-shadow(0 0 3px rgba(0,255,140,0.3))",
                }}>
                {s}×
              </button>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── TOP CHARTS PAGE ──────────────────────────────────────────────────────────
function TopChartsPage({ username, currentUser, mobileScreen, onHasChapter, onGoToWorkout }) {
  const [chartEx, setChartEx]           = useState("Bench Press");
  const [chartGroup, setChartGroup]     = useState("Chest");
  const [chartsTabInternal, setChartsTabInternal] = useState("global");
  const isMobileTC = useIsMobile();
  const chartsTab = (isMobileTC && mobileScreen !== null)
    ? (mobileScreen === "chapter" ? "chapter" : "global")
    : chartsTabInternal;
  const setChartsTab = (val) => { if (!isMobileTC) setChartsTabInternal(val); };
  const [membership, setMembership]     = useState(null);
  const [chapterCommunityPrs, setChapterCommunityPrs] = useState([]);
  const [chapterUsersLoading, setChapterUsersLoading] = useState(false);

  const { communityPrs, myPrs }         = useCommunityPrs(username);
  // Body-weight-adjusted exercises (e.g. Weighted Pull-up) can't be ranked until the user logs a body weight
  const [hasBodyWeight, setHasBodyWeight] = useState(true); // assume yes until loaded, avoids a flash of the prompt
  useEffect(() => {
    api.getBodyWeights().then(d => setHasBodyWeight((d.entries || []).length > 0)).catch(() => {});
  }, [username]);
  const needsBodyWeight = isBwExercise(chartEx) && !hasBodyWeight;
  const chartExDef = EXERCISE_MAP[chartEx] || EXERCISES[0];
  const isBodyweightChart = chartExDef.type === "bodyweight";
  const isDurationChart   = chartExDef.type === "duration";

  useEffect(() => {
    api.getMyMembership().then(m => {
      if (m && m.status === "approved") {
        setMembership(m);
        if (onHasChapter) onHasChapter(true);
      }
    }).catch(() => {});
  }, [username]);

  useEffect(() => {
    if (!membership) return;
    setChapterUsersLoading(true);
    api.getChapterCommunityUsers(membership.chapter_id)
      .then(setChapterCommunityPrs)
      .catch(() => {})
      .finally(() => setChapterUsersLoading(false));
  }, [membership]);

  usePolling(() => {
    if (!membership) return;
    api.getChapterCommunityUsers(membership.chapter_id).then(setChapterCommunityPrs).catch(() => {});
  }, 45000);

  // Unified leaderboard builder using PR data
  const buildLeaderboard = (exercise, communityData, myPrMap) => {
    const entries = [];
    const myVal = myPrMap?.[exercise];
    if (myVal != null && myVal > 0) entries.push({ name: username, weight: myVal, isMe: true, avatarUrl: currentUser?.avatarUrl || null });
    (communityData || []).forEach(u => {
      const v = u.prs?.[exercise];
      if (v != null && v > 0) entries.push({ name: u.name, weight: v, isMe: false, avatarUrl: u.avatarUrl || null });
    });
    return entries.sort((a, b) => b.weight - a.weight);
  };

  const leaders              = buildLeaderboard(chartEx, communityPrs, myPrs);
  const topWeight            = leaders[0]?.weight || 0;
  const chapterLeaders       = buildLeaderboard(chartEx, chapterCommunityPrs, myPrs);
  const chapterTopWeight     = chapterLeaders[0]?.weight || 0;

  // Snapshot table builders (for "All Exercises" card)
  const buildBodyweightLeaderboard      = (ex) => buildLeaderboard(ex, communityPrs, myPrs);
  const buildChapterLeaderboard         = (ex) => buildLeaderboard(ex, chapterCommunityPrs, myPrs);
  const buildChapterBodyweightLeaderboard = buildChapterLeaderboard;

  const medalColors = ["#ffdd00", "#c0c8d4", "#ff9944"];
  const medalLabels = ["#1", "#2", "#3"];

  const isMobile = useIsMobile();

  return (
    <div className="page" style={{position:"relative"}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:4}}>
        <div className="page-title" style={{marginBottom:0}}>TOP <span className="accentText">CHARTS</span></div>
        {membership && !isMobile && (
          <div style={{display:"flex",borderBottom:"1px solid rgba(136,255,0,0.12)",alignSelf:"flex-end"}}>
            {["global","chapter"].map(tab => (
              <button key={tab} onClick={()=>setChartsTab(tab)}
                style={{padding:"8px 18px",border:"none",background:"none",cursor:"pointer",
                  fontFamily:"'Orbitron',sans-serif",fontSize:9,letterSpacing:2,textTransform:"uppercase",
                  color: chartsTab===tab ? "#88ff00" : "var(--muted)",
                  borderBottom: chartsTab===tab ? "2px solid #88ff00" : "2px solid transparent",
                  transition:"all 0.2s"}}>
                {tab === "global" ? "Global" : membership.chapter_name}
              </button>
            ))}
          </div>
        )}
        {membership && isMobile && (
          <div className="tab-pill">
            {["global","chapter"].map(tab => (
              <div key={tab} style={{
                width: chartsTab===tab ? 18 : 6, height:6, borderRadius:3, flexShrink:0,
                background: chartsTab===tab ? "#88ff00" : "rgba(136,255,0,0.25)",
                transition:"all 0.25s ease",
              }} />
            ))}
            <PillScrollText label={chartsTab === "global" ? "GLOBAL" : membership.chapter_name.toUpperCase()} />
          </div>
        )}
      </div>
      <div className="page-sub">&ldquo;Non nobis, Domine, non nobis, sed nomini Tuo da gloriam.&rdquo;</div>

      {/* Two-panel carousel — slides between global (0) and chapter (-50%) on mobile */}
      <div style={{overflow: isMobileTC ? "hidden" : "visible"}}>
        <div style={{
          display: isMobileTC && membership ? "flex" : "block",
          width:   isMobileTC && membership ? "200%" : "100%",
          transform: isMobileTC && membership && chartsTab === "chapter" ? "translateX(-50%)" : "translateX(0)",
          transition: isMobileTC && membership ? "transform 0.32s cubic-bezier(0.4,0,0.2,1)" : "none",
          alignItems: "flex-start",
        }}>
          {/* Global panel */}
          <div style={{
            width: isMobileTC && membership ? "50%" : "100%", flexShrink: 0,
            display: !isMobileTC && membership && chartsTab === "chapter" ? "none" : "block",
          }}>
        <div style={{marginBottom:20}}>
          <div className="form-label" style={{marginBottom:6}}>Muscle Group</div>
          <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch",marginBottom:10,paddingBottom:4}}>
            <div className="tab-row" style={{flexWrap:"nowrap",minWidth:"max-content"}}>
              {EXERCISE_GROUPS.map(g => (
                <div key={g} className={`tab ${chartGroup===g?"active":""}`} onClick={() => {
                  setChartGroup(g);
                  const first = EXERCISES.find(e => e.group === g);
                  if (first) setChartEx(first.name);
                }}>{g}</div>
              ))}
            </div>
          </div>
          <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:4}}>
            <div className="tab-row" style={{flexWrap:"nowrap",minWidth:"max-content"}}>
              {EXERCISES.filter(e => e.group === chartGroup).map(e => (
                <div key={e.name} className={`tab ${chartEx===e.name?"active":""}`} onClick={() => setChartEx(e.name)}>{e.name}</div>
              ))}
            </div>
          </div>
        </div>

      <RankCard username={username} exercise={chartEx} myPr={myPrs[chartEx]} communityPrs={communityPrs} needsBodyWeight={needsBodyWeight} onGoToWorkout={onGoToWorkout} />

      <div className="card">
        <div className="card-title">
          <span style={{fontFamily:"'Orbitron',sans-serif",fontSize:11,color:"var(--accent)",letterSpacing:2,marginRight:8}}>▲▲▲</span>
          {chartEx} Leaderboard
        </div>
        {leaders.length === 0 ? (
          <div style={{textAlign:"center",padding:"32px 0",color:"var(--muted)"}}>No data yet for this exercise.</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {leaders.map((entry, i) => {
              const barPct = topWeight > 0 ? (entry.weight / topWeight) * 100 : 0;
              const isTop = i === 0;
              return (
                <div key={entry.name} style={{
                  background: entry.isMe ? "rgba(181,240,60,0.06)" : "var(--surface2)",
                  border: `1px solid ${entry.isMe ? "var(--accent)" : "var(--border)"}`,
                  borderRadius:10, padding:"14px 18px",
                  position:"relative", overflow:"hidden"
                }}>
                  <div style={{
                    position:"absolute", left:0, top:0, bottom:0,
                    width:`${barPct}%`,
                    background: isTop ? "rgba(181,240,60,0.08)" : "rgba(255,255,255,0.02)",
                    borderRadius:10, transition:"width 0.6s ease", pointerEvents:"none"
                  }} />
                  <div style={{position:"relative",display:"flex",alignItems:"center",gap:14}}>
                    <div style={{fontSize:22,width:30,textAlign:"center"}}>
                      {i < 3 ? <span style={{fontFamily:"'Orbitron',sans-serif",fontWeight:900,fontSize:13,color:medalColors[i],textShadow:`0 0 8px ${medalColors[i]}99`,letterSpacing:1}}>{medalLabels[i]}</span> : <span style={{color:"var(--muted)",fontWeight:700,fontSize:13,fontFamily:"'Orbitron',sans-serif"}}>#{i+1}</span>}
                    </div>
                    {entry.avatarUrl
                      ? <AvatarPhoto src={entry.avatarUrl} alt={entry.name} />
                      : <AvatarInitials name={entry.name} bg={entry.isMe ? "var(--accent)" : "var(--surface)"} color={entry.isMe ? "#000" : "var(--text)"} />
                    }
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:14,color: entry.isMe ? "var(--accent)" : "var(--text)"}}>
                        {entry.name}{entry.isMe ? " (You)" : ""}
                      </div>
                      <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>
                        {barPct < 100 ? `${Math.round(barPct)}% of top ${isBodyweightChart ? "count" : isDurationChart ? "duration" : "lift"}` : "◈ CURRENT LEADER"}
                      </div>
                    </div>
                    <div style={{fontFamily:"'Orbitron',sans-serif",fontSize:22,fontWeight:900,color: isTop ? "var(--accent)" : "var(--chrome)",letterSpacing:2,textShadow: isTop ? "var(--glow-sm)" : "none"}}>
                      {isDurationChart ? formatDuration(entry.weight) : entry.weight} <span style={{fontSize:11,color:"var(--muted)",fontFamily:"'Rajdhani',sans-serif",fontWeight:400}}>{isDurationChart ? "" : isBodyweightChart ? "reps" : "lbs"}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title"><span style={{fontFamily:"'Orbitron',sans-serif",fontSize:10,color:"var(--accent)",letterSpacing:2,marginRight:8}}>▐▌</span>All Exercises Snapshot</div>
        <div style={{fontSize:12,color:"var(--muted)",marginBottom:16}}>Top PR across the community for each exercise.</div>
        <table className="log-table">
          <thead><tr><th>Exercise</th><th>Leader</th><th>Top</th></tr></thead>
          <tbody>
            {EXERCISE_LIST.map(ex => {
              const exD = EXERCISE_MAP[ex] || { type: "weighted" };
              const board = buildLeaderboard(ex, communityPrs, myPrs);
              const top = board[0];
              return (
                <tr key={ex}>
                  <td><span className="badge">{ex}</span></td>
                  <td>
                    {top ? (
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        {top.avatarUrl
                          ? <AvatarPhoto src={top.avatarUrl} alt={top.name} />
                          : <AvatarInitials name={top.name} bg={top.isMe ? "var(--accent)" : "var(--surface2)"} color={top.isMe ? "#000" : "var(--text)"} />
                        }
                        <span style={{fontWeight:600,color: top.isMe ? "var(--accent)" : "var(--text)"}}>{top.name}{top.isMe?" (You)":""}</span>
                      </div>
                    ) : <span style={{color:"var(--muted)"}}>—</span>}
                  </td>
                  <td style={{fontWeight:700,color:"var(--accent)"}}>{top ? (exD.type === "duration" ? formatDuration(top.weight) : `${top.weight} ${exD.type === "bodyweight" ? "reps" : "lbs"}`) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
          </div>

          {/* Chapter panel */}
          {membership && (
            <div style={{
              width: isMobileTC ? "50%" : "100%", flexShrink: 0,
              display: !isMobileTC && chartsTab === "global" ? "none" : "block",
            }}>
        {chapterUsersLoading ? (
          <div style={{color:"var(--muted)",fontSize:12,padding:28,textAlign:"center",
            fontFamily:"'Orbitron',sans-serif",letterSpacing:2}}>LOADING…</div>
        ) : (
        <>
        <div style={{marginBottom:20}}>
          <div className="form-label" style={{marginBottom:6}}>Muscle Group</div>
          <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch",marginBottom:10,paddingBottom:4}}>
            <div className="tab-row" style={{flexWrap:"nowrap",minWidth:"max-content"}}>
              {EXERCISE_GROUPS.map(g => (
                <div key={g} className={`tab ${chartGroup===g?"active":""}`} onClick={() => {
                  setChartGroup(g);
                  const first = EXERCISES.find(e => e.group === g);
                  if (first) setChartEx(first.name);
                }}>{g}</div>
              ))}
            </div>
          </div>
          <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:4}}>
            <div className="tab-row" style={{flexWrap:"nowrap",minWidth:"max-content"}}>
              {EXERCISES.filter(e => e.group === chartGroup).map(e => (
                <div key={e.name} className={`tab ${chartEx===e.name?"active":""}`} onClick={() => setChartEx(e.name)}>{e.name}</div>
              ))}
            </div>
          </div>
        </div>

        <RankCard username={username} exercise={chartEx} myPr={myPrs[chartEx]} communityPrs={chapterCommunityPrs} needsBodyWeight={needsBodyWeight} onGoToWorkout={onGoToWorkout} />

        <div className="card">
          <div className="card-title">
            <span style={{fontFamily:"'Orbitron',sans-serif",fontSize:11,color:"var(--accent)",letterSpacing:2,marginRight:8}}>▲▲▲</span>
            {chartEx} Leaderboard
            <span style={{fontSize:10,color:"var(--muted)",marginLeft:8,fontFamily:"'Orbitron',sans-serif",letterSpacing:1}}>· {membership.chapter_name}</span>
          </div>
          {chapterLeaders.length === 0 ? (
            <div style={{textAlign:"center",padding:"32px 0",color:"var(--muted)"}}>No data yet for this exercise in your chapter.</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {chapterLeaders.map((entry, i) => {
                const barPct = chapterTopWeight > 0 ? (entry.weight / chapterTopWeight) * 100 : 0;
                const isTop = i === 0;
                const exD = EXERCISE_MAP[chartEx] || { type: "weighted" };
                return (
                  <div key={entry.name} style={{
                    background: entry.isMe ? "rgba(181,240,60,0.06)" : "var(--surface2)",
                    border: `1px solid ${entry.isMe ? "var(--accent)" : "var(--border)"}`,
                    borderRadius:10, padding:"14px 18px",
                    position:"relative", overflow:"hidden"
                  }}>
                    <div style={{
                      position:"absolute", left:0, top:0, bottom:0,
                      width:`${barPct}%`,
                      background: isTop ? "rgba(181,240,60,0.08)" : "rgba(255,255,255,0.02)",
                      borderRadius:10, transition:"width 0.6s ease", pointerEvents:"none"
                    }} />
                    <div style={{position:"relative",display:"flex",alignItems:"center",gap:14}}>
                      <div style={{fontSize:22,width:30,textAlign:"center"}}>
                        {i < 3
                          ? <span style={{fontFamily:"'Orbitron',sans-serif",fontWeight:900,fontSize:13,color:medalColors[i],textShadow:`0 0 8px ${medalColors[i]}99`,letterSpacing:1}}>{medalLabels[i]}</span>
                          : <span style={{color:"var(--muted)",fontWeight:700,fontSize:13,fontFamily:"'Orbitron',sans-serif"}}>#{i+1}</span>}
                      </div>
                      {entry.avatarUrl
                        ? <AvatarPhoto src={entry.avatarUrl} alt={entry.name} />
                        : <AvatarInitials name={entry.name} bg={entry.isMe ? "var(--accent)" : "var(--surface)"} color={entry.isMe ? "#000" : "var(--text)"} />
                      }
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:14,color: entry.isMe ? "var(--accent)" : "var(--text)"}}>
                          {entry.name}{entry.isMe ? " (You)" : ""}
                        </div>
                        <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>
                          {barPct < 100 ? `${Math.round(barPct)}% of top ${exD.type === "bodyweight" ? "count" : exD.type === "duration" ? "duration" : "lift"}` : "◈ CURRENT LEADER"}
                        </div>
                      </div>
                      <div style={{fontFamily:"'Orbitron',sans-serif",fontSize:22,fontWeight:900,color: isTop ? "var(--accent)" : "var(--chrome)",letterSpacing:2,textShadow: isTop ? "var(--glow-sm)" : "none"}}>
                        {exD.type === "duration" ? formatDuration(entry.weight) : entry.weight} <span style={{fontSize:11,color:"var(--muted)",fontFamily:"'Rajdhani',sans-serif",fontWeight:400}}>{exD.type === "duration" ? "" : exD.type === "bodyweight" ? "reps" : "lbs"}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title"><span style={{fontFamily:"'Orbitron',sans-serif",fontSize:10,color:"var(--accent)",letterSpacing:2,marginRight:8}}>▐▌</span>All Exercises Snapshot <span style={{fontSize:10,color:"var(--muted)",marginLeft:8,fontFamily:"'Orbitron',sans-serif",letterSpacing:1}}>· {membership.chapter_name}</span></div>
          <div style={{fontSize:12,color:"var(--muted)",marginBottom:16}}>Top PR across your chapter for each exercise.</div>
          <table className="log-table">
            <thead><tr><th>Exercise</th><th>Leader</th><th>Top</th></tr></thead>
            <tbody>
              {EXERCISE_LIST.map(ex => {
                const exD = EXERCISE_MAP[ex] || { type: "weighted" };
                const board = buildChapterLeaderboard(ex);
                const top = board[0];
                return (
                  <tr key={ex}>
                    <td><span className="badge">{ex}</span></td>
                    <td>
                      {top ? (
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          {top.avatarUrl
                            ? <AvatarPhoto src={top.avatarUrl} alt={top.name} />
                            : <AvatarInitials name={top.name} bg={top.isMe ? "var(--accent)" : "var(--surface2)"} color={top.isMe ? "#000" : "var(--text)"} />
                          }
                          <span style={{fontWeight:600,color: top.isMe ? "var(--accent)" : "var(--text)"}}>{top.name}{top.isMe?" (You)":""}</span>
                        </div>
                      ) : <span style={{color:"var(--muted)"}}>—</span>}
                    </td>
                    <td style={{fontWeight:700,color:"var(--accent)"}}>{top ? (exD.type === "duration" ? formatDuration(top.weight) : `${top.weight} ${exD.type === "bodyweight" ? "reps" : "lbs"}`) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
        )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ONBOARDING ───────────────────────────────────────────────────────────────
// ─── AUTH SCREEN ──────────────────────────────────────────────────────────────
// Defined outside AuthScreen so React doesn't recreate these as new component
// types on every render (which would unmount/remount inputs and lose focus).
// ─── BLADE RINGS AUTH ANIMATION ─────────────────────────────────────────────
const CHIRHO_SVG = `<?xml version="1.0"?><svg width="1280" height="1557" viewBox="0 0 1280 1557" xmlns="http://www.w3.org/2000/svg"><path fill="#ffffff" fill-rule="evenodd" stroke="none" d="M 785.032837 1504 L 785.032837 1464.325928 L 758.592346 1464.325928 C 741.325073 1464.325928 724.957336 1455.747803 709.488586 1438.591431 C 697.977051 1425.724121 690.422607 1410.355225 686.825378 1392.483765 C 682.148682 1368.893799 679.810547 1320.284424 679.810547 1246.654907 L 679.810547 969.718201 L 927.800903 1112.895508 C 988.243774 1147.792236 1024.233032 1173.555176 1035.77002 1190.185303 C 1053.211304 1216.039795 1054.377441 1242.05127 1039.268433 1268.220703 L 1026.317993 1290.651489 L 1053.814453 1306.526489 L 1210.568604 1035.020264 L 1183.072266 1019.145264 L 1169.852051 1042.043335 C 1161.218384 1056.997192 1147.089355 1067.739746 1127.464722 1074.271118 C 1112.791138 1079.091797 1098.362427 1079.484375 1084.177856 1075.44873 C 1065.490234 1070.05957 1030.632202 1052.634155 979.602661 1023.172241 L 731.621033 880 L 979.202942 737.058533 C 1039.645874 702.161804 1080.041748 684.031372 1100.392334 682.666748 C 1131.50354 680.48938 1154.523315 692.32959 1169.452271 718.187378 L 1182.672607 741.08551 L 1210.168945 725.21051 L 1053.414673 453.704224 L 1025.918335 469.579224 L 1038.868774 492.010132 C 1047.502441 506.963989 1049.831055 524.727173 1045.85498 545.299805 C 1042.513184 560.106262 1035.548828 572.642456 1024.961548 582.908813 C 1010.950623 596.397949 978.430725 617.873413 927.401245 647.335266 L 679.810547 790.281799 L 679.810547 542.886536 L 766.593323 542.886536 C 862.366272 542.886536 933.649353 517.185791 980.444824 465.783569 C 1022.148499 419.715698 1043 362.373901 1043 293.756836 C 1043 242.596924 1031.301392 197.499512 1007.903625 158.463013 C 984.505859 119.426392 953.592224 91.543457 915.161926 74.813599 C 876.731567 58.08374 820.66272 49.71875 746.953857 49.71875 L 546.195313 49.71875 L 546.195313 50 L 471.524384 50 L 471.524384 89.674072 L 497.425262 89.674072 C 514.692505 89.674072 531.240234 98.252197 547.068604 115.408569 C 558.22052 128.275757 565.594971 143.644775 569.192261 161.516235 C 573.868713 185.106201 576.207092 233.715576 576.207092 307.345093 L 576.207092 790.271729 L 328.607574 647.320068 C 268.164703 612.42334 232.26535 586.5047 220.908234 569.562866 C 203.466949 543.708374 202.211014 517.852661 217.140015 491.994873 L 230.360245 469.09668 L 202.863876 453.22168 L 46.109676 724.727905 L 73.606041 740.602966 L 86.556473 718.17218 C 95.190102 703.218262 109.409111 692.320007 129.213623 685.477112 C 143.707321 680.967896 158.046097 680.731079 172.230698 684.766785 C 190.918121 690.156067 225.776337 707.58136 276.805817 737.043274 L 524.414124 880 L 276.406128 1023.1875 C 215.963272 1058.084229 175.657257 1076.370239 155.486572 1078.046509 C 124.375313 1080.223877 101.265732 1068.228027 86.156784 1042.058594 L 73.206352 1019.627808 L 45.709984 1035.502808 L 202.464188 1307.009033 L 229.960556 1291.134033 L 216.740326 1268.235962 C 208.106705 1253.281982 205.867981 1235.674683 210.023941 1215.413574 C 213.185898 1200.295532 220.060226 1187.603516 230.647552 1177.337158 C 244.658401 1163.8479 277.178375 1142.372559 328.207886 1112.910645 L 576.207092 969.728271 L 576.207092 1246.654907 C 576.207092 1333.866821 571.890259 1388.909424 563.256653 1411.784668 C 549.58667 1446.8125 527.643188 1464.325928 497.425262 1464.325928 L 471.524384 1464.325928 L 471.524384 1504 L 785.032837 1504 Z M 686.944519 507.244629 C 717.737305 514.033569 743.559326 517.427979 764.411133 517.427979 C 820.662598 517.427979 867.336121 497.66748 904.432983 458.145996 C 941.529785 418.624634 960.078003 365.040894 960.078003 297.393677 C 960.078003 229.261719 941.529785 175.556885 904.432983 136.27771 C 867.336121 96.998535 819.692749 77.359375 761.501587 77.359375 C 739.679871 77.359375 714.827759 80.874878 686.944519 87.906494 L 686.944519 507.244629 Z"/></svg>`;

function BladeRingsCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let raf, ro, renderer;
    let destroyed = false;

    (async () => {
      // Build chrome environment
      const env = document.createElement('canvas');
      env.width = 512; env.height = 256;
      const g = env.getContext('2d');
      const grad = g.createLinearGradient(0, 0, 0, 256);
      grad.addColorStop(0.00, '#0a2200'); grad.addColorStop(0.28, '#66cc00');
      grad.addColorStop(0.42, '#aaff44'); grad.addColorStop(0.47, '#ffffff');
      grad.addColorStop(0.52, '#eeffcc'); grad.addColorStop(0.56, '#88ff00');
      grad.addColorStop(0.72, '#1d3d00'); grad.addColorStop(1.00, '#020800');
      g.fillStyle = grad; g.fillRect(0, 0, 512, 256);
      const spot = (x, y, r, c) => { const rg = g.createRadialGradient(x,y,0,x,y,r); rg.addColorStop(0,c); rg.addColorStop(1,'rgba(0,0,0,0)'); g.fillStyle=rg; g.fillRect(x-r,y-r,r*2,r*2); };
      spot(90,70,60,'rgba(255,255,255,.95)'); spot(300,55,45,'rgba(200,255,140,.8)'); spot(430,120,50,'rgba(136,255,0,.6)');

      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.15;
      if (destroyed) { renderer.dispose(); return; }

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 100);
      camera.position.set(0, 0, 14);
      camera.lookAt(0, -2.5, 0);

      const envTex = new THREE.CanvasTexture(env);
      envTex.mapping = THREE.EquirectangularReflectionMapping;
      envTex.colorSpace = THREE.SRGBColorSpace;
      const pmrem = new THREE.PMREMGenerator(renderer);
      const envRT = pmrem.fromEquirectangular(envTex);
      scene.environment = envRT.texture;
      pmrem.dispose(); envTex.dispose();

      const chrome = (tint, rough) => new THREE.MeshStandardMaterial({ color: tint, metalness: 1.0, roughness: rough, envMapIntensity: 2.2, flatShading: true });
      const outer = new THREE.Mesh(new THREE.TorusGeometry(2.55, 0.30, 3, 320), chrome(0x88ff00, 0.06));
      const inner = new THREE.Mesh(new THREE.TorusGeometry(1.55, 0.22, 3, 280), chrome(0xaaff44, 0.10));

      // Chi-Rho extrusion
      const chiRho = new THREE.Group();
      const bright = new THREE.MeshStandardMaterial({ color: 0xeeffcc, metalness: 1.0, roughness: 0.08, envMapIntensity: 2.6, emissive: 0x88ff00, emissiveIntensity: 0.55 });
      try {
        const { SVGLoader } = await import("three/examples/jsm/loaders/SVGLoader.js");
        const parsed = new SVGLoader().parse(CHIRHO_SVG);
        const svgGroup = new THREE.Group();
        const U = 1557;
        const svgExtrude = { depth: U*0.085, bevelEnabled: true, bevelThickness: U*0.012, bevelSize: U*0.010, bevelSegments: 4, curveSegments: 32 };
        const contours = [];
        parsed.paths.forEach(p => p.subPaths.forEach(sp => {
          const pts = sp.getPoints(64);
          if (pts.length > 2) contours.push({ pts, holes: [], area: Math.abs(THREE.ShapeUtils.area(pts)) });
        }));
        const inside = (pt, poly) => { let hit=false; for (let i=0,j=poly.length-1;i<poly.length;j=i++){const xi=poly[i].x,yi=poly[i].y,xj=poly[j].x,yj=poly[j].y; if(((yi>pt.y)!=(yj>pt.y))&&(pt.x<(xj-xi)*(pt.y-yi)/(yj-yi)+xi))hit=!hit;} return hit; };
        contours.sort((a,b) => b.area-a.area);
        const roots = [];
        contours.forEach(c => { const parent = roots.find(r => inside(c.pts[0], r.pts)); if (parent) parent.holes.push(c.pts); else roots.push(c); });
        roots.forEach(r => {
          const outerPts = THREE.ShapeUtils.isClockWise(r.pts) ? r.pts.slice().reverse() : r.pts;
          const shape = new THREE.Shape(outerPts);
          r.holes.forEach(h => { const hp = THREE.ShapeUtils.isClockWise(h) ? h : h.slice().reverse(); shape.holes.push(new THREE.Path(hp)); });
          svgGroup.add(new THREE.Mesh(new THREE.ExtrudeGeometry(shape, svgExtrude), bright));
        });
        const box0 = new THREE.Box3().setFromObject(svgGroup);
        const size = new THREE.Vector3(), center = new THREE.Vector3();
        box0.getSize(size); box0.getCenter(center);
        svgGroup.children.forEach(m => m.geometry.translate(-center.x, -center.y, -center.z));
        const fit = 1.95 / Math.max(size.x, size.y);
        svgGroup.scale.set(fit, -fit, fit);
        chiRho.add(svgGroup);
      } catch(e) { /* fallback: no symbol, rings only */ }

      chiRho.add(new THREE.PointLight(0xaaff44, 14, 12));
      const group = new THREE.Group();
      group.add(outer); group.add(inner); group.add(chiRho);
      group.rotation.x = 0.16;
      scene.add(group);

      const key = new THREE.DirectionalLight(0xccff99, 2.4); key.position.set(4,5,6);
      const rim = new THREE.DirectionalLight(0x66cc00, 3.0); rim.position.set(-5,-2,-4);
      const fill = new THREE.PointLight(0x88ff00, 18, 30); fill.position.set(0,0,3);
      scene.add(key, rim, fill, new THREE.AmbientLight(0x224400, 1.2));

      const resize = () => {
        const w = canvas.clientWidth, h = canvas.clientHeight;
        if (!w || !h) return;
        renderer.setSize(w, h, false);
        camera.aspect = w / h; camera.updateProjectionMatrix();
      };
      resize();
      ro = new ResizeObserver(resize); ro.observe(canvas);

      const clock = new THREE.Clock();
      const tick = () => {
        if (destroyed) return;
        const t = clock.getElapsedTime();
        outer.rotation.y = t * 0.6;
        outer.rotation.z = Math.sin(t * 0.3) * 0.15;
        inner.rotation.x = t * 0.85;
        inner.rotation.z = t * -0.35;
        group.rotation.y = Math.sin(t * 0.18) * 0.22;
        chiRho.rotation.y = Math.sin(t * 0.35) * 0.3;
        chiRho.children.forEach(c => { if (c.material) c.material.emissiveIntensity = 0.5 + Math.sin(t*1.6)*0.18; });
        renderer.render(scene, camera);
        raf = requestAnimationFrame(tick);
      };
      tick();
    })();

    return () => {
      destroyed = true;
      cancelAnimationFrame(raf);
      ro && ro.disconnect();
      renderer && renderer.dispose();
    };
  }, []);

  return (
    <div style={{position:"absolute",inset:0,zIndex:1,pointerEvents:"none"}}>
      <canvas ref={canvasRef} style={{display:"block",width:"100%",height:"100%"}} />
    </div>
  );
}

const AuthCard = ({ children }) => (
  <div style={{
    width:"100%", maxWidth:420, position:"relative", zIndex:1,
    background:"linear-gradient(155deg,rgba(0,14,8,0.98),rgba(0,5,3,1))",
    border:"1px solid rgba(6,51,34,0.9)", borderRadius:2, padding:34, overflow:"hidden",
    boxSizing:"border-box",
  }}>
    <div style={{position:"absolute",top:0,left:0,right:0,height:1,
      background:"linear-gradient(90deg,#88ff00,#aaff44,#88ff00,#88ff00)",
      backgroundSize:"300% 100%",opacity:0.8,animation:"sheen 2.5s ease-in-out infinite"}} />
    <div style={{position:"absolute",top:0,left:0,width:16,height:16,borderTop:"1px solid rgba(136,255,0,0.6)",borderLeft:"1px solid rgba(136,255,0,0.6)"}} />
    <div style={{position:"absolute",bottom:0,right:0,width:16,height:16,borderBottom:"1px solid rgba(170,255,30,0.5)",borderRight:"1px solid rgba(170,255,30,0.5)"}} />
    {children}
  </div>
);

const AuthTitle = ({ text }) => (
  <div style={{
    fontFamily:"'Orbitron',sans-serif", fontSize:15, fontWeight:900,
    letterSpacing:5, marginBottom:6, textTransform:"uppercase",
    background:"linear-gradient(155deg,#ffffff,#aaffee,#88ff00)",
    WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
    filter:"drop-shadow(0 0 10px rgba(136,255,0,0.6))"
  }}>{text}</div>
);

const AuthField = ({ label, type, value, onChange, onEnter }) => (
  <div style={{marginBottom:12}}>
    <div className="form-label" style={{marginBottom:5}}>{label}</div>
    <input
      type={type || "text"} value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => e.key === "Enter" && onEnter && onEnter()}
      style={{width:"100%", boxSizing:"border-box"}}
    />
  </div>
);

// ─── ADMIN PANEL ─────────────────────────────────────────────────────────────
function AuthScreen({ onAuth }) {
  const [mode, setMode]           = useState(() => {
    // If URL has ?reset=TOKEN, go straight to reset mode
    const params = new URLSearchParams(window.location.search);
    if (params.get("verify")) return "verifying";
    return params.get("reset") ? "reset" : "welcome";
  });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(""); // set after register
  const [resendSent, setResendSent] = useState(false);

  // Load Turnstile script when register mode is active
  useEffect(() => {
    if (mode !== "register") return;
    if (document.getElementById("turnstile-script")) return;
    const s = document.createElement("script");
    s.id = "turnstile-script";
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    s.async = true;
    document.head.appendChild(s);
  }, [mode]);

  // Render Turnstile widget when ref is available and script is loaded
  useEffect(() => {
    if (mode !== "register" || !turnstileRef.current) return;
    const render = () => {
      if (!window.turnstile || turnstileRef.current?.childElementCount > 0) return;
      window.turnstile.render(turnstileRef.current, {
        sitekey: "0x4AAAAAADdrJR1dx0gV2fwB",
        theme: "dark",
        callback: (token) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      });
    };
    // Script may not be loaded yet — poll briefly
    const id = setInterval(() => { if (window.turnstile) { render(); clearInterval(id); } }, 200);
    return () => clearInterval(id);
  }, [mode, turnstileRef.current]);
  useEffect(() => {
    if (mode !== "verifying") return;
    const token = new URLSearchParams(window.location.search).get("verify");
    if (!token) { setMode("login"); return; }
    api.verifyEmail(token).then(data => {
      window.history.replaceState({}, "", window.location.pathname);
      if (data.error) { setError(data.error); setMode("login"); return; }
      api.setToken(data.token);
      onAuth(data.user);
    }).catch(() => { setError("Verification failed. Please try again."); setMode("login"); });
  }, []);

  const reset = () => {
    setFirstName(""); setLastName(""); setEmail("");
    setPassword(""); setConfirm(""); setTurnstileToken(""); setError(""); setForgotSent(false);
    setResendSent(false);
    if (window.turnstile && turnstileRef.current) window.turnstile.reset(turnstileRef.current);
  };

  const switchMode = (m) => { reset(); setMode(m); };

  const handleForgot = async () => {
    setError("");
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setLoading(true);
    try {
      await api.forgotPassword({ email });
      setForgotSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    const token = new URLSearchParams(window.location.search).get("reset");
    if (!token) { setError("Invalid or expired reset link."); return; }
    setLoading(true);
    try {
      await api.resetPassword({ token, password });
      // Clear the token from URL and go to login
      window.history.replaceState({}, "", window.location.pathname);
      switchMode("login");
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setError("");
    if (!email.trim() || !password) { setError("Please enter your email and password."); return; }
    setLoading(true);
    try {
      const data = await api.login({ email, password });
      if (data.unverified) {
        setPendingEmail(data.email || email.trim());
        setMode("pending");
        return;
      }
      if (data.error) { setError(data.error); return; }
      api.setToken(data.token);
      onAuth(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setError("");
    if (!firstName.trim() || !lastName.trim()) { setError("Please enter your first and last name."); return; }
    if (!email.trim()) { setError("Please enter your email address."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (!turnstileToken) { setError("Please complete the CAPTCHA."); return; }
    setLoading(true);
    try {
      const data = await api.register({ firstName, lastName, email, password, turnstileToken });
      if (data.error) { setError(data.error); return; }
      setPendingEmail(data.email || email.trim());
      setMode("pending");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendSent(false);
    try {
      await api.resendVerification({ email: pendingEmail });
      setResendSent(true);
    } catch(e) {}
  };

  return (
    <div className="onboard-wrap">
      <BladeRingsCanvas />
      <div className="onboard-grid-floor" />
      <div className="onboard-grid-fog" />

      {/* Title fixed near top of screen — matches main app logo style */}
      <div className="onboard-title">
        <div style={{
          fontFamily:"'Orbitron',sans-serif", fontWeight:900, textTransform:"uppercase",
          background:"linear-gradient(155deg,#ffffff 0%,#eeffcc 30%,#aaff44 60%,#66dd00 85%,#338800 100%)",
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text",
          filter:"drop-shadow(0 0 14px rgba(136,255,0,0.5))",
          lineHeight:1.15,
        }}>
          <span style={{display:"block", fontSize:22, letterSpacing:5}}>BROS OF ST.</span>
          <span style={{display:"block", fontSize:22, letterSpacing:10.5}}>HYACINTH</span>
        </div>
      </div>

      {/* Fixed glowing bar at top of scroll zone */}
      <div className="onboard-scroll-bar" />

      {/* Scroll container starting 44% down */}
      <div className="onboard-scroll">
        <div style={{width:"100%",maxWidth:400}}>

        {/* VERIFYING */}
        {mode === "verifying" && (
          <AuthCard>
            <AuthTitle text="Verifying" />
            <div style={{color:"var(--muted)",fontSize:13,textAlign:"center",letterSpacing:1}}>
              {error || "Verifying your email address…"}
            </div>
          </AuthCard>
        )}

        {/* PENDING VERIFICATION */}
        {mode === "pending" && (
          <AuthCard>
            <AuthTitle text="Check Your Email" />
            <div style={{color:"var(--muted)",fontSize:13,marginBottom:20,textAlign:"center",lineHeight:1.7}}>
              We sent a verification link to<br/>
              <span style={{color:"var(--accent)",fontFamily:"'Orbitron',sans-serif",fontSize:12}}>{pendingEmail}</span>
              <br/><br/>
              Click the link in the email to activate your account.
            </div>
            {resendSent
              ? <div style={{color:"var(--accent)",fontSize:12,textAlign:"center",marginBottom:12}}>✓ Verification email resent.</div>
              : <button className="btn" style={{width:"100%",justifyContent:"center",marginBottom:10}} onClick={handleResend}>Resend Email</button>
            }
            <button className="btn" style={{width:"100%",justifyContent:"center"}} onClick={() => switchMode("login")}>Back to Login</button>
          </AuthCard>
        )}

        {/* WELCOME */}
        {mode === "welcome" && (
          <AuthCard>
            <AuthTitle text="Welcome" />
            <div style={{color:"var(--muted)",fontSize:12,marginBottom:28,letterSpacing:1,fontFamily:"'Orbitron',sans-serif",textTransform:"uppercase"}}>
              Choose an option to continue
            </div>
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"13px 20px",marginBottom:10}}
              onClick={() => switchMode("login")}>
              LOG IN ›
            </button>
            <button className="btn btn-ghost" style={{width:"100%",justifyContent:"center",padding:"13px 20px"}}
              onClick={() => switchMode("register")}>
              CREATE ACCOUNT ›
            </button>
          </AuthCard>
        )}

        {/* LOGIN */}
        {mode === "login" && (
          <AuthCard>
            <AuthTitle text="Log In" />
            <div style={{color:"var(--muted)",fontSize:11,marginBottom:22,letterSpacing:1,fontFamily:"'Orbitron',sans-serif",textTransform:"uppercase"}}>
              Enter your credentials
            </div>
            <AuthField label="Email" type="email" value={email} onChange={setEmail} />
            <AuthField label="Password" type="password" value={password} onChange={setPassword} onEnter={handleLogin} />
            {error && <div style={{color:"#ff4455",fontSize:12,marginBottom:12,fontWeight:600}}>{error}</div>}
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"13px 20px",marginBottom:10}}
              disabled={loading} onClick={handleLogin}>
              {loading ? "LOGGING IN…" : "LOG IN ›"}
            </button>
            <button className="btn btn-ghost" style={{width:"100%",justifyContent:"center",padding:"10px 20px",fontSize:10,marginBottom:6}}
              onClick={() => switchMode("welcome")}>← BACK</button>
            <button className="btn btn-ghost" style={{width:"100%",justifyContent:"center",padding:"8px 20px",fontSize:10,opacity:0.6}}
              onClick={() => switchMode("forgot")}>Forgot password?</button>
          </AuthCard>
        )}

        {/* FORGOT PASSWORD */}
        {mode === "forgot" && (
          <AuthCard>
            <AuthTitle text="Reset Password" />
            <div style={{color:"var(--muted)",fontSize:11,marginBottom:22,letterSpacing:1,fontFamily:"'Orbitron',sans-serif",textTransform:"uppercase"}}>
              Enter your email address
            </div>
            <AuthField label="Email" type="email" value={email} onChange={setEmail} onEnter={handleForgot} />
            {error && <div style={{color:"#ff4455",fontSize:12,marginBottom:12,fontWeight:600}}>{error}</div>}
            {forgotSent && <div style={{color:"var(--accent)",fontSize:12,marginBottom:12,fontWeight:600}}>
              Reset link sent — check your email.
            </div>}
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"13px 20px",marginBottom:10}}
              disabled={loading || forgotSent} onClick={handleForgot}>
              {loading ? "SENDING…" : forgotSent ? "EMAIL SENT ✓" : "SEND RESET LINK ›"}
            </button>
            <button className="btn btn-ghost" style={{width:"100%",justifyContent:"center",padding:"10px 20px",fontSize:10}}
              onClick={() => switchMode("login")}>← BACK</button>
          </AuthCard>
        )}

        {/* RESET PASSWORD (arrived via email link with ?reset=TOKEN) */}
        {mode === "reset" && (
          <AuthCard>
            <AuthTitle text="New Password" />
            <div style={{color:"var(--muted)",fontSize:11,marginBottom:22,letterSpacing:1,fontFamily:"'Orbitron',sans-serif",textTransform:"uppercase"}}>
              Choose a new password
            </div>
            <AuthField label="New Password (min. 8 characters)" type="password" value={password} onChange={setPassword} />
            <AuthField label="Confirm New Password" type="password" value={confirm} onChange={setConfirm} onEnter={handleReset} />
            {error && <div style={{color:"#ff4455",fontSize:12,marginBottom:12,fontWeight:600}}>{error}</div>}
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"13px 20px",marginBottom:10}}
              disabled={loading} onClick={handleReset}>
              {loading ? "SAVING…" : "SET NEW PASSWORD ›"}
            </button>
          </AuthCard>
        )}

        {/* REGISTER */}
        {mode === "register" && (
          <AuthCard>
            <AuthTitle text="Create Account" />
            <div style={{color:"var(--muted)",fontSize:11,marginBottom:22,letterSpacing:1,fontFamily:"'Orbitron',sans-serif",textTransform:"uppercase"}}>
              Join the brotherhood
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:0}}>
              <AuthField label="First Name" value={firstName} onChange={setFirstName} />
              <AuthField label="Last Name" value={lastName} onChange={setLastName} />
            </div>
            <AuthField label="Email" type="email" value={email} onChange={setEmail} />
            <AuthField label="Password (min. 8 characters)" type="password" value={password} onChange={setPassword} />
            <AuthField label="Confirm Password" type="password" value={confirm} onChange={setConfirm} />

            {/* Cloudflare Turnstile CAPTCHA */}
            <div ref={turnstileRef} style={{marginBottom:18}} />

            {error && <div style={{color:"#ff4455",fontSize:12,marginBottom:12,fontWeight:600}}>{error}</div>}
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"13px 20px",marginBottom:10}}
              disabled={loading} onClick={handleRegister}>
              {loading ? "CREATING ACCOUNT…" : "CREATE ACCOUNT ›"}
            </button>
            <button className="btn btn-ghost" style={{width:"100%",justifyContent:"center",padding:"10px 20px",fontSize:10}}
              onClick={() => switchMode("welcome")}>← BACK</button>
          </AuthCard>
        )}
        </div>
      </div>
    </div>
  );
}

// ─── PROFILE PAGE ─────────────────────────────────────────────────────────────
// ─── AVATAR UPLOAD ─────────────────────────────────────────────────────────────
function AvatarUpload({ user, onUpdate, uploadFn, removeFn, responseKey = "avatarUrl" }) {
  uploadFn = uploadFn || api.uploadAvatar;
  removeFn = removeFn || api.deleteAvatar;
  const [uploading, setUploading] = useState(false);
  const [removing,  setRemoving]  = useState(false);
  const [error,     setError]     = useState("");
  const [cropSrc,   setCropSrc]   = useState(null);
  const [imgSize,   setImgSize]   = useState({ w: 0, h: 0 }); // natural dimensions once loaded
  const fileRef   = useRef(null);
  const canvasRef = useRef(null);
  const imgRef    = useRef(null);

  const [scale,  setScale]  = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef  = useRef(null);

  const DISPLAY_SIZE = 240; // preview circle size on screen
  const CROP_SIZE = 600;    // actual canvas output resolution

  const handleFile = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please select an image file."); return; }
    setError("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCropSrc(ev.target.result);
      setScale(1);
      setOffset({ x: 0, y: 0 });
      setImgSize({ w: 0, h: 0 });
    };
    reader.readAsDataURL(file);
  };

  const onImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    setImgSize({ w, h });
    // Set initial scale so the image fills the crop window (shorter side = DISPLAY_SIZE)
    const fitScale = DISPLAY_SIZE / Math.min(w, h);
    setScale(fitScale);
  };

  // Minimum scale: image fits entirely within crop window (longer side = CROP_SIZE)
  const minScale = imgSize.w && imgSize.h
    ? DISPLAY_SIZE / Math.max(imgSize.w, imgSize.h)
    : 0.1;

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const img    = imgRef.current;
    if (!canvas || !img || !imgSize.w) return;
    const ctx = canvas.getContext("2d");
    canvas.width  = CROP_SIZE;
    canvas.height = CROP_SIZE;
    ctx.clearRect(0, 0, CROP_SIZE, CROP_SIZE);
    ctx.save();
    ctx.beginPath();
    ctx.arc(CROP_SIZE / 2, CROP_SIZE / 2, CROP_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    // Scale factor from display space to output canvas space
    const ratio = CROP_SIZE / DISPLAY_SIZE;
    const iw = imgSize.w * scale * ratio;
    const ih = imgSize.h * scale * ratio;
    ctx.drawImage(img, (CROP_SIZE - iw) / 2 + offset.x * ratio, (CROP_SIZE - ih) / 2 + offset.y * ratio, iw, ih);
    ctx.restore();
  };

  const confirmCrop = () => {
    drawCanvas();
    canvasRef.current.toBlob(async (blob) => {
      const file = new File([blob], "avatar.png", { type: "image/png" });
      setUploading(true); setCropSrc(null);
      try {
        const data = await uploadFn(file);
        if (data[responseKey]) onUpdate(data[responseKey]);
        else setError(data.error || "Upload failed.");
      } catch { setError("Upload failed."); }
      finally { setUploading(false); }
    }, "image/png");
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    const p = e.touches ? e.touches[0] : e;
    dragRef.current = { startX: p.clientX, startY: p.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const p = e.touches ? e.touches[0] : e;
    setOffset({ x: dragRef.current.ox + p.clientX - dragRef.current.startX, y: dragRef.current.oy + p.clientY - dragRef.current.startY });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const handleRemove = async () => {
    setRemoving(true); setError("");
    try { await removeFn(); onUpdate(null); }
    catch { setError("Remove failed."); }
    finally { setRemoving(false); }
  };

  if (cropSrc) {
    const iw = imgSize.w * scale;
    const ih = imgSize.h * scale;
    return (
      <div style={{marginBottom:20}}>
        <div className="form-label" style={{marginBottom:8}}>Position your photo</div>
        <div style={{
          width:DISPLAY_SIZE, height:DISPLAY_SIZE, borderRadius:"50%", overflow:"hidden",
          border:"2px solid rgba(136,255,0,0.4)", boxShadow:"0 0 16px rgba(136,255,0,0.15)",
          position:"relative", cursor:"grab", userSelect:"none", touchAction:"none",
          background:"var(--surface2)",
        }}
          onMouseDown={onPointerDown} onMouseMove={onPointerMove}
          onMouseUp={onPointerUp} onMouseLeave={onPointerUp}
          onTouchStart={onPointerDown} onTouchMove={onPointerMove} onTouchEnd={onPointerUp}
        >
          <img ref={imgRef} src={cropSrc} onLoad={onImgLoad}
            style={{
              position:"absolute",
              width: iw || "auto", height: ih || "auto",
              left: (DISPLAY_SIZE - iw) / 2 + offset.x,
              top:  (DISPLAY_SIZE - ih) / 2 + offset.y,
              maxWidth:"none", pointerEvents:"none", userSelect:"none",
              display: imgSize.w ? "block" : "none",
            }}
          />
          {!imgSize.w && <div style={{color:"var(--muted)",fontSize:12,textAlign:"center",paddingTop:100}}>Loading…</div>}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginTop:12,maxWidth:DISPLAY_SIZE}}>
          <span style={{fontSize:11,color:"var(--muted)"}}>−</span>
          <input type="range" min={minScale} max="3" step="0.01" value={scale}
            onChange={e => setScale(Number(e.target.value))}
            style={{flex:1,accentColor:"var(--accent)"}} />
          <span style={{fontSize:11,color:"var(--muted)"}}>+</span>
        </div>
        <div style={{display:"flex",gap:8,marginTop:12}}>
          <button className="btn btn-primary" onClick={confirmCrop} style={{fontSize:12,padding:"6px 18px"}}>Save Photo</button>
          <button className="btn" onClick={() => setCropSrc(null)} style={{fontSize:12,padding:"6px 14px"}}>Cancel</button>
        </div>
        <canvas ref={canvasRef} style={{display:"none"}} />
      </div>
    );
  }

  return (
    <div style={{marginBottom:20}}>
      <div className="form-label" style={{marginBottom:8}}>Profile Picture</div>
      <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleFile} />
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <button className="btn" onClick={() => fileRef.current?.click()} disabled={uploading}
          style={{fontSize:12,padding:"6px 14px"}}>
          {uploading ? "Uploading…" : user.avatarUrl ? "Change Photo" : "Upload Photo"}
        </button>
        {user.avatarUrl && (
          <button className="btn" onClick={handleRemove} disabled={removing}
            style={{fontSize:12,padding:"6px 14px",color:"rgba(255,68,85,0.8)",borderColor:"rgba(255,68,85,0.3)"}}>
            {removing ? "Removing…" : "Remove Photo"}
          </button>
        )}
      </div>
      {error && <div style={{color:"rgba(255,68,85,0.8)",fontSize:12,marginTop:6}}>{error}</div>}
    </div>
  );
}

// ─── CHAT ALIAS SECTION ────────────────────────────────────────────────────
function ChatAliasSection({ user, onUpdate }) {
  const [aliasName, setAliasName] = useState(user.chatAliasName || "");
  const [aliasAvatarUrl, setAliasAvatarUrl] = useState(user.chatAliasAvatarUrl || null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState("");

  // Preview: what the user will look like in global chat
  const previewName   = aliasName.trim() || user.displayName;
  const previewAvatar = aliasAvatarUrl || user.avatarUrl;
  const previewInitials = aliasName.trim()
    ? aliasName.trim().slice(0, 2).toUpperCase()
    : (user.displayName || "").slice(0, 2).toUpperCase();

  const saveName = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const data = await api.updateChatAliasName(aliasName.trim() || null);
      onUpdate({ chatAliasName: data.chatAliasName });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { setError("Save failed."); }
    finally { setSaving(false); }
  };

  const divider = { borderTop:"1px solid var(--border)", margin:"20px 0" };
  const label   = { fontSize:10, fontFamily:"'Orbitron',sans-serif", letterSpacing:1, color:"var(--muted)", marginBottom:6 };

  return (
    <div style={{marginTop:24}}>
      <div style={divider} />
      <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>Global Chat Alias</div>
      <div style={{fontSize:11,color:"var(--muted)",marginBottom:16,lineHeight:1.5}}>
        This identity only appears in the global chat. Your real name and photo are used everywhere else.
      </div>

      {/* Preview */}
      <div style={label}>PREVIEW</div>
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",
        background:"var(--surface2)",borderRadius:8,marginBottom:18,border:"1px solid var(--border)"}}>
        {previewAvatar
          ? <AvatarPhoto src={previewAvatar} size={36} />
          : <div className="avatar sm" style={{width:36,height:36,fontSize:12,flexShrink:0}}>
              {previewInitials}
            </div>
        }
        <div style={{fontWeight:600,fontSize:13}}>{previewName}</div>
      </div>

      {/* Alias name */}
      <div style={label}>CHAT NICKNAME</div>
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        <input
          value={aliasName}
          onChange={e => { setAliasName(e.target.value); setSaved(false); }}
          placeholder="Leave blank to use your real name"
          maxLength={32}
          style={{flex:1,background:"var(--surface2)",border:"1px solid var(--border)",
            borderRadius:4,padding:"8px 12px",color:"var(--text)",fontSize:13,outline:"none"}}
        />
        <button className="btn" onClick={saveName} disabled={saving}
          style={{padding:"8px 16px",fontSize:11,letterSpacing:1,whiteSpace:"nowrap",
            color: saved ? "#88ff00" : "var(--accent)", borderColor: saved ? "rgba(136,255,0,0.4)" : undefined}}>
          {saving ? "…" : saved ? "SAVED ✓" : "SAVE"}
        </button>
      </div>
      {error && <div style={{color:"#ff4455",fontSize:11,marginBottom:8}}>{error}</div>}

      {/* Alias avatar */}
      <div style={label}>CHAT PHOTO</div>
      <AvatarUpload
        user={{ avatarUrl: aliasAvatarUrl }}
        uploadFn={api.uploadChatAliasAvatar}
        removeFn={api.deleteChatAliasAvatar}
        responseKey="chatAliasAvatarUrl"
        onUpdate={(url) => {
          setAliasAvatarUrl(url);
          onUpdate({ chatAliasAvatarUrl: url });
        }}
      />
    </div>
  );
}

// ─── BODY WEIGHT (bottom of the Workout page) ─────────────────────────────────
// Weigh-in log + progress chart. Weigh-ins are also used to estimate 1RMs for
// Weighted Pull-up / Weighted Dips / Weighted Back Extension (see BW_FRACTIONS).
// State lives in WorkoutPage so the Progress card updates the moment a weigh-in is logged.
const bwPad = (n) => String(n).padStart(2, "0");
const bwTodayStr = () => { const d = new Date(); return `${d.getFullYear()}-${bwPad(d.getMonth() + 1)}-${bwPad(d.getDate())}`; };
const bwFmtDate = (ts) => new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const bwFmtLbs  = (v) => (Math.round(v * 10) / 10).toFixed(1);
// Local midnight of a "YYYY-MM-DD" string, as ms (NaN if invalid)
const bwDateToTs = (str) => {
  const [y, m, d] = (str || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1).getTime();
};

// Small "log today's weight" form shown inside the Progress card when a body weight is needed
function BodyWeightQuickEntry({ onLog }) {
  const [weight, setWeight] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const save = async () => {
    const w = parseFloat(weight);
    if (!isFinite(w) || w < 50 || w > 700) { setError("Enter a body weight between 50 and 700 lbs."); return; }
    setSaving(true); setError("");
    try { await onLog(w, bwDateToTs(bwTodayStr())); setWeight(""); }
    catch (err) { setError(err?.message || "Couldn't save. Try again."); }
    finally { setSaving(false); }
  };
  return (
    <div style={{maxWidth:300,margin:"0 auto"}}>
      <div style={{display:"flex",gap:8,alignItems:"stretch"}}>
        <input type="number" inputMode="decimal" step="0.1" value={weight} placeholder="Body weight (lbs)"
          onChange={e => { setWeight(e.target.value); setError(""); }}
          onKeyDown={e => e.key === "Enter" && save()}
          style={{flex:1,minWidth:0,boxSizing:"border-box"}} />
        <button className="btn btn-primary" onClick={save} disabled={saving || !weight}
          style={{padding:"0 18px",fontSize:12,letterSpacing:1,whiteSpace:"nowrap"}}>
          {saving ? "…" : "LOG"}
        </button>
      </div>
      {error && <div style={{color:"#ff4455",fontSize:12,marginTop:8}}>{error}</div>}
    </div>
  );
}

function BodyWeightPanel({ entries, onLog, onDelete }) {
  const [weight, setWeight] = useState("");
  const [date, setDate]     = useState(bwTodayStr());
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const [period, setPeriod] = useState("all"); // "1m" | "3m" | "1y" | "all"

  const save = async () => {
    setError("");
    const w = parseFloat(weight);
    if (!isFinite(w) || w < 50 || w > 700) { setError("Enter a body weight between 50 and 700 lbs."); return; }
    const loggedAt = bwDateToTs(date);
    if (!date || isNaN(loggedAt)) { setError("Pick a valid date."); return; }
    if (loggedAt > Date.now()) { setError("The date can't be in the future."); return; }
    setSaving(true);
    try {
      await onLog(w, loggedAt);
      setWeight("");
      setDate(bwTodayStr());
    } catch (err) {
      setError(err?.message || "Couldn't save. Try again.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await onDelete(id); } catch { setError("Couldn't delete that entry."); }
  };

  const list   = entries || [];
  const latest = list.length ? list[list.length - 1] : null;
  // Change vs. ~30 days before the latest weigh-in; falls back to the first weigh-in
  let deltaText = null;
  if (latest && list.length > 1) {
    const DAY = 24 * 60 * 60 * 1000;
    const older = list.filter(e => e.loggedAt <= latest.loggedAt - 30 * DAY);
    const ref = older.length ? older[older.length - 1] : list[0];
    const diff = latest.weight - ref.weight;
    const label = older.length ? "in the last 30 days" : `since ${bwFmtDate(ref.loggedAt)}`;
    deltaText = Math.abs(diff) < 0.05 ? `No change ${label}` : `${diff > 0 ? "▲" : "▼"} ${bwFmtLbs(Math.abs(diff))} lbs ${label}`;
  }

  const cutoffDays = { "1m": 30, "3m": 91, "1y": 365 }[period];
  const cutoffTs   = cutoffDays ? Date.now() - cutoffDays * 24 * 60 * 60 * 1000 : 0;
  const chartData  = list.filter(e => e.loggedAt >= cutoffTs).map(e => ({ ts: e.loggedAt, value: e.weight }));

  const label = { fontSize: 10, fontFamily: "'Orbitron',sans-serif", letterSpacing: 1, color: "var(--muted)", marginBottom: 6 };

  return (
    <div className="card" id="body-weight-panel" style={{position:"relative"}}>
      <div className="card-title">
        <span style={{fontFamily:"'Orbitron',sans-serif",fontSize:10,color:"var(--accent)",letterSpacing:2,marginRight:8}}>◈</span>
        Body Weight
      </div>

      {/* Current weight */}
      <div style={{marginBottom: 18}}>
        <div style={label}>CURRENT</div>
        {entries === null ? (
          <div style={{color:"var(--muted)",fontSize:13}}>Loading…</div>
        ) : latest ? (
          <>
            <div style={{fontFamily:"'Orbitron',sans-serif",fontWeight:900,fontSize:30,color:"var(--accent)",lineHeight:1.1}}>
              {bwFmtLbs(latest.weight)} <span style={{fontSize:13,fontWeight:700,color:"var(--muted)"}}>lbs</span>
            </div>
            <div style={{fontSize:12,color:"var(--muted)",marginTop:4}}>
              {deltaText ? `${deltaText} · ` : ""}logged {bwFmtDate(latest.loggedAt)}
            </div>
          </>
        ) : (
          <div style={{fontSize:13,color:"var(--muted)",lineHeight:1.6}}>
            No weigh-ins yet. Log one below to unlock progress tracking and rankings for Weighted Pull-up, Weighted Dips, and Weighted Back Extension.
          </div>
        )}
      </div>

      {/* Log a weigh-in */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end",marginBottom: error ? 8 : 18}}>
        <div style={{flex:"1 1 110px",minWidth:110}}>
          <div style={label}>WEIGHT (LBS)</div>
          <input type="number" inputMode="decimal" step="0.1" value={weight} placeholder="180.0"
            onChange={e => { setWeight(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && save()} style={{width:"100%",boxSizing:"border-box"}} />
        </div>
        <div style={{flex:"1 1 140px",minWidth:140}}>
          <div style={label}>DATE</div>
          <input type="date" value={date} max={bwTodayStr()}
            onChange={e => { setDate(e.target.value); setError(""); }} style={{width:"100%",boxSizing:"border-box"}} />
        </div>
        <button className="btn btn-primary" onClick={save} disabled={saving || !weight}
          style={{height:40,padding:"0 20px",fontSize:12,letterSpacing:1,whiteSpace:"nowrap"}}>
          {saving ? "…" : "LOG"}
        </button>
      </div>
      {error && <div style={{color:"#ff4455",fontSize:12,marginBottom:14}}>{error}</div>}

      {/* Progress chart */}
      {list.length > 0 && (
        <>
          <div style={{display:"flex",gap:6,marginBottom:12}}>
            {[["1m","1M"],["3m","3M"],["1y","1Y"],["all","All"]].map(([v, l]) => (
              <button key={v} onClick={() => setPeriod(v)}
                style={{padding:"5px 12px",border:`1px solid ${period===v?"var(--accent)":"var(--border)"}`,
                  background: period===v ? "rgba(136,255,0,0.08)" : "var(--surface2)",
                  color: period===v ? "var(--accent)" : "var(--muted)",
                  borderRadius:4,cursor:"pointer",fontSize:11,fontFamily:"'Orbitron',sans-serif",letterSpacing:1}}>
                {l}
              </button>
            ))}
          </div>
          {chartData.length > 1 ? (
            <div style={{height:200,marginBottom:14}}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{top:4,right:8,bottom:30,left:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a32" />
                  <XAxis dataKey="ts" type="number" scale="time" domain={["dataMin","dataMax"]}
                    stroke="#7a7a8a" tick={{fontSize:9}} angle={-30} textAnchor="end" height={36}
                    tickCount={Math.min(chartData.length, 7)}
                    tickFormatter={ts => { const d = new Date(ts); return `${d.getMonth()+1}/${d.getDate()}`; }} />
                  <YAxis stroke="#7a7a8a" tick={{fontSize:10}} width={42}
                    domain={[dataMin => Math.floor(dataMin - 2), dataMax => Math.ceil(dataMax + 2)]} />
                  <Tooltip contentStyle={{background:"#141417",border:"1px solid #2a2a32",borderRadius:8,fontSize:11}}
                    labelFormatter={ts => bwFmtDate(ts)} formatter={v => [`${bwFmtLbs(v)} lbs`, "Body weight"]} />
                  <Line type="monotone" dataKey="value" stroke="var(--accent)" strokeWidth={2.5}
                    dot={{fill:"var(--accent)",r:3}} activeDot={{r:5}} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{textAlign:"center",padding:"20px 0",color:"var(--muted)",fontSize:12}}>
              {list.length === 1 ? "Log another weigh-in to see your trend." : "No weigh-ins in this period."}
            </div>
          )}

          {/* Recent entries */}
          <div style={label}>RECENT WEIGH-INS</div>
          {[...list].reverse().slice(0, 6).map(e => (
            <div key={e.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
              padding:"6px 0",borderBottom:"1px solid rgba(136,255,0,0.06)",fontSize:13}}>
              <span style={{color:"var(--muted)"}}>{bwFmtDate(e.loggedAt)}</span>
              <span style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontWeight:600}}>{bwFmtLbs(e.weight)} lbs</span>
                <button onClick={() => remove(e.id)} title="Delete this weigh-in"
                  style={{background:"none",border:"none",color:"rgba(255,68,85,0.6)",cursor:"pointer",
                    fontSize:15,padding:"4px 8px",minWidth:32,minHeight:32}}>✕</button>
              </span>
            </div>
          ))}
        </>
      )}

      <div style={{fontSize:11,color:"var(--muted)",marginTop:16,lineHeight:1.6}}>
        Used to estimate your max on Weighted Pull-ups, Weighted Dips, and Weighted Back Extensions, including your older sets.
      </div>
    </div>
  );
}

function ProfilePage({ user, onDeleted, onLogout, onAvatarUpdate, onAliasUpdate }) {
  const isMobile = useIsMobile();
  const isArchAdmin = user.role === "arch_admin";
  const isAdmin = user.role === "arch_admin" || user.role === "admin";
  const isNewUser = user.role === "new_user";

  // Account section state
  const [deleteStep, setDeleteStep] = useState(false);
  const [pw1, setPw1]   = useState("");
  const [pw2, setPw2]   = useState("");
  const [delError, setDelError] = useState("");
  const [delLoading, setDelLoading] = useState(false);

  // Chapter state
  const [membership, setMembership]   = useState(undefined); // undefined=loading, null=none
  const [chapters, setChapters]       = useState([]);
  const [requests, setRequests]       = useState([]);
  const [chapterMsg, setChapterMsg]   = useState("");
  const [joiningId, setJoiningId]     = useState(null);
  const [chapLoading, setChapLoading] = useState(false);

  // Arch-admin chapter management state
  const [newChapName, setNewChapName]   = useState("");
  const [newChapDesc, setNewChapDesc]   = useState("");
  const [createError, setCreateError]   = useState("");
  const [allUsers, setAllUsers]         = useState([]);
  const [adminPickChap, setAdminPickChap] = useState(null); // chapter id being assigned admin
  const [adminPickUser, setAdminPickUser] = useState("");
  const [editChap, setEditChap]           = useState(null); // { id, name, description }
  const [editChapError, setEditChapError] = useState("");

  useEffect(() => {
    api.getMyMembership().then(m => setMembership(m)).catch(() => setMembership(null));
    api.getChapters().then(setChapters).catch(() => {});
    if (isArchAdmin) api.getAdminUsers().then(u => setAllUsers(u)).catch(() => {});
    // Clear chapter-membership and pending-new-user notifications on profile view
    // (the Admin/Arch-Admin panel, where pending approvals are shown, lives on this page)
    const token = api.getToken();
    if (token) {
      fetch(`${API_BASE}/api/badge/clear`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ types: ["chapter-membership", "new-member"] }),
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!membership || membership.status !== "approved") return;
    api.getChapterRequests(membership.chapter_id).then(setRequests).catch(() => {});
  }, [membership]);

  // Poll membership and pending requests every 30 seconds
  usePolling(() => {
    api.getMyMembership().then(m => setMembership(m)).catch(() => {});
    if (membership?.status === "approved") {
      api.getChapterRequests(membership.chapter_id).then(setRequests).catch(() => {});
    }
  }, 30000);

  const handleDelete = async () => {
    setDelError("");
    if (!pw1 || !pw2) { setDelError("Please enter your password twice to confirm."); return; }
    if (pw1 !== pw2)  { setDelError("Passwords do not match."); return; }
    setDelLoading(true);
    try {
      await api.deleteAccount({ password: pw1, passwordConfirm: pw2 });
      api.clearToken();
      onDeleted();
    } catch (err) { setDelError(err.message); }
    finally { setDelLoading(false); }
  };

  const handleJoin = async (chapterId) => {
    setJoiningId(chapterId);
    setChapterMsg("");
    try {
      await api.joinChapter(chapterId);
      const m = await api.getMyMembership();
      setMembership(m);
      setChapterMsg("Request submitted! Awaiting approval from the chapter admin.");
    } catch (err) { setChapterMsg(err.message); }
    finally { setJoiningId(null); }
  };

  const handleLeave = async () => {
    if (!window.confirm("Are you sure you want to leave your chapter?")) return;
    try {
      await api.leaveChapter(membership.chapter_id);
      setMembership(null);
      const updated = await api.getChapters();
      setChapters(updated);
    } catch (err) { setChapterMsg(err.message); }
  };

  const handleResolve = async (userId, action) => {
    try {
      await api.resolveChapterRequest(membership.chapter_id, userId, action);
      setRequests(r => r.filter(x => x.userId !== userId));
      if (action === "approve") {
        const m = await api.getMyMembership();
        setMembership(m);
      }
    } catch (err) { setChapterMsg(err.message); }
  };

  const handleRemoveMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the chapter?`)) return;
    try {
      await api.removeMember(membership.chapter_id, userId);
      const m = await api.getMyMembership();
      setMembership(m);
    } catch (err) { setChapterMsg(err.message); }
  };

  const handleCreateChapter = async () => {
    setCreateError("");
    if (!newChapName.trim()) { setCreateError("Name is required."); return; }
    setChapLoading(true);
    try {
      await api.createChapter({ name: newChapName.trim(), description: newChapDesc.trim() });
      setNewChapName(""); setNewChapDesc("");
      const updated = await api.getChapters();
      setChapters(updated);
    } catch (err) { setCreateError(err.message); }
    finally { setChapLoading(false); }
  };

  const handleDeleteChapter = async (id, name) => {
    if (!window.confirm(`Permanently delete chapter "${name}"? This cannot be undone. All members will be removed.`)) return;
    try {
      await api.deactivateChapter(id);
      const updated = await api.getChapters();
      setChapters(updated);
    } catch (err) { setChapterMsg(err.message); }
  };

  const handleEditChapter = async () => {
    setEditChapError("");
    if (!editChap.name.trim()) { setEditChapError("Name cannot be empty."); return; }
    try {
      await api.editChapter(editChap.id, { name: editChap.name.trim(), description: editChap.description.trim() });
      const updated = await api.getChapters();
      setChapters(updated);
      setEditChap(null);
    } catch (err) { setEditChapError(err.message); }
  };

  const handleAssignAdmin = async (chapterId) => {
    if (!adminPickUser) return;
    try {
      await api.assignChapterAdmin(chapterId, Number(adminPickUser));
      setAdminPickChap(null); setAdminPickUser("");
      const updated = await api.getChapters();
      setChapters(updated);
      setChapterMsg("Chapter admin assigned.");
    } catch (err) { setChapterMsg(err.message); }
  };

  // ── Shared card style ────────────────────────────────────────────────────────
  const cardStyle = {
    background:"linear-gradient(155deg,rgba(0,14,8,0.95),rgba(0,5,3,1))",
    border:"1px solid rgba(136,255,0,0.12)", borderRadius:4, padding: isMobile ? "20px 16px" : 28,
    position:"relative", overflow:"hidden", marginBottom:16,
  };
  const sectionTitle = (text) => (
    <div style={{fontFamily:"'Orbitron',sans-serif",fontSize:11,fontWeight:900,letterSpacing:3,
      color:"rgba(136,255,0,0.7)",textTransform:"uppercase",marginBottom:16}}>
      {text}
    </div>
  );
  const infoRow = (label, val) => (
    <div key={label} style={{display:"flex",justifyContent:"space-between",
      padding:"10px 0",borderBottom:"1px solid rgba(136,255,0,0.06)",fontSize:13}}>
      <span style={{color:"var(--muted)",fontFamily:"'Orbitron',sans-serif",fontSize:10,letterSpacing:1,textTransform:"uppercase"}}>{label}</span>
      <span style={{color:"var(--text)",fontWeight:600}}>{val}</span>
    </div>
  );

  return (
    <div className="page" style={{maxWidth:680}}>
      {/* Accent line */}
      <div style={{position:"fixed",top:0,left:0,right:0,height:1,zIndex:10,
        background:"linear-gradient(90deg,#88ff00,#aaffee,#88ff00)",
        backgroundSize:"300% 100%",opacity:0.4,animation:"sheen 2.5s ease-in-out infinite",pointerEvents:"none"}} />

      <div className="page-title">MY PROFILE</div>

      {/* ── Section 1: Account Info ── */}
      <div style={cardStyle}>
        <div style={{position:"absolute",top:0,left:0,right:0,height:1,
          background:"linear-gradient(90deg,#88ff00,#aaff44)",backgroundSize:"300% 100%",
          opacity:0.5,animation:"sheen 2.5s ease-in-out infinite"}} />
        {sectionTitle("Account")}
        <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:20}}>
          {user.avatarUrl
            ? <AvatarPhoto src={user.avatarUrl} alt={user.displayName} size={52} />
            : <div className="avatar" style={{width:52,height:52,fontSize:18}}>{initials(user.displayName)}</div>
          }
          <div>
            <div style={{fontWeight:700,fontSize:16,marginBottom:2}}>{user.displayName}</div>
            <div style={{color:"var(--muted)",fontSize:12}}>{user.email}</div>
          </div>
        </div>
        <AvatarUpload user={user} onUpdate={onAvatarUpdate} />

        {/* Chat Alias Section — hidden for pending accounts; it only affects
            the global chat, which they can't access until approved */}
        {!isNewUser && <ChatAliasSection user={user} onUpdate={onAliasUpdate || (() => {})} />}
        {[
          ["First Name",   user.firstName],
          ["Last Name",    user.lastName],
          ["Email",        user.email],
          ["Role",         isArchAdmin ? "Arch-Admin" : user.role === "admin" ? "Admin" : user.role === "new_user" ? "New Member (Pending Approval)" : "Member"],
          ["Member Since", new Date().toLocaleDateString("en-US",{month:"long",year:"numeric"})],
        ].map(([l,v]) => infoRow(l,v))}
        <div style={{display:"flex",gap:8,marginTop:20,flexWrap:"wrap"}}>
          <button className="btn" style={{flex:1,justifyContent:"center",padding:"10px 20px",
            color:"var(--muted)",borderColor:"rgba(136,255,0,0.15)",fontSize:11,letterSpacing:2}}
            onClick={onLogout}>LOG OUT</button>
          {!deleteStep ? (
            <button className="btn" style={{flex:1,justifyContent:"center",padding:"10px 20px",
              color:"#ff4455",borderColor:"rgba(255,68,85,0.3)",fontSize:11,letterSpacing:2}}
              onClick={() => setDeleteStep(true)}>DELETE ACCOUNT</button>
          ) : (
            <div style={{width:"100%",background:"rgba(255,68,85,0.06)",border:"1px solid rgba(255,68,85,0.25)",
              borderRadius:4,padding:18,marginTop:8}}>
              <div style={{color:"#ff4455",fontSize:12,fontWeight:700,marginBottom:12,
                fontFamily:"'Orbitron',sans-serif",letterSpacing:1}}>⚠ CONFIRM ACCOUNT DELETION</div>
              <div style={{color:"var(--muted)",fontSize:12,marginBottom:14}}>
                This is permanent and cannot be undone. Enter your password twice to confirm.
              </div>
              <div style={{marginBottom:10}}>
                <div className="form-label" style={{marginBottom:5}}>Password</div>
                <input type="password" value={pw1} onChange={e=>setPw1(e.target.value)} style={{width:"100%",boxSizing:"border-box"}} />
              </div>
              <div style={{marginBottom:14}}>
                <div className="form-label" style={{marginBottom:5}}>Confirm Password</div>
                <input type="password" value={pw2} onChange={e=>setPw2(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&handleDelete()} style={{width:"100%",boxSizing:"border-box"}} />
              </div>
              {delError && <div style={{color:"#ff4455",fontSize:12,marginBottom:12,fontWeight:600}}>{delError}</div>}
              <div style={{display:"flex",gap:8}}>
                <button className="btn btn-ghost" style={{flex:1,justifyContent:"center",fontSize:10}}
                  onClick={()=>{setDeleteStep(false);setPw1("");setPw2("");setDelError("");}}>CANCEL</button>
                <button className="btn" disabled={delLoading}
                  style={{flex:1,justifyContent:"center",fontSize:10,color:"#ff4455",borderColor:"rgba(255,68,85,0.4)"}}
                  onClick={handleDelete}>{delLoading?"DELETING…":"CONFIRM DELETE"}</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Section 2: Chapter ── */}
      <div style={cardStyle}>
        <div style={{position:"absolute",top:0,left:0,right:0,height:1,
          background:"linear-gradient(90deg,#00ffcc,#88ff00)",backgroundSize:"300% 100%",
          opacity:0.4,animation:"sheen 3s ease-in-out infinite"}} />
        {sectionTitle("Chapter")}
        {chapterMsg && (
          <div style={{background:"rgba(136,255,0,0.07)",border:"1px solid rgba(136,255,0,0.2)",
            borderRadius:3,padding:"10px 14px",fontSize:13,marginBottom:14,color:"#88ff00"}}>
            {chapterMsg}
          </div>
        )}

        {membership === undefined ? (
          <div style={{color:"var(--muted)",fontSize:13}}>Loading…</div>
        ) : isNewUser ? (
          <div style={{color:"var(--muted)",fontSize:13}}>
            Your account is awaiting admin approval. You'll be able to request a chapter once you're approved.
          </div>
        ) : membership === null ? (
          /* No membership — show chapter directory */
          <div>
            <div style={{color:"var(--muted)",fontSize:13,marginBottom:16}}>
              You are not currently a member of any chapter. Request to join one below.
            </div>
            {chapters.length === 0 ? (
              <div style={{color:"var(--muted)",fontSize:13}}>No chapters have been created yet.</div>
            ) : chapters.map(c => (
              <div key={c.id} style={{border:"1px solid rgba(136,255,0,0.1)",borderRadius:4,
                padding:"14px 16px",marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14,marginBottom:2}}>{c.name}</div>
                  {c.description && <div style={{color:"var(--muted)",fontSize:12,marginBottom:4}}>{c.description}</div>}
                  <div style={{color:"var(--muted)",fontSize:11}}>
                    {c.member_count} member{c.member_count !== 1 ? "s" : ""}
                    {c.admin_name ? ` · Admin: ${c.admin_name}` : " · No admin assigned"}
                  </div>
                </div>
                <button className="btn btn-primary" style={{fontSize:11,padding:"7px 14px",letterSpacing:1}}
                  disabled={joiningId === c.id}
                  onClick={() => handleJoin(c.id)}>
                  {joiningId === c.id ? "REQUESTING…" : "REQUEST TO JOIN"}
                </button>
              </div>
            ))}
          </div>
        ) : membership.status === "pending" ? (
          /* Pending */
          <div>
            {infoRow("Chapter", membership.chapter_name)}
            {infoRow("Status", "Pending approval")}
            <div style={{color:"var(--muted)",fontSize:12,marginTop:12}}>
              Your request is awaiting approval from the chapter admin.
            </div>
          </div>
        ) : membership.status === "rejected" ? (
          /* Rejected */
          <div>
            {infoRow("Status", "Request not approved")}
            <div style={{color:"var(--muted)",fontSize:12,marginTop:8,marginBottom:16}}>
              Your previous request was not approved. You may request to join another chapter.
            </div>
            {chapters.map(c => (
              <div key={c.id} style={{border:"1px solid rgba(136,255,0,0.1)",borderRadius:4,
                padding:"14px 16px",marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14}}>{c.name}</div>
                  <div style={{color:"var(--muted)",fontSize:11}}>{c.member_count} members</div>
                </div>
                <button className="btn btn-primary" style={{fontSize:11,padding:"7px 14px"}}
                  disabled={joiningId===c.id} onClick={()=>handleJoin(c.id)}>
                  {joiningId===c.id?"REQUESTING…":"REQUEST TO JOIN"}
                </button>
              </div>
            ))}
          </div>
        ) : (
          /* Approved member */
          <div>
            {infoRow("Chapter", membership.chapter_name)}
            {infoRow("Your Role", membership.role === "admin" ? "Chapter Admin" : "Member")}
            {membership.description && infoRow("Description", membership.description)}

            {/* Member roster */}
            <div style={{marginTop:16,marginBottom:8,fontFamily:"'Orbitron',sans-serif",
              fontSize:10,letterSpacing:2,color:"var(--muted)"}}>MEMBERS</div>
            {(membership.members||[]).map(m => (
              <div key={m.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                padding:"8px 0",borderBottom:"1px solid rgba(136,255,0,0.05)"}}>
                <div>
                  <span style={{fontSize:13,fontWeight:600}}>{m.displayName}</span>
                  {m.role==="admin" && <span style={{fontSize:10,color:"#88ff00",marginLeft:8,
                    fontFamily:"'Orbitron',sans-serif",letterSpacing:1}}>ADMIN</span>}
                </div>
                {/* Chapter admin can remove non-admin members */}
                {membership.role==="admin" && m.role!=="admin" && m.id!==user.id && (
                  <button className="btn" style={{fontSize:10,padding:"3px 8px",color:"#ff4455",
                    borderColor:"rgba(255,68,85,0.3)"}}
                    onClick={()=>handleRemoveMember(m.id, m.displayName)}>REMOVE</button>
                )}
              </div>
            ))}

            {/* Pending requests — chapter admin only */}
            {membership.role==="admin" && requests.length > 0 && (
              <div style={{marginTop:20}}>
                <div style={{fontFamily:"'Orbitron',sans-serif",fontSize:10,letterSpacing:2,
                  color:"var(--muted)",marginBottom:10}}>PENDING REQUESTS</div>
                {requests.map(r => (
                  <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                    padding:"10px 0",borderBottom:"1px solid rgba(136,255,0,0.05)",gap:8,flexWrap:"wrap"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600}}>{r.displayName}</div>
                      <div style={{color:"var(--muted)",fontSize:11}}>{r.email}</div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button className="btn btn-primary" style={{fontSize:10,padding:"4px 10px"}}
                        onClick={()=>handleResolve(r.userId,"approve")}>APPROVE</button>
                      <button className="btn" style={{fontSize:10,padding:"4px 10px",color:"#ff4455",
                        borderColor:"rgba(255,68,85,0.3)"}}
                        onClick={()=>handleResolve(r.userId,"reject")}>REJECT</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Leave chapter — non-admin only */}
            {membership.role !== "admin" && (
              <button className="btn" style={{marginTop:20,fontSize:11,padding:"8px 16px",
                color:"var(--muted)",borderColor:"rgba(136,255,0,0.15)"}}
                onClick={handleLeave}>LEAVE CHAPTER</button>
            )}
          </div>
        )}
      </div>

      {/* ── Section 4: Arch-Admin Chapter Management ── */}
      {isArchAdmin && (
        <div style={cardStyle}>
          <div style={{position:"absolute",top:0,left:0,right:0,height:1,
            background:"linear-gradient(90deg,#ffaa00,#ff6600)",backgroundSize:"300% 100%",
            opacity:0.5,animation:"sheen 2s ease-in-out infinite"}} />
          {sectionTitle("Arch-Admin: Chapter Management")}

          {/* Create chapter */}
          <div style={{marginBottom:20}}>
            <div style={{fontFamily:"'Orbitron',sans-serif",fontSize:10,letterSpacing:2,
              color:"var(--muted)",marginBottom:10}}>CREATE NEW CHAPTER</div>
            <div style={{marginBottom:8}}>
              <div className="form-label" style={{marginBottom:4}}>Chapter Name</div>
              <input value={newChapName} onChange={e=>setNewChapName(e.target.value)}
                placeholder="e.g. Chapter of St. Joseph"
                style={{width:"100%",boxSizing:"border-box"}} />
            </div>
            <div style={{marginBottom:10}}>
              <div className="form-label" style={{marginBottom:4}}>Description (optional)</div>
              <input value={newChapDesc} onChange={e=>setNewChapDesc(e.target.value)}
                placeholder="Location or brief description"
                style={{width:"100%",boxSizing:"border-box"}} />
            </div>
            {createError && <div style={{color:"#ff4455",fontSize:12,marginBottom:8}}>{createError}</div>}
            <button className="btn btn-primary" style={{fontSize:11,padding:"8px 16px"}}
              disabled={chapLoading} onClick={handleCreateChapter}>
              {chapLoading ? "CREATING…" : "CREATE CHAPTER"}
            </button>
          </div>

          {/* Existing chapters */}
          {chapters.length > 0 && (
            <div>
              <div style={{fontFamily:"'Orbitron',sans-serif",fontSize:10,letterSpacing:2,
                color:"var(--muted)",marginBottom:10}}>ALL CHAPTERS</div>
              {chapters.map(c => (
                <div key={c.id} style={{border:"1px solid rgba(136,255,0,0.1)",borderRadius:4,
                  padding:"14px 16px",marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,flexWrap:"wrap"}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:14,marginBottom:2}}>{c.name}</div>
                      {c.description && <div style={{color:"var(--muted)",fontSize:12,marginBottom:4}}>{c.description}</div>}
                      <div style={{color:"var(--muted)",fontSize:11}}>
                        {c.member_count} member{c.member_count!==1?"s":""}
                        {c.admin_name ? ` · Admin: ${c.admin_name}` : " · No admin assigned"}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      <button className="btn btn-primary" style={{fontSize:10,padding:"5px 10px"}}
                        onClick={()=>{setEditChap(editChap?.id===c.id?null:{id:c.id,name:c.name,description:c.description||""});setEditChapError("");}}>
                        EDIT
                      </button>
                      <button className="btn btn-primary" style={{fontSize:10,padding:"5px 10px"}}
                        onClick={()=>setAdminPickChap(adminPickChap===c.id?null:c.id)}>
                        ASSIGN ADMIN
                      </button>
                      <button className="btn" style={{fontSize:10,padding:"5px 10px",color:"#ff4455",
                        borderColor:"rgba(255,68,85,0.3)"}}
                        onClick={()=>handleDeleteChapter(c.id,c.name)}>
                        DELETE
                      </button>
                    </div>
                  </div>
                  {/* Edit form */}
                  {editChap?.id===c.id && (
                    <div style={{marginTop:12,borderTop:"1px solid rgba(136,255,0,0.08)",paddingTop:12}}>
                      <div style={{marginBottom:8}}>
                        <div className="form-label" style={{marginBottom:4}}>Name</div>
                        <input value={editChap.name} onChange={e=>setEditChap(v=>({...v,name:e.target.value}))}
                          style={{width:"100%",boxSizing:"border-box"}} />
                      </div>
                      <div style={{marginBottom:10}}>
                        <div className="form-label" style={{marginBottom:4}}>Description</div>
                        <input value={editChap.description} onChange={e=>setEditChap(v=>({...v,description:e.target.value}))}
                          style={{width:"100%",boxSizing:"border-box"}} />
                      </div>
                      {editChapError && <div style={{color:"#ff4455",fontSize:12,marginBottom:8}}>{editChapError}</div>}
                      <div style={{display:"flex",gap:8}}>
                        <button className="btn btn-primary" style={{fontSize:11,padding:"7px 14px"}}
                          onClick={handleEditChapter}>SAVE</button>
                        <button className="btn btn-ghost" style={{fontSize:11,padding:"7px 14px"}}
                          onClick={()=>setEditChap(null)}>CANCEL</button>
                      </div>
                    </div>
                  )}
                  {/* Assign admin form */}
                  {adminPickChap===c.id && (
                    <div style={{marginTop:12,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                      <select value={adminPickUser} onChange={e=>setAdminPickUser(e.target.value)}
                        style={{flex:1,padding:"8px 10px",background:"var(--surface)",border:"1px solid var(--border)",
                          color:"var(--text)",borderRadius:3,fontSize:13}}>
                        <option value="">Select a user…</option>
                        {[...allUsers].sort((a,b)=>{
                          const la=(a.lastName||a.displayName||"").toLowerCase();
                          const lb=(b.lastName||b.displayName||"").toLowerCase();
                          return la<lb?-1:la>lb?1:0;
                        }).map(u => (
                          <option key={u.id} value={u.id}>{u.displayName} ({u.email})</option>
                        ))}
                      </select>
                      <button className="btn btn-primary" style={{fontSize:11,padding:"8px 14px"}}
                        disabled={!adminPickUser} onClick={()=>handleAssignAdmin(c.id)}>
                        CONFIRM
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Section 5: Admin Panel (admin and arch-admin only) ── */}
      {isAdmin && (
        <AdminSection currentUser={user} cardStyle={cardStyle} sectionTitle={sectionTitle} />
      )}

    </div>
  );
}

// ─── ADMIN SECTION ────────────────────────────────────────────────────────────
function AdminSection({ currentUser, cardStyle, sectionTitle }) {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [confirm, setConfirm] = useState(null);
  const isArchAdmin = currentUser.role === "arch_admin";

  const load = () => {
    setLoading(true);
    api.getAdminUsers()
      .then(data => setUsers(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (userId) => {
    try {
      await api.adminDeleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setConfirm(null);
    } catch (err) { setError(err.message); }
  };

  const handleSetRole = async (userId, role) => {
    try {
      const { user: updated } = await api.adminSetRole(userId, role);
      setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
    } catch (err) { setError(err.message); }
  };

  const handleApprove = async (userId) => {
    try {
      const { user: updated } = await api.adminApproveUser(userId);
      setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
    } catch (err) { setError(err.message); }
  };

  const roleBadge = (role) => {
    if (role === "arch_admin") return { label: "ARCH-ADMIN", color: "#ffcc00" };
    if (role === "admin")      return { label: "ADMIN",      color: "#88ff00" };
    if (role === "new_user")   return { label: "NEW USER",   color: "#ff8800" };
    return                            { label: "MEMBER",     color: "#556655" };
  };

  const canDelete = (u) => {
    if (u.id === currentUser.id) return false;
    if (u.role === "arch_admin") return false;
    if (currentUser.role === "admin" && u.role === "admin") return false;
    return true;
  };

  const sortByLastName = (a, b) => {
    const la = (a.lastName || a.displayName || "").toLowerCase();
    const lb = (b.lastName || b.displayName || "").toLowerCase();
    return la < lb ? -1 : la > lb ? 1 : 0;
  };

  // One user row — shared by the main list and the pending-approval box below it.
  // The name block has a floor width instead of shrinking to 0, so on narrow
  // screens the controls wrap onto their own line instead of overlapping the name.
  const renderUserRow = (u) => {
    const badge = roleBadge(u.role);
    const isMe = u.id === currentUser.id;
    return (
      <div key={u.id} style={{
        display:"flex", alignItems:"center", gap:12, padding:"12px 0",
        borderBottom:"1px solid rgba(136,255,0,0.05)",
        background: isMe ? "rgba(136,255,0,0.03)" : "transparent",
        flexWrap:"wrap",
      }}>
        <div className="avatar sm" style={{
          flexShrink:0,
          background: u.role === "arch_admin" ? "linear-gradient(135deg,#332200,#664400)"
                    : u.role === "admin"      ? "linear-gradient(135deg,#003322,#006644)"
                    : "linear-gradient(135deg,#001a10,#002e1a)",
          color: badge.color,
        }}>{initials(u.displayName)}</div>
        <div style={{flex:"1 1 160px", minWidth:160}}>
          <div style={{display:"flex", alignItems:"center", gap:8}}>
            <span style={{fontWeight:700, fontSize:13}}>{u.displayName}</span>
            {isMe && <span style={{fontSize:9,color:"var(--muted)",fontFamily:"'Orbitron',sans-serif",letterSpacing:1}}>YOU</span>}
          </div>
          <div style={{color:"var(--muted)",fontSize:11,marginTop:1}}>{u.email}</div>
        </div>
        <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", flexShrink:0}}>
          <div style={{fontSize:9,fontWeight:700,letterSpacing:1.5,padding:"3px 8px",whiteSpace:"nowrap",
            border:`1px solid ${badge.color}44`,borderRadius:2,
            color:badge.color,fontFamily:"'Orbitron',sans-serif"}}>{badge.label}</div>
          {u.role === "new_user" && (
            <button onClick={() => handleApprove(u.id)}
              style={{fontSize:9,padding:"3px 8px",cursor:"pointer",borderRadius:2,whiteSpace:"nowrap",
                background:"rgba(255,136,0,0.1)",border:"1px solid rgba(255,136,0,0.4)",
                color:"#ff8800",fontFamily:"'Orbitron',sans-serif",letterSpacing:1}}>
              APPROVE
            </button>
          )}
          {isArchAdmin && u.role !== "arch_admin" && u.role !== "new_user" && !isMe && (
            <button onClick={() => handleSetRole(u.id, u.role === "admin" ? "user" : "admin")}
              style={{fontSize:9,padding:"3px 8px",cursor:"pointer",borderRadius:2,whiteSpace:"nowrap",
                background:"rgba(136,255,0,0.06)",border:"1px solid rgba(136,255,0,0.2)",
                color:"var(--accent)",fontFamily:"'Orbitron',sans-serif",letterSpacing:1}}>
              {u.role === "admin" ? "DEMOTE" : "MAKE ADMIN"}
            </button>
          )}
          {canDelete(u) && (
            <button onClick={() => setConfirm({ userId: u.id, name: u.displayName })}
              style={{fontSize:9,padding:"3px 8px",cursor:"pointer",borderRadius:2,whiteSpace:"nowrap",
                background:"rgba(255,68,85,0.06)",border:"1px solid rgba(255,68,85,0.25)",
                color:"#ff4455",fontFamily:"'Orbitron',sans-serif",letterSpacing:1}}>
              DELETE
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{...cardStyle, borderColor:"rgba(255,204,0,0.2)"}}>
      <div style={{position:"absolute",top:0,left:0,right:0,height:1,
        background:"linear-gradient(90deg,#ffcc00,#88ff00,#ffcc00)",backgroundSize:"300% 100%",
        opacity:0.7,animation:"sheen 2.5s ease-in-out infinite"}} />
      {sectionTitle(isArchAdmin ? "Arch-Admin Panel" : "Admin Panel")}
      {error && <div style={{color:"#ff4455",fontSize:12,marginBottom:12,fontWeight:600}}>{error}</div>}
      <div style={{color:"var(--muted)",fontSize:11,marginBottom:14,fontFamily:"'Orbitron',sans-serif",letterSpacing:1}}>
        {users.length} {users.length === 1 ? "MEMBER" : "MEMBERS"}
        {users.some(u => u.role === "new_user") && (
          <span style={{color:"#ff8800"}}> · {users.filter(u => u.role === "new_user").length} PENDING APPROVAL</span>
        )}
      </div>
      {loading ? (
        <div style={{color:"var(--muted)",fontSize:12,textAlign:"center",padding:20,
          fontFamily:"'Orbitron',sans-serif",letterSpacing:2}}>LOADING…</div>
      ) : (
        <>
          {users.filter(u => u.role !== "new_user").sort(sortByLastName).map(renderUserRow)}
          {(() => {
            const pending = users.filter(u => u.role === "new_user").sort(sortByLastName);
            if (pending.length === 0) return null;
            return (
              <div style={{marginTop:20, padding:"14px 16px",
                border:"1px solid rgba(255,136,0,0.3)", borderRadius:4,
                background:"rgba(255,136,0,0.04)"}}>
                <div style={{fontFamily:"'Orbitron',sans-serif",fontSize:10,letterSpacing:2,
                  color:"#ff8800",fontWeight:700,marginBottom:8}}>
                  PENDING APPROVAL ({pending.length})
                </div>
                {pending.map(renderUserRow)}
              </div>
            );
          })()}
        </>
      )}
      {confirm && (
        <div style={{
          position:"fixed",inset:0,zIndex:1002,
          background:"rgba(0,0,0,0.6)",backdropFilter:"blur(2px)",
          display:"flex",alignItems:"center",justifyContent:"center",
        }} onClick={() => setConfirm(null)}>
          <div style={{width:340,background:"linear-gradient(155deg,rgba(0,14,8,0.99),rgba(0,5,3,1))",
            border:"1px solid rgba(255,68,85,0.3)",borderRadius:4,padding:28}}
            onClick={e => e.stopPropagation()}>
            <div style={{color:"#ff4455",fontSize:12,fontWeight:700,marginBottom:10,
              fontFamily:"'Orbitron',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>
              ⚠ Confirm Deletion
            </div>
            <div style={{color:"var(--text)",fontSize:13,marginBottom:20}}>
              Delete <strong>{confirm.name}</strong>? This will permanently remove their account, all lift logs, and all chat messages.
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn btn-ghost" style={{flex:1,justifyContent:"center",fontSize:10}}
                onClick={() => setConfirm(null)}>CANCEL</button>
              <button className="btn" style={{flex:1,justifyContent:"center",fontSize:10,
                color:"#ff4455",borderColor:"rgba(255,68,85,0.4)"}}
                onClick={() => handleDelete(confirm.userId)}>CONFIRM DELETE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ─── MEET PAGE ─────────────────────────────────────────────────────────────────
function MeetPage({ currentUser, onCallActive }) {
  const isMobile = useIsMobile();
  const [meetings,    setMeetings]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [view,        setView]        = useState("list"); // "list" | "create" | "call"
  const [opacity,     setOpacity]     = useState(1);

  const switchView = useCallback((next, extraWork) => {
    setOpacity(0);
    setTimeout(() => {
      extraWork?.();
      setView(next);
      setOpacity(1);
    }, 350);
  }, []);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [callToken,   setCallToken]   = useState(null);
  const [callRoom,    setCallRoom]    = useState(null);
  const [callRoomUrl, setCallRoomUrl] = useState(null);

  // Create form state
  const [title,       setTitle]       = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [allUsers,    setAllUsers]    = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [chapterUsers,setChapterUsers]= useState([]);
  const [myMembership,setMyMembership]= useState(null);
  const [creating,    setCreating]    = useState(false);
  const [search,      setSearch]      = useState("");
  const [confirmDeclineId, setConfirmDeclineId] = useState(null);

  useEffect(() => {
    api.getMeetings().then(setMeetings).catch(() => {}).finally(() => setLoading(false));
    api.get("/api/users").then(u => setAllUsers(u || [])).catch(() => {});
    api.getMyMembership().then(m => {
      if (m?.status === "approved") {
        setMyMembership(m);
        api.getChapterCommunityUsers(m.chapter_id).then(u => setChapterUsers(u || [])).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  usePolling(() => {
    if (view === "call") return;
    api.getMeetings().then(setMeetings).catch(() => {});
  }, 30000);

  const [joinError, setJoinError] = useState("");

  const joinMeeting = async (meeting) => {
    setJoinError("");
    // Request permissions at join time so the prompt is contextually expected
    try {
      const stream = await navigator.mediaDevices?.getUserMedia({ audio: true, video: true });
      stream?.getTracks().forEach(t => t.stop());
    } catch(e) {} // proceed even if denied — Daily will handle gracefully
    try {
      const data = await api.getMeetingToken(meeting.id);
      if (data.error) { setJoinError(data.error); return; }
      if (!data.token) {
        setJoinError("Daily.co is not configured yet. Add DAILY_API_KEY to Railway to enable calls.");
        return;
      }
      setCallToken(data.token); setCallRoom(data.roomName); setCallRoomUrl(data.roomUrl);
      setActiveMeeting(meeting);
      onCallActive?.(true);
      const main = document.querySelector('.main');
      if (main) { main.scrollTop = 0; main.style.setProperty('overflow', 'hidden', 'important'); }
      document.body.style.setProperty('overflow', 'hidden', 'important');
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      switchView("call");
    } catch (err) {
      setJoinError(err.message || "Could not join call. Please try again.");
      console.warn("joinMeeting:", err);
    }
  };

  const createMeeting = async () => {
    if (!title.trim() || !scheduledAt) return;
    setCreating(true);
    try {
      let inviteeIds = [...selectedIds];
      const data = await api.createMeeting({ title: title.trim(), scheduledAt: new Date(scheduledAt).getTime(), inviteeIds });
      setMeetings(prev => [...prev, data.meeting].sort((a,b) => a.scheduledAt - b.scheduledAt));
      setTitle(""); setScheduledAt(""); setSelectedIds(new Set()); setSearch(""); setView("list");
    } catch (err) { console.warn("createMeeting:", err); }
    finally { setCreating(false); }
  };

  const cancelMeeting = async (id) => {
    await api.cancelMeeting(id).catch(() => {});
    setMeetings(prev => prev.filter(m => m.id !== id));
  };

  const rsvp = async (id, status) => {
    await api.rsvpMeeting(id, status).catch(() => {});
    setMeetings(prev => prev.map(m => m.id === id ? { ...m, myStatus: status } : m));
  };

  const now = Date.now();
  const canJoin = (m) => true; // joinable any time once scheduled
  const fmtTime = (ts) => new Date(ts).toLocaleString("en-US", { weekday:"short", month:"short", day:"numeric", hour:"numeric", minute:"2-digit" });

  if (view === "call" && activeMeeting && callToken && callRoom)
    return (
      <div style={{
        opacity, transition:"opacity 0.35s ease",
        position:"absolute", top:0, left:0,
        width:"100%", height:"100dvh",
        overflow:"hidden",
      }}>
        <DailyCallScreen roomName={callRoom} roomUrl={callRoomUrl} token={callToken} meeting={activeMeeting} meetingId={activeMeeting.id} currentUser={currentUser} allUsers={allUsers} onLeave={async () => {
          onCallActive?.(false);
          const main = document.querySelector('.main');
          if (main) main.style.removeProperty('overflow');
          document.body.style.removeProperty('overflow');
          document.documentElement.style.removeProperty('overflow');
          document.querySelector('.main')?.style.removeProperty('overflow');
          const mid = activeMeeting.id;
          switchView("list", () => {
            setCallToken(null); setCallRoom(null); setCallRoomUrl(null);
            setActiveMeeting(null);
          });
          try {
            const result = await api.leaveMeeting(mid);
            if (result?.deleted) {
              setMeetings(prev => prev.filter(m => m.id !== mid));
            } else {
              api.getMeetings().then(setMeetings).catch(() => {});
            }
          } catch(e) {
            api.getMeetings().then(setMeetings).catch(() => {});
          }
        }} />
      </div>
    );

  if (view === "create") return (
    <div style={{opacity,transition:"opacity 0.35s ease"}}>
    <div className="page">
      <div className="page-title">SCHEDULE <span className="accentText">CALL</span></div>
      <div className="card">
        <div className="form-label" style={{marginBottom:6}}>Title</div>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Weekly Check-In" style={{width:"100%",boxSizing:"border-box",marginBottom:16}} />
        <div className="form-label" style={{marginBottom:6}}>Date & Time</div>
        <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} style={{width:"100%",boxSizing:"border-box",marginBottom:16,background:"var(--surface2)",color:"var(--text)",border:"1px solid var(--border)",borderRadius:4,padding:"8px 10px",fontSize:14}} />
        <div className="form-label" style={{marginBottom:8}}>Invite</div>
        {/* Quick-select buttons */}
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
          {currentUser.role === "arch_admin" && (
            <button className="btn" onClick={() => setSelectedIds(new Set(allUsers.filter(u => u.id !== currentUser.id).map(u => u.id)))}
              style={{fontSize:11,padding:"4px 12px"}}>All Brothers</button>
          )}
          {myMembership && chapterUsers.length > 0 && (
            <button className="btn" onClick={() => {
              const ids = new Set(selectedIds);
              chapterUsers.filter(u => (u.userId || u.id) !== currentUser.id).forEach(u => ids.add(u.userId || u.id));
              setSelectedIds(ids);
            }} style={{fontSize:11,padding:"4px 12px"}}>{myMembership.chapter_name || "My Chapter"}</button>
          )}
          {selectedIds.size > 0 && (
            <button className="btn" onClick={() => setSelectedIds(new Set())}
              style={{fontSize:11,padding:"4px 12px",color:"rgba(255,68,85,0.7)",borderColor:"rgba(255,68,85,0.2)"}}>Clear</button>
          )}
        </div>
        {/* Search */}
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name…"
          style={{width:"100%",boxSizing:"border-box",marginBottom:8,fontSize:13,padding:"6px 10px"}}
        />
        {/* User list */}
        <div style={{maxHeight:200,overflowY:"auto",border:"1px solid var(--border)",borderRadius:6,padding:8,marginBottom:16}}>
          {[...allUsers]
            .sort((a,b)=>{
              const la = (a.lastName || a.displayName.split(" ").slice(-1)[0] || "").toLowerCase();
              const lb = (b.lastName || b.displayName.split(" ").slice(-1)[0] || "").toLowerCase();
              return la < lb ? -1 : la > lb ? 1 : 0;
            })
            .filter(u => u.id !== currentUser.id)
            .filter(u => !search.trim() || u.displayName.toLowerCase().includes(search.trim().toLowerCase()))
            .map(u => (
              <div key={u.id} onClick={() => setSelectedIds(prev => { const n = new Set(prev); n.has(u.id) ? n.delete(u.id) : n.add(u.id); return n; })}
                style={{display:"flex",alignItems:"center",gap:10,padding:"8px 4px",cursor:"pointer",borderBottom:"1px solid rgba(136,255,0,0.05)"}}>
                <div style={{width:18,height:18,borderRadius:3,border:"1px solid var(--border)",background:selectedIds.has(u.id)?"var(--accent)":"transparent",flexShrink:0}} />
                <div className="avatar sm" style={{width:28,height:28,fontSize:9}}>{initials(u.displayName)}</div>
                <span style={{fontSize:14}}>{u.displayName}</span>
              </div>
            ))}
        </div>
        <div style={{display:"flex",gap:8}}>
          <button className="btn btn-primary" onClick={createMeeting} disabled={creating||!title.trim()||!scheduledAt} style={{flex:1}}>{creating?"Scheduling…":"Schedule Call"}</button>
          <button className="btn" onClick={() => setView("list")} style={{padding:"0 16px"}}>Cancel</button>
        </div>
      </div>
    </div>
    </div>
  );

  return (
    <div style={{opacity,transition:"opacity 0.35s ease"}}>
    <div className="page">
      <div className="page-title">MEET</div>
      <div className="page-sub">&ldquo;For where there are two or three gathered together in my name, there am I in the midst of them.&rdquo; &mdash; Matthew 18:20</div>
      <button className="btn btn-primary" onClick={() => setView("create")} style={{marginBottom:20,width:"100%",fontSize:13,padding:"10px 0"}}>+ Schedule a Call</button>
      {joinError && <div style={{color:"rgba(255,68,85,0.8)",fontSize:13,marginBottom:12,padding:"8px 12px",border:"1px solid rgba(255,68,85,0.2)",borderRadius:4}}>{joinError}</div>}
      {loading && <div style={{color:"var(--muted)",textAlign:"center",padding:32,fontFamily:"'Orbitron',sans-serif",letterSpacing:2,fontSize:12}}>LOADING…</div>}
      {!loading && meetings.length === 0 && <div style={{color:"var(--muted)",textAlign:"center",padding:32,fontSize:13}}>No upcoming calls scheduled.</div>}
      {meetings.filter(m => m.myStatus !== "declined" || m.createdBy === currentUser.id).map(m => {
        const isCreator = m.createdBy === currentUser.id;
        const accepted  = m.myStatus === "accepted" || isCreator;
        const joinable  = canJoin(m) && accepted;
        const started   = now >= m.scheduledAt;
        const declining = confirmDeclineId === m.id;
        return (
          <div key={m.id} className="card" style={{marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
              <div>
                <div style={{fontWeight:700,fontSize:16,marginBottom:2}}>{m.title}</div>
                <div style={{fontSize:12,color:"var(--muted)"}}>{fmtTime(m.scheduledAt)}</div>
                <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>{isCreator?`You scheduled this`:`Scheduled by ${m.creatorName}`}</div>
              </div>
              {isCreator && <button onClick={() => cancelMeeting(m.id)} style={{background:"none",border:"none",color:"rgba(255,68,85,0.6)",cursor:"pointer",fontSize:18,padding:"0 4px"}}>✕</button>}
            </div>
            {m.invitees?.length > 0 && (
              <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:10}}>
                {m.invitees.map(inv => (
                  <div key={inv.userId} style={{padding:"2px 8px",borderRadius:999,fontSize:11,
                    background:inv.status==="accepted"?"rgba(136,255,0,0.1)":inv.status==="declined"?"rgba(255,68,85,0.08)":"var(--surface2)",
                    border:`1px solid ${inv.status==="accepted"?"rgba(136,255,0,0.3)":inv.status==="declined"?"rgba(255,68,85,0.2)":"var(--border)"}`,
                    color:inv.status==="accepted"?"var(--accent)":inv.status==="declined"?"rgba(255,68,85,0.7)":"var(--muted)"}}>
                    {inv.name.split(" ")[0]}
                  </div>
                ))}
              </div>
            )}
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              {joinable && <button className="btn btn-primary" onClick={() => joinMeeting(m)} style={{fontSize:12,padding:"6px 16px"}}>{started?"Join Call":"Join Early"}</button>}
              {!isCreator && m.myStatus==="invited" && !declining && <>
                <button className="btn" onClick={() => rsvp(m.id,"accepted")} style={{fontSize:12,padding:"6px 14px",borderColor:"rgba(136,255,0,0.3)",color:"var(--accent)"}}>Accept</button>
                <button className="btn" onClick={() => setConfirmDeclineId(m.id)} style={{fontSize:12,padding:"6px 14px",color:"rgba(255,68,85,0.7)",borderColor:"rgba(255,68,85,0.2)"}}>Decline</button>
              </>}
              {declining && <>
                <span style={{fontSize:12,color:"var(--muted)"}}>Are you sure?</span>
                <button className="btn" onClick={async () => { await rsvp(m.id,"declined"); setConfirmDeclineId(null); setMeetings(prev => prev.filter(x => x.id !== m.id)); }} style={{fontSize:12,padding:"6px 14px",color:"rgba(255,68,85,0.7)",borderColor:"rgba(255,68,85,0.2)"}}>Yes, Decline</button>
                <button className="btn" onClick={() => setConfirmDeclineId(null)} style={{fontSize:12,padding:"6px 14px"}}>Cancel</button>
              </>}
              {m.myStatus==="accepted"&&!joinable&&<div style={{fontSize:12,color:"var(--accent)",padding:"6px 0"}}>✓ Accepted</div>}
            </div>
          </div>
        );
      })}
    </div>
    </div>
  );
}

// ─── DAILY CALL SCREEN ─────────────────────────────────────────────────────────
function DailyCallScreen({ roomName, roomUrl, token, meeting, meetingId, currentUser, onLeave, allUsers }) {
  const [participants,  setParticipants]  = useState({});
  const [localStream,   setLocalStream]   = useState(null);
  const [micOn,         setMicOn]         = useState(true);
  const [camOn,         setCamOn]         = useState(false);
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [joined,        setJoined]        = useState(false);
  const [error,         setError]         = useState("");
  const isMobile = useIsMobile();
  const videoEls      = useRef({}).current;
  const pendingTracks = useRef({}).current;
  const trackSetters  = useRef({}).current;
  const audioLevels   = useRef({}).current; // sid -> current amplitude 0..1
  const callRef       = useRef(null);
  const localVideoRef = useRef(null);

  useEffect(() => {
    let destroyed = false;
    let didJoin = false;
    const setup = async () => {
      try {
        if (!DailyIframe) { setError("Daily.co SDK not loaded."); return; }
        // If an instance exists and we already joined it, reuse it
        if (window.__dailyCallInstance) {
          const state = window.__dailyCallInstance.meetingState();
          if (state === 'joined-meeting' || state === 'joining-meeting') {
            callRef.current = window.__dailyCallInstance;
            didJoin = true;
            return; // already in call, don't create a new one
          }
          try { window.__dailyCallInstance.destroy(); } catch(e) {}
          window.__dailyCallInstance = null;
        }
        const call = DailyIframe.createCallObject({
          audioSource: true,
          videoSource: true,
          subscribeToTracksAutomatically: true,
        });
        window.__dailyCallInstance = call;
        callRef.current = call;
        const update = () => {
          if (destroyed) return;
          const p = call.participants();
          // Deep copy to force React re-render on any participant change
          const copy = {};
          for (const [k, v] of Object.entries(p)) {
            // Skip the "local" alias key — Daily stores local participant under both
            // "local" and their session_id; we only want the session_id keyed version
            if (k === "local") continue;
            copy[k] = { ...v,
              tracks: {
                audio: v.tracks?.audio ? { ...v.tracks.audio } : undefined,
                video: v.tracks?.video ? { ...v.tracks.video } : undefined,
              }
            };
          }
          // Also add local under "local" key for self-view updates
          if (p.local) copy["local"] = { ...p.local };
          setParticipants(copy);
          const local = p.local;
          const localVideoTrack = local?.tracks?.video?.persistentTrack;
          if (localVideoTrack) {
            setLocalStream(prev => {
              // Only create new stream if the track actually changed
              if (prev?.getTracks()[0] === localVideoTrack) return prev;
              return new MediaStream([localVideoTrack]);
            });
          }
          // Directly attach video tracks to any registered video elements
          // Also retry after React renders (videoEls may be empty on first pass)
          const attachAll = () => {
            const fresh = call.participants();
            for (const [sid, el] of Object.entries(videoEls)) {
              const vtrack = fresh[sid]?.tracks?.video?.persistentTrack;
              if (el && vtrack && el.srcObject?.getTracks()[0] !== vtrack) {
                el.srcObject = new MediaStream([vtrack]);
                el.play().catch(() => {});
              }
            }
          };
          attachAll();
          setTimeout(() => { if (!destroyed) attachAll(); }, 50);
          setTimeout(() => { if (!destroyed) attachAll(); }, 500);
          // Track join count for debug overlay
          const others = Object.values(p).filter(x => !x.local);
          if (others.length > 0) joinedAtRef.current = joinedAtRef.current || Date.now();
        };
        const joinedAtRef = { current: null };
        call.on("joined-meeting", () => {
          joinedAtRef.current = Date.now();
          setJoined(true); update();
          const p2=call.participants();
          if(p2.local){setMicOn(!p2.local.tracks?.audio?.off);setCamOn(!p2.local.tracks?.video?.off);}
          // Audio-first: cap video bitrate so audio is never starved
          try { call.setBandwidth({ kbs: 150, trackConstraints: { frameRate: 15 } }); } catch(e) {}
          // Set RTCRtpSender priority: audio=high, video=low
          try {
            const pc = call.peerConnections?.();
            const connections = pc ? Object.values(pc) : [];
            for (const conn of connections) {
              if (!conn?.getSenders) continue;
              for (const sender of conn.getSenders()) {
                if (!sender.track) continue;
                const params = sender.getParameters();
                if (!params.encodings?.length) continue;
                const isAudio = sender.track.kind === 'audio';
                params.encodings = params.encodings.map(enc => ({
                  ...enc,
                  priority: isAudio ? 'high' : 'low',
                  networkPriority: isAudio ? 'high' : 'low',
                }));
                sender.setParameters(params).catch(() => {});
              }
            }
          } catch(e) {}
        });
        call.on("participant-joined",   update);
        call.on("participant-left",     update);
        call.on("participant-updated",  (e) => {
          update();
          if (e?.participant && !e.participant.local) {
            const sid    = e.participant.session_id;
            const vtrack = e.participant.tracks?.video;
            const vstate = vtrack?.state;
            const off    = vstate === "off" || vstate === "blocked" || vtrack?.off === true;
            if (off) {
              trackSetters[sid]?.(false);
              delete pendingTracks[sid];
              const el = videoEls[sid];
              if (el) { el.srcObject = null; el.load(); }
            } else if (vstate === "playable" && vtrack?.persistentTrack) {
              pendingTracks[sid] = vtrack.persistentTrack;
              trackSetters[sid]?.(true);
              const el = videoEls[sid];
              if (el) { el.srcObject = new MediaStream([vtrack.persistentTrack]); el.play().catch(()=>{}); }
            }
          }
        });
        call.on("track-started", (e) => {
          update();
          if (e?.track?.kind === "video" && e?.participant && !e.participant.local) {
            const sid = e.participant.session_id;
            pendingTracks[sid] = e.track;
            trackSetters[sid]?.(true); // tell the component to show video
            const el = videoEls[sid];
            if (el) {
              el.srcObject = new MediaStream([e.track]);
              el.play().catch(() => {});
            }
            setTimeout(() => {
              const el2 = videoEls[sid];
              if (el2 && pendingTracks[sid] === e.track) {
                el2.srcObject = new MediaStream([e.track]);
                el2.play().catch(() => {});
              }
            }, 100);
          }
        });
        call.on("track-stopped", (e) => {
          update();
          if (e?.track?.kind === "video" && e?.participant && !e.participant.local) {
            const sid = e.participant.session_id;
            delete pendingTracks[sid];
            trackSetters[sid]?.(false); // tell the component to show avatar
            const el = videoEls[sid];
            if (el) el.srcObject = null;
          }
        });
        call.on("active-speaker-change",e => { if (!destroyed) setActiveSpeaker(e?.activeSpeaker?.peerId || null); });
        call.on("left-meeting", () => {
          if (!destroyed) {
            destroyed = true;
            callRef.current = null;
            window.__dailyCallInstance = null;
            call.destroy();
            onLeave();
          }
        });
        call.on("error",                e => { if (!destroyed) setError(e.errorMsg || "Call error"); });
        await call.join({ url: roomUrl, token, startVideoOff: true, startAudioOff: false });
        didJoin = true;
      } catch(err) {
        if (!destroyed) setError(err.message || "Could not connect.");
        console.error("Daily setup:", err);
      }
    };
    setup();
    return () => {
      destroyed = true;
      // Only destroy if we actually joined — prevents StrictMode double-mount
      // from destroying an instance that's still initializing
      if (didJoin && callRef.current) {
        callRef.current.destroy();
        callRef.current = null;
        window.__dailyCallInstance = null;
      }
    };
  }, []);

  useEffect(() => { if (localVideoRef.current && localStream) localVideoRef.current.srcObject = localStream; }, [localStream]);

  // Wake lock + visibility change: keep audio alive during sleep
  useEffect(() => {
    let wakeLock = null;
    let destroyed = false;

    const acquireWakeLock = async () => {
      if (!navigator.wakeLock) return;
      try {
        wakeLock = await navigator.wakeLock.request('screen');
        wakeLock.addEventListener('release', () => {
          // Re-acquire if released by OS and call is still active
          if (!destroyed && !document.hidden) acquireWakeLock();
        });
      } catch (e) {
        // Wake lock not available — not fatal
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        // Going to background — nothing to do, audio session keeps running
        // RAF loops in ParticipantBubble pause automatically (browser stops RAF when hidden)
      } else {
        // Returning to foreground
        // Re-acquire wake lock
        acquireWakeLock();
        // Verify Daily connection still alive, rejoin if dropped
        const call = callRef.current;
        if (call) {
          try {
            const state = call.meetingState();
            if (state === 'left' || state === 'error') {
              // Connection dropped during sleep — rejoin
              call.join({ url: roomUrl, token });
            }
          } catch (e) {}
        }
        // Resume any suspended AudioContexts (iOS suspends them on background)
        if (window.__bshm_audio_contexts) {
          for (const ctx of window.__bshm_audio_contexts) {
            if (ctx.state === 'suspended') ctx.resume().catch(() => {});
          }
        }
      }
    };

    acquireWakeLock();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      destroyed = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (wakeLock) { try { wakeLock.release(); } catch (e) {} }
    };
  }, [roomUrl, token]);

  const toggleMic = async () => { if (!callRef.current) return; await callRef.current.setLocalAudio(!micOn); setMicOn(!micOn); };
  const toggleCam = async () => {
    if (!callRef.current) return;
    const next = !camOn;
    await callRef.current.setLocalVideo(next);
    setCamOn(next);
    setTimeout(() => {
      if (!callRef.current) return;
      const vtrack = callRef.current.participants().local?.tracks?.video?.persistentTrack;
      setLocalStream(vtrack ? new MediaStream([vtrack]) : null);
    }, 200);
  };
  const leave = async () => { if (callRef.current) await callRef.current.leave(); else onLeave(); };

  const [facingMode, setFacingMode] = useState("user");
  const switchCamera = async () => {
    if (!callRef.current || !camOn) return;
    const next = facingMode === "user" ? "environment" : "user";
    try {
      // Get a stream from the specific camera direction
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: next } }
      });
      const newTrack = stream.getVideoTracks()[0];
      // Tell Daily to use this track
      await callRef.current.setInputDevicesAsync({ videoDeviceId: newTrack.getSettings().deviceId });
      newTrack.stop(); // Daily will request its own stream with that device ID
      setFacingMode(next);
    } catch(e) {
      // exact constraint failed — try ideal instead
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: next }
        });
        const newTrack = stream.getVideoTracks()[0];
        await callRef.current.setInputDevicesAsync({ videoDeviceId: newTrack.getSettings().deviceId });
        newTrack.stop();
        setFacingMode(next);
      } catch(e2) {
        console.warn("switchCamera failed:", e2);
      }
    }
  };

  const remotes = Object.values(participants)
    .filter(p => !p.local)
    .filter((p, i, arr) => arr.findIndex(x => x.session_id === p.session_id) === i);

  const gridLayout = useMemo(() => {
    const n = remotes.length;
    if (n === 0) return { positions: [], D: 180 };

    const isMob = window.innerWidth <= 768;
    const vh = (window.visualViewport?.height || window.innerHeight);
    const availW = window.innerWidth - 28;
    // header ~45px + paddingBottom on grid ~96px + some breathing room
    const availH = vh - 45 - 96 - 20;

    const sq2 = Math.sqrt(2);
    const GAP_FRAC = 0.15;

    // Layouts in grid coords [a,b] for 1-9
    const DESKTOP_LAYOUTS = {
      1: [[0,0]],
      2: [[1,-1],[-1,1]],
      3: [[-1,-1],[1,-1],[0,0]],
      4: [[0,-2],[-1,-1],[1,-1],[0,0]],
      5: [[-2,-1],[0,-1],[2,-1],[-1,0],[1,0]],
      6: [[-1,-1],[1,-1],[3,-1],[-2,0],[0,0],[2,0]],
      7: [[0,-2],[2,-2],[-1,0],[1,0],[3,0],[0,2],[2,2]],
      8: [[-2,-1],[0,-1],[2,-1],[4,-1],[-3,0],[-1,0],[1,0],[3,0]],
      9: [[0,-2],[2,-2],[4,-2],[-1,0],[1,0],[3,0],[-2,2],[0,2],[2,2]],
    };
    const rot90cw = pts => pts.map(([a,b]) => [b,-a]);
    const MOBILE_LAYOUTS = {
      ...DESKTOP_LAYOUTS,
      5: rot90cw(DESKTOP_LAYOUTS[5]),
      6: rot90cw(DESKTOP_LAYOUTS[6]),
      8: rot90cw(DESKTOP_LAYOUTS[8]),
      9: rot90cw(DESKTOP_LAYOUTS[9]),
    };

    let layout;
    if (n <= 9) {
      layout = (isMob ? MOBILE_LAYOUTS : DESKTOP_LAYOUTS)[n];
    } else {
      // 10+ : standard grid, each row offset by 1 on diagonal
      layout = [];
      const cols = Math.ceil(Math.sqrt(n));
      for (let i = 0; i < n; i++) {
        const row = Math.floor(i / cols);
        const col = i % cols;
        layout.push([col * 2 + row, row]);
      }
    }

    // Find min distance between any two points
    let minD = Infinity;
    for (let i = 0; i < layout.length; i++)
      for (let j = i+1; j < layout.length; j++) {
        const da = layout[i][0]-layout[j][0], db = layout[i][1]-layout[j][1];
        const d = Math.sqrt(da*da+db*db);
        if (d < minD) minD = d;
      }
    if (layout.length === 1) minD = 1;

    const as = layout.map(p=>p[0]), bs = layout.map(p=>p[1]);
    const spanA = Math.max(...as)-Math.min(...as);
    const spanB = Math.max(...bs)-Math.min(...bs);

    // D*(spanA*(1+GAP_FRAC)/minD + 1) = availW
    const kA = spanA*(1+GAP_FRAC)/minD + 1;
    const kB = spanB*(1+GAP_FRAC)/minD + 1;
    const DfromW = availW / kA;
    const DfromH = availH / kB;
    const D = Math.max(40, Math.floor(Math.min(DfromW, DfromH)));
    const U = D*(1+GAP_FRAC)/minD;

    const pxPts = layout.map(([a,b]) => [a*U, b*U]);
    const pxAs = pxPts.map(p=>p[0]), pxBs = pxPts.map(p=>p[1]);
    const boxCX = (Math.min(...pxAs)+Math.max(...pxAs))/2;
    const boxCY = (Math.min(...pxBs)+Math.max(...pxBs))/2;

    const positions = pxPts.map(([px,py]) => ({
      x: px - boxCX,
      y: py - boxCY,
    }));

    const groupW = Math.max(...pxAs) - Math.min(...pxAs) + D;
    const groupH = Math.max(...pxBs) - Math.min(...pxBs) + D;

    return { positions, D, groupW, groupH };
  }, [remotes.length, window.innerWidth, window.innerHeight]);

  if (error) return (
    <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16}}>
      <div style={{color:"rgba(255,68,85,0.8)",fontSize:14,textAlign:"center",padding:"0 32px"}}>{error}</div>
      <button className="btn" onClick={onLeave}>Leave</button>
    </div>
  );

  const SPHERE_OVERLAY = <>
    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(circle at 50% 50%,transparent 50%,rgba(0,0,0,0.4) 68%,rgba(0,0,0,0.85) 100%)",pointerEvents:"none"}} />
    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"linear-gradient(155deg,rgba(255,255,255,0.28) 0%,rgba(255,255,255,0.06) 25%,transparent 42%)",pointerEvents:"none"}} />
    <div style={{position:"absolute",inset:0,borderRadius:"50%",boxShadow:"inset 0 1px 0 rgba(255,255,255,0.35),inset 1px 0 0 rgba(255,255,255,0.1)",pointerEvents:"none"}} />
    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"conic-gradient(from 198deg at 34% 24%, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.7) 22deg, rgba(255,255,255,0) 44deg, rgba(255,255,255,0) 360deg)",WebkitMaskImage:"radial-gradient(circle at 50% 50%,transparent 51%,black 57%,transparent 65%)",maskImage:"radial-gradient(circle at 50% 50%,transparent 51%,black 57%,transparent 65%)",pointerEvents:"none"}} />
    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 40% 28% at 33% 22%,rgba(255,255,255,0.22) 0%,transparent 62%)",pointerEvents:"none"}} />
    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 11% 8% at 31% 18%,rgba(255,255,255,1) 0%,transparent 100%)",pointerEvents:"none"}} />
    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 6% 4% at 44% 29%,rgba(200,230,255,0.6) 0%,transparent 100%)",pointerEvents:"none"}} />
    <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(ellipse 60% 18% at 50% 100%,rgba(0,0,0,0.38) 0%,transparent 65%)",pointerEvents:"none"}} />
  </>;

  return (
    <div style={{
      position:"absolute", inset:0,
      display:"flex", flexDirection:"column",
      overflow:"hidden",
    }}>
      {/* Header */}
      <div style={{padding:"12px 16px",borderBottom:"1px solid rgba(136,255,0,0.1)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
        <div style={{fontFamily:"'Orbitron',sans-serif",fontSize:12,letterSpacing:2,color:"var(--accent)"}}>{meeting.title}</div>
        {!joined && <div style={{fontSize:11,color:"var(--muted)"}}>Connecting…</div>}
      </div>
      {remotes.map(p => <ParticipantAudio key={p.session_id} participant={p} audioLevels={audioLevels} totalRemotes={remotes.length} />)}
      {/* Remote grid */}
      <div style={{flex:1,position:"relative",overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",paddingBottom:96}}>
        {remotes.length === 0 && joined && (
          <div style={{color:"var(--muted)",fontSize:13}}>Waiting for others to join…</div>
        )}
        <div style={{position:"relative",width:gridLayout.groupW||0,height:gridLayout.groupH||0}}>
          {remotes.map((p, i) => {
            const pos = gridLayout.positions[i];
            if (!pos) return null;
            return (
              <div key={p.session_id} style={{position:"absolute",
                left: (gridLayout.groupW||0)/2 + pos.x - gridLayout.D/2,
                top:  (gridLayout.groupH||0)/2 + pos.y - gridLayout.D/2}}>
                <ParticipantBubble participant={p} size={gridLayout.D} isSpeaking={activeSpeaker===p.session_id} sphereOverlay={SPHERE_OVERLAY} videoEls={videoEls} pendingTracks={pendingTracks} trackSetters={trackSetters} allUsers={allUsers} audioLevels={audioLevels} totalRemotes={remotes.length} />
              </div>
            );
          })}
        </div>
      </div>
      {/* Local self-view pip */}
      <div style={{position:"absolute",bottom:90,right:16,width:72,height:72,borderRadius:"50%",overflow:"hidden",
        border:"1px solid rgba(136,255,0,0.35)",boxShadow:"0 0 0 1px rgba(0,0,0,0.5),0 4px 12px rgba(0,0,0,0.7),0 0 10px rgba(136,255,0,0.2)",
        background:"var(--surface2)",zIndex:10}}>
        {camOn && localStream
          ? <video ref={localVideoRef} autoPlay muted playsInline style={{width:"100%",height:"100%",objectFit:"cover",transform:"scaleX(-1)"}} />
          : currentUser.avatarUrl
            ? <img src={currentUser.avatarUrl} alt={currentUser.displayName} crossOrigin="anonymous" style={{width:"100%",height:"100%",objectFit:"cover"}} />
            : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{fontFamily:"'Orbitron',sans-serif",fontSize:14,color:"var(--accent)",fontWeight:900}}>{initials(currentUser.displayName)}</span>
              </div>}
        {SPHERE_OVERLAY}
      </div>
      {/* Controls */}
      <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"12px 24px 20px",display:"flex",alignItems:"center",justifyContent:"center",gap:16}}>
        <CallButton active={micOn} onClick={toggleMic} label={micOn?"Mute":"Unmute"} icon={micOn ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="2" width="6" height="12" rx="3"/>
            <path d="M5 10a7 7 0 0 0 14 0"/><line x1="12" y1="17" x2="12" y2="21"/><line x1="8" y1="21" x2="16" y2="21"/>
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <line x1="2" y1="2" x2="22" y2="22"/>
            <path d="M18.89 13.23A7 7 0 0 0 19 12"/><path d="M5 10a7 7 0 0 0 11.06 5.78"/>
            <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12"/>
            <line x1="12" y1="17" x2="12" y2="21"/><line x1="8" y1="21" x2="16" y2="21"/>
          </svg>
        )} />
        <CallButton active={camOn} onClick={toggleCam} label={camOn?"Cam Off":"Cam On"} icon={
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2"/>
            {!camOn && <line x1="2" y1="2" x2="22" y2="22"/>}
          </svg>
        } />
        {<CallButton onClick={switchCamera} label="Flip" extraClass="mobile-only" icon={
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 7h-9"/><path d="M14 17H5"/><polyline points="17 4 20 7 17 10"/><polyline points="7 14 4 17 7 20"/>
          </svg>
        } active={false} />}
        <CallButton danger onClick={leave} label="Leave" icon={
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.42 19.42 0 0 1 4.43 9.6a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 3.34 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.4 9.9"/>
            <line x1="18" y1="6" x2="6" y2="18"/>
          </svg>
        } />
      </div>
    </div>
  );
}

function CallButton({ active, danger, onClick, label, icon, extraClass }) {
  return (
    <button onClick={onClick} className={extraClass || ""} style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,
      background:danger?"rgba(255,68,85,0.15)":active?"rgba(136,255,0,0.1)":"rgba(255,255,255,0.05)",
      border:`1px solid ${danger?"rgba(255,68,85,0.4)":active?"rgba(136,255,0,0.3)":"rgba(255,255,255,0.1)"}`,
      borderRadius:12,padding:0,cursor:"pointer",
      width:72, height:64,
      color:danger?"rgba(255,68,85,0.9)":active?"var(--accent)":"var(--muted)",
      transition:"all 0.15s", flexShrink:0}}>
      {icon}
      <span style={{fontSize:9,fontFamily:"'Orbitron',sans-serif",letterSpacing:1}}>{label.toUpperCase()}</span>
    </button>
  );
}

function ParticipantAudio({ participant, audioLevels, totalRemotes }) {
  const audioRef = useRef(null);
  const track    = participant.tracks?.audio?.persistentTrack;
  const sid      = participant.session_id;
  const useAnalyser = totalRemotes <= 6;

  useEffect(() => {
    if (!audioRef.current || !track) return;
    const stream = new MediaStream([track]);
    audioRef.current.srcObject = stream;
    audioRef.current.play().catch(() => {});

    if (!useAnalyser) return;

    let ctx, destroyed = false;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Register in global set so visibility handler can resume on foreground
      if (!window.__bshm_audio_contexts) window.__bshm_audio_contexts = new Set();
      window.__bshm_audio_contexts.add(ctx);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512; // 256 time-domain samples — fills full circle with detail
      analyser.smoothingTimeConstant = 0.5;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);

      audioLevels[sid] = { waveform: data, analyser };

      const tick = () => {
        if (destroyed) return;
        if (!document.hidden) analyser.getByteTimeDomainData(data);
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    } catch(e) {}

    return () => {
      destroyed = true;
      try { ctx?.close(); window.__bshm_audio_contexts?.delete(ctx); } catch(e) {}
      delete audioLevels[sid];
    };
  }, [track, sid, useAnalyser]);

  return <audio ref={audioRef} autoPlay playsInline style={{display:"none"}} />;
}

function ParticipantBubble({ participant, size, isSpeaking, sphereOverlay, videoEls, pendingTracks, trackSetters, allUsers, audioLevels, totalRemotes }) {
  const sid        = participant.session_id;
  const track      = participant.tracks?.video?.persistentTrack;
  const videoState = participant.tracks?.video?.state;
  const [hasActiveTrack, setHasActiveTrack] = useState(() => !!pendingTracks[sid]);
  const cameraOff  = !hasActiveTrack;

  // Register setter so track-started/stopped can update this component's state
  useEffect(() => {
    trackSetters[sid] = setHasActiveTrack;
    return () => { delete trackSetters[sid]; };
  }, [sid]);

  const registerRef = useCallback((el) => {
    if (el) {
      videoEls[sid] = el;
      const pending = pendingTracks[sid];
      if (pending) {
        el.srcObject = new MediaStream([pending]);
        el.play().catch(() => {});
        setHasActiveTrack(true);
      }
    } else {
      delete videoEls[sid];
    }
  }, [sid]);

  // Clear video element whenever hasActiveTrack goes false
  useEffect(() => {
    if (!hasActiveTrack) {
      const el = videoEls[sid];
      if (el) { el.srcObject = null; el.load(); }
    }
  }, [hasActiveTrack, sid]);

  useEffect(() => {
    const el = videoEls[sid];
    if (!el) return;
    // videoState=off/blocked always wins — clear regardless of whether track exists
    if (videoState === "off" || videoState === "blocked" || !videoState) {
      el.srcObject = null;
      el.load();
      setHasActiveTrack(false);
    } else if (track && (videoState === "playable" || videoState === "loading" || videoState === "interrupted")) {
      el.srcObject = new MediaStream([track]);
      el.play().catch(() => {});
      setHasActiveTrack(true);
    }
  }, [track, videoState, sid]);

  const showVideo  = hasActiveTrack;

  const name      = participant.user_name || "Brother";
  const matched   = allUsers?.find(u => u.displayName === name);
  const avatarUrl = matched?.avatarUrl || null;

  // Reticle sizing — slightly outside the bubble
  const pad       = Math.max(4, size * 0.06);
  const svgSize   = size + pad * 2;

  // Waveform ring — drive SVG path directly via DOM, no React re-renders
  const waveGlowRef  = useRef(null);
  const waveMidRef   = useRef(null);
  const waveSharpRef = useRef(null);
  const waveCoreRef  = useRef(null);
  const rafRef       = useRef(null);
  const useReactive  = totalRemotes <= 6;
  const N_POINTS     = 128; // more points = more peak detail

  useEffect(() => {
    if (!waveGlowRef.current) return;
    let destroyed = false;
    const svgCx  = svgSize / 2;
    const baseR  = size / 2 + pad * 0.5;
    const maxDisp = Math.max(6, size * 0.14); // dramatic displacement

    const tick = () => {
      if (destroyed) return;
      if (document.hidden) { rafRef.current = requestAnimationFrame(tick); return; }
      const entry    = audioLevels[sid];
      const waveform = entry?.waveform;
      let pathD = '';

      for (let i = 0; i < N_POINTS; i++) {
        const angle = (i / N_POINTS) * 2 * Math.PI - Math.PI / 2;
        let r = baseR;
        if (waveform) {
          const wIdx = Math.floor((i / N_POINTS) * waveform.length) % waveform.length;
          const v = (waveform[wIdx] - 128) / 128; // -1..1 centered
          r = baseR + v * maxDisp;
        }
        const x = svgCx + r * Math.cos(angle);
        const y = svgCx + r * Math.sin(angle);
        pathD += i === 0 ? `M${x.toFixed(1)},${y.toFixed(1)}` : `L${x.toFixed(1)},${y.toFixed(1)}`;
      }
      pathD += 'Z';
      waveGlowRef.current?.setAttribute('d', pathD);
      waveMidRef.current?.setAttribute('d', pathD);
      waveSharpRef.current?.setAttribute('d', pathD);
      waveCoreRef.current?.setAttribute('d', pathD);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      destroyed = true;
      cancelAnimationFrame(rafRef.current);
    };
  }, [sid, size, svgSize, pad, useReactive]);

  const glowSize1 = Math.round(size * 0.2);
  const glowSize2 = Math.round(size * 0.6);
  const glowSize3 = Math.round(size * 1.0);
  const glow = `0 0 ${glowSize1}px rgba(136,255,0,0.9), 0 0 ${glowSize2}px rgba(136,255,0,0.5), 0 0 ${glowSize3}px rgba(136,255,0,0.2)`;

  return (
    <div style={{display:"flex",flexDirection:"column-reverse",alignItems:"center",gap:6,width:size,alignSelf:"center"}}>
      <div style={{position:"relative",width:svgSize,height:svgSize,display:"flex",alignItems:"center",justifyContent:"center"}}>
        {/* Waveform ring — no static circle, just the live waveform layers */}
        <svg width={svgSize} height={svgSize} style={{position:"absolute",inset:0,pointerEvents:"none",overflow:"visible"}}>
          <defs>
            <filter id={`wf-blur-${sid}`} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation={Math.max(1.5, size*0.02)} result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
            <filter id={`wf-halo-${sid}`} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation={Math.max(4, size*0.05)}/>
            </filter>
          </defs>
          {/* Wide blurred halo layer */}
          <path ref={waveGlowRef}
            fill="none" stroke="rgba(136,255,0,0.35)"
            strokeWidth={Math.max(2, size*0.025)} strokeLinejoin="round"
            filter={`url(#wf-halo-${sid})`}/>
          {/* Mid glow */}
          <path ref={waveMidRef}
            fill="none" stroke="rgba(136,255,0,0.4)"
            strokeWidth={Math.max(1.5, size*0.015)} strokeLinejoin="round"
            filter={`url(#wf-blur-${sid})`}/>
          {/* Sharp bright core */}
          <path ref={waveSharpRef}
            fill="none" stroke="rgba(180,255,100,0.9)"
            strokeWidth={Math.max(0.8, size*0.008)} strokeLinejoin="round"/>
          {/* Invisible ref for core — same path, used for sync */}
          <path ref={waveCoreRef} fill="none" stroke="none"/>
        </svg>
        {/* Bubble itself */}
        <div style={{width:size,height:size,borderRadius:"50%",overflow:"hidden",position:"relative",
          boxShadow:glow,transition:"box-shadow 0.4s ease",background:"var(--surface2)",flexShrink:0}}>
          <video ref={registerRef} autoPlay playsInline muted={false}
            style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",
              display:showVideo?"block":"none"}} />
          {cameraOff && (avatarUrl
            ? <img src={avatarUrl} crossOrigin="anonymous" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}} alt={name} />
            : <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",
                fontFamily:"'Orbitron',sans-serif",fontWeight:900,color:"var(--accent)",fontSize:size*0.28}}>
                {initials(name)}
              </div>)}
          {sphereOverlay}
        </div>
      </div>
      <div style={{fontSize:10,fontFamily:"'Orbitron',sans-serif",letterSpacing:1,
        color:"var(--accent)",transition:"color 0.3s ease",
        textAlign:"center",maxWidth:size,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
        {name.split(" ")[0].toUpperCase()}
      </div>
    </div>
  );
}

export default function App() {
  const isMobile = useIsMobile();
  const [user, setUser]                         = useState(null);   // { id, firstName, lastName, email, displayName }
  const [page, setPage]                         = useState("workout");
  const [pressedId, setPressedId]               = useState(null); // mobile: instant active highlight on touch
  // Backdrops are always mounted (keep-alive). Visibility driven by page state.
  const [navExpanded, setNavExpanded]           = useState(true);
  const [mobileScreen, setMobileScreen]         = useState("global"); // "global" | "chapter" — for split pages on mobile
  const [hasMobileChapter, setHasMobileChapter] = useState(false); // true when user has an approved chapter
  const [loaded, setLoaded]                     = useState(false);
  const mainRef = useRef(null);
  const orbVideoRef  = useRef(null);
  const orbCanvasRef = useRef(null);
  const glbCanvasRef = useRef(null);
  useVideoCanvas(orbVideoRef, orbCanvasRef, { circular: true, visibleRef: null });

  // ── GLB orb renderer ──────────────────────────────────────────────
  useEffect(() => {
    const RAILWAY_URL = "https://bros-of-st-hyacinth-production.up.railway.app";
    const GLB_URL = `${RAILWAY_URL}/Hyacinth_Sphere.glb`;

    let animId;
    let renderer;

    function init() {
      const canvas = glbCanvasRef.current;
      if (!canvas) { setTimeout(init, 150); return; }

      // Measure the parent wrapper, which has explicit CSS dimensions (220×220).
      // The canvas itself reports 0×0 in Firefox/Safari until Three.js sizes it,
      // so reading canvas.offsetWidth directly is unreliable across browsers.
      const parent = canvas.parentElement;
      const W = (parent && parent.offsetWidth  > 10) ? parent.offsetWidth  : (canvas.offsetWidth  || 220);
      const H = (parent && parent.offsetHeight > 10) ? parent.offsetHeight : (canvas.offsetHeight || 220);
      if (W < 10) { setTimeout(init, 150); return; }

      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
      renderer.setSize(W, H);
      renderer.setClearColor(0x000000, 0);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 3.5;

      const scene = new THREE.Scene();
      const cam = new THREE.PerspectiveCamera(42, W / H, 0.1, 1000);
      cam.position.set(0, 0, 3.2);

      // Ambient — warm yellow-green tint
      const ambLight = new THREE.AmbientLight(0xccff44, 0.12);
      scene.add(ambLight);
      // Key — bright yellow-lime from top-front-right
      const keyLight = new THREE.PointLight(0xeeff44, 10.0, 20);
      keyLight.position.set(2, 3, 4);
      scene.add(keyLight);
      // Fill — deep green
      const fillLight = new THREE.PointLight(0x44cc00, 1.2, 20);
      fillLight.position.set(-2, -1, 2);
      scene.add(fillLight);
      // Rim — yellow-green from behind for edge shine
      const rimLight = new THREE.PointLight(0x88cc00, 1.5, 20);
      rimLight.position.set(0, -3, -3);
      scene.add(rimLight);
      // Top highlight — bright specular for Xbox orb top-left shine
      const topLight = new THREE.PointLight(0xffffff, 2.0, 10);
      topLight.position.set(-1, 4, 3);
      scene.add(topLight);

      const clock = new THREE.Clock();
      const loader = new GLTFLoader();
      loader.load(GLB_URL, (gltf) => {
        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const centre = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        model.position.sub(centre);
        model.scale.setScalar(2.0 / maxDim);
        model.rotation.set(0, -Math.PI / 2, 0);

        model.traverse(child => {
          if (!child.isMesh) return;
          const orig = child.material;
          const isTrans = orig && orig.name === 'Material.002';

          if (isTrans) {
            // Outer shell — increased opacity, still transparent
            child.material = new THREE.MeshStandardMaterial({
              color:             new THREE.Color(0.55, 0.95, 0.05),
              emissive:          new THREE.Color(0.18, 0.45, 0.0),
              emissiveIntensity: 0.6,
              metalness:         0.1,
              roughness:         0.02,
              transparent:       true,
              opacity:           0.35,
              side:              THREE.DoubleSide,
              depthWrite:        false,
            });
            // EdgesGeometry — hard edges only (cross contours), blurred via CSS
            const edgesGeo = new THREE.EdgesGeometry(child.geometry, 15);
            const edgesMat = new THREE.LineBasicMaterial({
              color:       0xccff00,
              transparent: true,
              opacity:     0.75,
              blending:    THREE.AdditiveBlending,
              depthWrite:  false,
              linewidth:   2,
            });
            const edgesMesh = new THREE.LineSegments(edgesGeo, edgesMat);
            child.add(edgesMesh);
          } else {
            // Inner orb — restored to reference values
            child.material = new THREE.MeshStandardMaterial({
              color:             new THREE.Color(0.30, 0.80, 0.0),
              emissive:          new THREE.Color(0.05, 0.20, 0.0),
              emissiveIntensity: 0.3,
              metalness:         0.1,
              roughness:         0.02,
              transparent:       false,
              opacity:           1.0,
              side:              THREE.DoubleSide,
            });
          }
        });

        scene.add(model);

        let mixer = null;
        if (gltf.animations && gltf.animations.length) {
          mixer = new THREE.AnimationMixer(model);
          gltf.animations.forEach(clip => mixer.clipAction(clip).play());
        }

        const FRAME_MS_ORB = 1000 / 30;
        let lastOrbFrame = 0;
        function animate(now) {
          animId = requestAnimationFrame(animate);
          // Correct renderer if parent dimensions have changed after init
          const p = canvas.parentElement;
          const cW = (p && p.offsetWidth  > 10) ? p.offsetWidth  : canvas.offsetWidth;
          const cH = (p && p.offsetHeight > 10) ? p.offsetHeight : canvas.offsetHeight;
          if (cW > 0 && cH > 0 && (cam.aspect !== cW / cH)) {
            renderer.setSize(cW, cH);
            cam.aspect = cW / cH;
            cam.updateProjectionMatrix();
          }
          if (now - lastOrbFrame < FRAME_MS_ORB) return;
          lastOrbFrame = now - ((now - lastOrbFrame) % FRAME_MS_ORB);
          const dt = clock.getDelta();
          if (mixer) mixer.update(dt);
          model.position.y = Math.sin(clock.elapsedTime * 0.9) * 0.06;
          renderer.render(scene, cam);
        }
        animate(0);
      }, undefined, err => console.warn("GLB load error:", err));
    }

    const t = setTimeout(init, 100);
    return () => {
      clearTimeout(t);
      if (animId) cancelAnimationFrame(animId);
      if (renderer) renderer.dispose();
    };
  }, []);


  const navWrapRef = useRef(null);
  const orbWrapRef = useRef(null);
  const swipeTouchRef = useRef(null); // { x, y } of touchstart

  const [currentTrack, setCurrentTrack] = useState(PERMANENT_TRACKS[0] ?? null);
  const [isPlaying, setIsPlaying]       = useState(false);
  const [inCall, setInCall]             = useState(false);

  // Push notifications — mobile only, only when logged in
  usePushNotifications(isMobile ? api.getToken() : null);

  // Mobile: center orb vertically relative to the full nav-wrap (all tabs including user/admin)
  useEffect(() => {
    if (!isMobile) return;
    const centerOrb = () => {
      const nav = navWrapRef.current;
      const orb = orbWrapRef.current;
      if (!nav || !orb) return;
      const navTop  = parseInt(getComputedStyle(nav).top, 10) || 62;
      const navH    = nav.offsetHeight;
      const orbH    = orb.offsetHeight;
      orb.style.top = (navTop + navH / 2 - orbH / 2) + "px";
    };
    centerOrb();
    // Re-center whenever the window resizes (orientation change etc.)
    window.addEventListener("resize", centerOrb);
    return () => window.removeEventListener("resize", centerOrb);
  }, [isMobile, user]); // re-run when user changes (admin tab appears/disappears)

  // On mount: try to restore session from stored JWT
  useEffect(() => {
    const token = api.getToken();
    if (!token) { setLoaded(true); return; }
    api.me()
      .then(({ user: u }) => setUser(u))
      .catch(() => api.clearToken())          // token expired/invalid — clear it
      .finally(() => setLoaded(true));

    store.get("player:lastTrackId").then(saved => {
      if (saved) {
        const track = PERMANENT_TRACKS.find(t => t.id === parseInt(saved));
        if (track) setCurrentTrack(track);
      }
    });
  }, []);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  useEffect(() => {
    if (currentTrack?.id) store.set("player:lastTrackId", String(currentTrack.id));
  }, [currentTrack]);

  const handleAuth = (u) => setUser(u);

  const handleLogout = () => {
    api.clearToken();
    setUser(null);
  };

  const handleDeleted = () => {
    setUser(null);
  };

  const handleSetPage = (id) => {
    setPage(id);
    setMobileScreen("global");
    setHasMobileChapter(false); // reset until new page reports its chapter status
    if (!navExpanded) setNavExpanded(false);
    if (id === "boards") clearBadge(["global-chat", "reaction"]);
    if (id === "meet")   clearBadge(["meeting"]);
    if (id === "profile") clearBadge(["chapter-chat"]);
  };

  // Clear notification badge for specific types when user views the relevant page
  const clearBadge = (types = ["global-chat"]) => {
    const token = api.getToken();
    if (token) {
      fetch(`${API_BASE}/api/badge/clear`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ types }),
      })
        .then(r => r.json())
        .then(data => {
          // Only clear the app icon badge if no unread counts remain across all types
          if (navigator.clearAppBadge && data.totalRemaining === 0) {
            navigator.clearAppBadge().catch(() => {});
          }
        })
        .catch(() => {});
    }
  };

  // Also clear badge if user is already on boards and the app comes back into focus
  useEffect(() => {
    if (page !== "boards") return;
    const onFocus = () => clearBadge(["global-chat", "reaction"]);
    window.addEventListener("focus", onFocus);
    clearBadge(["global-chat", "reaction"]);
    return () => window.removeEventListener("focus", onFocus);
  }, [page]);

  if (!loaded) return null;

  if (!user) return (
    <>
      <style>{css}</style>
      <AuthScreen onAuth={handleAuth} />
    </>
  );

  const username  = user.displayName;
  const isNewUser = user.role === "new_user";

  // Y2K chrome SVG nav icons
  const NavIcon = ({ id, active }) => {
    const c = active ? "#88ff00" : "#1a5540";
    const glow = active ? `drop-shadow(0 0 5px #88ff00cc) drop-shadow(0 0 12px #88ff0055)` : "none";
    const s = { filter: glow, transition: "filter 0.15s" };
    if (id === "workout") return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" style={s}>
        <line x1="6" y1="12" x2="18" y2="12"/>
        <rect x="1" y="10" width="4" height="4" rx="1"/>
        <rect x="19" y="10" width="4" height="4" rx="1"/>
        <rect x="8" y="7" width="3" height="10" rx="1"/>
        <rect x="13" y="7" width="3" height="10" rx="1"/>
      </svg>
    );
    if (id === "topcharts") return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" style={s}>
        <polyline points="22,6 13,15 8,10 2,18"/>
        <polyline points="18,6 22,6 22,10"/>
        <line x1="2" y1="21" x2="22" y2="21"/>
      </svg>
    );
    if (id === "boards") return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" style={s}>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        <line x1="9" y1="10" x2="15" y2="10"/>
        <line x1="9" y1="13" x2="13" y2="13"/>
      </svg>
    );
    if (id === "audio") return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" style={s}>
        <circle cx="12" cy="12" r="2"/>
        <path d="M12 2 a10 10 0 0 1 0 20 a10 10 0 0 1 0-20"/>
        <path d="M12 7 a5 5 0 0 1 0 10 a5 5 0 0 1 0-10"/>
      </svg>
    );
    return null;
  };

  // Pending "new user" accounts don't get the community-facing pages until an
  // admin/arch-admin approves them (see isNewUser above).
  const GATED_NAV_IDS = ["topcharts", "boards", "meet", "rule"];
  const navItems = [
    { id: "workout",   label: "Workout" },
    { id: "topcharts", label: "Top Charts" },
    { id: "boards",    label: "Chat" },
    { id: "meet",      label: "Meet" },
    { id: "audio",     label: "Bible" },
    { id: "rule",      label: "Rule" },
  ].filter(n => !isNewUser || !GATED_NAV_IDS.includes(n.id));

  return (
    <>
      <style>{css}</style>
      <div className="app-bg" />
      <div className="grid-stars" />
      {/* Landscape mode blocker */}
      <div className="landscape-block" style={{
        display:"none", position:"fixed", inset:0, zIndex:99999,
        background:"rgba(0,8,4,0.97)", flexDirection:"column",
        alignItems:"center", justifyContent:"center", gap:16,
      }}>
        <div style={{fontSize:36}}>↩</div>
        <div style={{fontFamily:"'Orbitron',sans-serif",fontSize:13,letterSpacing:2,color:"var(--accent)",textAlign:"center"}}>
          PLEASE ROTATE<br/>YOUR DEVICE
        </div>
      </div>
      <div className="app"
          onTouchStart={isMobile ? (e) => {
            const t = e.touches[0];
            // Only block swipe if touch starts inside an element explicitly set to scroll horizontally
            let el = e.target;
            let insideHScroll = false;
            while (el && el !== e.currentTarget) {
              const ox = window.getComputedStyle(el).overflowX;
              if ((ox === "auto" || ox === "scroll") && el.scrollWidth > el.clientWidth + 2) {
                insideHScroll = true; break;
              }
              el = el.parentElement;
            }
            swipeTouchRef.current = insideHScroll ? null : { x: t.clientX, y: t.clientY };
          } : undefined}
          onTouchEnd={isMobile ? (e) => {
            if (!swipeTouchRef.current) return;
            const dx = e.changedTouches[0].clientX - swipeTouchRef.current.x;
            const dy = e.changedTouches[0].clientY - swipeTouchRef.current.y;
            swipeTouchRef.current = null;
            if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
            const isSplitPage = hasMobileChapter && (page === "boards" || page === "topcharts");
            if (dx < 0) {
              // Swipe left
              if (navExpanded) {
                setNavExpanded(false);
                setMobileScreen("global");
              } else if (isSplitPage && mobileScreen === "global") {
                setMobileScreen("chapter");
              }
            } else {
              // Swipe right
              if (isSplitPage && mobileScreen === "chapter") {
                setMobileScreen("global");
              } else if (!navExpanded) {
                setNavExpanded(true);
              }
            }
          } : undefined}
        >
        <div className={`sidebar${navExpanded ? "" : " nav-collapsed"}`}>
          {/* Logo */}
          <div className="logo" style={{
            fontSize: isMobile ? "5.5vw" : (navExpanded ? "22px" : "15px"),
            whiteSpace: "nowrap",
          }}>
            {isMobile
              ? <span style={{display:"block", letterSpacing:"1.5px"}}>BROS OF ST. HYACINTH</span>
              : <>
                  <span className="logo-l1" style={{display:"block", letterSpacing: navExpanded ? "5px" : "2.5px"}}>BROS OF ST.</span>
                  <span className="logo-l2" style={{display:"block", letterSpacing: navExpanded ? "10.5px" : "5px"}}>HYACINTH</span>
                </>
            }
          </div>
          {/* Orb — click to toggle nav */}
          <div ref={orbWrapRef} className="xbox-orb-wrap" onClick={() => setNavExpanded(v => !v)}
            style={{}}>
            <div className="xbox-orb" />
            <div style={{position:"absolute",inset:0,borderRadius:"50%",background:"radial-gradient(circle at 50% 55%, transparent 30%, rgba(0,5,0,0.5) 62%, rgba(0,0,0,0.80) 100%)",zIndex:1,pointerEvents:"none"}} />
            {needsCanvasForAlpha
              ? <canvas ref={glbCanvasRef} style={{position:"absolute",inset:0,width:"100%",height:"100%",borderRadius:"50%",pointerEvents:"none",zIndex:2,filter:"blur(0.5px) drop-shadow(0 0 10px #aaff00cc) drop-shadow(0 0 25px #88ff0099) drop-shadow(0 0 55px #55dd0066) drop-shadow(0 0 90px #33aa0033)"}} />
              : <video ref={orbVideoRef} autoPlay muted loop playsInline
                  style={{position:"absolute",width:"343px",height:"343px",left:"50%",top:"50%",transform:"translate(-50%,-50%)",borderRadius:"50%",objectFit:"cover",pointerEvents:"none",zIndex:2,filter:"blur(0.5px) drop-shadow(0 0 10px #aaff00cc) drop-shadow(0 0 25px #88ff0099) drop-shadow(0 0 55px #55dd0066) drop-shadow(0 0 90px #33aa0033)"}}>
                  <source src={PR+"Hyacinth_Sphere.webm"} type="video/webm" />
                </video>
            }
            <div className="xbox-bubble" />
            <div className="xbox-bubble" />
            <div className="xbox-bubble" />
            <div className="xbox-bubble" />
            <div className="xbox-bubble" />
          </div>
          {/* Blade nav */}
          <div ref={navWrapRef} className={`nav-wrap${navExpanded ? "" : " retracted"}`}
            style={{}}>
            {navItems.map(n => {
              const isActive = (pressedId ?? page) === n.id;
              return (
                <div key={n.id} className={`nav-item-wrap${isActive ? " active-wrap" : ""}`}
                  onTouchStart={() => { setPressedId(n.id); handleSetPage(n.id); }}
                  onClick={() => handleSetPage(n.id)}>
                  <div className={`nav-item${isActive ? " active" : ""}`}>{n.label}</div>
                </div>
              );
            })}
            <div className={`nav-item-wrap${page === "profile" ? " active-wrap" : ""}`}
              onTouchStart={() => { setPressedId("profile"); handleSetPage("profile"); }}
              onClick={() => handleSetPage("profile")}>
              <div className={`nav-item${page === "profile" ? " active" : ""}`}>{username}</div>
            </div>
          </div>
        </div>
        <div ref={mainRef} className={`main${isMobile ? (navExpanded ? " nav-open" : " nav-closed") : ""}${isMobile && page === "boards" ? " chat-active" : ""}${inCall ? " in-call" : ""}`} style={{display:"flex", flexDirection:"column"}}>
          {page === "workout" && <div><WorkoutPage username={username} isNewUser={isNewUser} /></div>}
          {page === "topcharts" && !isNewUser && <div><TopChartsPage username={username} currentUser={user} mobileScreen={isMobile ? mobileScreen : null} onHasChapter={setHasMobileChapter} onGoToWorkout={() => { bodyWeightScrollPending = true; handleSetPage("workout"); }} /></div>}
          {page === "boards" && !isNewUser && <div><BoardPage username={username} currentUser={user} mobileScreen={isMobile ? mobileScreen : null} onHasChapter={setHasMobileChapter} /></div>}
          {(page === "meet" || inCall) && !isNewUser && (
            <div style={{
              visibility: page === "meet" ? "visible" : "hidden",
              pointerEvents: page === "meet" ? "auto" : "none",
              position: page === "meet" ? "static" : "absolute",
              width: "100%", height: "100%",
            }}>
              <MeetPage currentUser={user} onCallActive={setInCall} />
            </div>
          )}
          {page === "audio" && <AudioPage currentTrack={currentTrack} setCurrentTrack={setCurrentTrack} isPlaying={isPlaying} setIsPlaying={setIsPlaying} />}
          {page === "rule" && !isNewUser && <RulePage user={user} />}
          {page === "profile" && <ProfilePage user={user} onDeleted={() => { api.clearToken(); setUser(null); }} onLogout={() => { handleLogout(); setPage("workout"); }} onAvatarUpdate={(url) => setUser(u => ({ ...u, avatarUrl: url }))} onAliasUpdate={(alias) => setUser(u => ({ ...u, ...alias }))} />}
        </div>
        <FigureBackdrop variant="boards"    visible={page === "boards"}    isMobile={isMobile} />
        <FigureBackdrop variant="meet"      visible={page === "meet" && !inCall}      isMobile={isMobile} />
        <AudioFigureBackdrop               visible={page === "audio"}     isMobile={isMobile} />
        <FigureBackdrop variant="topcharts" visible={page === "topcharts"} isMobile={isMobile} />
        <RuleBackdrop visible={page === "rule"} isMobile={isMobile} />
        <WorkoutFigureBackdrop             visible={page === "workout"}   isMobile={isMobile} />
      </div>
      {currentTrack && <PlayerBar track={currentTrack} isPlaying={isPlaying} setIsPlaying={setIsPlaying} tracks={PERMANENT_TRACKS} setTrack={setCurrentTrack} navExpanded={navExpanded} />}
    </>
  );
}
